import React, { useState, useEffect, useMemo } from "react";
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import api, { login, verifyOTP, logout, setAuthToken, getInflows, getOutflows, addCashInflow, addCashOutflow, updateCashInflow, updateCashOutflow, deleteInflow, deleteOutflow, getDashboardData, getRecentTransactions, getAnalytics, getFinancialYearMonths, listUsers, createUserApi, updateUserApi, deleteUserApi, toggleUserOtpApi, toggleUserLoginApi, listInflowSourcesApi, listBankAccountsApi, listOutflowCategoriesApi, listOutflowSubcategoriesApi, createInflowSourceApi, updateInflowSourceApi, deleteInflowSourceApi, createBankAccountApi, updateBankAccountApi, deleteBankAccountApi, createOutflowCategoryApi, updateOutflowCategoryApi, deleteOutflowCategoryApi, createOutflowSubcategoryApi, updateOutflowSubcategoryApi, deleteOutflowSubcategoryApi, syncOutflowSubcategoryTreatmentApi, listRecurringItemsApi, createRecurringItemApi, updateRecurringItemApi, deleteRecurringItemApi, getRecurringRemindersApi, markRecurringItemPaidApi, skipRecurringOccurrenceApi, unskipRecurringOccurrenceApi, listBankLimitsApi, upsertBankLimitApi, checkBankLimitApi, getLimitsUsageApi, getBackupSettingsApi, updateBackupSettingsApi, runBackupNowApi, listBackupHistoryApi, downloadExportApi, importInflowsApi, importOutflowsApi, getInflowsPaginated, getOutflowsPaginated, insightsPreview, insightsRun, insightsPreviewInflow, insightsPreviewOutflow, listReportsApi, createReportApi, getReportApi, updateReportApi, deleteReportApi, deleteAllDataApi } from "./api";
import {
  ChartBarIcon,
  CurrencyDollarIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  EyeIcon,
  XMarkIcon,
  CheckIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChartPieIcon,
  Cog6ToothIcon,
  UserIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  FunnelIcon,
  DocumentChartBarIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import gypLogo from './assets/gyp-logo.png';
import { Button, Modal, Box, Typography, IconButton } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Papa from 'papaparse';
import * as Yup from 'yup';
import TanTable from './components/TanTable';
import FilterPopup from './components/FilterPopup';
import AdaptiveReportCard from './components/AdaptiveReportCard';
import { useColumnFilters } from './hooks/useColumnFilters';

const DASHBOARD_CONTRIBUTION_COLORS = [
  '#2563eb',
  '#14b8a6',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#0ea5e9',
  '#10b981',
  '#f97316',
  '#e11d48',
  '#6366f1',
  '#84cc16',
  '#06b6d4'
];

// Custom inline SVG icons for Insights, Filter, and Visualization
const InsightIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="currentColor" {...props}>
    <path d="M2.75 3c-.41 0-.75.34-.75.75v3.8c-.01 3.87-.02 8.47 0 11.47 0 1.1.9 1.98 2 1.98h17.25c.41 0 .75-.34.75-.75v-.23c0-.07-.03-.15-.1-.2L15 14.3c-.1-.08-.24-.07-.33.03l-3.4 3.65c-.09.1-.24.1-.34.02l-2.67-2.17c-.1-.09-.26-.07-.35.03l-2.97 3.55c-.05.06-.12.09-.2.09H4c-.28 0-.5-.22-.5-.5v-.52L8 13.06 10.59 15c.3.25.76.22 1.03-.07l4.63-5.02V12c0 .41.34.75.75.75s.75-.34.75-.75V8c0-.41-.34-.75-.75-.75h-4c-.41 0-.75.34-.75.75s.34.75.75.75h2.29L11 13.4l-2.52-1.98c-.32-.26-.8-.22-1.06.1l-3.92 4.7V3.76c0-.41-.34-.75-.75-.75Z" />
  </svg>
);

const FilterIconSvg = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
  </svg>
);

const VizIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    <path d="M12 16v5"/><path d="M16 14v7"/><path d="M20 10v11"/><path d="m22 3-8.646 8.646a.5.5 0 0 1-.708 0L9.354 8.354a.5.5 0 0 0-.707 0L2 15"/><path d="M4 18v3"/><path d="M8 14v7"/>
  </svg>
);

// Feature flags
const SHOW_LEGACY_INSIGHTS_REPORTS = false;
const INSIGHTS_DASHBOARD_ONLY = false;
const INSIGHTS_VIEW = {
  DASHBOARD: 'dashboard',
  CUSTOM: 'custom',
};

const NAV_ITEMS = [
  { key: "dashboard", label: "Dashboard", icon: ChartPieIcon },
  { key: "inflow", label: "Cash Inflow", icon: ArrowTrendingUpIcon },
  { key: "outflow", label: "Cash Outflow", icon: ArrowTrendingDownIcon },
  { key: "reports", label: "Reports", icon: DocumentTextIcon },
  ...(SHOW_LEGACY_INSIGHTS_REPORTS ? [
    { key: "insights", label: "Insights", icon: InsightIcon },
  ] : []),
  { key: "reminders", label: "Reminders", icon: CalendarIcon },
  { key: "settings", label: "Settings", icon: Cog6ToothIcon },
];

// Time period options
const TIME_PERIODS = [
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "this_quarter", label: "This Quarter" },
  { value: "this_year", label: "This Financial Year" },
  { value: "previous_fy", label: "Previous Financial Year" },
  { value: "custom_fy", label: "Select FY" },
  { value: "custom", label: "Select Period" }, // FY + Month selector
  { value: "custom_range", label: "Custom Period" }, // Free date range
  { value: "all", label: "All Time" },
];

// Generate financial years (FY 2000 to FY 2100)
const FINANCIAL_YEARS = Array.from({ length: 101 }, (_, i) => {
  const year = 2000 + i;
  return { value: year, label: `FY ${year}-${(year + 1).toString().slice(-2)}` };
});

// Months
const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

// Placeholder for financial year months (will be loaded from backend)
let FINANCIAL_YEAR_MONTHS = [];

// Constants
const INFLOW_SOURCES = [
  "Upwork Withdrawal- Qusai",
  "Upwork Withdrawal- Anas",
  "Upwork Withdrawal- Samreen",
  "Upwork Withdrawal- GYP",
  "Direct- Paypal",
  "Direct- Bank Transfer",
  "Loan Repayment",
  "Balance C/F from Previous FY",
  "Balance C/F from Last Month",
  "Direct- Bank Transfer ( Anas )",
  "Other"
];
const BANKS = ["GYP Bank", "Anas", "Qusai"];
const DEDUCTED_FROM = ["NA", "GYP Bank", "Anas", "Qusai"];
const BULK_EDIT_EMPTY_FORM = {
  date: "",
  reflecting_month: "",
  source: "",
  amount_rs: "",
  amount_usd: "",
  bank: "",
  reflecting_bank: "",
  request_inward: "",
  expense: "",
  sub_category: "",
  category: "",
  deducted_from: "",
  mode: "",
  nature: "",
  vendor: "",
  remark: "",
};

const BULK_EDIT_INFLOW_FIELDS = [
  { name: "date", label: "Date", type: "date", required: true },
  { name: "reflecting_month", label: "Reflecting Month", type: "reflecting_month", required: true },
  { name: "source", label: "Source", type: "select", required: true },
  { name: "amount_rs", label: "Amount in Rs", type: "number", required: true, step: "0.01" },
  { name: "amount_usd", label: "Amount in $", type: "number", step: "0.01" },
  { name: "bank", label: "Bank", type: "select", required: true },
  { name: "reflecting_bank", label: "Reflecting Bank", type: "select", required: true },
  { name: "request_inward", label: "Request for Inwards Remittance", type: "boolean", required: true },
  { name: "remark", label: "Remark", type: "text" },
];

const BULK_EDIT_OUTFLOW_FIELDS = [
  { name: "date", label: "Date", type: "date", required: true },
  { name: "reflecting_month", label: "Reflecting Month", type: "reflecting_month", required: true },
  { name: "expense", label: "Expense", type: "text", required: true },
  { name: "amount_rs", label: "Amount in Rs", type: "number", required: true, step: "0.01" },
  { name: "sub_category", label: "Subcategory", type: "subcategory", required: true },
  { name: "deducted_from", label: "Deducted From", type: "select", required: true },
  { name: "mode", label: "Mode", type: "select", required: true },
  { name: "nature", label: "Nature", type: "text" },
  { name: "vendor", label: "Vendor", type: "text" },
  { name: "remark", label: "Remark", type: "text" },
];
const MODES = ["Cash", "Online"];
const OUTFLOW_TREATMENT_EXPENSE = "expense";
const OUTFLOW_TREATMENT_PROFIT_WITHDRAWAL = "profit_withdrawal";
const OUTFLOW_TREATMENT_OPTIONS = [
  { value: OUTFLOW_TREATMENT_EXPENSE, label: "Expense" },
  { value: OUTFLOW_TREATMENT_PROFIT_WITHDRAWAL, label: "Profit Withdrawal" },
];
const SUBCATEGORY_CATEGORY = [
  { sub: "Team Salaries", cat: "COGS" },
  { sub: "HA Professional Fee", cat: "COGS" },
  { sub: "Partner Qusai", cat: "COGS" },
  { sub: "Partner Anas", cat: "COGS" },
  { sub: "Data Software/Tool/Outsource", cat: "COGS" },
  { sub: "Operating Software", cat: "Operating Expenses" },
  { sub: "Rent", cat: "Operating Expenses" },
  { sub: "Electricity Bill", cat: "Operating Expenses" },
  { sub: "Food & Water", cat: "Operating Expenses" },
  { sub: "Internet", cat: "Operating Expenses" },
  { sub: "Office Boy Salary", cat: "Operating Expenses" },
  { sub: "AC", cat: "Fixed Assets" },
  { sub: "Pc's and accessories", cat: "Fixed Assets" },
  { sub: "Furniture", cat: "Fixed Assets" },
  { sub: "Construction & Repairment", cat: "Construction & Repairment" },
  { sub: "Company Taxation", cat: "Accounting & Audit Expenses" },
  { sub: "CA Professional Fee", cat: "Accounting & Audit Expenses" },
  { sub: "Banking", cat: "Financial Expenses" },
  { sub: "Trip", cat: "Staff Welfare Expenses" },
  { sub: "Basic Expense + Purchase", cat: "Basic Expense" },
  { sub: "Security Deposit", cat: "Security Deposit" },
  { sub: "Other", cat: "Other" }
];

const INFLOW_REQUIRED_FIELDS = [
  { key: 'date', label: 'Date' },
  { key: 'reflecting_month', label: 'Reflecting Month' },
  { key: 'source', label: 'Source' },
  { key: 'amount_rs', label: 'Amount in Rs' },
  { key: 'bank', label: 'Bank' },
  { key: 'reflecting_bank', label: 'Reflecting Bank' },
  { key: 'request_inward', label: 'Request for Inwards Remittance' },
];

const INFLOW_OPTIONAL_FIELDS = [
  { key: 'amount_usd', label: 'Amount in $' },
  { key: 'remark', label: 'Remark' },
];

const OUTFLOW_REQUIRED_FIELDS = [
  { key: 'date', label: 'Date' },
  { key: 'reflecting_month', label: 'Reflecting Month' },
  { key: 'expense', label: 'Expense' },
  { key: 'amount_rs', label: 'Amount in Rs' },
];

const OUTFLOW_OPTIONAL_FIELDS = [
  { key: 'sub_category', label: 'Subcategory' },
  { key: 'category', label: 'Category' },
  { key: 'deducted_from', label: 'Deducted From' },
  { key: 'mode', label: 'Mode' },
  { key: 'nature', label: 'Nature' },
  { key: 'vendor', label: 'Vendor' },
  { key: 'remark', label: 'Remark' },
];

const inflowSchema = Yup.object().shape({
  date: Yup.string()
    .required('Date is required')
    .test('valid-date', 'Date must be in DD/MM/YYYY or YYYY-MM-DD format', function(value) {
      if (!value) return false;
      // Accept both DD/MM/YYYY and YYYY-MM-DD formats
      const dateRegex1 = /^\d{2}\/\d{2}\/\d{4}$/;
      const dateRegex2 = /^\d{4}-\d{2}-\d{2}$/;
      return dateRegex1.test(value) || dateRegex2.test(value);
    }),
  reflecting_month: Yup.string()
    .required('Reflecting Month is required')
    .test('valid-date', 'Reflecting Month must be in DD/MM/YYYY or YYYY-MM-DD format', function(value) {
      if (!value) return false;
      const dateRegex1 = /^\d{2}\/\d{2}\/\d{4}$/;
      const dateRegex2 = /^\d{4}-\d{2}-\d{2}$/;
      return dateRegex1.test(value) || dateRegex2.test(value);
    }),
  source: Yup.string()
    .required('Source is required')
    .min(1, 'Source cannot be empty')
    .max(255, 'Source is too long'),
  amount_rs: Yup.number()
    .typeError('Amount in Rs must be a valid number')
    .required('Amount in Rs is required')
    .positive('Amount in Rs must be positive')
    .max(999999999, 'Amount in Rs is too large'),
  bank: Yup.string()
    .required('Bank is required')
    .min(1, 'Bank cannot be empty')
    .max(255, 'Bank is too long'),
  request_inward: Yup.string()
    .required('Request for Inwards Remittance is required')
    .test('valid-boolean', 'Must be Yes/No, True/False, or 1/0', function(value) {
      if (!value) return false;
      const validValues = ['yes', 'no', 'true', 'false', '1', '0', 'y', 'n'];
      return validValues.includes(value.toLowerCase());
    }),
  amount_usd: Yup.number()
    .typeError('Amount in USD must be a valid number')
    .nullable()
    .positive('Amount in USD must be positive')
    .max(999999999, 'Amount in USD is too large'),
  remark: Yup.string()
    .nullable()
    .max(1000, 'Remark is too long'),
});

const outflowSchema = Yup.object().shape({
  date: Yup.string()
    .required('Date is required')
    .test('valid-date', 'Date must be in DD/MM/YYYY or YYYY-MM-DD format', function(value) {
      if (!value) return false;
      const dateRegex1 = /^\d{2}\/\d{2}\/\d{4}$/;
      const dateRegex2 = /^\d{4}-\d{2}-\d{2}$/;
      return dateRegex1.test(value) || dateRegex2.test(value);
    }),
  reflecting_month: Yup.string()
    .required('Reflecting Month is required')
    .test('valid-date', 'Reflecting Month must be in DD/MM/YYYY or YYYY-MM-DD format', function(value) {
      if (!value) return false;
      const dateRegex1 = /^\d{2}\/\d{2}\/\d{4}$/;
      const dateRegex2 = /^\d{4}-\d{2}-\d{2}$/;
      return dateRegex1.test(value) || dateRegex2.test(value);
    }),
  expense: Yup.string()
    .required('Expense is required')
    .min(1, 'Expense cannot be empty')
    .max(255, 'Expense is too long'),
  amount_rs: Yup.number()
    .typeError('Amount in Rs must be a valid number')
    .required('Amount in Rs is required')
    .positive('Amount in Rs must be positive')
    .max(999999999, 'Amount in Rs is too large'),
  sub_category: Yup.string()
    .transform(v => (v === '' ? null : v))
    .nullable()
    .max(255, 'Subcategory is too long'),
  category: Yup.string()
    .transform(v => (v === '' ? null : v))
    .nullable()
    .max(255, 'Category is too long'),
  deducted_from: Yup.string()
    .transform(v => (v === '' ? null : v))
    .nullable()
    .max(255, 'Deducted From is too long'),
  mode: Yup.string()
    .transform(v => (v === '' ? null : v))
    .nullable()
    .max(255, 'Mode is too long'),
  nature: Yup.string()
    .nullable()
    .max(255, 'Nature is too long'),
  vendor: Yup.string()
    .nullable()
    .max(255, 'Vendor is too long'),
  remark: Yup.string()
    .nullable()
    .max(1000, 'Remark is too long'),
});

const App = () => {
  // App state
  const [user, setUser] = useState(null);
  const [nav, setNav] = useState("dashboard");
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [timePeriod, setTimePeriod] = useState(() => {
    const saved = localStorage.getItem('timePeriod');
    return saved || "this_month";
  });
  const [customStart, setCustomStart] = useState(() => {
    return localStorage.getItem('customStart') || null;
  });
  const [customEnd, setCustomEnd] = useState(() => {
    return localStorage.getItem('customEnd') || null;
  });
  const [financialYearMonths, setFinancialYearMonths] = useState([]);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [inflows, setInflows] = useState([]);
  const [outflows, setOutflows] = useState([]);
  // Store original data for filtering
  const [originalInflows, setOriginalInflows] = useState([]);
  const [originalOutflows, setOriginalOutflows] = useState([]);
  // Server-side table states
  const [inflowPageSize, setInflowPageSize] = useState(() => {
    const v = Number(localStorage.getItem('inflowPageSize'));
    return Number.isFinite(v) && v > 0 ? v : 25;
  });
  const [inflowPage, setInflowPage] = useState(0); // zero-based
  const [inflowTotal, setInflowTotal] = useState(0);
  const [inflowSortBy, setInflowSortBy] = useState('date');
  const [inflowSortDir, setInflowSortDir] = useState('desc');
  const [inflowQuery, setInflowQuery] = useState('');
  const [inflowBankFilter, setInflowBankFilter] = useState('');
  const [inflowReflectingBankFilter, setInflowReflectingBankFilter] = useState('');
  const [inflowReflectingMonthFilter, setInflowReflectingMonthFilter] = useState('');
  const [inflowSourceFilter, setInflowSourceFilter] = useState('');
  const [inflowMinAmount, setInflowMinAmount] = useState('');
  const [inflowMaxAmount, setInflowMaxAmount] = useState('');
  const [inflowInwardFilter, setInflowInwardFilter] = useState(undefined);

  const [outflowPageSize, setOutflowPageSize] = useState(() => {
    const v = Number(localStorage.getItem('outflowPageSize'));
    return Number.isFinite(v) && v > 0 ? v : 25;
  });
  const [outflowPage, setOutflowPage] = useState(0);
  const [outflowTotal, setOutflowTotal] = useState(0);
  const [outflowSortBy, setOutflowSortBy] = useState('date');
  const [outflowSortDir, setOutflowSortDir] = useState('desc');
  const [outflowQuery, setOutflowQuery] = useState('');
  const [outflowCategoryFilter, setOutflowCategoryFilter] = useState('');
  const [outflowSubcategoryFilter, setOutflowSubcategoryFilter] = useState('');
  const [outflowModeFilter, setOutflowModeFilter] = useState('');
  const [outflowVendorFilter, setOutflowVendorFilter] = useState('');
  const [outflowMinAmount, setOutflowMinAmount] = useState('');
  const [outflowMaxAmount, setOutflowMaxAmount] = useState('');
  const [outflowReflectingMonthFilter, setOutflowReflectingMonthFilter] = useState('');
  const [outflowExpenseFilter, setOutflowExpenseFilter] = useState('');
  const [outflowRemarksFilter, setOutflowRemarksFilter] = useState('');
  const [inflowForm, setInflowForm] = useState({
    date: "",
    reflecting_month: "",
    source: "",
    amount_rs: "",
    amount_usd: "",
    bank: "",
    reflecting_bank: "",
    request_inward: true,
    invoice_file: null,
    remark: "",
  });
  // UI: toggle add forms in Inflow/Outflow
  const [showInflowForm, setShowInflowForm] = useState(false);
  const [showOutflowForm, setShowOutflowForm] = useState(false);
  
  // Mark-as-paid modal state
  const [showMarkPaidModal, setShowMarkPaidModal] = useState(false);
  const [markPaidMessage, setMarkPaidMessage] = useState('');
  
  // Filtered totals for inflows and outflows
  const [filteredInflowTotals, setFilteredInflowTotals] = useState({ inr: 0, usd: 0 });
  const [filteredOutflowTotals, setFilteredOutflowTotals] = useState({ inr: 0, withdrawals: 0 });
  
  // Dashboard metric popup modal state
  const [showMetricModal, setShowMetricModal] = useState(false);
  const [metricModalData, setMetricModalData] = useState({ type: '', title: '', data: [], loading: false });

  // Handle metric card clicks
  const handleMetricCardClick = async (metricType) => {
    setMetricModalData({ type: metricType, title: '', data: [], loading: true, totalInr: 0, totalUsd: 0 });
    setShowMetricModal(true);
    
    try {
      // Fetch ALL transactions for the current time period (not paginated)
      const opts = ((timePeriod === 'custom' || timePeriod === 'custom_range' || timePeriod === 'custom_fy') && customStart && customEnd) ? { start: customStart, end: customEnd } : {};
      const apiPeriod = (timePeriod === 'custom_range' || timePeriod === 'custom_fy') ? 'custom' : timePeriod;
      
      const [allInflowsRes, allOutflowsRes] = await Promise.all([
        getInflows(apiPeriod, opts),
        getOutflows(apiPeriod, opts)
      ]);
      
      const allInflows = allInflowsRes.data || [];
      const allOutflows = allOutflowsRes.data || [];
      const expenseOutflows = allOutflows.filter((item) => !isProfitWithdrawalRow(item));
      const withdrawalOutflows = allOutflows.filter((item) => isProfitWithdrawalRow(item));
      
      let title = '';
      let data = [];
      let totalInr = 0;
      let totalUsd = 0;
      
      switch (metricType) {
        case 'total_inflow':
          title = 'Total Inflow Transactions';
          data = allInflows.map(item => ({
            date: item.date,
            description: item.source || 'Inflow',
            amount_rs: item.amount_rs,
            amount_usd: item.amount_usd || 0,
            bank: item.bank,
            request_inward: item.request_inward ? 'Yes' : 'No',
            remark: item.remark
          }));
          totalInr = allInflows.reduce((sum, item) => sum + (parseFloat(item.amount_rs) || 0), 0);
          totalUsd = allInflows.reduce((sum, item) => sum + (parseFloat(item.amount_usd) || 0), 0);
          break;
          
        case 'total_outflow':
          title = 'Total Outflow Transactions';
          data = expenseOutflows.map(item => ({
            date: item.date,
            description: item.expense,
            amount_rs: item.amount_rs,
            category: item.category,
            sub_category: item.sub_category,
            mode: item.mode,
            vendor: item.vendor,
            remark: item.remark
          }));
          totalInr = expenseOutflows.reduce((sum, item) => sum + (parseFloat(item.amount_rs) || 0), 0);
          // No USD for outflows
          totalUsd = 0;
          break;

        case 'total_withdrawals':
          title = 'Withdrawal Transactions';
          data = withdrawalOutflows.map(item => ({
            date: item.date,
            description: item.expense,
            amount_rs: item.amount_rs,
            category: item.category,
            sub_category: item.sub_category,
            mode: item.mode,
            vendor: item.vendor,
            remark: item.remark
          }));
          totalInr = withdrawalOutflows.reduce((sum, item) => sum + (parseFloat(item.amount_rs) || 0), 0);
          // No USD for outflows
          totalUsd = 0;
          break;
          
        case 'sales_revenue':
          title = 'Sales Revenue Transactions';
          const salesInflows = allInflows.filter(item => {
            const sourceObj = inflowSources.find(s => s.name === item.source);
            return sourceObj && sourceObj.consider_for_sales;
          });
          data = salesInflows.map(item => ({
            date: item.date,
            description: item.source || 'Sales Revenue',
            amount_rs: item.amount_rs,
            amount_usd: item.amount_usd || 0,
            bank: item.bank,
            request_inward: item.request_inward ? 'Yes' : 'No',
            remark: item.remark
          }));
          totalInr = salesInflows.reduce((sum, item) => sum + (parseFloat(item.amount_rs) || 0), 0);
          totalUsd = salesInflows.reduce((sum, item) => sum + (parseFloat(item.amount_usd) || 0), 0);
          break;
          
        case 'other_income':
          title = 'Other Income Transactions';
          const otherInflows = allInflows.filter(item => {
            const sourceObj = inflowSources.find(s => s.name === item.source);
            return !sourceObj || !sourceObj.consider_for_sales;
          });
          data = otherInflows.map(item => ({
            date: item.date,
            description: item.source || 'Other Income',
            amount_rs: item.amount_rs,
            amount_usd: item.amount_usd || 0,
            bank: item.bank,
            request_inward: item.request_inward ? 'Yes' : 'No',
            remark: item.remark
          }));
          totalInr = otherInflows.reduce((sum, item) => sum + (parseFloat(item.amount_rs) || 0), 0);
          totalUsd = otherInflows.reduce((sum, item) => sum + (parseFloat(item.amount_usd) || 0), 0);
          break;
          
        default:
          title = 'Transactions';
          data = [];
          totalInr = 0;
          totalUsd = 0;
      }
      
      setMetricModalData({ type: metricType, title, data, loading: false, totalInr, totalUsd });
    } catch (error) {
      setMetricModalData({ type: metricType, title: 'Error', data: [], loading: false, totalInr: 0, totalUsd: 0 });
    }
  };

  // Build FY months list based on selected financial year
  const getFinancialYearMonthsFor = (selectedFY = null) => {
    const fyYear = selectedFY || selectedFinancialYear;
    // FY starts in April of the selected year
    const fyStart = new Date(fyYear, 3, 1); // April 1st of selected FY year
    const months = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(fyStart);
      d.setMonth(fyStart.getMonth() + i);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
      const label = d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
      months.push({ value, label });
    }
    return months;
  };

  // Load Bank Limits
  const loadBankLimits = async () => {
    try {
      const res = await listBankLimitsApi();
      setBankLimits(res?.data || []);
    } catch (e) {
      // Failed to load bank limits
    }
  };
  const [outflowForm, setOutflowForm] = useState({
    date: "",
    reflecting_month: "",
    expense: "",
    amount_rs: "",
    sub_category: "",
    category: "",
    deducted_from: "",
    mode: "",
    nature: "",
    vendor: "",
    invoice_file: null,
    remark: "",
  });

  // Generate FY months based on form date for reflecting month dropdown
  const getFormFYMonths = (dateStr) => {
    if (!dateStr) return getFinancialYearMonthsFor(new Date().getFullYear());
    const date = new Date(dateStr);
    const fyYear = date.getMonth() >= 3 ? date.getFullYear() : date.getFullYear() - 1;
    const months = getFinancialYearMonthsFor(fyYear);

    // At FY start (April), allow users to attribute entries back to the previous March.
    if (date.getMonth() === 3) {
      const previousMarch = new Date(date.getFullYear(), 2, 1);
      const value = `${previousMarch.getFullYear()}-${String(previousMarch.getMonth() + 1).padStart(2, '0')}-01`;
      if (!months.some((month) => month.value === value)) {
        months.unshift({
          value,
          label: previousMarch.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
        });
      }
    }

    return months;
  };
  
  const inflowFYMonths = getFormFYMonths(inflowForm.date);
  const outflowFYMonths = getFormFYMonths(outflowForm.date);
  
  // IST time utilities and live clock
  const getISTDate = () => new Date(new Date().toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
  const formatIST = (d) => {
    const weekday = d.toLocaleString('en-GB', { weekday: 'short', timeZone: 'Asia/Kolkata' });
    const day = String(d.getDate()).padStart(2, '0');
    const monthShort = d.toLocaleString('en-GB', { month: 'short', timeZone: 'Asia/Kolkata' });
    const year = d.getFullYear();
    const time = d.toLocaleTimeString('en-GB', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Asia/Kolkata' });
    return `${weekday}, ${day} ${monthShort} ${year} - ${time}`;
  };
  const [istNow, setIstNow] = useState(getISTDate());
  useEffect(() => {
    const id = setInterval(() => setIstNow(getISTDate()), 1000);
    return () => clearInterval(id);
  }, []);
  const istDisplay = useMemo(() => formatIST(istNow), [istNow]);

  // Dashboard period label: show exact dates for custom/custom_range
  const formatExactDate = (yyyyMmDd) => {
    if (!yyyyMmDd) return "";
    const [y, m, d] = yyyyMmDd.split('-').map(Number);
    const dt = new Date(Date.UTC(y, (m || 1) - 1, d || 1));
    const day = dt.getUTCDate();
    const monthName = dt.toLocaleString('en-GB', { month: 'long' });
    const year = dt.getUTCFullYear();
    return `${day} ${monthName} ${year}`;
  };
  // Inject simple header styling class for DataGrid
  const styleTagId = 'gyp-dg-style';
  useEffect(() => {
    if (!document.getElementById(styleTagId)) {
      const s = document.createElement('style');
      s.id = styleTagId;
      s.innerHTML = `.gyp-header { font-weight: 700; color: #111827; background: #f9fafb; }`;
      document.head.appendChild(s);
    }
  }, []);
  const [inflowError, setInflowError] = useState("");
  const [outflowError, setOutflowError] = useState("");
  const [analytics, setAnalytics] = useState({ inflow: 0, inflow_usd: 0, outflow: 0, withdrawals: 0, profit: 0 });
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedInflows, setSelectedInflows] = useState([]);
  const [selectedOutflows, setSelectedOutflows] = useState([]);
  const [inflowQueryPending, setInflowQueryPending] = useState('');
  const [outflowQueryPending, setOutflowQueryPending] = useState('');
  const [showInflowSelections, setShowInflowSelections] = useState(false);
  const [showOutflowSelections, setShowOutflowSelections] = useState(false);
  const [editingInflow, setEditingInflow] = useState(null);
  const [editingOutflow, setEditingOutflow] = useState(null);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkEditType, setBulkEditType] = useState("inflow");
  const [bulkEditSelectedFields, setBulkEditSelectedFields] = useState({});
  const [bulkEditForm, setBulkEditForm] = useState({ ...BULK_EDIT_EMPTY_FORM });
  const [bulkEditError, setBulkEditError] = useState("");
  const [bulkEditSaving, setBulkEditSaving] = useState(false);

  // Insights Section State
  const [insightFilters, setInsightFilters] = useState({
    filterType: 'inflow', // 'inflow' or 'outflow'
    field: 'source', // field to filter by
    value: '', // filter value
    dateRange: 'this_year', // date range
    groupBy: 'month' // how to group data
  });
  const [insightData, setInsightData] = useState([]);
  const [savedInsights, setSavedInsights] = useState([]);
  const [customDashboards, setCustomDashboards] = useState([
    { id: 'default', name: 'Default Dashboard', insights: [] }
  ]);
  const [activeDashboard, setActiveDashboard] = useState('default');
  // Insights: legacy preview state (kept for compatibility with existing UI parts)
  const [insightsPreviewRows, setInsightsPreviewRows] = useState([]);
  const [insightsPreviewTotal, setInsightsPreviewTotal] = useState(0);
  const [insightsPreviewPage, setInsightsPreviewPage] = useState(0);
  const [insightsPreviewPageSize, setInsightsPreviewPageSize] = useState(25);
  const [insightsSortField, setInsightsSortField] = useState('date');
  const [insightsSortDir, setInsightsSortDir] = useState('desc');
  const [insightsSeries, setInsightsSeries] = useState([]);
  const [insightsTotals, setInsightsTotals] = useState({});
  const [inflowGroupBy, setInflowGroupBy] = useState('month');
  const [outflowGroupBy, setOutflowGroupBy] = useState('month');
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [showCreateDashboard, setShowCreateDashboard] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState('');
  const showLegacyInsights = false;
  // Sidebar Dashboard submenu state and CRUD helpers
  const [sidebarDashOpen, setSidebarDashOpen] = useState(false);
  const [newSubName, setNewSubName] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renamingName, setRenamingName] = useState('');
  // Custom Reports State
  const [customReports, setCustomReports] = useState([]);
  const [showReportCreateModal, setShowReportCreateModal] = useState(false);
  const [newReportName, setNewReportName] = useState("");
  const [reportCreationType, setReportCreationType] = useState("inflow");
  const [isCreatingReport, setIsCreatingReport] = useState(false);

  const monthNameToIso = (monthNameStr) => {
    if (!monthNameStr) return null;
    const parts = monthNameStr.trim().split(/\s+/);
    if (parts.length !== 2) return monthNameStr;
    const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
    const monthIdx = monthNames.indexOf(parts[0]);
    if (monthIdx === -1) return monthNameStr;
    const year = parseInt(parts[1]);
    if (isNaN(year)) return monthNameStr;
    const monthNum = String(monthIdx + 1).padStart(2, '0');
    return `${year}-${monthNum}-01`;
  };

  const isoToMonthName = (isoStr) => {
    if (!isoStr) return null;
    const d = new Date(isoStr + 'T00:00:00.000Z');
    if (isNaN(d.getTime())) return isoStr;
    return d.toLocaleString("default", { month: "long", year: "numeric", timeZone: 'UTC' });
  };

  const getSavedPeriod = (report) => {
    const tags = report.tags || [];
    const periodTag = tags.find(t => t.startsWith('period:'));
    if (!periodTag) return null;
    const type = periodTag.replace('period:', '');
    const startTag = tags.find(t => t.startsWith('start:'));
    const endTag = tags.find(t => t.startsWith('end:'));
    return {
      type,
      start: startTag ? startTag.replace('start:', '') : null,
      end: endTag ? endTag.replace('end:', '') : null
    };
  };

  // Helper to convert TanStack filters to backend Insight rules
  const convertFiltersToInsightRules = (filters, type) => {
    const rules = [];
    Object.keys(filters).forEach(columnId => {
      const filter = filters[columnId];
      if (!filter) return;

      const field = `${type}.${columnId}`;
      const dataType = filter.dataType || (columnId === 'date' ? 'date' : 'text');

      // Handle structured filter object from useColumnFilters { dataType, rules: [{operator, value, values}] }
      if (Array.isArray(filter.rules) && filter.rules.length > 0) {
        filter.rules.forEach(rule => {
          if (!rule.operator) return;
          // Map frontend operators to backend ops
          const opMap = {
            equals: 'equals',
            contains: 'contains',
            starts_with: 'contains',
            ends_with: 'contains',
            not_contains: 'not_in',
            gte: 'gte',
            lte: 'lte',
            gt: 'gte',
            lt: 'lte',
            between: 'between',
            before: 'lte',
            after: 'gte'
          };
          const backendOp = opMap[rule.operator] || 'equals';

          if (dataType === 'text') {
            let vals = Array.isArray(rule.values) && rule.values.length > 0
              ? rule.values.filter(v => v != null && v !== '')
              : rule.value != null && rule.value !== ''
                ? [rule.value]
                : [];
            if (columnId === 'reflecting_month') {
              vals = vals.map(v => monthNameToIso(v)).filter(Boolean);
            }
            if (vals.length === 0) return;
            if (vals.length === 1) {
              rules.push({ field, op: backendOp, value: vals[0] });
            } else {
              rules.push({ field, op: 'in', value: vals });
            }
          } else if (dataType === 'number') {
            if (rule.operator === 'between') {
              rules.push({ field, op: 'between', value: [rule.value, rule.value2] });
            } else {
              const numVal = parseFloat(rule.value);
              if (!isNaN(numVal)) rules.push({ field, op: backendOp, value: numVal });
            }
          } else if (dataType === 'date') {
            if (rule.operator === 'between') {
              if (rule.dateFrom && rule.dateTo) {
                rules.push({ field, op: 'between', value: [rule.dateFrom, rule.dateTo] });
              }
            } else {
              if (rule.dateFrom) {
                rules.push({ field, op: backendOp, value: rule.dateFrom });
              }
            }
          }
        });
      } else if (filter.value != null && filter.value !== '') {
        // Simple single-value filter
        let val = filter.value;
        if (columnId === 'reflecting_month') {
          val = monthNameToIso(val);
        }
        if (val != null) {
          rules.push({ field, op: 'equals', value: val });
        }
      } else if (Array.isArray(filter) && filter.length > 0) {
        let vals = filter;
        if (columnId === 'reflecting_month') {
          vals = vals.map(v => monthNameToIso(v)).filter(Boolean);
        }
        if (vals.length > 0) {
          rules.push({ field, op: 'in', value: vals });
        }
      }
    });
    return rules;
  };

  const convertInsightRulesToFilters = (insightRules) => {
    const filters = {};
    if (!Array.isArray(insightRules)) return filters;

    insightRules.forEach(rule => {
      if (!rule.field) return;
      const parts = rule.field.split('.');
      const columnId = parts[parts.length - 1];
      
      // Determine dataType
      let dataType = 'text';
      if (columnId === 'amount_rs' || columnId === 'amount_usd') {
        dataType = 'number';
      } else if (columnId === 'date') {
        dataType = 'date';
      } else if (columnId === 'request_inward' || columnId === 'invoice_file') {
        dataType = 'boolean';
      }

      const op = rule.op;
      const val = rule.value;

      let filterRule = { dataType };

      if (dataType === 'text') {
        if (columnId === 'reflecting_month') {
          // Convert ISO dates back to localized month names
          if (op === 'in' && Array.isArray(val)) {
            const monthNames = val.map(v => isoToMonthName(v)).filter(Boolean);
            filterRule.rules = [{ operator: 'equals', values: monthNames }];
          } else {
            const monthName = isoToMonthName(val);
            if (monthName) {
              filterRule.rules = [{ operator: 'equals', value: monthName }];
            }
          }
        } else {
          if (op === 'in' && Array.isArray(val)) {
            filterRule.rules = [{ operator: 'equals', values: val }];
          } else if (op === 'not_in') {
            if (Array.isArray(val)) {
              filterRule.rules = [{ operator: 'not_contains', values: val }];
            } else {
              filterRule.rules = [{ operator: 'not_contains', value: val }];
            }
          } else {
            filterRule.rules = [{ operator: op === 'contains' ? 'contains' : 'equals', value: val }];
          }
        }
      } else if (dataType === 'number') {
        if (op === 'between' && Array.isArray(val)) {
          filterRule.rules = [{ operator: 'between', min: val[0], max: val[1], value: val[0], value2: val[1] }];
        } else {
          const opMap = { gte: 'greater_than', lte: 'less_than', equals: 'equals' };
          filterRule.rules = [{ operator: opMap[op] || 'equals', value: val }];
        }
      } else if (dataType === 'date') {
        if (op === 'between' && Array.isArray(val)) {
          filterRule.rules = [{ operator: 'between', dateFrom: val[0], dateTo: val[1] }];
        } else {
          const opMap = { gte: 'after', lte: 'before', equals: 'equals' };
          filterRule.rules = [{ operator: opMap[op] || 'equals', dateFrom: val }];
        }
      } else if (dataType === 'boolean') {
        filterRule.rules = [{ operator: 'equals', booleanValue: val }];
      }

      if (filterRule.rules) {
        filters[columnId] = filterRule;
      }
    });

    return filters;
  };

  const loadCustomReports = async () => {
    try {
      const res = await listReportsApi();
      const rawData = res.data || [];
      
      // Sort reports by their order:X tag if present
      const getReportOrder = (r) => {
        const orderTag = r.tags?.find(t => t.startsWith('order:'));
        return orderTag ? parseInt(orderTag.replace('order:', '')) : 9999;
      };
      
      rawData.sort((a, b) => getReportOrder(a) - getReportOrder(b));
      
      setCustomReports(rawData);

      // Also sync standard reports state with updated data
      const reportsData = rawData.map((r) => ({
        id: r.id,
        name: r.name,
        description: r.description,
        type: r.type,
        category: r.category,
        tags: r.tags || [],
        inflowRules: r.inflow_rules || [],
        outflowRules: r.outflow_rules || [],
        inflowGroupBy: r.inflow_group_by || 'month',
        outflowGroupBy: r.outflow_group_by || 'month',
        createdAt: r.created_at,
        updatedAt: r.updated_at,
      }));
      setReports(reportsData);
    } catch (err) {
      console.error("Failed to load reports:", err);
    }
  };

  const handleEditReportFilters = (report) => {
    const type = report.type; // 'inflow' or 'outflow'
    if (type !== 'inflow' && type !== 'outflow') {
      alert("Only Inflow and Outflow reports can be edited in the table view.");
      return;
    }

    setEditingReport(report);
    setReportCreationType(type);

    // Apply period
    const savedPeriod = getSavedPeriod(report);
    if (savedPeriod) {
      setTimePeriod(savedPeriod.type);
      localStorage.setItem('timePeriod', savedPeriod.type);
      if (savedPeriod.type === 'custom' || savedPeriod.type === 'custom_range') {
        setCustomStart(savedPeriod.start || '');
        setCustomEnd(savedPeriod.end || '');
      }
    } else {
      setTimePeriod('all');
      localStorage.setItem('timePeriod', 'all');
    }

    // Apply filters
    const rawRules = type === 'inflow' ? report.inflow_rules : report.outflow_rules;
    const rules = typeof rawRules === 'string' ? JSON.parse(rawRules) : (rawRules || []);
    const filters = convertInsightRulesToFilters(rules);

    if (type === 'inflow') {
      inflowColumnFilters.setFilters(filters);
      outflowColumnFilters.clearAllFilters();
      setNav('inflow');
    } else {
      outflowColumnFilters.setFilters(filters);
      inflowColumnFilters.clearAllFilters();
      setNav('outflow');
    }
  };

  const handleCancelEditReport = () => {
    setEditingReport(null);
    if (reportCreationType === 'inflow') {
      inflowColumnFilters.clearAllFilters();
    } else {
      outflowColumnFilters.clearAllFilters();
    }
    setNav('dashboard');
  };

  const handleCreateQuickReport = async () => {
    if (!newReportName.trim()) {
      alert("Please enter a report name");
      return;
    }
    setIsCreatingReport(true);
    try {
      const activeFilters = reportCreationType === 'inflow' ? inflowColumnFilters.filters : outflowColumnFilters.filters;
      const rules = convertFiltersToInsightRules(activeFilters, reportCreationType);
      
      const reportTags = ['dashboard_card', `period:${timePeriod}`];
      if ((timePeriod === 'custom' || timePeriod === 'custom_range' || timePeriod === 'custom_fy') && customStart && customEnd) {
        reportTags.push(`start:${customStart}`);
        reportTags.push(`end:${customEnd}`);
      }

      const payload = {
        name: newReportName.trim(),
        type: reportCreationType,
        category: editingReport ? (editingReport.category || 'general') : 'general',
        [reportCreationType === 'inflow' ? 'inflow_rules' : 'outflow_rules']: rules,
        tags: reportTags
      };

      if (editingReport) {
        await updateReportApi(editingReport.id, payload);
      } else {
        await createReportApi(payload);
      }

      setNewReportName("");
      setShowReportCreateModal(false);
      setEditingReport(null);
      await loadCustomReports();
      setNav('dashboard');
      // Scroll to custom reports section
      setTimeout(() => {
        const section = document.getElementById('custom-reports-section');
        if (section) section.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    } catch (err) {
      alert(err.userMessage || "Failed to save report");
    } finally {
      setIsCreatingReport(false);
    }
  };

  const handleDeleteReport = async (id) => {
    if (!window.confirm("Are you sure you want to delete this report?")) return;
    try {
      await deleteReportApi(id);
      await loadCustomReports();
    } catch (err) {
      alert("Failed to delete report");
    }
  };

  const handleResizeReport = async (id, width, height) => {
    const report = customReports.find(r => r.id === id);
    if (!report) return;

    const cleanTags = (report.tags || []).filter(
      t => !t.startsWith('width:') && !t.startsWith('height:')
    );
    const newTags = [...cleanTags, `width:${width}`, `height:${height}`];

    const payload = {
      name: report.name,
      type: report.type,
      category: report.category || 'general',
      [report.type === 'inflow' ? 'inflow_rules' : 'outflow_rules']: report.type === 'inflow'
        ? (typeof report.inflow_rules === 'string' ? JSON.parse(report.inflow_rules) : (report.inflow_rules || []))
        : (typeof report.outflow_rules === 'string' ? JSON.parse(report.outflow_rules) : (report.outflow_rules || []))
      ,
      tags: newTags
    };

    try {
      await updateReportApi(id, payload);
      await loadCustomReports();
    } catch (err) {
      console.error("Failed to resize report:", err);
    }
  };

  const handleTogglePinReport = async (id) => {
    const report = customReports.find(r => r.id === id);
    if (!report) return;

    const isPinned = report.tags?.includes('dashboard_card');
    let newTags;
    if (isPinned) {
      newTags = report.tags.filter(t => t !== 'dashboard_card');
    } else {
      newTags = [...(report.tags || []), 'dashboard_card'];
    }

    const payload = {
      name: report.name,
      type: report.type,
      category: report.category || 'general',
      [report.type === 'inflow' ? 'inflow_rules' : 'outflow_rules']: report.type === 'inflow'
        ? (typeof report.inflow_rules === 'string' ? JSON.parse(report.inflow_rules) : (report.inflow_rules || []))
        : (typeof report.outflow_rules === 'string' ? JSON.parse(report.outflow_rules) : (report.outflow_rules || []))
      ,
      tags: newTags
    };

    try {
      await updateReportApi(id, payload);
      await loadCustomReports();
    } catch (err) {
      console.error("Failed to toggle pin report:", err);
    }
  };

  const handleReportDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleReportDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    setCustomReports(prev => {
      const dashboardItems = prev.filter(r => r.tags?.includes('dashboard_card'));
      const nonDashboardItems = prev.filter(r => !r.tags?.includes('dashboard_card'));
      
      const nextDashboard = [...dashboardItems];
      const draggedItem = nextDashboard[draggedIndex];
      nextDashboard.splice(draggedIndex, 1);
      nextDashboard.splice(index, 0, draggedItem);
      
      return [...nextDashboard, ...nonDashboardItems];
    });
    setDraggedIndex(index);
  };

  const handleReportDragEnd = async () => {
    setDraggedIndex(null);
    try {
      for (let i = 0; i < customReports.length; i++) {
        const report = customReports[i];
        const oldOrderTag = report.tags?.find(t => t.startsWith('order:'));
        const newOrderTag = `order:${i}`;

        if (oldOrderTag !== newOrderTag) {
          const cleanTags = (report.tags || []).filter(t => !t.startsWith('order:'));
          const newTags = [...cleanTags, newOrderTag];

          const payload = {
            name: report.name,
            type: report.type,
            category: report.category || 'general',
            [report.type === 'inflow' ? 'inflow_rules' : 'outflow_rules']: report.type === 'inflow'
              ? (typeof report.inflow_rules === 'string' ? JSON.parse(report.inflow_rules) : (report.inflow_rules || []))
              : (typeof report.outflow_rules === 'string' ? JSON.parse(report.outflow_rules) : (report.outflow_rules || []))
            ,
            tags: newTags
          };

          await updateReportApi(report.id, payload);
        }
      }
      await loadCustomReports();
    } catch (err) {
      console.error("Failed to persist report order:", err);
    }
  };

  useEffect(() => {
    if (nav === 'dashboard' || nav === 'reports') {
      loadCustomReports();
    }
  }, [nav]);

  const SmallDualTable = ({ kind, card }) => {
    const [rows, setRows] = React.useState([]);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(card.pageSize || 10);
    const [sortField, setSortField] = React.useState('date');
    const [sortDir, setSortDir] = React.useState('desc');

    React.useEffect(() => {
      const load = async () => {
        setLoading(true);
        try {
          const payload = {
            period: card.period,
            root: kind === 'inflow'
              ? buildGroupFromRules(card.inflowRules || [], mapInflowUIRuleToBackend)
              : buildGroupFromRules(card.outflowRules || [], mapOutflowUIRuleToBackend),
            pagination: { limit: pageSize, offset: page * pageSize },
            sort: { field: sortField, dir: sortDir },
          };
          const res = kind === 'inflow' ? await insightsPreviewInflow(payload) : await insightsPreviewOutflow(payload);
          const pr = res.data || {};
          setRows((pr.items || []).map((r)=> ({ id: `${kind}-${r.id}`, ...r })));
          setTotal(pr.total || 0);
        } catch (e) { /* Card load error */ } finally { setLoading(false); }
      };
      load();
    }, [
      kind,
      page,
      pageSize,
      sortField,
      sortDir,
      card?.id,
      card?.updatedAt,
      JSON.stringify(card?.inflowRules || []),
      JSON.stringify(card?.outflowRules || []),
    ]);

    return (
      <DataGrid
        autoHeight
        rows={rows}
        columns={kind === 'inflow' ? [
          { field: 'date', headerName: 'Date', width: 110 },
          { field: 'source', headerName: 'Source', width: 140 },
          { field: 'bank', headerName: 'Bank', width: 120 },
          { field: 'amount_rs', headerName: 'Amount ()', width: 140, renderCell: (p) => `${(p.value || 0).toLocaleString()}` },
        ] : [
          { field: 'date', headerName: 'Date', width: 110 },
          { field: 'expense', headerName: 'Expense', width: 160 },
          { field: 'sub_category', headerName: 'Subcategory', width: 140 },
          { field: 'deducted_from', headerName: 'Deducted From', width: 140 },
          { field: 'amount_rs', headerName: 'Amount ()', width: 140, renderCell: (p) => `${(p.value || 0).toLocaleString()}` },
        ]}
        rowCount={total}
        page={page}
        pageSize={pageSize}
        onPageChange={(p)=> setPage(p)}
        onPageSizeChange={(ps)=> setPageSize(ps)}
        paginationMode="server"
        sortingMode="server"
        onSortModelChange={(model)=> { if (model.length>0) { setSortField(model[0].field); setSortDir(model[0].sort || 'asc'); } }}
        loading={loading}
      />
    );
  };

  // Dual filter builders UI state
  const [inflowBuilder, setInflowBuilder] = useState({ field: 'date', op: 'is', start: '', end: '', value: '', values: [], min: '', max: '', yesno: 'true', fyYear: '', reflectingMonth: '' });
  const [outflowBuilder, setOutflowBuilder] = useState({ field: 'date', op: 'is', start: '', end: '', value: '', values: [], min: '', max: '', fyYear: '', reflectingMonth: '' });

  const addInflowRule = () => {
    const f = inflowBuilder.field;
    const op = inflowBuilder.op;
    let rule = null;
    if (f === 'date') {
      if (!inflowBuilder.start || !inflowBuilder.end) return;
      rule = { field: 'date', op: 'between', start: inflowBuilder.start, end: inflowBuilder.end };
    } else if (f === 'reflecting_month') {
      if (!inflowBuilder.reflectingMonth) return;
      rule = { field: 'reflecting_month', op: 'is', value: inflowBuilder.reflectingMonth };
    } else if (f === 'amount_rs' || f === 'amount_usd') {
      const min = inflowBuilder.min !== '' ? Number(inflowBuilder.min) : null;
      const max = inflowBuilder.max !== '' ? Number(inflowBuilder.max) : null;
      if (min == null && max == null) return;
      rule = { field: f, min, max };
    } else if (f === 'source' || f === 'bank' || f === 'reflecting_bank') {
      if (op === 'is' && inflowBuilder.value) rule = { field: f, op: 'is', value: inflowBuilder.value };
      if (op === 'is_not' && inflowBuilder.value) rule = { field: f, op: 'is_not', value: inflowBuilder.value };
      if (op === 'is_any' && inflowBuilder.values?.length) rule = { field: f, op: 'is_any', values: inflowBuilder.values };
    } else if (f === 'request_inward') {
      rule = { field: 'request_inward', value: inflowBuilder.yesno === 'true' };
    }
    if (rule) setInflowRules(prev => [...prev, rule]);
  };

  const removeInflowRule = (idx) => setInflowRules(prev => prev.filter((_, i) => i !== idx));

  const editInflowRule = (idx) => {
    const rule = inflowRules[idx];
    setInflowBuilder({
      field: rule.field,
      op: rule.op,
      start: rule.start || '',
      end: rule.end || '',
      value: rule.value || '',
      values: rule.values || [],
      min: rule.min || '',
      max: rule.max || '',
      yesno: rule.yesno || 'true',
      fyYear: '',
      reflectingMonth: rule.field === 'reflecting_month' ? rule.value : ''
    });
    setEditingInflowRule(idx);
  };

  const saveInflowRule = () => {
    if (editingInflowRule === null) return;
    const f = inflowBuilder.field;
    const op = inflowBuilder.op;
    let rule = null;
    if (f === 'date') {
      if (!inflowBuilder.start || !inflowBuilder.end) return;
      rule = { field: f, op, start: inflowBuilder.start, end: inflowBuilder.end };
    } else if (f === 'reflecting_month') {
      if (!inflowBuilder.reflectingMonth) return;
      rule = { field: f, op: 'is', value: inflowBuilder.reflectingMonth };
    } else if (f === 'amount_rs' || f === 'amount_usd') {
      if (inflowBuilder.min && inflowBuilder.max) {
        rule = { field: f, op: 'between', min: parseFloat(inflowBuilder.min), max: parseFloat(inflowBuilder.max) };
      } else if (inflowBuilder.min) {
        rule = { field: f, op: 'gte', min: parseFloat(inflowBuilder.min) };
      } else if (inflowBuilder.max) {
        rule = { field: f, op: 'lte', max: parseFloat(inflowBuilder.max) };
      }
    } else if (f === 'source' || f === 'bank' || f === 'reflecting_bank') {
      if (op === 'is_any') {
        if (!inflowBuilder.values?.length) return;
        rule = { field: f, op, values: inflowBuilder.values };
      } else {
        if (!inflowBuilder.value) return;
        rule = { field: f, op, value: inflowBuilder.value };
      }
    } else if (f === 'request_inward') {
      rule = { field: f, op: 'equals', value: inflowBuilder.yesno === 'true' };
    }
    if (rule) {
      setInflowRules(prev => prev.map((r, i) => i === editingInflowRule ? rule : r));
      setInflowBuilder({ field: 'date', op: 'is', start: '', end: '', value: '', values: [], min: '', max: '', yesno: 'true', fyYear: '', reflectingMonth: '' });
      setEditingInflowRule(null);
    }
  };

  const cancelEditInflowRule = () => {
    setInflowBuilder({ field: 'date', op: 'is', start: '', end: '', value: '', values: [], min: '', max: '', yesno: 'true', fyYear: '', reflectingMonth: '' });
    setEditingInflowRule(null);
  };
  const describeInflowRule = (r) => {
    if (r.field === 'date') return `Date between ${r.start} and ${r.end}`;
    if (r.field === 'reflecting_month') {
      const monthObj = financialYearMonths.find(m => m.value === r.value);
      if (monthObj) {
        return `Reflecting Month is ${monthObj.label}`;
      } else {
        // Format date as "Month Year" if not found in financialYearMonths
        const date = new Date(r.value);
        const monthName = date.toLocaleDateString('en-US', { month: 'long' });
        const year = date.getFullYear();
        return `Reflecting Month is ${monthName} ${year}`;
      }
    }
    if (r.field === 'amount_rs' || r.field === 'amount_usd') {
      const label = r.field === 'amount_rs' ? 'Amount (Rs)' : 'Amount (USD)';
      if (r.min != null && r.max != null) return `${label} between ${r.min} and ${r.max}`;
      if (r.min != null) return `${label} >= ${r.min}`;
      if (r.max != null) return `${label} <= ${r.max}`;
    }
    if (r.field === 'source' || r.field === 'bank' || r.field === 'reflecting_bank') {
      const fieldLabel = r.field === 'source' ? 'Source' : r.field === 'bank' ? 'Bank' : 'Reflecting Bank';
      if (r.op === 'is') return `${fieldLabel} is ${r.value}`;
      if (r.op === 'is_not') return `${fieldLabel} is not ${r.value}`;
      if (r.op === 'is_any') return `${fieldLabel} in ${r.values.join(', ')}`;
    }
    if (r.field === 'request_inward') return `Inward Remittance is ${r.value ? 'Yes' : 'No'}`;
    return JSON.stringify(r);
  };
  const addOutflowRule = () => {
    const f = outflowBuilder.field;
    const op = outflowBuilder.op;
    let rule = null;
    if (f === 'date') {
      if (!outflowBuilder.start || !outflowBuilder.end) return;
      rule = { field: 'date', op: 'between', start: outflowBuilder.start, end: outflowBuilder.end };
    } else if (f === 'reflecting_month') {
      if (!outflowBuilder.reflectingMonth) return;
      rule = { field: 'reflecting_month', op: 'is', value: outflowBuilder.reflectingMonth };
    } else if (f === 'amount_rs') {
      const min = outflowBuilder.min !== '' ? Number(outflowBuilder.min) : null;
      const max = outflowBuilder.max !== '' ? Number(outflowBuilder.max) : null;
      if (min == null && max == null) return;
      rule = { field: 'amount_rs', min, max };
    } else if (f === 'sub_category' || f === 'deducted_from') {
      if (op === 'is' && outflowBuilder.value) rule = { field: f, op: 'is', value: outflowBuilder.value };
      if (op === 'is_not' && outflowBuilder.value) rule = { field: f, op: 'is_not', value: outflowBuilder.value };
      if (op === 'is_any' && outflowBuilder.values?.length) rule = { field: f, op: 'is_any', values: outflowBuilder.values };
    } else if (f === 'expense') {
      if (outflowBuilder.value) rule = { field: 'expense', op: 'contains', value: outflowBuilder.value };
    }
    if (rule) setOutflowRules(prev => [...prev, rule]);
  };

  const removeOutflowRule = (idx) => setOutflowRules(prev => prev.filter((_, i) => i !== idx));

  const editOutflowRule = (idx) => {
    const rule = outflowRules[idx];
    setOutflowBuilder({
      field: rule.field,
      op: rule.op,
      start: rule.start || '',
      end: rule.end || '',
      value: rule.value || '',
      values: rule.values || [],
      min: rule.min || '',
      max: rule.max || '',
      fyYear: '',
      reflectingMonth: rule.field === 'reflecting_month' ? rule.value : ''
    });
    setEditingOutflowRule(idx);
  };

  const saveOutflowRule = () => {
    if (editingOutflowRule === null) return;
    const f = outflowBuilder.field;
    const op = outflowBuilder.op;
    let rule = null;
    if (f === 'date') {
      if (!outflowBuilder.start || !outflowBuilder.end) return;
      rule = { field: f, op, start: outflowBuilder.start, end: outflowBuilder.end };
    } else if (f === 'reflecting_month') {
      if (!outflowBuilder.reflectingMonth) return;
      rule = { field: f, op: 'is', value: outflowBuilder.reflectingMonth };
    } else if (f === 'amount_rs') {
      if (outflowBuilder.min && outflowBuilder.max) {
        rule = { field: f, op: 'between', min: parseFloat(outflowBuilder.min), max: parseFloat(outflowBuilder.max) };
      } else if (outflowBuilder.min) {
        rule = { field: f, op: 'gte', min: parseFloat(outflowBuilder.min) };
      } else if (outflowBuilder.max) {
        rule = { field: f, op: 'lte', max: parseFloat(outflowBuilder.max) };
      }
    } else if (f === 'sub_category' || f === 'deducted_from') {
      if (op === 'is_any') {
        if (!outflowBuilder.values?.length) return;
        rule = { field: f, op, values: outflowBuilder.values };
      } else {
        if (!outflowBuilder.value) return;
        rule = { field: f, op, value: outflowBuilder.value };
      }
    } else if (f === 'expense') {
      if (!outflowBuilder.value) return;
      rule = { field: f, op: 'contains', value: outflowBuilder.value };
    }
    if (rule) {
      setOutflowRules(prev => prev.map((r, i) => i === editingOutflowRule ? rule : r));
      setOutflowBuilder({ field: 'date', op: 'is', start: '', end: '', value: '', values: [], min: '', max: '', fyYear: '', reflectingMonth: '' });
      setEditingOutflowRule(null);
    }
  };

  const cancelEditOutflowRule = () => {
    setOutflowBuilder({ field: 'date', op: 'is', start: '', end: '', value: '', values: [], min: '', max: '', fyYear: '', reflectingMonth: '' });
    setEditingOutflowRule(null);
  };

  // Reports System Functions
  const saveAsReport = async () => {
    if (!reportBuilder.name.trim()) return;
    
    const reportData = {
      name: reportBuilder.name.trim(),
      description: reportBuilder.description.trim(),
      type: reportBuilder.type,
      category: reportBuilder.category,
      tags: [...reportBuilder.tags],
      inflowRules: [...inflowRules],
      outflowRules: [...outflowRules],
      inflowGroupBy,
      outflowGroupBy,
      updatedAt: new Date().toISOString()
    };
    
    try {
      if (editingReport) {
        // Update existing report
        const payload = {
          name: reportData.name,
          description: reportData.description,
          type: reportData.type,
          category: reportData.category,
          tags: reportData.tags,
          inflow_rules: reportData.inflowRules,
          outflow_rules: reportData.outflowRules,
          inflow_group_by: reportData.inflowGroupBy,
          outflow_group_by: reportData.outflowGroupBy,
        };
        const resp = await updateReportApi(editingReport.id, payload);
        const updatedReport = {
          id: resp.data.id,
          name: resp.data.name,
          description: resp.data.description,
          type: resp.data.type,
          category: resp.data.category,
          tags: resp.data.tags || [],
          inflowRules: resp.data.inflow_rules || [],
          outflowRules: resp.data.outflow_rules || [],
          inflowGroupBy: resp.data.inflow_group_by || 'month',
          outflowGroupBy: resp.data.outflow_group_by || 'month',
          createdAt: resp.data.created_at,
          updatedAt: resp.data.updated_at,
        };
        setReports(prev => prev.map(r => r.id === editingReport.id ? updatedReport : r));
        updateReportCardsInDashboards(updatedReport);
      } else {
        // Create new report
        const payload = {
          name: reportData.name,
          description: reportData.description,
          type: reportData.type,
          category: reportData.category,
          tags: reportData.tags,
          inflow_rules: reportData.inflowRules,
          outflow_rules: reportData.outflowRules,
          inflow_group_by: reportData.inflowGroupBy,
          outflow_group_by: reportData.outflowGroupBy,
        };
        const resp = await createReportApi(payload);
        const newReport = {
          id: resp.data.id,
          name: resp.data.name,
          description: resp.data.description,
          type: resp.data.type,
          category: resp.data.category,
          tags: resp.data.tags || [],
          inflowRules: resp.data.inflow_rules || [],
          outflowRules: resp.data.outflow_rules || [],
          inflowGroupBy: resp.data.inflow_group_by || 'month',
          outflowGroupBy: resp.data.outflow_group_by || 'month',
          createdAt: resp.data.created_at,
          updatedAt: resp.data.updated_at,
        };
        setReports(prev => [...prev, newReport]);
      }
    } catch (e) {
      // Show error to user instead of falling back to localStorage
      alert('Failed to save report. Please check your connection and try again.');
      return;
    }
    
    setReportBuilder({ name: '', description: '', type: 'combined', category: 'general', tags: [] });
    setEditingReport(null);
    setNewTag('');
    setShowReportBuilder(false);
  };

  const loadReport = (report) => {
    setInflowRules([...report.inflowRules]);
    setOutflowRules([...report.outflowRules]);
    setInflowGroupBy(report.inflowGroupBy || 'month');
    setOutflowGroupBy(report.outflowGroupBy || 'month');
    setNav('insights');
  };

  const deleteReport = async (reportId) => {
    try {
      await deleteReportApi(reportId);
      setReports(prev => prev.filter(r => r.id !== reportId));
    } catch (e) {
      alert('Failed to delete report. Please check your connection and try again.');
    }
  };

  const editReport = (report) => {
    setEditingReport(report);
    setReportBuilder({
      name: report.name,
      description: report.description || '',
      type: report.type,
      category: report.category || 'general',
      tags: report.tags || []
    });
    setShowReportBuilder(true);
  };

  const addTag = () => {
    if (newTag.trim() && !reportBuilder.tags.includes(newTag.trim())) {
      setReportBuilder(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  // Handle Save Report (modal) -> delegate to server-side save
  const handleSaveReport = async () => {
    const name = (reportBuilder.name || '').trim();
    if (!name) return;
    await saveAsReport();
    setShowSaveReportModal(false);
  };

  // Robust extractor for totals coming from preview APIs (supports multiple key shapes)
  const extractTotals = (payload) => {
    const p = payload || {};
    const fromKeys = (obj, keys) => {
      for (const k of keys) {
        if (obj && Object.prototype.hasOwnProperty.call(obj, k) && obj[k] != null) {
          const n = Number(obj[k]);
          if (!Number.isNaN(n)) return n;
        }
      }
      return null;
    };
    const totalsObj = p.totals || p.summary || {};
    // Support flat and nested (inflow/outflow) totals
    const inflowObj = p.inflow || {};
    const outflowObj = p.outflow || {};
    const amountRs = fromKeys(p, ['sum_amount_rs', 'total_amount_rs', 'sum_rs', 'total_rs'])
      ?? fromKeys(totalsObj, ['amount_rs', 'sum_amount_rs', 'total_amount_rs'])
      ?? fromKeys(inflowObj, ['sum_amount_rs'])
      ?? fromKeys(outflowObj, ['sum_amount_rs'])
      ?? null;
    const amountUsd = fromKeys(p, ['sum_amount_usd', 'total_amount_usd', 'sum_usd', 'total_usd'])
      ?? fromKeys(totalsObj, ['amount_usd', 'sum_amount_usd', 'total_amount_usd'])
      ?? fromKeys(inflowObj, ['sum_amount_usd'])
      ?? null;
    const count = p.total ?? fromKeys(p, ['total', 'count']) ?? fromKeys(totalsObj, ['total', 'count']) ?? (Array.isArray(p.items) ? p.items.length : 0);
    return { amountRs, amountUsd, count };
  };

  const removeTag = (tagToRemove) => {
    setReportBuilder(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const duplicateReport = async (report) => {
    try {
      const payload = {
        name: `${report.name} (Copy)`,
        description: report.description,
        type: report.type,
        category: report.category,
        tags: report.tags || [],
        inflow_rules: report.inflowRules || [],
        outflow_rules: report.outflowRules || [],
        inflow_group_by: report.inflowGroupBy || 'month',
        outflow_group_by: report.outflowGroupBy || 'month',
      };
      const resp = await createReportApi(payload);
      const duplicatedReport = {
        id: resp.data.id,
        name: resp.data.name,
        description: resp.data.description,
        type: resp.data.type,
        category: resp.data.category,
        tags: resp.data.tags || [],
        inflowRules: resp.data.inflow_rules || [],
        outflowRules: resp.data.outflow_rules || [],
        inflowGroupBy: resp.data.inflow_group_by || 'month',
        outflowGroupBy: resp.data.outflow_group_by || 'month',
        createdAt: resp.data.created_at,
        updatedAt: resp.data.updated_at,
      };
      setReports(prev => [...prev, duplicatedReport]);
    } catch (e) {
      alert('Failed to duplicate report. Please check your connection and try again.');
    }
  };

  const exportReport = (report) => {
    const exportData = {
      reportName: report.name,
      description: report.description,
      type: report.type,
      filters: {
        inflowRules: report.inflowRules,
        outflowRules: report.outflowRules
      },
      analytics: {
        inflowGroupBy: report.inflowGroupBy,
        outflowGroupBy: report.outflowGroupBy
      },
      exportedAt: new Date().toISOString(),
      version: '1.0'
    };
    
    const dataStr = JSON.stringify(exportData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${report.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_report.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const describeOutflowRule = (r) => {
    if (r.field === 'date') return `Date between ${r.start} and ${r.end}`;
    if (r.field === 'reflecting_month') {
      const monthObj = financialYearMonths.find(m => m.value === r.value);
      if (monthObj) {
        return `Reflecting Month is ${monthObj.label}`;
      } else {
        // Format date as "Month Year" if not found in financialYearMonths
        const date = new Date(r.value);
        const monthName = date.toLocaleDateString('en-US', { month: 'long' });
        const year = date.getFullYear();
        return `Reflecting Month is ${monthName} ${year}`;
      }
    }
    if (r.field === 'amount_rs') {
      if (r.min != null && r.max != null) return `Amount (Rs) between ${r.min} and ${r.max}`;
      if (r.min != null) return `Amount (Rs) >= ${r.min}`;
      if (r.max != null) return `Amount (Rs) <= ${r.max}`;
    }
    if (r.field === 'sub_category') {
      if (r.op === 'is') return `Subcategory is ${r.value}`;
      if (r.op === 'is_not') return `Subcategory is not ${r.value}`;
      if (r.op === 'is_any') return `Subcategory in ${r.values.join(', ')}`;
    }
    if (r.field === 'deducted_from') {
      if (r.op === 'is') return `Deducted From is ${r.value}`;
      if (r.op === 'is_not') return `Deducted From is not ${r.value}`;
      if (r.op === 'is_any') return `Deducted From in ${r.values.join(', ')}`;
    }
    if (r.field === 'expense') return `Expense contains "${r.value}"`;
    return JSON.stringify(r);
  };

  // Professional Dashboard State
  const [dashboardData, setDashboardData] = useState({
    opening_balance: 0,
    total_inflow: 0,
    total_outflow: 0,
    total_withdrawals: 0,
    closing_balance: 0,
    net_profit: 0,
    profit_margin: 0,
    total_sales: 0,
    sales_by_source: {},
    sales_count: 0,
    expense_breakdown: [],
    inflow_source_breakdown: [],
    outflow_subcategory_breakdown: [],
    outflow_category_breakdown: [],
    total_expenses: 0,
    cash_flow_data: [],
    inflow_change_percent: 0,
    outflow_change_percent: 0,
    profit_change_percent: 0,
    period: "this_month",
    period_label: ""
  });
  // Derived label for dashboard period (exact dates for custom ranges)
  const displayPeriodLabel = useMemo(() => {
    if (timePeriod === 'custom_fy' && customStart) {
      const startYear = new Date(customStart).getFullYear();
      return `FY ${startYear}-${(startYear + 1).toString().slice(-2)}`;
    }
    if ((timePeriod === 'custom' || timePeriod === 'custom_range') && customStart && customEnd) {
      return `${formatExactDate(customStart)} to ${formatExactDate(customEnd)}`;
    }
    return dashboardData.period_label || '';
  }, [timePeriod, customStart, customEnd, dashboardData.period_label]);
  const dashboardOtherIncome = Number(
    dashboardData.other_inflow ?? (Number(dashboardData.total_inflow || 0) - Number(dashboardData.total_sales || 0))
  );
  const [recentTransactions, setRecentTransactions] = useState([]);
  // Derived auth role helpers
  const currentUserRole = user?.role || null;
  const isITAdmin = currentUserRole === 'IT Admin';
  const isOwner = currentUserRole === 'Owner';
  const adminCanWrite = isITAdmin || isOwner;

  // Settings: Users & Access state (Phase 1 - UI only)
  const [settingsTab, setSettingsTab] = useState('users'); // 'users' | 'financial' | 'categories' | 'automation'
  const [usersList, setUsersList] = useState([]);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  const [showDeleteAllDataModal, setShowDeleteAllDataModal] = useState(false);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    role: 'Owner',
    otp_enabled: true,
    password: '', // only for set/update
  });

  // Settings: Phase 1 lists
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [inflowSources, setInflowSources] = useState([]); // [{id,name,is_active}]
  const [bankAccounts, setBankAccounts] = useState([]);   // [{id,name,is_active,is_default_cash}]
  const [outflowCategories, setOutflowCategories] = useState([]); // [{id,name}]
  const [outflowSubcategories, setOutflowSubcategories] = useState([]); // [{id,name,category_id}]
  const [bankLimits, setBankLimits] = useState([]); // [{bank_account_id, bank_name, monthly_limit_rs, quarterly_limit_rs, yearly_limit_rs, is_active}]
  const [limitCheck, setLimitCheck] = useState(null); // {exceeds_monthly, exceeds_quarterly, exceeds_yearly, details}
  const [limitCheckLoading, setLimitCheckLoading] = useState(false);

  // Drag & Drop state for settings reordering
  const [dragging, setDragging] = useState(null); // { type: 'source'|'bank'|'category'|'subcategory', id: number }

  // Settings CRUD state (Financial & Categories)
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [editingSourceId, setEditingSourceId] = useState(null);
  const [sourceForm, setSourceForm] = useState({ name: '' });

  const [showBankModal, setShowBankModal] = useState(false);
  const [editingBankId, setEditingBankId] = useState(null);
  const [bankForm, setBankForm] = useState({ name: '', is_default_cash: false });

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [categoryForm, setCategoryForm] = useState({ name: '' });

  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [editingSubcategoryId, setEditingSubcategoryId] = useState(null);
  const [subcategoryForm, setSubcategoryForm] = useState({ name: '', category_id: '', accounting_treatment: OUTFLOW_TREATMENT_EXPENSE });
  // Automation: Recurring Items state
  const [recurringItems, setRecurringItems] = useState([]);
  const sortedRecurringItems = useMemo(() => {
    const items = Array.isArray(recurringItems) ? [...recurringItems] : [];
    return items.sort((a, b) => {
      // Sort by next_due_date in ascending order (earliest first)
      const aDate = a.next_due_date || '';
      const bDate = b.next_due_date || '';
      if (aDate && bDate && aDate !== bDate) return aDate.localeCompare(bDate);
      // Fallback to id for stable sort
      return (a.id || 0) - (b.id || 0);
    });
  }, [recurringItems]);
  const recurringItemsTotalRs = useMemo(
    () => (Array.isArray(recurringItems) ? recurringItems.reduce((sum, item) => sum + (Number(item?.amount_rs) || 0), 0) : 0),
    [recurringItems]
  );
  const recurringTransactionsSectionRef = React.useRef(null);
  const recurringLastRowRef = React.useRef(null);
  const [pendingRecurringScroll, setPendingRecurringScroll] = useState(false);
  const [showRecurringForm, setShowRecurringForm] = useState(false);
  const [editingRecurringId, setEditingRecurringId] = useState(null);
  const [recurringForm, setRecurringForm] = useState({
    name: '',
    amount_rs: '',
    schedule_type: 'monthly', // 'monthly' | 'quarterly' | 'yearly_financial'
    day_of_month: '1', // used only for monthly (1-28)
    is_active: true,
    
    // Outflow fields
    expense: '',
    sub_category: '',
    category: '',
    deducted_from: '',
    mode: MODES[0] || '',
    nature: '',
    vendor: '',
    remark: '',
  });
  // BACKUP FUNCTIONALITY DISABLED - Future Updates
  // Automation: Backup & Export state
  // const [backupSettings, setBackupSettings] = useState({ enabled: true, time_ist: '00:15', recipients_csv: '', retention_days: 30 });
  // const [backupHistory, setBackupHistory] = useState([]);
  // const [backupLoading, setBackupLoading] = useState(false);
  // // Automation UI helpers
  // const [recipientList, setRecipientList] = useState([]);
  // const [recipientInput, setRecipientInput] = useState('');

  useEffect(() => {
    if (inflowQueryPending === inflowQuery) return;
    const t = setTimeout(() => {
      setInflowQuery(inflowQueryPending);
      refreshAllData();
    }, 350);
    return () => clearTimeout(t);
  }, [inflowQueryPending]);

  useEffect(() => {
    if (outflowQueryPending === outflowQuery) return;
    const t = setTimeout(() => {
      setOutflowQuery(outflowQueryPending);
      refreshAllData();
    }, 350);
    return () => clearTimeout(t);
  }, [outflowQueryPending]);
  // BACKUP FUNCTIONALITY DISABLED - Future Updates
  // const csv = (backupSettings.recipients_csv || '').trim();
  // useEffect(() => {
  //   const csv = (backupSettings.recipients_csv || '').trim();
  //   const parsed = csv ? csv.split(',').map(s => s.trim()).filter(Boolean) : [];
  //   setRecipientList(parsed);
  // }, [backupSettings.recipients_csv]);

  // const removeRecipient = (email) => {
  //   const next = recipientList.filter(e => e !== email);
  //   setRecipientList(next);
  //   setBackupSettings(p => ({ ...p, recipients_csv: next.join(',') }));
  // };

  // const addRecipient = () => {
  //   const email = (recipientInput || '').trim();
  //   if (!email) return;
  //   // basic email pattern
  //   const isValid = /[^\s@]+@[^\s@]+\.[^\s@]+/.test(email);
  //   if (!isValid) return; // silently ignore invalid for now
  //   if (recipientList.includes(email)) {
  //     setRecipientInput('');
  //     return;
  //   }
  //   const next = [...recipientList, email];
  //   setRecipientList(next);
  //   setBackupSettings(p => ({ ...p, recipients_csv: next.join(',') }));
  //   setRecipientInput('');
  // };

  // Time formatter to IST with UTC fallback parsing (for history timestamps)
  const formatISTTs = (ts) => {
    if (!ts) return '-';
    let d;
    if (typeof ts === 'string') {
      const hasTZ = /[zZ]|[+\-]\d{2}:?\d{2}/.test(ts);
      if (!hasTZ) {
        const normalized = ts.replace(' ', 'T') + 'Z';
        d = new Date(normalized);
      } else {
        d = new Date(ts);
      }
    } else {
      d = new Date(ts);
    }
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  };
  const [exportForm, setExportForm] = useState({ includeInflows: true, includeOutflows: true, all: false, fromDate: '', toDate: '' });

  // Function to load all inflows data without pagination
  const getAllInflowsData = async (params) => {
    let allData = [];
    let offset = 0;
    const limit = 200; // Maximum allowed by API
    let hasMore = true;
    
    while (hasMore) {
      const response = await getInflowsPaginated({
        ...params,
        limit: limit,
        offset: offset
      });
      
      if (response.data && response.data.items) {
        allData = [...allData, ...response.data.items];
        hasMore = response.data.items.length === limit;
        offset += limit;
      } else {
        hasMore = false;
      }
    }
    
    return { data: { items: allData, total: allData.length } };
  };

  // Function to load all outflows data without pagination
  const getAllOutflowsData = async (params) => {
    let allData = [];
    let offset = 0;
    const limit = 200; // Maximum allowed by API
    let hasMore = true;
    
    while (hasMore) {
      const response = await getOutflowsPaginated({
        ...params,
        limit: limit,
        offset: offset
      });
      
      if (response.data && response.data.items) {
        allData = [...allData, ...response.data.items];
        hasMore = response.data.items.length === limit;
        offset += limit;
      } else {
        hasMore = false;
      }
    }
    
    return { data: { items: allData, total: allData.length } };
  };
  const toggleExport = (key) => {
    setExportForm(prev => {
      const next = { ...prev, [key]: !prev[key] };
      // enforce at least one true
      if (!next.includeInflows && !next.includeOutflows) {
        // revert the toggle to keep at least one selected
        return prev;
      }
      return next;
    });
  };
  

  // Reminders state
  const [dueReminders, setDueReminders] = useState([]);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [skippedReminders, setSkippedReminders] = useState([]);
  const [skipConfirm, setSkipConfirm] = useState(null); // { recurring_item_id, occurrence_id, name, next_due_date, amount_rs }
  const [skipBusy, setSkipBusy] = useState(false);
  const [remindersLoading, setRemindersLoading] = useState(false);

  // Calculate reminder totals
  const dueRemindersTotal = useMemo(() => {
    return dueReminders.reduce((sum, reminder) => sum + (reminder.amount_rs || 0), 0);
  }, [dueReminders]);

  const upcomingRemindersTotal = useMemo(() => {
    return upcomingReminders.reduce((sum, reminder) => sum + (reminder.amount_rs || 0), 0);
  }, [upcomingReminders]);

  const skippedRemindersTotal = useMemo(() => {
    return skippedReminders.reduce((sum, reminder) => sum + (reminder.amount_rs || 0), 0);
  }, [skippedReminders]);

  // Reminders: Bank balance and projections
  const [bankBalanceThisMonth, setBankBalanceThisMonth] = useState(null);
  const [cbLoading, setCbLoading] = useState(false);
  const duesTotal = useMemo(() => {
    return Number(dueRemindersTotal || 0) + Number(upcomingRemindersTotal || 0);
  }, [dueRemindersTotal, upcomingRemindersTotal]);
  const projectedBalance = useMemo(() => {
    const base = Number(bankBalanceThisMonth || 0);
    return base - Number(duesTotal || 0);
  }, [bankBalanceThisMonth, duesTotal]);
  const [limitsUsageMonthly, setLimitsUsageMonthly] = useState([]);
  const [limitsUsageQuarterly, setLimitsUsageQuarterly] = useState([]);
  const [limitsUsageYearly, setLimitsUsageYearly] = useState([]);
  const [limitsUsageLoading, setLimitsUsageLoading] = useState(false);
  const [limitsUsageTab, setLimitsUsageTab] = useState('yearly'); // 'yearly' | 'quarterly' | 'monthly'

  // When any settings modal is opened from Dashboard, auto-switch to Settings with correct sub-tab
  useEffect(() => {
    if (nav !== 'settings') {
      if (showSourceModal || showBankModal) {
        setNav('settings');
        setSettingsTab('financial');
      } else if (showCategoryModal || showSubcategoryModal) {
        setNav('settings');
        setSettingsTab('categories');
      }
    }
  }, [showSourceModal, showBankModal, showCategoryModal, showSubcategoryModal, nav]);

  // Load recurring items when Automation tab is opened
  useEffect(() => {
    if (nav === 'settings' && settingsTab === 'automation') {
      loadRecurringItems();
      // BACKUP FUNCTIONALITY DISABLED - Future Updates
      // Only load backup settings for Owners
      // if (user?.role === 'Owner') {
      // loadBackupSettings();
      // loadBackupHistory();
      // }
    }
  }, [nav, settingsTab, user?.role]);

  // Auto-load reminders when navigating to Reminders page
  useEffect(() => {
    if (nav === 'reminders') {
      loadReminders();
      loadLimitsUsage();
      loadBankBalanceThisMonth();
    }
  }, [nav]);

  useEffect(() => {
    if (!pendingRecurringScroll) return;
    if (nav !== 'settings' || settingsTab !== 'automation') return;

    const timer = setTimeout(() => {
      const target = recurringLastRowRef.current || recurringTransactionsSectionRef.current;
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setPendingRecurringScroll(false);
    }, 250);

    return () => clearTimeout(timer);
  }, [pendingRecurringScroll, nav, settingsTab, sortedRecurringItems.length]);

  const handleViewAllRecurringTransactions = () => {
    setNav('settings');
    setSettingsTab('automation');
    setPendingRecurringScroll(true);
  };

  const loadRecurringItems = async () => {
    try {
      const res = await listRecurringItemsApi(true);
      setRecurringItems(res?.data || []);
    } catch (e) {
      // Failed to load recurring items
    }
  };

  // BACKUP FUNCTIONALITY DISABLED - Future Updates
  // Backup & Export: loaders and actions
  // const loadBackupSettings = async () => {
  //   try {
  //     const res = await getBackupSettingsApi();
  //     if (res?.data) setBackupSettings(res.data);
  //   } catch (e) {
  //     // Failed to load backup settings
  //   }
  // };

  // const loadBackupHistory = async () => {
  //   try {
  //     const res = await listBackupHistoryApi(20, 0);
  //     setBackupHistory(res?.data || []);
  //   } catch (e) {
  //     // Failed to load backup history
  //   }
  // };

  // const saveBackupSettings = async (e) => {
  //   e?.preventDefault?.();
  //   if (!adminCanWrite) return;
  //   try {
  //     setBackupLoading(true);
  //     const payload = { ...backupSettings };
  //     await updateBackupSettingsApi(payload);
  //     await loadBackupSettings();
  //     alert('Backup settings saved');
  //   } catch (err) {
  //     alert(err?.response?.data?.detail || 'Failed to save backup settings');
  //   } finally { setBackupLoading(false); }
  // };
  // const runBackupNow = async () => {
  //   if (!adminCanWrite) return;
  //   try {
  //     setBackupLoading(true);
  //     const res = await runBackupNowApi();
  //     // Optionally show result status
  //     alert(res?.data?.status || 'Backup triggered');
  //     await loadBackupHistory();
  //   } catch (err) {
  //     alert(err?.response?.data?.detail || 'Failed to run backup');
  //   } finally { setBackupLoading(false); }
  // };

  const onExportField = (e) => {
    const { name, type, checked, value } = e.target || {};
    if (!name) return;
    setExportForm((p) => ({ ...p, [name]: type === 'checkbox' ? !!checked : value }));
  };

  const downloadManualExport = async (e) => {
    e?.preventDefault?.();
    try {
      if (!exportForm.includeInflows && !exportForm.includeOutflows) {
        alert('Select at least one of Inflows or Outflows');
        return;
      }
      const params = {
        includeInflows: !!exportForm.includeInflows,
        includeOutflows: !!exportForm.includeOutflows,
        all: !!exportForm.all,
        fromDate: exportForm.all ? undefined : (exportForm.fromDate || undefined),
        toDate: exportForm.all ? undefined : (exportForm.toDate || undefined),
      };
      const res = await downloadExportApi(params);
      const blob = res?.data; // already a Blob
      const cd = res.headers?.['content-disposition'] || '';
      let filename = 'export.zip';
      const m = cd && cd.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
      if (m) filename = decodeURIComponent(m[1] || m[2] || filename);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to download export');
    }
  };

  const openAddRecurring = () => {
    setEditingRecurringId(null);
    setRecurringForm({ 
      name: '', 
      amount_rs: '', 
      schedule_type: 'monthly', 
      day_of_month: '1', 
      is_active: true,
      expense: '',
      sub_category: '',
      category: '',
      deducted_from: '',
      mode: MODES[0] || '',
      nature: '',
      vendor: '',
      remark: ''
    });
    setShowRecurringForm(true);
  };

  const openEditRecurring = (item) => {
    setEditingRecurringId(item.id);
    setRecurringForm({
      name: item.name || '',
      amount_rs: String(item.amount_rs ?? ''),
      schedule_type: item.schedule_type || 'monthly',
      day_of_month: item.day_of_month !== null ? String(item.day_of_month) : '1',
      is_active: !!item.is_active,
      
      // Outflow fields
      expense: item.expense || '',
      sub_category: item.sub_category || '',
      category: item.category || '',
      deducted_from: item.deducted_from || '',
      mode: item.mode || MODES[0] || '',
      nature: item.nature || '',
      vendor: item.vendor || '',
      remark: item.remark || '',
    });
    setShowRecurringForm(true);
  };

  const handleRecurringChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setRecurringForm((prev) => {
      const updated = { ...prev, [name]: newValue };
      
      // Auto-select category when sub-category is selected
      if (name === 'sub_category' && value) {
        const selectedSubcat = outflowSubcategories.find(sub => sub.name === value);
        if (selectedSubcat) {
          const parentCategory = outflowCategories.find(cat => cat.id === selectedSubcat.category_id);
          if (parentCategory) {
            updated.category = parentCategory.name;
          }
        }
      }
      
      return updated;
    });
  };
  const saveRecurring = async (e) => {
    e?.preventDefault?.();
    try {
      // Create payload with all required fields that the API expects
      const payload = {
        name: recurringForm.name.trim(),
        amount_rs: parseFloat(recurringForm.amount_rs || '0'),
        schedule_type: recurringForm.schedule_type,
        day_of_month: recurringForm.schedule_type === 'monthly' ? parseInt(recurringForm.day_of_month || '1', 10) : null,
        is_active: !!recurringForm.is_active,
        
        // Required outflow fields (backend expects these to be present)
        expense: recurringForm.expense.trim(),
        sub_category: recurringForm.sub_category.trim(),
        category: recurringForm.category.trim(),
        deducted_from: recurringForm.deducted_from.trim(),
        mode: recurringForm.mode.trim(),
        
        // Optional outflow fields
        nature: recurringForm.nature.trim() || null,
        vendor: recurringForm.vendor.trim() || null,
        remark: recurringForm.remark.trim() || null,
      };
      if (!payload.name || !payload.amount_rs || !payload.expense || !payload.sub_category || !payload.category || !payload.deducted_from || !payload.mode) {
        throw new Error('Please fill all required fields');
      }
      if (editingRecurringId) {
        await updateRecurringItemApi(editingRecurringId, payload);
      } else {
        await createRecurringItemApi(payload);
      }
      setShowRecurringForm(false);
      await loadRecurringItems();
    } catch (err) {
      alert(err?.response?.data?.detail || err.message || 'Failed to save');
    }
  };

  const archiveRecurring = async (id) => {
    if (!window.confirm('Archive this recurring item?')) return;
    try {
      await deleteRecurringItemApi(id);
      await loadRecurringItems();
    } catch (err) {
      alert('Failed to archive');
    }
  };

  const loadReminders = async () => {
    setRemindersLoading(true);
    try {
      const res = await getRecurringRemindersApi(); // { due: [], upcoming: [], skipped: [] }
      setDueReminders(res?.data?.due || []);
      setUpcomingReminders(res?.data?.upcoming || []);
      setSkippedReminders(res?.data?.skipped || []);
    } catch (e) {
      // Failed to load reminders
    } finally {
      setRemindersLoading(false);
    }
  };

  const loadBankBalanceThisMonth = async () => {
    try {
      setCbLoading(true);
      const res = await getDashboardData('this_month');
      const val = Number(res?.data?.closing_balance ?? 0);
      setBankBalanceThisMonth(val);
    } catch (e) {
      setBankBalanceThisMonth(0);
    } finally {
      setCbLoading(false);
    }
  };
  const loadLimitsUsage = async () => {
    if (limitsUsageLoading) return; // Prevent multiple concurrent calls
    setLimitsUsageLoading(true);
    const anchor = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    try {
      // Load with timeout to prevent hanging
      const timeout = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Request timeout')), 10000)
      );
      
      const [m, q, y] = await Promise.race([
        Promise.all([
          getLimitsUsageApi('monthly', anchor),
          getLimitsUsageApi('quarterly', anchor),
          getLimitsUsageApi('yearly', anchor),
        ]),
        timeout
      ]);
      setLimitsUsageMonthly(m?.data || []);
      setLimitsUsageQuarterly(q?.data || []);
      setLimitsUsageYearly(y?.data || []);
    } catch (e) {
      setLimitsUsageMonthly([]);
      setLimitsUsageQuarterly([]);
      setLimitsUsageYearly([]);
    } finally {
      setLimitsUsageLoading(false);
    }
  };

  const markReminderPaid = async (id) => {
    try {
      // Find the recurring item to get its details
      const recurringItem = dueReminders.find(item => item.id === id) || 
                           upcomingReminders.find(item => item.id === id);
      
      if (recurringItem) {
        // Pre-fill outflow form with recurring item data
        const today = new Date().toISOString().split('T')[0];
        const reflectingMonth = getFormFYMonths().find(m => {
          const monthDate = new Date(m.value);
          const currentDate = new Date();
          return monthDate.getMonth() === currentDate.getMonth() && 
                 monthDate.getFullYear() === currentDate.getFullYear();
        })?.value || today;
        
        // Enhanced remark format: RECURRING_PAID:{id}:DUE_{due_date}:PAID_{paid_date}
        // This tracks which specific due date is being paid
        const dueDate = recurringItem.next_due_date;
        const recurringItemId = recurringItem.recurring_item_id || recurringItem.id;
        const occurrenceId = recurringItem.occurrence_id || recurringItem.id;
        const enhancedRemark = `RECURRING_PAID:${recurringItemId}:OCC_${occurrenceId}:DUE_${dueDate}:PAID_${today}`;
        
        // Add user-friendly note if original remark exists
        const userRemark = recurringItem.remark ? ` | ${recurringItem.remark}` : '';
        const finalRemark = enhancedRemark + userRemark;
        
        setOutflowForm({
          date: today,
          reflecting_month: reflectingMonth,
          expense: recurringItem.expense || recurringItem.name || '',
          amount_rs: String(recurringItem.amount_rs || ''),
          sub_category: recurringItem.sub_category || '',
          category: recurringItem.category || '',
          deducted_from: recurringItem.deducted_from || '',
          mode: recurringItem.mode || MODES[0] || '',
          nature: recurringItem.nature || '',
          vendor: recurringItem.vendor || '',
          remark: finalRemark,
          invoice_file: null
        });
        
        // Switch to outflow section and auto-open form
        setNav('outflow');
        setShowOutflowForm(true); // Auto-open the outflow form
        
        // Show success modal instead of alert
        setShowMarkPaidModal(true);
        setMarkPaidMessage(`Successfully redirected to Outflow section with pre-filled data from "${recurringItem.name}". Please review and submit the transaction.`);
      } else {
        // Fallback to original behavior if item not found
        await markRecurringItemPaidApi(id, undefined, id);
        await loadReminders();
        if (nav === 'settings' && settingsTab === 'automation') {
          await loadRecurringItems();
        }
      }
    } catch (e) {
      setShowMarkPaidModal(true);
      setMarkPaidMessage('Failed to process reminder. Please try again.');
    }
  };

  const requestSkipReminder = (r) => {
    setSkipConfirm({
      recurring_item_id: r.recurring_item_id || r.id,
      occurrence_id: r.occurrence_id || r.id,
      name: r.name,
      next_due_date: r.next_due_date,
      amount_rs: r.amount_rs,
    });
  };

  const confirmSkipReminder = async () => {
    if (!skipConfirm || skipBusy) return;
    setSkipBusy(true);
    try {
      await skipRecurringOccurrenceApi(skipConfirm.recurring_item_id, skipConfirm.occurrence_id);
      setSkipConfirm(null);
      await loadReminders();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to skip occurrence.';
      alert(detail);
    } finally {
      setSkipBusy(false);
    }
  };

  const unskipReminder = async (r) => {
    try {
      const recurringId = r.recurring_item_id || r.id;
      const occurrenceId = r.occurrence_id || r.id;
      await unskipRecurringOccurrenceApi(recurringId, occurrenceId);
      await loadReminders();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to unskip occurrence.';
      alert(detail);
    }
  };

  const refreshSettingsLists = async () => {
    try {
      const [srcRes, bankRes, catRes, subRes] = await Promise.all([
        listInflowSourcesApi(),
        listBankAccountsApi(),
        listOutflowCategoriesApi(),
        listOutflowSubcategoriesApi(),
      ]);
      setInflowSources(srcRes.data || []);
      setBankAccounts(bankRes.data || []);
      setOutflowCategories(catRes.data || []);
      setOutflowSubcategories((subRes.data || []).map(normalizeOutflowSubcategory));
      setSettingsLoaded(true);

      // Initialize defaults using backend data with fallback
      const inflowSourceNames = (srcRes.data && srcRes.data.length) ? srcRes.data.map(s => s.name) : INFLOW_SOURCES;
      const inflowBankNames = (bankRes.data && bankRes.data.length) ? bankRes.data.filter(b => !b.is_default_cash).map(b => b.name) : BANKS;
      const subNames = (subRes.data && subRes.data.length) ? subRes.data.map(s => s.name) : SUBCATEGORY_CATEGORY.map(s => s.sub);
      const catNames = (catRes.data && catRes.data.length) ? catRes.data.map(c => c.name) : SUBCATEGORY_CATEGORY.map(s => s.cat);

      setInflowForm(prev => ({
        ...prev,
        source: prev.source || "",
        bank: prev.bank || "",
      }));
      setOutflowForm(prev => ({
        ...prev,
        sub_category: prev.sub_category || "",
        category: prev.category || "",
        deducted_from: prev.deducted_from || "",
      }));
    } catch (err) {
      // Error loading initial data
    }
  };

  // Auto-load when opening Financial tab or when switching to inflow/outflow
  useEffect(() => {
    if (nav === 'settings' && settingsTab === 'financial') {
      (async () => {
        await refreshSettingsLists();
        await loadBankLimits();
      })();
    }
  }, [nav, settingsTab]);
  
  // Ensure settings are loaded when accessing inflow/outflow sections
  useEffect(() => {
    if ((nav === 'inflow' || nav === 'outflow') && !settingsLoaded) {
      (async () => {
        try {
          await refreshSettingsLists();
          setSettingsLoaded(true);
        } catch (settingsErr) {
          // Continue with other data loading even if settings fail
        }
      })();
    }
  }, [nav, settingsLoaded]);

  // Debounced inline limit check for Add Inflow form
  useEffect(() => {
    const bank = inflowForm.bank;
    const amount = parseFloat(inflowForm.amount_rs);
    const date = inflowForm.date;
    if (!bank || !date || isNaN(amount) || amount <= 0) {
      setLimitCheck(null);
      return;
    }
    setLimitCheckLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await checkBankLimitApi({ bank, amount, date });
        setLimitCheck(res?.data || null);
      } catch (e) {
        setLimitCheck(null);
      } finally {
        setLimitCheckLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [inflowForm.bank, inflowForm.amount_rs, inflowForm.date]);

  // Update local bankLimits state when editing inputs
  const changeBankLimitField = (bankAccountId, field, value) => {
    setBankLimits((prev) => prev.map((bl) => bl.bank_account_id === bankAccountId ? { ...bl, [field]: value } : bl));
  };

  const saveBankLimit = async (bankAccountId) => {
    if (!adminCanWrite) return;
    const bl = bankLimits.find((x) => x.bank_account_id === bankAccountId);
    if (!bl) return;
    const toNumOrNull = (v) => {
      if (v === null || v === undefined) return null;
      if (v === '') return null;
      const n = Number(v);
      return isNaN(n) ? null : n;
    };
    const payload = {
      monthly_limit_rs: toNumOrNull(bl.monthly_limit_rs),
      quarterly_limit_rs: toNumOrNull(bl.quarterly_limit_rs),
      yearly_limit_rs: toNumOrNull(bl.yearly_limit_rs),
      is_active: true,
    };
    try {
      await upsertBankLimitApi(bankAccountId, payload);
      await loadBankLimits();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed to save bank limit');
    }
  };

  // Reorder helpers (persist sort_order via update endpoints)
  const moveItem = async (list, id, dir, updater, key = 'sort_order') => {
    const sorted = [...list].sort((a, b) => ((a[key] ?? 999999) - (b[key] ?? 999999)) || a.name.localeCompare(b.name));
    const i = sorted.findIndex(x => x.id === id);
    if (i < 0) return;
    const j = dir === 'up' ? i - 1 : i + 1;
    if (j < 0 || j >= sorted.length) return;
    const A = sorted[i];
    const B = sorted[j];
    const soA = A[key] ?? (i + 1);
    const soB = B[key] ?? (j + 1);
    setLoading(true);
    try {
      await updater(A.id, { [key]: soB });
      await updater(B.id, { [key]: soA });
      await refreshSettingsLists();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Reorder failed');
    } finally { setLoading(false); }
  };
  const moveSource = (id, dir) => moveItem(inflowSources, id, dir, updateInflowSourceApi);
  const moveBank = (id, dir) => moveItem(bankAccounts, id, dir, updateBankAccountApi);
  const moveCategory = (id, dir) => moveItem(outflowCategories, id, dir, updateOutflowCategoryApi);
  const moveSubcategory = (id, dir) => moveItem(outflowSubcategories, id, dir, updateOutflowSubcategoryApi);

  // Toggle: Consider for Sales (Inflow Sources)
  const toggleSourceSales = async (source) => {
    if (!adminCanWrite) return;
    try {
      await updateInflowSourceApi(source.id, { consider_for_sales: !source.consider_for_sales });
      await refreshSettingsLists();
    } catch (err) {
      alert('Failed to update: Consider for Sales');
    }
  };
  // Drag & Drop helpers (swap sort_order between dragged and target item)
  const getListAndUpdater = (type) => {
    switch (type) {
      case 'source': return { list: inflowSources, updater: updateInflowSourceApi };
      case 'bank': return { list: bankAccounts, updater: updateBankAccountApi };
      case 'category': return { list: outflowCategories, updater: updateOutflowCategoryApi };
      case 'subcategory': return { list: outflowSubcategories, updater: updateOutflowSubcategoryApi };
      default: return { list: [], updater: null };
    }
  };

  const handleDragStart = (type, id) => {
    if (!adminCanWrite) return;
    setDragging({ type, id });
  };

  const handleDragOver = (e) => {
    if (!adminCanWrite) return;
    e.preventDefault();
  };

  const handleDrop = async (type, targetId) => {
    if (!adminCanWrite) return;
    if (!dragging || dragging.type !== type || dragging.id === targetId) {
      setDragging(null);
      return;
    }
    const { list, updater } = getListAndUpdater(type);
    if (!updater) return;
    const a = list.find(x => x.id === dragging.id);
    const b = list.find(x => x.id === targetId);
    if (!a || !b) { setDragging(null); return; }
    const soA = a.sort_order ?? 999999;
    const soB = b.sort_order ?? 999999;
    try {
      setLoading(true);
      await updater(a.id, { sort_order: soB });
      await updater(b.id, { sort_order: soA });
      await refreshSettingsLists();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Reorder failed');
    } finally {
      setDragging(null);
      setLoading(false);
    }
  };

  // Open handlers
  const openAddSource = () => { setNav('settings'); setSettingsTab('financial'); setEditingSourceId(null); setSourceForm({ name: '' }); setShowSourceModal(true); };
  const openEditSource = (s) => { setNav('settings'); setSettingsTab('financial'); setEditingSourceId(s.id); setSourceForm({ name: s.name }); setShowSourceModal(true); };

  const openAddBank = () => { setNav('settings'); setSettingsTab('financial'); setEditingBankId(null); setBankForm({ name: '', is_default_cash: false }); setShowBankModal(true); };
  const openEditBank = (b) => { setNav('settings'); setSettingsTab('financial'); setEditingBankId(b.id); setBankForm({ name: b.name, is_default_cash: !!b.is_default_cash }); setShowBankModal(true); };

  const openAddCategory = () => { setNav('settings'); setSettingsTab('categories'); setEditingCategoryId(null); setCategoryForm({ name: '' }); setShowCategoryModal(true); };
  const openEditCategory = (c) => { setNav('settings'); setSettingsTab('categories'); setEditingCategoryId(c.id); setCategoryForm({ name: c.name }); setShowCategoryModal(true); };

  function normalizeOutflowSubcategory(subcategory) {
    return {
      ...subcategory,
      accounting_treatment: subcategory?.accounting_treatment || OUTFLOW_TREATMENT_EXPENSE,
    };
  }
  const openAddSubcategory = () => { setNav('settings'); setSettingsTab('categories'); setEditingSubcategoryId(null); setSubcategoryForm({ name: '', category_id: outflowCategories[0]?.id || '', accounting_treatment: OUTFLOW_TREATMENT_EXPENSE }); setShowSubcategoryModal(true); };
  const openEditSubcategory = (s) => { setNav('settings'); setSettingsTab('categories'); setEditingSubcategoryId(s.id); setSubcategoryForm({ name: s.name, category_id: s.category_id, accounting_treatment: s.accounting_treatment || OUTFLOW_TREATMENT_EXPENSE }); setShowSubcategoryModal(true); };

  // Save/Delete handlers
  const saveSource = async () => {
    if (!adminCanWrite) return;
    if (!sourceForm.name.trim()) return;
    try {
      setLoading(true);
      if (editingSourceId) await updateInflowSourceApi(editingSourceId, { name: sourceForm.name });
      else await createInflowSourceApi({ name: sourceForm.name });
      await refreshSettingsLists();
      setShowSourceModal(false);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save source');
    } finally { setLoading(false); }
  };

  const deleteSource = async (id) => {
    if (!adminCanWrite) return;
    if (!window.confirm('Archive this source?')) return;
    try { setLoading(true); await deleteInflowSourceApi(id); await refreshSettingsLists(); } catch (e) { alert(e?.response?.data?.detail || 'Failed to delete'); } finally { setLoading(false); }
  };

  const saveBank = async () => {
    if (!adminCanWrite) return;
    
    // Enhanced validation
    if (!bankForm.name || !bankForm.name.trim()) { 
      alert('Bank account name is required'); 
      return; 
    }
    
    if (bankForm.name.trim().length < 2) {
      alert('Bank account name must be at least 2 characters long');
      return;
    }
    
    setLoading(true);
    try {
      if (editingBankId) {
        await updateBankAccountApi(editingBankId, { 
          name: bankForm.name.trim(), 
          is_default_cash: bankForm.is_default_cash 
        });
      } else {
        await createBankAccountApi({ 
          name: bankForm.name.trim(), 
          is_default_cash: bankForm.is_default_cash 
        });
      }
      
      // Refresh settings and close modal
      await refreshSettingsLists();
      setShowBankModal(false);
      setBankForm({ name: '', is_default_cash: false });
      setEditingBankId(null);
      
    } catch (err) {
      const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to save bank account';
      alert(errorMessage);
      
      // If authentication error, try to refresh auth
      if (err.response?.status === 401 || err.response?.status === 403) {
        // Don't auto-logout, let user retry
      }
    }
    setLoading(false);
  };

  const deleteBank = async (b) => {
    if (!adminCanWrite) return;
    if (b.is_default_cash) { alert('Default cash account cannot be archived.'); return; }
    if (!window.confirm('Archive this bank account?')) return;
    try { setLoading(true); await deleteBankAccountApi(b.id); await refreshSettingsLists(); } catch (e) { alert(e?.response?.data?.detail || 'Failed to delete'); } finally { setLoading(false); }
  };

  const saveCategory = async () => {
    if (!adminCanWrite) return;
    if (!categoryForm.name.trim()) return;
    try {
      setLoading(true);
      if (editingCategoryId) await updateOutflowCategoryApi(editingCategoryId, { name: categoryForm.name });
      else await createOutflowCategoryApi({ name: categoryForm.name });
      await refreshSettingsLists();
      setShowCategoryModal(false);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save category');
    } finally { setLoading(false); }
  };

  const deleteCategory = async (id) => {
    if (!adminCanWrite) return;
    if (!window.confirm('Archive this category?')) return;
    try { setLoading(true); await deleteOutflowCategoryApi(id); await refreshSettingsLists(); } catch (e) { alert(e?.response?.data?.detail || 'Failed to delete'); } finally { setLoading(false); }
  };

  const saveSubcategory = async () => {
    if (!adminCanWrite) return;
    if (!subcategoryForm.name.trim() || !subcategoryForm.category_id) return;
    try {
      setLoading(true);
      const payload = {
        name: subcategoryForm.name,
        category_id: Number(subcategoryForm.category_id),
        accounting_treatment: subcategoryForm.accounting_treatment || OUTFLOW_TREATMENT_EXPENSE,
      };
      const res = editingSubcategoryId
        ? await updateOutflowSubcategoryApi(editingSubcategoryId, payload)
        : await createOutflowSubcategoryApi(payload);
      const savedSubcategory = normalizeOutflowSubcategory({
        ...res?.data,
        ...payload,
        id: res?.data?.id || editingSubcategoryId,
      });
      setOutflowSubcategories((prev) => {
        const next = editingSubcategoryId
          ? prev.map((item) => (item.id === editingSubcategoryId ? { ...item, ...savedSubcategory } : item))
          : [...prev, savedSubcategory];
        return next.map(normalizeOutflowSubcategory);
      });
      await refreshSettingsLists();
      setShowSubcategoryModal(false);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save subcategory');
    } finally { setLoading(false); }
  };

  const syncSubcategoryTreatment = async (subcategory) => {
    if (!adminCanWrite || !subcategory?.id) return;
    if (!window.confirm(`Sync existing transactions for "${subcategory.name}" to "${subcategory.accounting_treatment === OUTFLOW_TREATMENT_PROFIT_WITHDRAWAL ? 'Profit Withdrawal' : 'Expense'}"?`)) return;
    try {
      setLoading(true);
      const res = await syncOutflowSubcategoryTreatmentApi(subcategory.id);
      await refreshSettingsLists();
      await refreshAllData();
      alert(`Updated ${Number(res?.data?.updated || 0)} transaction(s).`);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to sync treatment');
    } finally {
      setLoading(false);
    }
  };

  const deleteSubcategory = async (id) => {
    if (!adminCanWrite) return;
    if (!window.confirm('Archive this subcategory?')) return;
    try { setLoading(true); await deleteOutflowSubcategoryApi(id); await refreshSettingsLists(); } catch (e) { alert(e?.response?.data?.detail || 'Failed to delete'); } finally { setLoading(false); }
  };

  const openAddUser = () => {
    setEditingUserId(null);
    setUserForm({ name: '', email: '', role: 'Owner', otp_enabled: true, password: '' });
    setShowUserModal(true);
  };

  const openEditUser = (u) => {
    if (isITAdmin && u.role === 'Owner') return;
    setEditingUserId(u.id);
    setUserForm({ name: u.name, email: u.email, role: u.role, otp_enabled: u.otp_enabled, password: '' });
    setShowUserModal(true);
  };

  const handleUserFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setUserForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };
  const saveUser = async () => {
    if (!userForm.name || !userForm.email) return;
    // IT Admin cannot create or update Owner users
    if (isITAdmin) {
      if (userForm.role === 'Owner') return;
      if (editingUserId) {
        const target = usersList.find((u) => u.id === editingUserId);
        if (target?.role === 'Owner') return;
      }
    }
    try {
      setLoading(true);
      if (editingUserId) {
        const payload = {
          name: userForm.name,
          email: userForm.email,
          role: userForm.role,
          otp_enabled: userForm.otp_enabled,
        };
        if (userForm.password && userForm.password.trim() !== '') {
          payload.password = userForm.password;
        }
        await updateUserApi(editingUserId, payload);
      } else {
        const payload = {
          name: userForm.name,
          email: userForm.email,
          role: userForm.role,
          otp_enabled: userForm.otp_enabled,
          password: userForm.password || '',
        };
        await createUserApi(payload);
      }
      // Refresh users from backend
      const res = await listUsers();
      const refreshed = res.data || [];
      setUsersList(refreshed);
      // If current user updated themselves, refresh local user data
      if (editingUserId && user && String(editingUserId) === String(user.id)) {
        const updatedSelf = refreshed.find(u => String(u.id) === String(user.id));
        if (updatedSelf) {
          const newUserData = {
            email: updatedSelf.email,
            id: updatedSelf.id,
            role: updatedSelf.role,
            name: updatedSelf.name || updatedSelf.email,
          };
          setUser(newUserData);
          localStorage.setItem('user', JSON.stringify(newUserData));
        }
      }
      setShowUserModal(false);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to save user.');
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (id) => {
    const target = usersList.find((u) => u.id === id);
    if (isITAdmin && target?.role === 'Owner') return;
    if (!window.confirm('Delete this user?')) return;
    try {
      setLoading(true);
      await deleteUserApi(id);
      const res = await listUsers();
      setUsersList(res.data || []);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to delete user.');
    } finally {
      setLoading(false);
    }
  };

  const toggleUserOTP = async (id) => {
    const target = usersList.find((u) => u.id === id);
    if (isITAdmin && target?.role === 'Owner') return;
    try {
      setLoading(true);
      await toggleUserOtpApi(id);
      const res = await listUsers();
      setUsersList(res.data || []);
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to toggle OTP.');
    } finally {
      setLoading(false);
    }
  };

  // Dashboard contribution chart preferences
  const [inflowChartType, setInflowChartType] = useState('pie');
  const [outflowChartType, setOutflowChartType] = useState('pie');
  const [outflowContributionGroupBy, setOutflowContributionGroupBy] = useState('sub_category');

  useEffect(() => {
    if (!user?.email) return;
    try {
      const raw = localStorage.getItem(`dashboard_chart_prefs_${user.email}`);
      if (!raw) return;
      const prefs = JSON.parse(raw);
      if (prefs?.inflowChartType === 'pie' || prefs?.inflowChartType === 'bar') {
        setInflowChartType(prefs.inflowChartType);
      }
      if (prefs?.outflowChartType === 'pie' || prefs?.outflowChartType === 'bar') {
        setOutflowChartType(prefs.outflowChartType);
      }
      if (prefs?.outflowContributionGroupBy === 'sub_category' || prefs?.outflowContributionGroupBy === 'category') {
        setOutflowContributionGroupBy(prefs.outflowContributionGroupBy);
      }
    } catch (_) {}
  }, [user?.email]);

  useEffect(() => {
    if (!user?.email) return;
    localStorage.setItem(`dashboard_chart_prefs_${user.email}`, JSON.stringify({
      inflowChartType,
      outflowChartType,
      outflowContributionGroupBy,
    }));
  }, [user?.email, inflowChartType, outflowChartType, outflowContributionGroupBy]);
  // Utility
  function getMonth(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d)) return "";
    return d.toLocaleString("default", { month: "short", year: "numeric" });
  }

  function formatReflectingMonth(dateStr) {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (isNaN(d)) return "-";
    return d.toLocaleString("default", { month: "long", year: "numeric" });
  }

  function formatCurrency(amount) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  function formatLongDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  const CHART_TOP_N = 8;
  const UNKNOWN_LABELS = new Set(['unknown', 'unknown source', 'uncategorized', 'n/a', '-']);

  const prepareContributionDataset = (rows, labelKey, amountKey = 'amount') => {
    const valid = [];
    let excludedCount = 0;
    let excludedAmount = 0;

    (Array.isArray(rows) ? rows : []).forEach((row) => {
      const rawLabel = String(row?.[labelKey] ?? '').trim();
      const normalized = rawLabel.toLowerCase();
      const amount = Number(row?.[amountKey] || 0);
      const isMissingLabel = !rawLabel || UNKNOWN_LABELS.has(normalized);

      if (isMissingLabel || !Number.isFinite(amount) || amount <= 0) {
        if (Number.isFinite(amount) && amount > 0) {
          excludedCount += 1;
          excludedAmount += amount;
        }
        return;
      }

      valid.push({ key: rawLabel, amount });
    });

    const grouped = valid.reduce((acc, item) => {
      acc[item.key] = (acc[item.key] || 0) + item.amount;
      return acc;
    }, {});

    const sorted = Object.entries(grouped)
      .map(([key, amount]) => ({ key, amount: Number(amount) }))
      .sort((a, b) => b.amount - a.amount);

    const top = sorted.slice(0, CHART_TOP_N);
    const rest = sorted.slice(CHART_TOP_N);
    const othersAmount = rest.reduce((sum, item) => sum + item.amount, 0);
    const chartRows = othersAmount > 0 ? [...top, { key: 'Others', amount: othersAmount, isOthers: true }] : top;

    const totalPlottedAmount = chartRows.reduce((sum, item) => sum + item.amount, 0);
    const totalRawAmount = totalPlottedAmount + excludedAmount;
    const rowsWithPercent = chartRows.map((item) => ({
      ...item,
      percentage: totalPlottedAmount > 0 ? Number(((item.amount / totalPlottedAmount) * 100).toFixed(2)) : 0,
    }));

    return {
      chartRows: rowsWithPercent,
      excludedCount,
      excludedAmount,
      totalPlottedAmount,
      totalRawAmount,
    };
  };

  const buildRowsFromTransactions = (rows, labelKey) =>
    (Array.isArray(rows) ? rows : []).map((row) => ({
      [labelKey]: row?.[labelKey],
      amount: Number(row?.amount_rs || 0),
    }));

  const isProfitWithdrawalRow = (row) => (
    (row?.accounting_treatment || OUTFLOW_TREATMENT_EXPENSE) === OUTFLOW_TREATMENT_PROFIT_WITHDRAWAL
  );

  const inflowContributionData = useMemo(() => {
    const apiRows = dashboardData?.inflow_source_breakdown;
    const hasApiRows = Array.isArray(apiRows) && apiRows.length > 0;
    if (hasApiRows) {
      const prepared = prepareContributionDataset(apiRows, 'source', 'amount');
      return {
        ...prepared,
        excludedCount: Number(dashboardData?.inflow_source_excluded_count ?? prepared.excludedCount ?? 0),
        excludedAmount: Number(dashboardData?.inflow_source_excluded_amount ?? prepared.excludedAmount ?? 0),
      };
    }

    const txRows = (originalInflows && originalInflows.length > 0) ? originalInflows : inflows;
    return prepareContributionDataset(buildRowsFromTransactions(txRows, 'source'), 'source', 'amount');
  }, [dashboardData?.inflow_source_breakdown, dashboardData?.inflow_source_excluded_count, dashboardData?.inflow_source_excluded_amount, originalInflows, inflows]);

  const outflowSubcategoryData = useMemo(() => {
    const apiRows = dashboardData?.outflow_subcategory_breakdown;
    const hasApiRows = Array.isArray(apiRows) && apiRows.length > 0;
    if (hasApiRows) {
      const prepared = prepareContributionDataset(apiRows, 'sub_category', 'amount');
      return {
        ...prepared,
        excludedCount: Number(dashboardData?.outflow_subcategory_excluded_count ?? prepared.excludedCount ?? 0),
        excludedAmount: Number(dashboardData?.outflow_subcategory_excluded_amount ?? prepared.excludedAmount ?? 0),
      };
    }

    const txRows = ((originalOutflows && originalOutflows.length > 0) ? originalOutflows : outflows).filter((row) => !isProfitWithdrawalRow(row));
    return prepareContributionDataset(buildRowsFromTransactions(txRows, 'sub_category'), 'sub_category', 'amount');
  }, [dashboardData?.outflow_subcategory_breakdown, dashboardData?.outflow_subcategory_excluded_count, dashboardData?.outflow_subcategory_excluded_amount, originalOutflows, outflows]);

  const outflowCategoryData = useMemo(() => {
    const apiRows = dashboardData?.outflow_category_breakdown;
    const hasApiRows = Array.isArray(apiRows) && apiRows.length > 0;
    if (hasApiRows) {
      const prepared = prepareContributionDataset(apiRows, 'category', 'amount');
      return {
        ...prepared,
        excludedCount: Number(dashboardData?.outflow_category_excluded_count ?? prepared.excludedCount ?? 0),
        excludedAmount: Number(dashboardData?.outflow_category_excluded_amount ?? prepared.excludedAmount ?? 0),
      };
    }

    const txRows = ((originalOutflows && originalOutflows.length > 0) ? originalOutflows : outflows).filter((row) => !isProfitWithdrawalRow(row));
    return prepareContributionDataset(buildRowsFromTransactions(txRows, 'category'), 'category', 'amount');
  }, [dashboardData?.outflow_category_breakdown, dashboardData?.outflow_category_excluded_count, dashboardData?.outflow_category_excluded_amount, originalOutflows, outflows]);

  const activeOutflowContributionData = outflowContributionGroupBy === 'category'
    ? outflowCategoryData
    : outflowSubcategoryData;

  const headerPeriodRange = useMemo(() => {
    const start = formatLongDate(dashboardData?.start_date);
    const end = formatLongDate(dashboardData?.end_date);
    if (start && end) return `${start} to ${end}`;
    return displayPeriodLabel || '';
  }, [dashboardData?.start_date, dashboardData?.end_date, displayPeriodLabel]);

  // Login and OTP state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [showOTPForm, setShowOTPForm] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [tempToken, setTempToken] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoading(true);
    logout();
    try {
      const response = await login(loginEmail, loginPassword);

      if (response.data.requires_otp) {
        // Store temp token and show OTP form
        setTempToken(response.data.access_token);
        setShowOTPForm(true);
        setLoginError("");
      } else {
        // Direct login (when OTP disabled)
        setAuthToken(response.data.access_token);
        const userData = {
          email: response.data.user.email,
          id: response.data.user.id,
          role: response.data.user.role,
          name: response.data.user.name || response.data.user.email,
        };
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
      }
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Invalid credentials.");
    }
    setLoading(false);
  };
  const handleOTPVerification = async (e) => {
    e.preventDefault();
    setOtpError("");
    setLoading(true);
    try {
      const response = await verifyOTP(loginEmail, otpCode);

      // Set the full access token
      setAuthToken(response.data.access_token);

      // Set user data
      const userData = {
        email: response.data.user.email,
        id: response.data.user.id,
        role: response.data.user.role,
        name: response.data.user.name || response.data.user.email,
      };
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));

      // Reset forms
      setShowOTPForm(false);
      setOtpCode("");
      setLoginEmail("");
      setLoginPassword("");
      setTempToken("");
    } catch (err) {
      setOtpError(err.response?.data?.detail || "Invalid or expired OTP.");
    }
    setLoading(false);
  };
  const handleLogout = () => {
    logout(); // Clear tokens from localStorage
    setUser(null);
    setInflows([]);
    setOutflows([]);
    setAnalytics({ inflow: 0, inflow_usd: 0, outflow: 0, withdrawals: 0, profit: 0 });
    setChartData([]);
    setShowOTPForm(false);
    setNav('dashboard');
  };

  // Helper function to get unique values from current data for filters
  const getUniqueValues = (data, field) => {
    const values = data.map(item => item[field]).filter(val => val != null && val !== '');
    return [...new Set(values)].sort();
  };

  // Helper function to format date for display (e.g., "2025-09-01" -> "September 2025")
  const formatMonthForDisplay = (dateStr) => {
    if (!dateStr) return dateStr;
    try {
      const date = new Date(dateStr + 'T00:00:00'); // Add time to avoid timezone issues
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Helper function to get unique reflecting months formatted for display
  const getUniqueReflectingMonths = (data) => {
    const months = getUniqueValues(data, 'reflecting_month');
    return months.map(month => ({
      value: month,
      label: formatMonthForDisplay(month)
    }));
  };

  // New filter popup states
  const [showInflowFilterPopup, setShowInflowFilterPopup] = useState(false);
  const [showOutflowFilterPopup, setShowOutflowFilterPopup] = useState(false);
  
  // Filter rules for inflows and outflows (separate from insights)
  const [inflowFilterRules, setInflowFilterRules] = useState([]);
  const [outflowFilterRules, setOutflowFilterRules] = useState([]);

  // Column filters for inflow and outflow tables
  const inflowColumnFilters = useColumnFilters(inflows);
  const outflowColumnFilters = useColumnFilters(outflows);

  // Calculate filtered totals whenever filtered data changes
  useEffect(() => {
    // Calculate inflow totals from filtered data
    const inflowInr = inflowColumnFilters.filteredAndSortedData.reduce((sum, item) => sum + (parseFloat(item.amount_rs) || 0), 0);
    const inflowUsd = inflowColumnFilters.filteredAndSortedData.reduce((sum, item) => sum + (parseFloat(item.amount_usd) || 0), 0);
    setFilteredInflowTotals({ inr: inflowInr, usd: inflowUsd });
  }, [inflowColumnFilters.filteredAndSortedData]);

  useEffect(() => {
    // Calculate outflow totals from filtered data
    const outflowInr = outflowColumnFilters.filteredAndSortedData.reduce((sum, item) => {
      if (isProfitWithdrawalRow(item)) return sum;
      return sum + (parseFloat(item.amount_rs) || 0);
    }, 0);
    const withdrawalInr = outflowColumnFilters.filteredAndSortedData.reduce((sum, item) => {
      if (!isProfitWithdrawalRow(item)) return sum;
      return sum + (parseFloat(item.amount_rs) || 0);
    }, 0);
    setFilteredOutflowTotals({ inr: outflowInr, withdrawals: withdrawalInr });
  }, [outflowColumnFilters.filteredAndSortedData]);

  const selectedInflowSummary = useMemo(() => {
    const rowMap = new Map();
    (inflowColumnFilters.filteredAndSortedData || []).forEach((row) => {
      if (row?.id != null) rowMap.set(row.id, row);
    });
    (inflows || []).forEach((row) => {
      if (row?.id != null && !rowMap.has(row.id)) rowMap.set(row.id, row);
    });

    return selectedInflows.reduce((summary, id) => {
      const row = rowMap.get(id);
      if (!row) return summary;
      summary.amountRs += parseFloat(row.amount_rs) || 0;
      summary.amountUsd += parseFloat(row.amount_usd) || 0;
      return summary;
    }, {
      count: selectedInflows.length,
      amountRs: 0,
      amountUsd: 0,
    });
  }, [selectedInflows, inflowColumnFilters.filteredAndSortedData, inflows]);

  const selectedOutflowSummary = useMemo(() => {
    const rowMap = new Map();
    (outflowColumnFilters.filteredAndSortedData || []).forEach((row) => {
      if (row?.id != null) rowMap.set(row.id, row);
    });
    (outflows || []).forEach((row) => {
      if (row?.id != null && !rowMap.has(row.id)) rowMap.set(row.id, row);
    });

    return selectedOutflows.reduce((summary, id) => {
      const row = rowMap.get(id);
      if (!row) return summary;
      summary.amountRs += parseFloat(row.amount_rs) || 0;
      return summary;
    }, {
      count: selectedOutflows.length,
      amountRs: 0,
    });
  }, [selectedOutflows, outflowColumnFilters.filteredAndSortedData, outflows]);

  const inflowSourceOptions = useMemo(
    () => (inflowSources.length ? inflowSources.map((s) => s.name) : INFLOW_SOURCES),
    [inflowSources]
  );

  const inflowBankOptions = useMemo(
    () => (bankAccounts.length ? bankAccounts.filter((b) => b.is_default_cash !== true).map((b) => b.name) : BANKS),
    [bankAccounts]
  );

  const outflowSubcategoryOptions = useMemo(
    () => (outflowSubcategories.length
      ? outflowSubcategories.filter((sub) => sub.is_active !== false).map((sub) => sub.name)
      : SUBCATEGORY_CATEGORY.map((item) => item.sub)),
    [outflowSubcategories]
  );

  const outflowBankOptions = useMemo(
    () => (bankAccounts.length
      ? bankAccounts.filter((b) => b.is_active !== false).map((b) => b.name)
      : DEDUCTED_FROM),
    [bankAccounts]
  );

  const getBulkEditFields = (type) => (type === "outflow" ? BULK_EDIT_OUTFLOW_FIELDS : BULK_EDIT_INFLOW_FIELDS);

  const resetBulkEditState = (type = "inflow") => {
    setBulkEditType(type);
    setBulkEditSelectedFields({});
    setBulkEditForm({ ...BULK_EDIT_EMPTY_FORM });
    setBulkEditError("");
    setBulkEditSaving(false);
  };

  const openBulkEditModal = (type) => {
    resetBulkEditState(type);
    setShowBulkEditModal(true);
  };

  const closeBulkEditModal = () => {
    setShowBulkEditModal(false);
    resetBulkEditState("inflow");
  };

  const getOutflowCategoryForSubcategory = (subCategory) => {
    if (!subCategory) return "";
    const selectedSubcat = outflowSubcategories.find((sub) => sub.name === subCategory);
    if (selectedSubcat) {
      const parentCategory = outflowCategories.find((cat) => cat.id === selectedSubcat.category_id);
      if (parentCategory?.name) return parentCategory.name;
    }
    return SUBCATEGORY_CATEGORY.find((item) => item.sub === subCategory)?.cat || "";
  };

  const getOutflowAccountingTreatmentForSubcategory = (subCategory) => {
    if (!subCategory) return OUTFLOW_TREATMENT_EXPENSE;
    const selectedSubcat = outflowSubcategories.find((sub) => sub.name === subCategory);
    return selectedSubcat?.accounting_treatment || OUTFLOW_TREATMENT_EXPENSE;
  };

  const handleBulkEditFieldToggle = (name, checked) => {
    setBulkEditSelectedFields((prev) => {
      const next = { ...prev, [name]: checked };
      if (!checked && name === "sub_category") {
        delete next.category;
      }
      return next;
    });
    setBulkEditError("");
  };

  const handleBulkEditFieldChange = (name, value) => {
    setBulkEditForm((prev) => {
      const next = { ...prev, [name]: value };
      if (bulkEditType === "outflow" && name === "sub_category") {
        if (value === "Other") {
          next.category = "";
        } else {
          next.category = getOutflowCategoryForSubcategory(value);
        }
      }
      return next;
    });
    setBulkEditError("");
  };

  const buildSelectedRowMap = (preferredRows, fallbackRows) => {
    const rowMap = new Map();
    (preferredRows || []).forEach((row) => {
      if (row?.id != null) rowMap.set(row.id, row);
    });
    (fallbackRows || []).forEach((row) => {
      if (row?.id != null && !rowMap.has(row.id)) rowMap.set(row.id, row);
    });
    return rowMap;
  };

  const getSelectedRowsForBulkEdit = (type) => {
    const isOutflow = type === "outflow";
    const ids = isOutflow ? selectedOutflows : selectedInflows;
    const filteredRows = isOutflow ? outflowColumnFilters.filteredAndSortedData : inflowColumnFilters.filteredAndSortedData;
    const fallbackRows = isOutflow ? outflows : inflows;
    const rowMap = buildSelectedRowMap(filteredRows, fallbackRows);
    return ids.map((id) => rowMap.get(id)).filter(Boolean);
  };

  const getRowsForExport = (type) => {
    const isOutflow = type === "outflow";
    const ids = isOutflow ? selectedOutflows : selectedInflows;
    const filteredRows = isOutflow ? outflowColumnFilters.filteredAndSortedData : inflowColumnFilters.filteredAndSortedData;
    const fallbackRows = isOutflow ? outflows : inflows;

    if (ids.length > 0) {
      const rowMap = buildSelectedRowMap(filteredRows, fallbackRows);
      return {
        scope: "selected",
        rows: ids.map((id) => rowMap.get(id)).filter(Boolean),
      };
    }

    return {
      scope: "filtered",
      rows: Array.isArray(filteredRows) ? filteredRows : [],
    };
  };

  const escapeCsvCell = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    if (/[",\n\r]/.test(str)) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const downloadCsvFile = (filename, headers, rows) => {
    const csvLines = [
      headers.join(","),
      ...rows.map((row) => headers.map((header) => escapeCsvCell(row?.[header])).join(",")),
    ];
    const blob = new Blob([csvLines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const getExportHeaders = (type) => (
    type === "outflow"
      ? ["date", "reflecting_month", "expense", "amount_rs", "sub_category", "category", "deducted_from", "mode", "nature", "vendor", "remark"]
      : ["date", "reflecting_month", "source", "amount_rs", "amount_usd", "bank", "reflecting_bank", "request_inward", "remark"]
  );

  const mapRowsForExport = (type, rows) => (
    (rows || []).map((row) => {
      if (type === "outflow") {
        return {
          date: row?.date || "",
          reflecting_month: row?.reflecting_month ? formatReflectingMonth(row.reflecting_month) : "",
          expense: row?.expense || "",
          amount_rs: row?.amount_rs ?? "",
          sub_category: row?.sub_category || "",
          category: row?.category || "",
          deducted_from: row?.deducted_from || "",
          mode: row?.mode || "",
          nature: row?.nature || "",
          vendor: row?.vendor || "",
          remark: row?.remark || "",
        };
      }

      return {
        date: row?.date || "",
        reflecting_month: row?.reflecting_month ? formatReflectingMonth(row.reflecting_month) : "",
        source: row?.source || "",
        amount_rs: row?.amount_rs ?? "",
        amount_usd: row?.amount_usd ?? "",
        bank: row?.bank || "",
        reflecting_bank: row?.reflecting_bank || "",
        request_inward: row?.request_inward === true ? "Yes" : row?.request_inward === false ? "No" : "",
        remark: row?.remark || "",
      };
    })
  );

  const handleTableExport = (type) => {
    const { scope, rows } = getRowsForExport(type);
    if (!rows.length) {
      alert("No rows available to export.");
      return;
    }

    const headers = getExportHeaders(type);
    const exportRows = mapRowsForExport(type, rows);

    const safeDate = new Date().toISOString().slice(0, 10);
    const filename = `${type === "outflow" ? "outflows" : "inflows"}_${scope}_${safeDate}.csv`;

    downloadCsvFile(filename, headers, exportRows);
  };

  const applyBulkRemarkValue = (existingRemark, nextRemark) => {
    const trimmedRemark = (nextRemark || "").trim();
    const recurringMatch = String(existingRemark || "").match(/^(RECURRING_PAID:\d+:(?:OCC_\d+:)?DUE_[0-9-]+:PAID_[0-9-]+)(?:\s*\|.*)?$/);
    if (!recurringMatch) return trimmedRemark;
    return trimmedRemark ? `${recurringMatch[1]} | ${trimmedRemark}` : recurringMatch[1];
  };

  const validateBulkEdit = () => {
    const activeFields = Object.entries(bulkEditSelectedFields).filter(([, checked]) => !!checked).map(([name]) => name);
    if (activeFields.length === 0) return "Select at least one field to update.";

    const isChecked = (name) => !!bulkEditSelectedFields[name];
    const isBlank = (name) => String(bulkEditForm[name] ?? "").trim() === "";

    if (isChecked("date") && !/^\d{4}-\d{2}-\d{2}$/.test(bulkEditForm.date || "")) {
      return "Date must be in YYYY-MM-DD format.";
    }
    if (isChecked("reflecting_month") && !/^\d{4}-\d{2}-\d{2}$/.test(bulkEditForm.reflecting_month || "")) {
      return "Reflecting Month must be a valid month value.";
    }
    if (isChecked("amount_rs")) {
      const amount = parseFloat(bulkEditForm.amount_rs);
      if (isBlank("amount_rs") || Number.isNaN(amount) || amount <= 0) {
        return "Amount in Rs must be a valid number greater than 0.";
      }
    }
    if (isChecked("amount_usd") && !isBlank("amount_usd")) {
      const amount = parseFloat(bulkEditForm.amount_usd);
      if (Number.isNaN(amount) || amount < 0) {
        return "Amount in $ must be a valid number.";
      }
    }
    if (isChecked("request_inward") && !["yes", "no"].includes(bulkEditForm.request_inward)) {
      return "Please select a value for Request for Inwards Remittance.";
    }

    if (bulkEditType === "inflow") {
      const requiredFields = ["date", "reflecting_month", "source", "amount_rs", "bank", "reflecting_bank", "request_inward"];
      const invalidRequired = requiredFields.find((field) => isChecked(field) && isBlank(field) && field !== "request_inward");
      if (invalidRequired) {
        return `${BULK_EDIT_INFLOW_FIELDS.find((field) => field.name === invalidRequired)?.label || "Selected field"} is required.`;
      }
    } else {
      const requiredFields = ["date", "reflecting_month", "expense", "amount_rs", "sub_category", "deducted_from", "mode"];
      const invalidRequired = requiredFields.find((field) => isChecked(field) && isBlank(field));
      if (invalidRequired) {
        return `${BULK_EDIT_OUTFLOW_FIELDS.find((field) => field.name === invalidRequired)?.label || "Selected field"} is required.`;
      }
      if (isChecked("sub_category")) {
        if (bulkEditForm.sub_category === "Other") {
          if (!String(bulkEditForm.category || "").trim()) return "Category is required when Subcategory is Other.";
        } else if (!getOutflowCategoryForSubcategory(bulkEditForm.sub_category)) {
          return "Unable to resolve Category for the selected Subcategory.";
        }
      }
    }

    return "";
  };

  const buildBulkInflowPayload = (row) => {
    const payload = {
      date: row.date || "",
      reflecting_month: row.reflecting_month || "",
      source: row.source || "",
      amount_rs: row.amount_rs ?? "",
      amount_usd: row.amount_usd ?? "",
      bank: row.bank || "",
      reflecting_bank: row.reflecting_bank || "",
      request_inward: !!row.request_inward,
      invoice_file: null,
      remark: row.remark || "",
    };

    if (bulkEditSelectedFields.date) payload.date = bulkEditForm.date;
    if (bulkEditSelectedFields.reflecting_month) payload.reflecting_month = bulkEditForm.reflecting_month;
    if (bulkEditSelectedFields.source) payload.source = bulkEditForm.source;
    if (bulkEditSelectedFields.amount_rs) payload.amount_rs = bulkEditForm.amount_rs;
    if (bulkEditSelectedFields.amount_usd) payload.amount_usd = bulkEditForm.amount_usd;
    if (bulkEditSelectedFields.bank) payload.bank = bulkEditForm.bank;
    if (bulkEditSelectedFields.reflecting_bank) payload.reflecting_bank = bulkEditForm.reflecting_bank;
    if (bulkEditSelectedFields.request_inward) payload.request_inward = bulkEditForm.request_inward === "yes";
    if (bulkEditSelectedFields.remark) payload.remark = (bulkEditForm.remark || "").trim();

    return payload;
  };

  const buildBulkOutflowPayload = (row) => {
    const payload = {
      date: row.date || "",
      reflecting_month: row.reflecting_month || "",
      expense: row.expense || "",
      amount_rs: row.amount_rs ?? "",
      sub_category: row.sub_category || "",
      category: row.category || "",
      deducted_from: row.deducted_from || "",
      mode: row.mode || "",
      nature: row.nature || "",
      vendor: row.vendor || "",
      invoice_file: null,
      remark: row.remark || "",
    };

    if (bulkEditSelectedFields.date) payload.date = bulkEditForm.date;
    if (bulkEditSelectedFields.reflecting_month) payload.reflecting_month = bulkEditForm.reflecting_month;
    if (bulkEditSelectedFields.expense) payload.expense = (bulkEditForm.expense || "").trim();
    if (bulkEditSelectedFields.amount_rs) payload.amount_rs = bulkEditForm.amount_rs;
    if (bulkEditSelectedFields.sub_category) {
      payload.sub_category = bulkEditForm.sub_category;
      payload.category = bulkEditForm.sub_category === "Other"
        ? (bulkEditForm.category || "").trim()
        : getOutflowCategoryForSubcategory(bulkEditForm.sub_category);
    }
    if (bulkEditSelectedFields.deducted_from) payload.deducted_from = bulkEditForm.deducted_from;
    if (bulkEditSelectedFields.mode) payload.mode = bulkEditForm.mode;
    if (bulkEditSelectedFields.nature) payload.nature = (bulkEditForm.nature || "").trim();
    if (bulkEditSelectedFields.vendor) payload.vendor = (bulkEditForm.vendor || "").trim();
    if (bulkEditSelectedFields.remark) payload.remark = applyBulkRemarkValue(row.remark, bulkEditForm.remark);

    return payload;
  };

  const handleBulkEditSubmit = async (e) => {
    e.preventDefault();
    const validationMessage = validateBulkEdit();
    if (validationMessage) {
      setBulkEditError(validationMessage);
      return;
    }

    const rows = getSelectedRowsForBulkEdit(bulkEditType);
    if (rows.length === 0) {
      setBulkEditError(`Selected ${bulkEditType} record(s) not found.`);
      return;
    }

    setBulkEditSaving(true);
    setBulkEditError("");

    const isOutflow = bulkEditType === "outflow";
    const updateFn = isOutflow ? updateCashOutflow : updateCashInflow;
    const buildPayload = isOutflow ? buildBulkOutflowPayload : buildBulkInflowPayload;

    try {
      const results = await Promise.allSettled(
        rows.map((row) => updateFn(row.id, buildPayload(row)))
      );

      const failedIds = results
        .map((result, idx) => (result.status === "rejected" ? rows[idx].id : null))
        .filter(Boolean);
      const successCount = results.length - failedIds.length;

      if (successCount > 0) {
        await refreshAllData();
      }

      if (failedIds.length === 0) {
        if (isOutflow) {
          setSelectedOutflows([]);
        } else {
          setSelectedInflows([]);
        }
        closeBulkEditModal();
        alert(`Updated ${successCount} ${bulkEditType}(s) successfully.`);
        return;
      }

      if (successCount > 0) {
        if (isOutflow) {
          setSelectedOutflows(failedIds);
        } else {
          setSelectedInflows(failedIds);
        }
        closeBulkEditModal();
        alert(`Updated ${successCount} ${bulkEditType}(s). ${failedIds.length} failed and remain selected.`);
        return;
      }

      const firstFailure = results.find((result) => result.status === "rejected");
      setBulkEditError(
        firstFailure?.reason?.response?.data?.detail ||
        firstFailure?.reason?.userMessage ||
        firstFailure?.reason?.message ||
        `Failed to update selected ${bulkEditType}(s).`
      );
    } finally {
      setBulkEditSaving(false);
    }
  };

  const renderBulkEditField = (field) => {
    const isChecked = !!bulkEditSelectedFields[field.name];
    const sharedClasses = `w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${!isChecked ? 'opacity-60 cursor-not-allowed' : ''}`;
    const inputDisabled = !isChecked || bulkEditSaving;
    const optionLists = {
      source: inflowSourceOptions,
      bank: inflowBankOptions,
      reflecting_bank: inflowBankOptions,
      deducted_from: outflowBankOptions,
      mode: MODES,
    };

    return (
      <div key={field.name} className="rounded-xl border border-gray-200 p-3 bg-white">
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            checked={isChecked}
            onChange={(e) => handleBulkEditFieldToggle(field.name, e.target.checked)}
            disabled={bulkEditSaving}
            className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-gray-900">{field.label}</span>
              {!field.required && <span className="text-xs text-gray-500">Optional</span>}
            </div>
            {field.type === "date" && (
              <input type="date" value={bulkEditForm[field.name]} onChange={(e) => handleBulkEditFieldChange(field.name, e.target.value)} disabled={inputDisabled} className={sharedClasses} />
            )}
            {field.type === "reflecting_month" && (
              <input
                type="month"
                value={bulkEditForm[field.name] ? String(bulkEditForm[field.name]).slice(0, 7) : ""}
                onChange={(e) => handleBulkEditFieldChange(field.name, e.target.value ? `${e.target.value}-01` : "")}
                disabled={inputDisabled}
                className={sharedClasses}
              />
            )}
            {field.type === "number" && (
              <input type="number" step={field.step || "0.01"} value={bulkEditForm[field.name]} onChange={(e) => handleBulkEditFieldChange(field.name, e.target.value)} disabled={inputDisabled} className={sharedClasses} />
            )}
            {field.type === "text" && (
              <input type="text" value={bulkEditForm[field.name]} onChange={(e) => handleBulkEditFieldChange(field.name, e.target.value)} disabled={inputDisabled} className={sharedClasses} />
            )}
            {field.type === "select" && (
              <select value={bulkEditForm[field.name]} onChange={(e) => handleBulkEditFieldChange(field.name, e.target.value)} disabled={inputDisabled} className={sharedClasses}>
                <option value="">Select {field.label}</option>
                {(optionLists[field.name] || []).map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            )}
            {field.type === "boolean" && (
              <select value={bulkEditForm[field.name]} onChange={(e) => handleBulkEditFieldChange(field.name, e.target.value)} disabled={inputDisabled} className={sharedClasses}>
                <option value="">Select Value</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            )}
            {field.type === "subcategory" && (
              <div className="space-y-3">
                <select value={bulkEditForm.sub_category} onChange={(e) => handleBulkEditFieldChange("sub_category", e.target.value)} disabled={inputDisabled} className={sharedClasses}>
                  <option value="">Select Subcategory</option>
                  {outflowSubcategoryOptions.map((sub) => (
                    <option key={sub} value={sub}>{sub}</option>
                  ))}
                </select>
                {isChecked && bulkEditForm.sub_category && bulkEditForm.sub_category !== "Other" && (
                  <div className="rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-700">
                    Category: <span className="font-semibold">{bulkEditForm.category || getOutflowCategoryForSubcategory(bulkEditForm.sub_category)}</span>
                  </div>
                )}
                {isChecked && bulkEditForm.sub_category === "Other" && (
                  <input type="text" value={bulkEditForm.category} onChange={(e) => handleBulkEditFieldChange("category", e.target.value)} disabled={inputDisabled} placeholder="Enter category" className={sharedClasses} />
                )}
              </div>
            )}
          </div>
        </label>
      </div>
    );
  };

  // Reset page when data changes
  useEffect(() => {
    setInflowPage(0);
  }, [inflows.length, inflows[0]?.id]);

  useEffect(() => {
    setOutflowPage(0);
  }, [outflows.length, outflows[0]?.id]);

  // Clear filters when data changes
  useEffect(() => {
    inflowColumnFilters.clearAllFilters();
  }, [inflows.length, inflows[0]?.id]);

  useEffect(() => {
    outflowColumnFilters.clearAllFilters();
  }, [outflows.length, outflows[0]?.id]);
  
  // Dropdown data for filters (all unique values from server)
  const [inflowFilterDropdownData, setInflowFilterDropdownData] = useState({
    sources: [], banks: [], reflectingBanks: [], reflectingMonths: []
  });
  const [outflowFilterDropdownData, setOutflowFilterDropdownData] = useState({
    expenses: [], categories: [], subCategories: [], modes: [], vendors: [], reflectingMonths: []
  });
  
  // Filter builders for popups
  const [inflowFilterBuilder, setInflowFilterBuilder] = useState({ 
    field: 'reflecting_month', op: 'is', value: '', values: [], min: '', max: '', yesno: 'true', fyYear: '' 
  });
  const [outflowFilterBuilder, setOutflowFilterBuilder] = useState({ 
    field: 'reflecting_month', op: 'is', value: '', values: [], min: '', max: '', yesno: 'true', fyYear: '' 
  });

  // Filter functions for inflows
  const addInflowFilterRule = () => {
    const f = inflowFilterBuilder.field;
    const op = inflowFilterBuilder.op;
    
    let rule = null;
    if (f === 'reflecting_month') {
      if (!inflowFilterBuilder.value) return;
      rule = { field: 'reflecting_month', op: 'is', value: inflowFilterBuilder.value };
    } else if (f === 'amount_rs' || f === 'amount_usd') {
      const min = inflowFilterBuilder.min !== '' ? Number(inflowFilterBuilder.min) : null;
      const max = inflowFilterBuilder.max !== '' ? Number(inflowFilterBuilder.max) : null;
      if (min == null && max == null) return;
      rule = { field: f, min, max };
    } else if (f === 'source' || f === 'bank' || f === 'reflecting_bank') {
      if (op === 'is' && inflowFilterBuilder.value) rule = { field: f, op: 'is', value: inflowFilterBuilder.value };
      if (op === 'is_not' && inflowFilterBuilder.value) rule = { field: f, op: 'is_not', value: inflowFilterBuilder.value };
      if (op === 'is_any' && inflowFilterBuilder.values?.length) rule = { field: f, op: 'is_any', values: inflowFilterBuilder.values };
    } else if (f === 'remark') {
      if (op === 'contains' && inflowFilterBuilder.value) rule = { field: f, op: 'contains', value: inflowFilterBuilder.value };
      if (op === 'not_contains' && inflowFilterBuilder.value) rule = { field: f, op: 'not_contains', value: inflowFilterBuilder.value };
    } else if (f === 'request_inward') {
      rule = { field: 'request_inward', value: inflowFilterBuilder.yesno === 'true' };
    }
    
    if (rule) setInflowFilterRules(prev => [...prev, rule]);
  };

  const removeInflowFilterRule = (idx) => setInflowFilterRules(prev => prev.filter((_, i) => i !== idx));

  // Clear all inflow filters and refresh data
  const clearInflowFilters = () => {
    setInflowFilterRules([]);
    // Refresh data without filters
    refreshAllData();
  };

  // Filter functions for outflows  
  const addOutflowFilterRule = () => {
    const f = outflowFilterBuilder.field;
    const op = outflowFilterBuilder.op;
    
    let rule = null;
    if (f === 'reflecting_month') {
      if (!outflowFilterBuilder.value) return;
      rule = { field: 'reflecting_month', op: 'is', value: outflowFilterBuilder.value };
    } else if (f === 'amount_rs') {
      const min = outflowFilterBuilder.min !== '' ? Number(outflowFilterBuilder.min) : null;
      const max = outflowFilterBuilder.max !== '' ? Number(outflowFilterBuilder.max) : null;
      if (min == null && max == null) return;
      rule = { field: f, min, max };
    } else if (f === 'category' || f === 'sub_category') {
      if (op === 'is' && outflowFilterBuilder.value) rule = { field: f, op: 'is', value: outflowFilterBuilder.value };
      if (op === 'is_not' && outflowFilterBuilder.value) rule = { field: f, op: 'is_not', value: outflowFilterBuilder.value };
      if (op === 'is_any' && outflowFilterBuilder.values?.length) rule = { field: f, op: 'is_any', values: outflowFilterBuilder.values };
    } else if (f === 'expense' || f === 'mode' || f === 'vendor' || f === 'remark') {
      if (op === 'contains' && outflowFilterBuilder.value) rule = { field: f, op: 'contains', value: outflowFilterBuilder.value };
      if (op === 'not_contains' && outflowFilterBuilder.value) rule = { field: f, op: 'not_contains', value: outflowFilterBuilder.value };
    }
    
    if (rule) setOutflowFilterRules(prev => [...prev, rule]);
  };

  const removeOutflowFilterRule = (idx) => setOutflowFilterRules(prev => prev.filter((_, i) => i !== idx));

  // Clear all outflow filters and refresh data
  const clearOutflowFilters = () => {
    setOutflowFilterRules([]);
    // Refresh data without filters
    refreshAllData();
  };

  // Load dropdown data when filter popups are opened
  const loadInflowFilterDropdownData = async () => {
    const data = await getInflowFilterDropdownData();
    setInflowFilterDropdownData(data);
  };

  const loadOutflowFilterDropdownData = async () => {
    const data = await getOutflowFilterDropdownData();
    setOutflowFilterDropdownData(data);
  };

  // Get unique values for filter dropdowns (fetch ALL data from database)
  const getInflowFilterDropdownData = async () => {
    try {
      // Use custom date range to get ALL data from database
      // Set a very broad range: from 2020 to 2030 to capture all possible data
      const startDate = '2020-01-01';
      const endDate = '2030-12-31';
      
      // Fetch multiple pages to get ALL data (API limit is 200 per request)
      let allData = [];
      let offset = 0;
      const limit = 200; // Maximum allowed by API
      let hasMore = true;
      
      while (hasMore) {
        const response = await getInflowsPaginated({
          period: 'custom',
          start: startDate,
          end: endDate,
          limit: limit,
          offset: offset
        });
        
        const pageData = response.data.items || [];
        allData = [...allData, ...pageData];
        
        // Check if we got less than limit, meaning no more data
        hasMore = pageData.length === limit;
        offset += limit;
        
        // Safety check to prevent infinite loop
        if (offset > 10000) break;
      }
      
      
      // Extract unique values for each field
      const uniqueValues = {
        sources: [...new Set(allData.map(item => item.source).filter(Boolean))].sort(),
        banks: [...new Set(allData.map(item => item.bank).filter(Boolean))].sort(),
        reflectingBanks: [...new Set(allData.map(item => item.reflecting_bank).filter(Boolean))].sort(),
        reflectingMonths: [...new Set(allData.map(item => item.reflecting_month).filter(Boolean))].sort()
      };
      
      return uniqueValues;
    } catch (error) {
      return { sources: [], banks: [], reflectingBanks: [], reflectingMonths: [] };
    }
  };
  const getOutflowFilterDropdownData = async () => {
    try {
      // Use custom date range to get ALL data from database
      // Set a very broad range: from 2020 to 2030 to capture all possible data
      const startDate = '2020-01-01';
      const endDate = '2030-12-31';
      
      // Fetch multiple pages to get ALL data (API limit is 200 per request)
      let allData = [];
      let offset = 0;
      const limit = 200; // Maximum allowed by API
      let hasMore = true;
      
      while (hasMore) {
        const response = await getOutflowsPaginated({
          period: 'custom',
          start: startDate,
          end: endDate,
          limit: limit,
          offset: offset
        });
        
        const pageData = response.data.items || [];
        allData = [...allData, ...pageData];
        
        // Check if we got less than limit, meaning no more data
        hasMore = pageData.length === limit;
        offset += limit;
        
        // Safety check to prevent infinite loop
        if (offset > 10000) break;
      }
      
      
      // Extract unique values for each field
      const uniqueValues = {
        expenses: [...new Set(allData.map(item => item.expense).filter(Boolean))].sort(),
        categories: [...new Set(allData.map(item => item.category).filter(Boolean))].sort(),
        subCategories: [...new Set(allData.map(item => item.sub_category).filter(Boolean))].sort(),
        modes: [...new Set(allData.map(item => item.mode).filter(Boolean))].sort(),
        vendors: [...new Set(allData.map(item => item.vendor).filter(Boolean))].sort(),
        reflectingMonths: [...new Set(allData.map(item => item.reflecting_month).filter(Boolean))].sort()
      };
      
      return uniqueValues;
    } catch (error) {
      return { expenses: [], categories: [], subCategories: [], modes: [], vendors: [], reflectingMonths: [] };
    }
  };

  // Convert filter rules to API parameters
  const convertFilterRulesToApiParams = (filterRules) => {
    const params = {};
    const textSearchTerms = []; // Collect all text search terms
    
    filterRules.forEach(rule => {
      switch (rule.field) {
        case 'reflecting_month':
          params.reflecting_month = rule.value;
          break;
        case 'source':
          if (rule.op === 'is') params.source = rule.value;
          // Note: API might not support 'is_not' and 'is_any' for all fields
          // This would need to be implemented on the server side
          break;
        case 'bank':
          if (rule.op === 'is') params.bank = rule.value;
          break;
        case 'reflecting_bank':
          if (rule.op === 'is') params.reflecting_bank = rule.value;
          break;
        case 'category':
          if (rule.op === 'is') params.category = rule.value;
          break;
        case 'sub_category':
          if (rule.op === 'is') params.sub_category = rule.value;
          break;
        case 'expense':
          if (rule.op === 'is') params.expense = rule.value;
          if (rule.op === 'contains' || rule.op === 'not_contains') {
            textSearchTerms.push(rule.value);
          }
          break;
        case 'mode':
          if (rule.op === 'is') params.mode = rule.value;
          if (rule.op === 'contains' || rule.op === 'not_contains') {
            textSearchTerms.push(rule.value);
          }
          break;
        case 'vendor':
          if (rule.op === 'is') params.vendor = rule.value;
          if (rule.op === 'contains' || rule.op === 'not_contains') {
            textSearchTerms.push(rule.value);
          }
          break;
        case 'remark':
          if (rule.op === 'is' || rule.op === 'contains' || rule.op === 'not_contains') {
            textSearchTerms.push(rule.value);
          }
          break;
        case 'amount_rs':
          if (rule.min !== null) params.min_amount = rule.min;
          if (rule.max !== null) params.max_amount = rule.max;
          break;
        case 'amount_usd':
          // Note: API might not support USD amount filtering
          break;
        case 'request_inward':
          params.request_inward = rule.value ? 'yes' : 'no';
          break;
      }
    });
    
    // Combine all text search terms into a single 'q' parameter
    if (textSearchTerms.length > 0) {
      params.q = textSearchTerms.join(' '); // Join with space for combined search
    }
    
    if (Object.keys(params).length > 0) {
      // Applying filters
    }
    return params;
  };
  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    const savedToken = localStorage.getItem('authToken');
    const setAt = parseInt(localStorage.getItem('authTokenSetAt') || '0', 10);
    const FORTY_EIGHT_HOURS = 48 * 60 * 60 * 1000;

    if (savedUser && savedToken) {
      try {
        // Keep logged in only if within 48 hours
        if (setAt && (Date.now() - setAt) < FORTY_EIGHT_HOURS) {
          const userData = JSON.parse(savedUser);
          setUser(userData);
          setAuthToken(savedToken);
        } else {
          handleLogout();
        }
      } catch (err) {
        handleLogout();
      }
    }
  }, []);
  // Load financial year months and settings when user logs in
  useEffect(() => {
    if (!user || showOTPForm) return;
    const loadInitialData = async () => {
      try {
        // Load FY months
        const response = await getFinancialYearMonths();
        setFinancialYearMonths(response.data);
        const currentMonth = response.data.find(month => {
          const monthDate = new Date(month.value);
          const today = new Date();
          return monthDate.getMonth() === today.getMonth() && monthDate.getFullYear() === today.getFullYear();
        }) || response.data[0];

        setInflowForm(prev => ({ ...prev, reflecting_month: currentMonth?.value || "" }));
        setOutflowForm(prev => ({ ...prev, reflecting_month: currentMonth?.value || "" }));
        setSelectedMonth(currentMonth?.value || response.data[0]?.value || "");

        // Load users list
        const usersRes = await listUsers();
        setUsersList(usersRes.data || []);

        // Load settings lists (Phase 1 - read-only)
        const [srcRes, bankRes, catRes, subRes] = await Promise.all([
          listInflowSourcesApi(),
          listBankAccountsApi(),
          listOutflowCategoriesApi(),
          listOutflowSubcategoriesApi(),
        ]);
        const inflowSrc = srcRes.data || [];
        const banks = bankRes.data || [];
        const cats = catRes.data || [];
        const subs = (subRes.data || []).map(normalizeOutflowSubcategory);

        setInflowSources(inflowSrc);
        setBankAccounts(banks);
        setOutflowCategories(cats);
        setOutflowSubcategories(subs);
        setSettingsLoaded(true);

        // Initialize defaults using backend data with fallback
        const inflowSourceNames = inflowSrc.length ? inflowSrc.map(s => s.name) : INFLOW_SOURCES;
        const inflowBankNames = banks.length ? banks.filter(b => !b.is_default_cash).map(b => b.name) : BANKS;
        const subNames = subs.length ? subs.map(s => s.name) : SUBCATEGORY_CATEGORY.map(s => s.sub);
        const catNames = cats.length ? cats.map(c => c.name) : SUBCATEGORY_CATEGORY.map(c => c.cat);

        setInflowForm(prev => ({
          ...prev,
          source: prev.source || "",
          bank: prev.bank || "",
        }));
        setOutflowForm(prev => ({
          ...prev,
          sub_category: prev.sub_category || "",
          category: prev.category || "",
          deducted_from: prev.deducted_from || "",
        }));
      } catch (err) {
        // Error loading initial data
      }
    };
    loadInitialData();
  }, [user, showOTPForm]);

  // Fetch professional dashboard data based on time period
  useEffect(() => {
    if (!user || showOTPForm) return;
    setLoading(true);
    // Clear selections when time period changes
    setSelectedInflows([]);
    setSelectedOutflows([]);
    const fetchDashboardData = async () => {
      try {
        const opts = ((timePeriod === 'custom' || timePeriod === 'custom_range' || timePeriod === 'custom_fy') && customStart && customEnd) ? { start: customStart, end: customEnd } : {};
        const apiPeriod = (timePeriod === 'custom_range' || timePeriod === 'custom_fy') ? 'custom' : timePeriod;
        const [dashboardRes, recentTransactionsRes] = await Promise.all([
          getDashboardData(apiPeriod, opts),
          getRecentTransactions(10),
        ]);
        setDashboardData(dashboardRes.data);
        setRecentTransactions(recentTransactionsRes.data.recent_transactions);

        // Load ALL data for client-side filtering and pagination
        const [inflowRes, outflowRes] = await Promise.all([
          getAllInflowsData({
            period: apiPeriod,
            start: opts.start,
            end: opts.end,
            sort_by: inflowSortBy,
            sort_dir: inflowSortDir,
            q: (opts.remark ?? inflowQuery) || undefined,
            source: (opts.source ?? inflowSourceFilter) || undefined,
            bank: (opts.bank ?? inflowBankFilter) || undefined,
            reflecting_bank: (opts.reflecting_bank ?? inflowReflectingBankFilter) || undefined,
            reflecting_month: (opts.reflecting_month ?? inflowReflectingMonthFilter) || undefined,
            min_amount: inflowMinAmount ? Number(inflowMinAmount) : undefined,
            max_amount: inflowMaxAmount ? Number(inflowMaxAmount) : undefined,
          }),
          getAllOutflowsData({
            period: apiPeriod,
            start: opts.start,
            end: opts.end,
            sort_by: outflowSortBy,
            sort_dir: outflowSortDir,
            q: outflowQuery || undefined,
            reflecting_month: outflowReflectingMonthFilter || undefined,
            expense: outflowExpenseFilter || undefined,
            category: outflowCategoryFilter || undefined,
            sub_category: outflowSubcategoryFilter || undefined,
            mode: outflowModeFilter || undefined,
            vendor: outflowVendorFilter || undefined,
            remark: outflowRemarksFilter || undefined,
            min_amount: outflowMinAmount ? Number(outflowMinAmount) : undefined,
            max_amount: outflowMaxAmount ? Number(outflowMaxAmount) : undefined,
          }),
        ]);
        setInflows(inflowRes.data.items || []);
        setOriginalInflows(inflowRes.data.items || []);
        setInflowTotal(inflowRes.data.total || 0);
        setOutflows(outflowRes.data.items || []);
        setOriginalOutflows(outflowRes.data.items || []);
        setOutflowTotal(outflowRes.data.total || 0);
        // Clear any existing filters when new data is loaded
        setInflowFilterRules([]);
        setOutflowFilterRules([]);

        // Set analytics from dashboard data
        setAnalytics({
          inflow: dashboardRes.data.total_inflow,
          inflow_usd: dashboardRes.data.total_inflow_usd || 0,
          outflow: dashboardRes.data.total_outflow,
          withdrawals: dashboardRes.data.total_withdrawals || 0,
          profit: dashboardRes.data.net_profit,
        });
        setChartData(dashboardRes.data.cash_flow_data);
      } catch (err) {
        // Error fetching dashboard data
      }
      setLoading(false);
    };
    fetchDashboardData();
  }, [timePeriod, user, showOTPForm, customStart, customEnd]);

  const handleInflowChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      setInflowForm((prev) => ({ ...prev, [name]: files[0] }));
    } else if (name === "request_inward") {
      setInflowForm((prev) => ({ ...prev, [name]: value === "yes" }));
    } else if (name === "date") {
      // When date changes, auto-set reflecting_month to the 1st of that month
      const selected = value;
      if (selected) {
        const d = new Date(selected);
        const reflecting = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
        setInflowForm((prev) => ({ ...prev, date: selected, reflecting_month: reflecting }));
      } else {
        setInflowForm((prev) => ({ ...prev, date: "" }));
      }
    } else {
      // Only update amount_usd if user manually enters a value
      if (name === "amount_usd" && value === "") {
        setInflowForm((prev) => ({ ...prev, [name]: "" }));
      } else {
        setInflowForm((prev) => ({ ...prev, [name]: value }));
      }
    }
  };
  const handleInflowSubmit = async (e) => {
    e.preventDefault();
    
    // Enhanced validation for mandatory fields
    if (!inflowForm.date || !inflowForm.reflecting_month || !inflowForm.source || !inflowForm.amount_rs || !inflowForm.bank || !inflowForm.reflecting_bank) {
      setInflowError("Date, Reflecting Month, Source, Amount in Rs, Bank, and Reflecting Bank are required.");
      return;
    }
    
    // Validate amount
    const amount = parseFloat(inflowForm.amount_rs);
    if (isNaN(amount) || amount <= 0) {
      setInflowError("Amount must be a valid number greater than 0.");
      return;
    }
    
    // Validate date format
    if (!/^\d{4}-\d{2}-\d{2}$/.test(inflowForm.date)) {
      setInflowError("Date must be in YYYY-MM-DD format.");
      return;
    }
    
    setLoading(true);
    setInflowError(""); // Clear previous errors
    
    try {
      // Ensure bank accounts are loaded
      if (bankAccounts.length === 0) {
        await refreshSettingsLists();
      }
      
      if (editingInflow) {
        // Update existing inflow
        await updateCashInflow(editingInflow.id, inflowForm);
      } else {
        // Add new inflow
        await addCashInflow(inflowForm);
      }
      
      // Refresh data with current time period
      const opts = ((timePeriod === 'custom' || timePeriod === 'custom_range' || timePeriod === 'custom_fy') && customStart && customEnd) ? { start: customStart, end: customEnd } : {};
      const apiPeriod = (timePeriod === 'custom_range' || timePeriod === 'custom_fy') ? 'custom' : timePeriod;
      
      const [inflowRes, outflowRes, analyticsRes] = await Promise.all([
        getInflows(apiPeriod, opts),
        getOutflows(apiPeriod, opts),
        getAnalytics(apiPeriod, opts),
      ]);
      
      setInflows(inflowRes.data);
      setOriginalInflows(inflowRes.data);
      setOutflows(outflowRes.data);
      setOriginalOutflows(outflowRes.data);
      // Clear any existing filters when new data is loaded
      setInflowFilterRules([]);
      setOutflowFilterRules([]);
      setAnalytics({
        inflow: analyticsRes.data.total_inflow,
        inflow_usd: analyticsRes.data.total_inflow_usd || 0,
        outflow: analyticsRes.data.total_outflow,
        withdrawals: analyticsRes.data.total_withdrawals || 0,
        profit: analyticsRes.data.profit,
      });
      setChartData(analyticsRes.data.chart_data);

      // If user is on Reminders page, also refresh limits usage overview
      if (nav === 'reminders') {
        await loadLimitsUsage();
      }

      // Reset with dynamic defaults
      setInflowForm({
        date: "",
        reflecting_month: "",
        source: "",
        amount_rs: "",
        amount_usd: "",
        bank: "",
        reflecting_bank: "",
        request_inward: true,
        invoice_file: null,
        remark: "",
      });
      setInflowError("");
      setEditingInflow(null); // Clear edit state
      setShowInflowForm(false); // Close form
      
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || (editingInflow ? "Failed to update inflow." : "Failed to add inflow.");
      setInflowError(errorMessage);
      
      // If it's an authentication error, refresh settings
      if (err.response?.status === 401 || err.response?.status === 403) {
        setTimeout(() => refreshSettingsLists(), 1000);
      }
      
      // Show user-friendly error if available
      if (err.userMessage) {
        setInflowError(err.userMessage);
      }
    }
    setLoading(false);
  };

  const handleOutflowChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === "file") {
      setOutflowForm((prev) => ({ ...prev, [name]: files[0] }));
    } else if (name === "sub_category") {
      if (value === "Other") {
        setOutflowForm((prev) => ({ ...prev, sub_category: value, category: "" }));
      } else {
        // Prefer backend mapping
        if (outflowSubcategories.length && outflowCategories.length) {
          const sub = outflowSubcategories.find(s => s.name === value);
          if (sub) {
            const catObj = outflowCategories.find(c => c.id === sub.category_id);
            const catName = catObj?.name || "";
            setOutflowForm((prev) => ({ ...prev, sub_category: value, category: catName }));
            return;
          }
        }
        const cat = SUBCATEGORY_CATEGORY.find((s) => s.sub === value)?.cat || "";
        setOutflowForm((prev) => ({ ...prev, sub_category: value, category: cat }));
      }
    } else if (name === "category" && outflowForm.sub_category === "Other") {
      setOutflowForm((prev) => ({ ...prev, category: value }));
    } else if (name === "date") {
      // Special handling for date changes in recurring transactions
      setOutflowForm((prev) => {
        const newForm = { ...prev, date: value };
        
        // Check if this is a recurring transaction (remark contains RECURRING_PAID)
        if (prev.remark && prev.remark.includes('RECURRING_PAID:')) {
          // Extract the recurring ID, occurrence ID, and due date from the remark
          const match = prev.remark.match(/RECURRING_PAID:(\d+):(?:OCC_(\d+):)?DUE_([0-9-]+):/);
          if (match) {
            const recurringId = match[1];
            const occurrenceId = match[2];
            const dueDate = match[3];
            
            // Extract any user remark after the tracking part
            const userRemarkMatch = prev.remark.match(/\|(.+)$/);
            const userRemark = userRemarkMatch ? userRemarkMatch[1] : '';
            
            // Update the remark with new paid date
            const occSegment = occurrenceId ? `:OCC_${occurrenceId}` : '';
            newForm.remark = `RECURRING_PAID:${recurringId}${occSegment}:DUE_${dueDate}:PAID_${value}${userRemark ? ' |' + userRemark : ''}`;
          }
        }
        
        return newForm;
      });
    } else {
      setOutflowForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleOutflowSubmit = async (e) => {
    e.preventDefault();
    // Enhanced validation for mandatory fields
    if (!outflowForm.date || !outflowForm.reflecting_month || !outflowForm.expense || !outflowForm.amount_rs || !outflowForm.sub_category || !outflowForm.category || !outflowForm.deducted_from || !outflowForm.mode) {
      setOutflowError("Date, Reflecting Month, Expense, Amount in Rs, Sub-Category, Category, Deducted From, and Mode are required.");
      return;
    }
    setLoading(true);
    try {
      if (editingOutflow) {
        // Update existing outflow
        await updateCashOutflow(editingOutflow.id, outflowForm);
      } else {
        // Add new outflow
        await addCashOutflow(outflowForm);
      }
      
      // Refresh data with current time period
      const opts = ((timePeriod === 'custom' || timePeriod === 'custom_range' || timePeriod === 'custom_fy') && customStart && customEnd) ? { start: customStart, end: customEnd } : {};
      const apiPeriod = (timePeriod === 'custom_range' || timePeriod === 'custom_fy') ? 'custom' : timePeriod;
      const [inflowRes, outflowRes, analyticsRes] = await Promise.all([
        getInflows(apiPeriod, opts),
        getOutflows(apiPeriod, opts),
        getAnalytics(apiPeriod, opts),
      ]);
      setInflows(inflowRes.data);
      setOriginalInflows(inflowRes.data);
      setOutflows(outflowRes.data);
      setOriginalOutflows(outflowRes.data);
      // Clear any existing filters when new data is loaded
      setInflowFilterRules([]);
      setOutflowFilterRules([]);
      setAnalytics({
        inflow: analyticsRes.data.total_inflow,
        inflow_usd: analyticsRes.data.total_inflow_usd || 0,
        outflow: analyticsRes.data.total_outflow,
        withdrawals: analyticsRes.data.total_withdrawals || 0,
        profit: analyticsRes.data.profit,
      });
      setChartData(analyticsRes.data.chart_data);
      
      setOutflowForm({
        date: "",
        reflecting_month: financialYearMonths.find(m => {
          const monthDate = new Date(m.value);
          const today = new Date();
          return monthDate.getMonth() === today.getMonth() && monthDate.getFullYear() === today.getFullYear();
        })?.value || financialYearMonths[0]?.value || "",
        expense: "",
        amount_rs: "",
        sub_category: "",
        category: "",
        deducted_from: "",
        mode: "",
        nature: "",
        vendor: "",
        invoice_file: null,
        remark: "",
      });
      setOutflowError("");
      setEditingOutflow(null); // Clear edit state
      setShowOutflowForm(false); // Close form
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || (editingOutflow ? "Failed to update outflow." : "Failed to add outflow.");
      setOutflowError(errorMessage);
    }
    setLoading(false);
  };

  const handleDeleteInflow = async (id) => {
    if (window.confirm("Are you sure you want to delete this inflow?")) {
      try {
        await deleteInflow(id);
        // Refresh data with current time period
        const opts = ((timePeriod === 'custom' || timePeriod === 'custom_range' || timePeriod === 'custom_fy') && customStart && customEnd) ? { start: customStart, end: customEnd } : {};
        const apiPeriod = (timePeriod === 'custom_range' || timePeriod === 'custom_fy') ? 'custom' : timePeriod;
        const [inflowRes, outflowRes, analyticsRes] = await Promise.all([
          getInflows(apiPeriod, opts),
          getOutflows(apiPeriod, opts),
          getAnalytics(apiPeriod, opts),
        ]);
        setInflows(inflowRes.data);
        setOriginalInflows(inflowRes.data);
        setOutflows(outflowRes.data);
        setOriginalOutflows(outflowRes.data);
        // Clear any existing filters when new data is loaded
        setInflowFilterRules([]);
        setOutflowFilterRules([]);
        setAnalytics({
          inflow: analyticsRes.data.total_inflow,
          outflow: analyticsRes.data.total_outflow,
          withdrawals: analyticsRes.data.total_withdrawals || 0,
          profit: analyticsRes.data.profit,
        });
        setChartData(analyticsRes.data.chart_data);
      } catch (err) {
        // Error deleting inflow
      }
    }
  };

  const handleDeleteOutflow = async (id) => {
    if (window.confirm("Are you sure you want to delete this outflow?")) {
      try {
        await deleteOutflow(id);
        // Refresh data with current time period
        const opts = ((timePeriod === 'custom' || timePeriod === 'custom_range' || timePeriod === 'custom_fy') && customStart && customEnd) ? { start: customStart, end: customEnd } : {};
        const apiPeriod = (timePeriod === 'custom_range' || timePeriod === 'custom_fy') ? 'custom' : timePeriod;
        const [inflowRes, outflowRes, analyticsRes] = await Promise.all([
          getInflows(apiPeriod, opts),
          getOutflows(apiPeriod, opts),
          getAnalytics(apiPeriod, opts),
        ]);
        setInflows(inflowRes.data);
        setOriginalInflows(inflowRes.data);
        setOutflows(outflowRes.data);
        setOriginalOutflows(outflowRes.data);
        // Clear any existing filters when new data is loaded
        setInflowFilterRules([]);
        setOutflowFilterRules([]);
        setAnalytics({
          inflow: analyticsRes.data.total_inflow,
          outflow: analyticsRes.data.total_outflow,
          withdrawals: analyticsRes.data.total_withdrawals || 0,
          profit: analyticsRes.data.profit,
        });
        setChartData(analyticsRes.data.chart_data);
      } catch (err) {
        // Error deleting outflow
      }
    }
  };

  // Bulk delete handlers
  const handleBulkDeleteInflows = async () => {
    if (selectedInflows.length === 0) return;
    if (window.confirm(`Are you sure you want to delete ${selectedInflows.length} inflow(s)?`)) {
      try {
        await Promise.all(selectedInflows.map(id => deleteInflow(id)));
        setSelectedInflows([]);
        // Refresh data with current time period
        const opts = ((timePeriod === 'custom' || timePeriod === 'custom_range' || timePeriod === 'custom_fy') && customStart && customEnd) ? { start: customStart, end: customEnd } : {};
        const apiPeriod = (timePeriod === 'custom_range' || timePeriod === 'custom_fy') ? 'custom' : timePeriod;
        const [inflowRes, outflowRes, analyticsRes] = await Promise.all([
          getInflows(apiPeriod, opts),
          getOutflows(apiPeriod, opts),
          getAnalytics(apiPeriod, opts),
        ]);
        setInflows(inflowRes.data);
        setOriginalInflows(inflowRes.data);
        setOutflows(outflowRes.data);
        setOriginalOutflows(outflowRes.data);
        // Clear any existing filters when new data is loaded
        setInflowFilterRules([]);
        setOutflowFilterRules([]);
        setAnalytics({
          inflow: analyticsRes.data.total_inflow,
          outflow: analyticsRes.data.total_outflow,
          withdrawals: analyticsRes.data.total_withdrawals || 0,
          profit: analyticsRes.data.profit,
        });
        setChartData(analyticsRes.data.chart_data);
      } catch (err) {
        // Error bulk deleting inflows
      }
    }
  };
  const handleBulkDeleteOutflows = async () => {
    if (selectedOutflows.length === 0) return;
    if (window.confirm(`Are you sure you want to delete ${selectedOutflows.length} outflow(s)?`)) {
      try {
        await Promise.all(selectedOutflows.map(id => deleteOutflow(id)));
        setSelectedOutflows([]);
        // Refresh data with current time period
        const opts = ((timePeriod === 'custom' || timePeriod === 'custom_range' || timePeriod === 'custom_fy') && customStart && customEnd) ? { start: customStart, end: customEnd } : {};
        const apiPeriod = (timePeriod === 'custom_range' || timePeriod === 'custom_fy') ? 'custom' : timePeriod;
        const [inflowRes, outflowRes, analyticsRes] = await Promise.all([
          getInflows(apiPeriod, opts),
          getOutflows(apiPeriod, opts),
          getAnalytics(apiPeriod, opts),
        ]);
        setInflows(inflowRes.data);
        setOriginalInflows(inflowRes.data);
        setOutflows(outflowRes.data);
        setOriginalOutflows(outflowRes.data);
        // Clear any existing filters when new data is loaded
        setInflowFilterRules([]);
        setOutflowFilterRules([]);
        setAnalytics({
          inflow: analyticsRes.data.total_inflow,
          outflow: analyticsRes.data.total_outflow,
          withdrawals: analyticsRes.data.total_withdrawals || 0,
          profit: analyticsRes.data.profit,
        });
        setChartData(analyticsRes.data.chart_data);
      } catch (err) {
        // Error bulk deleting outflows
      }
    }
  };
  // Selection handlers
  const handleSelectAllInflows = (checked) => {
    if (checked) {
      setSelectedInflows(inflows.map(inflow => inflow.id));
    } else {
      setSelectedInflows([]);
    }
  };
  const handleSelectInflow = (id, checked) => {
    if (checked) {
      setSelectedInflows(prev => [...prev, id]);
    } else {
      setSelectedInflows(prev => prev.filter(item => item !== id));
    }
  };

  const handleSelectAllOutflows = (checked) => {
    if (checked) {
      setSelectedOutflows(outflows.map(outflow => outflow.id));
    } else {
      setSelectedOutflows([]);
    }
  };

  const handleSelectOutflow = (id, checked) => {
    if (checked) {
      setSelectedOutflows([...selectedOutflows, id]);
    } else {
      setSelectedOutflows(selectedOutflows.filter(selectedId => selectedId !== id));
    }
  };

  // Edit functionality
  const handleEditInflow = (inflow) => {
    setEditingInflow(inflow);
    setShowInflowForm(true);
    setInflowForm({
      date: inflow.date,
      reflecting_month: inflow.reflecting_month,
      source: inflow.source,
      amount_rs: inflow.amount_rs.toString(),
      amount_usd: inflow.amount_usd ? inflow.amount_usd.toString() : "",
      bank: inflow.bank,
      reflecting_bank: inflow.reflecting_bank || inflow.bank,
      request_inward: inflow.request_inward,
      invoice_file: null,
      remark: inflow.remark || "",
    });
  };

  const handleDuplicateInflow = (inflow) => {
    setEditingInflow(null);
    setShowInflowForm(true);
    setInflowForm({
      date: "",
      reflecting_month: inflow.reflecting_month,
      source: inflow.source,
      amount_rs: inflow.amount_rs.toString(),
      amount_usd: inflow.amount_usd ? inflow.amount_usd.toString() : "",
      bank: inflow.bank,
      reflecting_bank: inflow.reflecting_bank || inflow.bank,
      request_inward: inflow.request_inward,
      invoice_file: null,
      remark: inflow.remark || "",
    });
  };

  const handleEditOutflow = (outflow) => {
    setEditingOutflow(outflow);
    setShowOutflowForm(true);
    setOutflowForm({
      date: outflow.date,
      reflecting_month: outflow.reflecting_month,
      expense: outflow.expense,
      amount_rs: outflow.amount_rs.toString(),
      sub_category: outflow.sub_category,
      category: outflow.category,
      deducted_from: outflow.deducted_from,
      mode: outflow.mode,
      nature: outflow.nature || "",
      vendor: outflow.vendor || "",
      invoice_file: null,
      remark: outflow.remark || "",
    });
  };

  const handleDuplicateOutflow = (outflow) => {
    setEditingOutflow(null);
    setShowOutflowForm(true);
    setOutflowForm({
      date: "",
      reflecting_month: outflow.reflecting_month,
      expense: outflow.expense,
      amount_rs: outflow.amount_rs.toString(),
      sub_category: outflow.sub_category,
      category: outflow.category,
      deducted_from: outflow.deducted_from,
      mode: outflow.mode,
      nature: outflow.nature || "",
      vendor: outflow.vendor || "",
      invoice_file: null,
      remark: outflow.remark || "",
    });
  };

  const openCreateRecurringFromOutflow = (outflow) => {
    if (!adminCanWrite || !outflow) return;

    // Parse transaction day safely from YYYY-MM-DD, fallback to 1.
    let dayOfMonth = 1;
    if (typeof outflow.date === 'string') {
      const m = outflow.date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (m) {
        const parsedDay = parseInt(m[3], 10);
        if (parsedDay >= 1 && parsedDay <= 31) dayOfMonth = parsedDay;
      } else {
        const parsedDate = new Date(outflow.date);
        if (!isNaN(parsedDate.getTime())) {
          const parsedDay = parsedDate.getDate();
          if (parsedDay >= 1 && parsedDay <= 31) dayOfMonth = parsedDay;
        }
      }
    }

    setEditingRecurringId(null);
    setRecurringForm({
      name: outflow.expense || '',
      amount_rs: String(outflow.amount_rs ?? ''),
      schedule_type: 'monthly',
      day_of_month: String(dayOfMonth),
      is_active: true,
      expense: outflow.expense || '',
      sub_category: outflow.sub_category || '',
      category: outflow.category || '',
      deducted_from: outflow.deducted_from || '',
      mode: outflow.mode || MODES[0] || '',
      nature: outflow.nature || '',
      vendor: outflow.vendor || '',
      remark: outflow.remark || '',
    });
    setNav('settings');
    setSettingsTab('automation');
    setShowRecurringForm(true);
  };

  const handleCreateRecurringFromSelectedOutflow = () => {
    if (!adminCanWrite) return;
    if (selectedOutflows.length !== 1) return;

    const selectedId = selectedOutflows[0];
    const selectedRow =
      outflowColumnFilters.filteredAndSortedData.find((r) => r?.id === selectedId) ||
      outflows.find((r) => r?.id === selectedId);

    if (!selectedRow) {
      alert('Selected outflow record not found.');
      return;
    }

    openCreateRecurringFromOutflow(selectedRow);
  };

  const handleCancelEdit = () => {
    setEditingInflow(null);
    setEditingOutflow(null);
    // Reset forms to default values
    setInflowForm({
      date: "",
      reflecting_month: "",
      source: "",
      amount_rs: "",
      amount_usd: "",
      bank: "",
      reflecting_bank: "",
      request_inward: true,
      invoice_file: null,
      remark: "",
    });
    setOutflowForm({
      date: "",
      reflecting_month: "",
      expense: "",
      amount_rs: "",
      sub_category: "",
      category: "",
      deducted_from: "",
      mode: "",
      nature: "",
      vendor: "",
      invoice_file: null,
      remark: "",
    });
    setShowInflowForm(false);
    setShowOutflowForm(false);
  };

  // Advanced filtering handlers
  const handleTimePeriodChange = (newPeriod) => {
    setTimePeriod(newPeriod);
    localStorage.setItem('timePeriod', newPeriod);
    // Clear filters and reset pagination when time period changes
    setInflowFilterRules([]);
    setOutflowFilterRules([]);
    setInflowPage(0);
    setOutflowPage(0);
    if (newPeriod === "custom" || newPeriod === "custom_range" || newPeriod === "custom_fy") {
      setShowAdvancedFilters(true);
    } else {
      refreshAllData(null, newPeriod);
    }
  };

  const handleCustomRangeApply = () => {
    if (!customStart || !customEnd) return;
    // Ensure start <= end
    if (new Date(customStart) > new Date(customEnd)) {
      alert('Start date must be before End date');
      return;
    }
    setTimePeriod('custom_range');
    localStorage.setItem('timePeriod', 'custom_range');
    localStorage.setItem('customStart', customStart);
    localStorage.setItem('customEnd', customEnd);
    // Reset pagination when applying custom range
    setInflowPage(0);
    setOutflowPage(0);
    refreshAllData({ start: customStart, end: customEnd }, 'custom_range');
  };
  const handleCustomPeriodApply = () => {
  // Compute start/end using selected FY and the chosen FY month (Apr-Mar)
    if (!selectedMonth) return;
    const monthObj = financialYearMonths.find(m => m.value === selectedMonth);
    const base = monthObj ? new Date(monthObj.value) : new Date(selectedMonth);
    const monthIdx = base.getMonth(); // 0-11
    // FIXED: In FY, Apr(3)..Dec(11) belong to selectedFinancialYear; Jan(0)..Mar(2) belong to selectedFinancialYear+1
    // But we need to use the correct year from the month selection, not calculate it
    const yearForMonth = base.getFullYear();
    const start = new Date(yearForMonth, monthIdx, 1);
    const end = new Date(yearForMonth, monthIdx + 1, 0);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const startStr = fmt(start);
    const endStr = fmt(end);
    setCustomStart(startStr);
    setCustomEnd(endStr);
    setTimePeriod("custom");
    localStorage.setItem('timePeriod', 'custom');
    localStorage.setItem('customStart', startStr);
    localStorage.setItem('customEnd', endStr);
    // Reset pagination when applying custom period
    setInflowPage(0);
    setOutflowPage(0);
    // Keep the custom panel OPEN so user can edit further
    // Immediately refresh with computed dates
    refreshAllData({ start: startStr, end: endStr }, "custom");
  };

  const handleCustomFyApply = () => {
    const startStr = `${selectedFinancialYear}-04-01`;
    const endStr = `${selectedFinancialYear + 1}-03-31`;
    setCustomStart(startStr);
    setCustomEnd(endStr);
    setTimePeriod("custom_fy");
    localStorage.setItem('timePeriod', 'custom_fy');
    localStorage.setItem('customStart', startStr);
    localStorage.setItem('customEnd', endStr);
    setInflowPage(0);
    setOutflowPage(0);
    refreshAllData({ start: startStr, end: endStr }, "custom_fy");
  };

  const handleDuplicateReport = async (report) => {
    try {
      const payload = {
        name: `${report.name} (Copy)`,
        description: report.description || "",
        type: report.type,
        category: report.category || "general",
        tags: report.tags || [],
        inflow_rules: report.inflow_rules || [],
        outflow_rules: report.outflow_rules || [],
        inflow_group_by: report.inflow_group_by || 'month',
        outflow_group_by: report.outflow_group_by || 'month',
      };
      const res = await createReportApi(payload);
      const newReport = res.data;
      
      setCustomReports(prev => {
        const idx = prev.findIndex(r => r.id === report.id);
        if (idx === -1) return [...prev, newReport];
        const next = [...prev];
        next.splice(idx + 1, 0, newReport);
        return next;
      });
    } catch (err) {
      console.error("Failed to duplicate report:", err);
      alert("Failed to duplicate report. Please try again.");
    }
  };
  // Real-time data refresh
  const refreshAllData = async (optsOverride = null, periodOverride = null, inflowOverrides = {}, outflowOverrides = {}) => {
    try {
      const effectivePeriod = periodOverride || timePeriod;
      const opts = optsOverride || ((effectivePeriod === 'custom' || effectivePeriod === 'custom_range' || effectivePeriod === 'custom_fy') && customStart && customEnd ? { start: customStart, end: customEnd } : {});
      const apiPeriod = (effectivePeriod === 'custom_range' || effectivePeriod === 'custom_fy') ? 'custom' : effectivePeriod;
      
      // Merge filter rules with overrides
      const inflowFilterParams = convertFilterRulesToApiParams(inflowFilterRules);
      const outflowFilterParams = convertFilterRulesToApiParams(outflowFilterRules);
      const mergedInflowOverrides = { ...inflowFilterParams, ...inflowOverrides };
      const mergedOutflowOverrides = { ...outflowFilterParams, ...outflowOverrides };
      const [inflowRes, outflowRes, dashboardRes, recentRes] = await Promise.all([
        getAllInflowsData({
          period: apiPeriod,
          start: opts.start,
          end: opts.end,
          sort_by: mergedInflowOverrides.sortBy ?? inflowSortBy,
          sort_dir: mergedInflowOverrides.sortDir ?? inflowSortDir,
          q: (mergedInflowOverrides.remark ?? mergedInflowOverrides.q ?? inflowQuery) || undefined,
          source: (mergedInflowOverrides.source ?? inflowSourceFilter) || undefined,
          bank: (mergedInflowOverrides.bank ?? inflowBankFilter) || undefined,
          reflecting_bank: (mergedInflowOverrides.reflecting_bank ?? inflowReflectingBankFilter) || undefined,
          reflecting_month: (mergedInflowOverrides.reflecting_month ?? inflowReflectingMonthFilter) || undefined,
          request_inward: (() => {
            const val = mergedInflowOverrides.request_inward ?? inflowInwardFilter;
            if (val === undefined || val === null || val === '') return undefined;
            if (typeof val === 'boolean') return val ? 'yes' : 'no';
            return val;
          })(),
          min_amount: (mergedInflowOverrides.amount_rs?.min ?? mergedInflowOverrides.min_amount ?? inflowMinAmount) ? Number(mergedInflowOverrides.amount_rs?.min ?? mergedInflowOverrides.min_amount ?? inflowMinAmount) : undefined,
          max_amount: (mergedInflowOverrides.amount_rs?.max ?? mergedInflowOverrides.max_amount ?? inflowMaxAmount) ? Number(mergedInflowOverrides.amount_rs?.max ?? mergedInflowOverrides.max_amount ?? inflowMaxAmount) : undefined,
        }),
        getAllOutflowsData({
          period: apiPeriod,
          start: opts.start,
          end: opts.end,
          sort_by: mergedOutflowOverrides.sortBy ?? outflowSortBy,
          sort_dir: mergedOutflowOverrides.sortDir ?? outflowSortDir,
          q: (mergedOutflowOverrides.remark ?? mergedOutflowOverrides.q ?? outflowQuery) || undefined,
          reflecting_month: (mergedOutflowOverrides.reflecting_month ?? outflowReflectingMonthFilter) || undefined,
          expense: (mergedOutflowOverrides.expense ?? outflowExpenseFilter) || undefined,
          category: (mergedOutflowOverrides.category ?? outflowCategoryFilter) || undefined,
          sub_category: (mergedOutflowOverrides.sub_category ?? outflowSubcategoryFilter) || undefined,
          mode: (mergedOutflowOverrides.mode ?? outflowModeFilter) || undefined,
          vendor: (mergedOutflowOverrides.vendor ?? outflowVendorFilter) || undefined,
          remark: (mergedOutflowOverrides.remark ?? outflowRemarksFilter) || undefined,
          min_amount: (mergedOutflowOverrides.amount_rs?.min ?? mergedOutflowOverrides.min_amount ?? outflowMinAmount) ? Number(mergedOutflowOverrides.amount_rs?.min ?? mergedOutflowOverrides.min_amount ?? outflowMinAmount) : undefined,
          max_amount: (mergedOutflowOverrides.amount_rs?.max ?? mergedOutflowOverrides.max_amount ?? outflowMaxAmount) ? Number(mergedOutflowOverrides.amount_rs?.max ?? mergedOutflowOverrides.max_amount ?? outflowMaxAmount) : undefined,
        }),
        getDashboardData(apiPeriod, opts),
        getRecentTransactions(10),
      ]);
      setInflows(inflowRes.data.items || []);
      setOriginalInflows(inflowRes.data.items || []);
      setInflowTotal(inflowRes.data.total || 0);
      setOutflows(outflowRes.data.items || []);
      setOriginalOutflows(outflowRes.data.items || []);
      setOutflowTotal(outflowRes.data.total || 0);
      // Note: Don't clear filter rules here as this function is called when applying filters
      setDashboardData(dashboardRes.data);
      setRecentTransactions(recentRes.data.recent_transactions || []);
      setAnalytics({
        inflow: dashboardRes.data.total_inflow,
        inflow_usd: dashboardRes.data.total_inflow_usd || 0,
        outflow: dashboardRes.data.total_outflow,
        withdrawals: dashboardRes.data.total_withdrawals || 0,
        profit: dashboardRes.data.net_profit,
      });
    } catch (error) {
      // Error refreshing data
    }
  };

  // Wrapper used by UI and auto-refresh to refresh dashboard and related data
  const loadDashboardData = () => {
    return refreshAllData();
  };

  // Manual refresh function for the sync button
  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      await refreshAllData();
    } finally {
      setRefreshing(false);
    }
  };

  // User Management Functions
  const toggleUserLogin = async (userId) => {
    try {
      const response = await toggleUserLoginApi(userId);
      // Update the usersList state with the new login status
      setUsersList(prev => prev.map(u => 
        u.id === userId ? { ...u, login_enabled: response.data.login_enabled } : u
      ));
    } catch (error) {
      alert('Failed to toggle user login. Please try again.');
    }
  };

  // Delete All Data Function
  const handleDeleteAllData = async () => {
    const confirmation = prompt(
      `WARNING: DANGER: This will permanently delete ALL ${user?.role === 'Owner' ? 'Owner' : 'your'} data!\n\n` +
      `This includes:\n` +
      `- All inflows\n` +
      `- All outflows\n` +
      `- All recurring transactions\n` +
      `- All reports\n\n` +
      `Type "DELETE_ALL_DATA" to confirm:`
    );

    if (confirmation !== 'DELETE_ALL_DATA') {
      alert('Deletion cancelled. You must type "DELETE_ALL_DATA" exactly to confirm.');
      return;
    }

    try {
      const response = await deleteAllDataApi('DELETE_ALL_DATA');
      alert(
        `SUCCESS: ${response.data.message}\n\n` +
        `Deleted:\n` +
        `- ${response.data.deleted_counts.inflows} inflows\n` +
        `- ${response.data.deleted_counts.outflows} outflows\n` +
        `- ${response.data.deleted_counts.recurring_transactions} recurring transactions\n` +
        `- ${response.data.deleted_counts.reports} reports\n\n` +
        `Please refresh the page to see the changes.`
      );
      
    } catch (error) {
      alert(error?.response?.data?.detail || 'Failed to delete data. Please try again.');
    }
  };

  // Persist custom date changes to localStorage
  useEffect(() => {
    if (customStart) {
      localStorage.setItem('customStart', customStart);
    }
  }, [customStart]);

  useEffect(() => {
    if (customEnd) {
      localStorage.setItem('customEnd', customEnd);
    }
  }, [customEnd]);

  // Insights Section Handlers
  // New dual-insights state and builder
  const [inflowRules, setInflowRules] = useState([]); // [{ field, op, value }]
  const [outflowRules, setOutflowRules] = useState([]);
  const [inflowTable, setInflowTable] = useState({ items: [], total: 0, page: 0, pageSize: 25, sortField: 'date', sortDir: 'desc' });
  const [outflowTable, setOutflowTable] = useState({ items: [], total: 0, page: 0, pageSize: 25, sortField: 'date', sortDir: 'desc' });
  const [inflowChartData, setInflowChartData] = useState([]);
  const [outflowChartData, setOutflowChartData] = useState([]);
  const [editingInflowRule, setEditingInflowRule] = useState(null);
  const [editingOutflowRule, setEditingOutflowRule] = useState(null);
  
  // Reports System
  const [reports, setReports] = useState([]);
  const [showReportBuilder, setShowReportBuilder] = useState(false);
  const [editingReport, setEditingReport] = useState(null);
  const [newTag, setNewTag] = useState('');
  const [reportSearch, setReportSearch] = useState('');
  const [reportCategoryFilter, setReportCategoryFilter] = useState('all');
  const [showDashboardSelector, setShowDashboardSelector] = useState(null);
  // Insights: which dashboard to preview on the right canvas
  const [insightsActiveDashId, setInsightsActiveDashId] = useState('default');
  const [showTemplateBuilder, setShowTemplateBuilder] = useState(false);
  const [dashboardTemplates, setDashboardTemplates] = useState([]);
  // Insights view mode
  const [insightsMode, setInsightsMode] = useState(INSIGHTS_VIEW.DASHBOARD);
  // New Insights: side panel collapsed (responsive)
  const [niCollapsed, setNiCollapsed] = useState(false);
  // Save Report modal
  const [showSaveReportModal, setShowSaveReportModal] = useState(false);
  const [showReportTypeSelection, setShowReportTypeSelection] = useState(false);
  // Insights totals (accurate across all pages)
  const [inflowTotalRs, setInflowTotalRs] = useState(0);
  const [inflowTotalUsd, setInflowTotalUsd] = useState(0);
  const [outflowTotalRs, setOutflowTotalRs] = useState(0);
  const [outflowWithdrawalsRs, setOutflowWithdrawalsRs] = useState(0);
  useEffect(() => {
    if (nav === 'insights') {
      setInsightsMode(INSIGHTS_VIEW.DASHBOARD);
    }
  }, [nav]);
  const [templateBuilder, setTemplateBuilder] = useState({
    name: '',
    description: '',
    selectedReports: []
  });
  // Table load states
  const [inflowLoading, setInflowLoading] = useState(false);
  const [outflowLoading, setOutflowLoading] = useState(false);
  // auto-refresh removed per requirements
  const [reportBuilder, setReportBuilder] = useState({
    name: '',
    description: '',
    type: 'combined',
    category: 'general',
    tags: []
  });

  const buildBasePeriod = () => {
    if ((timePeriod === 'custom' || timePeriod === 'custom_range' || timePeriod === 'custom_fy') && customStart && customEnd) {
      return { type: 'custom', start: customStart, end: customEnd };
    }
    // Map frontend period names to backend API names
    if (timePeriod === 'this_year') return { type: 'this_fy' };
    if (timePeriod === 'previous_fy') return { type: 'previous_fy' };
    return { type: timePeriod };
  };

  // Filter reports based on search
  const filteredReports = useMemo(() => {
    return reports.filter(report => {
      const matchesSearch = !reportSearch || 
        report.name.toLowerCase().includes(reportSearch.toLowerCase());
      return matchesSearch;
    });
  }, [reports, reportSearch]);

  const getDateBoundsFor = (rules) => {
    const starts = [];
    const ends = [];
    for (const r of rules) {
      if (r.field === 'date' && r.start && r.end) { starts.push(r.start); ends.push(r.end); }
    }
    if (starts.length && ends.length) {
      const minStart = starts.sort()[0];
      const maxEnd = ends.sort().slice(-1)[0];
      return { start: minStart, end: maxEnd };
    }
    return null;
  };

  const mapInflowUIRuleToBackend = (r) => {
    const prefix = 'inflow.';
    if (r.field === 'date') {
      return { field: prefix + 'date', op: 'between', value: [r.start, r.end] };
    }
    if (r.field === 'reflecting_month') {
      return { field: prefix + 'reflecting_month', op: 'equals', value: r.value };
    }
    if (r.field === 'amount_rs' || r.field === 'amount_usd') {
      const key = r.field === 'amount_rs' ? 'amount_rs' : 'amount_usd';
      if (r.min != null && r.max != null) return { field: prefix + key, op: 'between', value: [r.min, r.max] };
      if (r.min != null) return { field: prefix + key, op: 'gte', value: r.min };
      if (r.max != null) return { field: prefix + key, op: 'lte', value: r.max };
      return null;
    }
    if (r.field === 'source' || r.field === 'bank' || r.field === 'reflecting_bank') {
      const field = prefix + r.field;
      if (r.op === 'is') return { field, op: 'equals', value: r.value };
      if (r.op === 'is_not') return { field, op: 'not_in', value: r.value ? [r.value] : [] };
      if (r.op === 'is_any') return { field, op: 'in', value: Array(r.values) ? r.values : [] };
      return null;
    }
    if (r.field === 'request_inward') {
      return { field: prefix + 'request_inward', op: 'equals', value: !!r.value };
    }
    return null;
  };

  const mapOutflowUIRuleToBackend = (r) => {
    const prefix = 'outflow.';
    if (r.field === 'date') {
      return { field: prefix + 'date', op: 'between', value: [r.start, r.end] };
    }
    if (r.field === 'reflecting_month') {
      return { field: prefix + 'reflecting_month', op: 'equals', value: r.value };
    }
    if (r.field === 'amount_rs') {
      if (r.min != null && r.max != null) return { field: prefix + 'amount_rs', op: 'between', value: [r.min, r.max] };
      if (r.min != null) return { field: prefix + 'amount_rs', op: 'gte', value: r.min };
      if (r.max != null) return { field: prefix + 'amount_rs', op: 'lte', value: r.max };
      return null;
    }
    if (r.field === 'sub_category' || r.field === 'deducted_from') {
      const field = prefix + r.field;
      if (r.op === 'is') return { field, op: 'equals', value: r.value };
      if (r.op === 'is_not') return { field, op: 'not_in', value: [r.value] };
      if (r.op === 'is_any') return { field, op: 'in', value: r.values || [] };
      return null;
    }
    if (r.field === 'expense') {
      if (r.op === 'is') return { field: prefix + 'expense', op: 'equals', value: r.value };
      if (r.op === 'is_not') return { field: prefix + 'expense', op: 'not_in', value: [r.value] };
      if (r.op === 'contains') return { field: prefix + 'expense', op: 'contains', value: r.value };
      return null;
    }
    return null;
  };

  const buildGroupFromRules = (rules, mapper) => ({
        logic: 'AND',
    rules: (rules.map(mapper).filter(Boolean)),
    groups: [],
  });

  const loadInflowInsights = async () => {
    if (!user?.email) return;
    setInsightsLoading(true);
    try {
      const payload = {
        // No global period; date rules control the range
        root: buildGroupFromRules(inflowRules, mapInflowUIRuleToBackend),
        pagination: { limit: inflowTable.pageSize, offset: inflowTable.page * inflowTable.pageSize },
        sort: { field: inflowTable.sortField, dir: inflowTable.sortDir },
      };
      const res = await insightsPreviewInflow(payload);
      const pr = res.data || {};
      const totals = extractTotals(pr);
      setInflowTable((prev) => ({ ...prev, items: (pr.items || []).map((r) => ({ id: `in-${r.id ?? Math.random()}`, ...r })), total: totals.count }));
      if (totals.amountRs != null && totals.amountUsd != null) {
        setInflowTotalRs(totals.amountRs);
        setInflowTotalUsd(totals.amountUsd);
      } else {
        // Second lightweight call for global totals (no pagination)
        try {
          const totalsRes = await insightsPreviewInflow({ ...payload, pagination: { limit: 0, offset: 0 } });
          const t = extractTotals(totalsRes.data || {});
          setInflowTotalRs(t.amountRs != null ? t.amountRs : (pr.items || []).reduce((s, r) => s + (Number(r.amount_rs) || 0), 0));
          setInflowTotalUsd(t.amountUsd != null ? t.amountUsd : (pr.items || []).reduce((s, r) => s + (Number(r.amount_usd) || 0), 0));
        } catch (_) {
          setInflowTotalRs((pr.items || []).reduce((s, r) => s + (Number(r.amount_rs) || 0), 0));
          setInflowTotalUsd((pr.items || []).reduce((s, r) => s + (Number(r.amount_usd) || 0), 0));
        }
      }
    } catch (e) {
      // Inflow insights error
    } finally {
      setInsightsLoading(false);
    }
  };

  const loadOutflowInsights = async () => {
    if (!user?.email) return;
    setInsightsLoading(true);
    try {
      const payload = {
        // No global period; date rules control the range
        root: buildGroupFromRules(outflowRules, mapOutflowUIRuleToBackend),
        pagination: { limit: outflowTable.pageSize, offset: outflowTable.page * outflowTable.pageSize },
        sort: { field: outflowTable.sortField, dir: outflowTable.sortDir },
      };
      const res = await insightsPreviewOutflow(payload);
      const pr = res.data || {};
      const totalsOut = extractTotals(pr);
      setOutflowTable((prev) => ({ ...prev, items: (pr.items || []).map((r) => ({ id: `out-${r.id ?? Math.random()}`, ...r })), total: totalsOut.count }));
      setOutflowWithdrawalsRs(Number(pr.sum_withdrawals_rs || 0));
      if (totalsOut.amountRs != null) {
        setOutflowTotalRs(totalsOut.amountRs);
      } else {
        try {
          const totalsRes = await insightsPreviewOutflow({ ...payload, pagination: { limit: 0, offset: 0 } });
          const t = extractTotals(totalsRes.data || {});
          setOutflowWithdrawalsRs(Number(totalsRes?.data?.sum_withdrawals_rs || 0));
          setOutflowTotalRs(t.amountRs != null ? t.amountRs : (pr.items || []).reduce((s, r) => s + (isProfitWithdrawalRow(r) ? 0 : (Number(r.amount_rs) || 0)), 0));
        } catch (_) {
          setOutflowWithdrawalsRs((pr.items || []).reduce((s, r) => s + (isProfitWithdrawalRow(r) ? (Number(r.amount_rs) || 0) : 0), 0));
          setOutflowTotalRs((pr.items || []).reduce((s, r) => s + (isProfitWithdrawalRow(r) ? 0 : (Number(r.amount_rs) || 0)), 0));
        }
      }
    } catch (e) {
      // Outflow insights error
    } finally {
      setInsightsLoading(false);
    }
  };

  const loadInflowChartData = async () => {
    if (!user?.email) return;
    try {
      const payload = {
        root: buildGroupFromRules(inflowRules, mapInflowUIRuleToBackend),
        viz: { type: 'bar', groupBy: inflowGroupBy },
      };
      const res = await insightsRun(payload);
      const series = res.data?.series || [];
      setInflowChartData(series);
    } catch (e) {
      // Inflow chart error
    }
  };

  const loadOutflowChartData = async () => {
    if (!user?.email) return;
    try {
      const payload = {
        root: buildGroupFromRules(outflowRules, mapOutflowUIRuleToBackend),
        viz: { type: 'bar', groupBy: outflowGroupBy },
      };
      const res = await insightsRun(payload);
      const series = res.data?.series || [];
      setOutflowChartData(series);
    } catch (e) {
      // Outflow chart error
    }
  };

  const saveInsight = () => {
    const newInsight = {
      id: Date.now().toString(),
      name: `${insightFilters.filterType} - ${insightFilters.field} - ${insightFilters.value || 'All'}`,
      filters: { ...insightFilters },
      period: timePeriod,
      customStart,
      customEnd,
      data: [...insightsSeries],
      totals: insightsTotals,
      createdAt: new Date().toISOString()
    };
    const updated = [...savedInsights, newInsight];
    setSavedInsights(updated);
    // Persist to localStorage per user
    const userKey = user?.email || 'anonymous';
    localStorage.setItem(`insights_${userKey}`, JSON.stringify(updated));
  };

  const addInsightToDashboard = (insightId, dashboardId) => {
    const insight = savedInsights.find(i => i.id === insightId);
    if (insight) {
      const updated = customDashboards.map(dashboard =>
        dashboard.id === dashboardId
          ? { ...dashboard, insights: [...dashboard.insights, insight] }
          : dashboard
      );
      setCustomDashboards(updated);
      // Persist to localStorage per user
      const userKey = user?.email || 'anonymous';
      localStorage.setItem(`dashboards_${userKey}`, JSON.stringify(updated));
    }
  };

  const addCurrentDualToDashboard = (dashboardId) => {
    const basePeriod = buildBasePeriod();
    const card = {
      id: `dual-${Date.now()}`,
      kind: 'dual_tables',
      name: 'Custom Tables',
      inflowRules: [...inflowRules],
      outflowRules: [...outflowRules],
      period: basePeriod,
      pageSize: 10,
      createdAt: new Date().toISOString(),
    };
    const updated = customDashboards.map(d => d.id === dashboardId ? { ...d, insights: [...d.insights, card] } : d);
    setCustomDashboards(updated);
    const userKey = user?.email || 'anonymous';
    localStorage.setItem(`dashboards_${userKey}`, JSON.stringify(updated));
  };
  const addReportToDashboard = (reportId, dashboardId) => {
    let card;
    
    if (reportId === 'current') {
      // Adding current insights as a widget
      const basePeriod = buildBasePeriod();
      card = {
        id: `widget-${Date.now()}`,
        kind: 'dual_tables',
        name: 'Current Insights Widget',
        inflowRules: [...inflowRules],
        outflowRules: [...outflowRules],
        period: basePeriod,
        pageSize: 10,
        createdAt: new Date().toISOString(),
      };
    } else {
      // Adding a saved report
      const report = reports.find(r => r.id === reportId);
      if (!report) return;
      
      card = {
        id: `report-${reportId}-${Date.now()}`,
        kind: 'report',
        name: report.name,
        reportId: report.id,
        reportType: report.type,
        category: report.category,
        tags: report.tags,
        inflowRules: [...report.inflowRules],
        outflowRules: [...report.outflowRules],
        inflowGroupBy: report.inflowGroupBy,
        outflowGroupBy: report.outflowGroupBy,
        pageSize: 10,
        createdAt: new Date().toISOString(),
      };
    }
    
    const updated = customDashboards.map(d => d.id === dashboardId ? { ...d, insights: [...d.insights, card] } : d);
    setCustomDashboards(updated);
    const userKey = user?.email || 'anonymous';
    localStorage.setItem(`dashboards_${userKey}`, JSON.stringify(updated));
  };

  const createSubDashboardFromSidebar = () => {
    const name = (newSubName || '').trim();
    if (!name) return;
    const newDashboard = { id: `d-${Date.now()}`, name, insights: [] };
    const updated = [...customDashboards, newDashboard];
    setCustomDashboards(updated);
    setNewSubName('');
    const userKey = user?.email || 'anonymous';
    localStorage.setItem(`dashboards_${userKey}`, JSON.stringify(updated));
  };

  const removeCardFromDashboard = (dashboardId, cardId) => {
    const updated = customDashboards.map(dashboard =>
      dashboard.id === dashboardId
        ? { ...dashboard, insights: dashboard.insights.filter(card => card.id !== cardId) }
        : dashboard
    );
    setCustomDashboards(updated);
    const userKey = user?.email || 'anonymous';
    localStorage.setItem(`dashboards_${userKey}`, JSON.stringify(updated));
  };

  const updateReportCardsInDashboards = (updatedReport) => {
    const updated = customDashboards.map(dashboard => ({
      ...dashboard,
      insights: dashboard.insights.map(card => 
        card.kind === 'report' && card.reportId === updatedReport.id
          ? {
              ...card,
              name: updatedReport.name,
              reportType: updatedReport.type,
              category: updatedReport.category,
              tags: updatedReport.tags,
              inflowRules: [...updatedReport.inflowRules],
              outflowRules: [...updatedReport.outflowRules],
              inflowGroupBy: updatedReport.inflowGroupBy,
              outflowGroupBy: updatedReport.outflowGroupBy,
            }
          : card
      )
    }));
    setCustomDashboards(updated);
    const userKey = user?.email || 'anonymous';
    localStorage.setItem(`dashboards_${userKey}`, JSON.stringify(updated));
  };

  const saveDashboardTemplate = () => {
    if (!templateBuilder.name.trim() || templateBuilder.selectedReports.length === 0) return;
    
    const template = {
      id: Date.now().toString(),
      name: templateBuilder.name.trim(),
      description: templateBuilder.description.trim(),
      reportIds: [...templateBuilder.selectedReports],
      createdAt: new Date().toISOString()
    };
    
    setDashboardTemplates(prev => [...prev, template]);
    setTemplateBuilder({ name: '', description: '', selectedReports: [] });
    setShowTemplateBuilder(false);
    
    // Save to localStorage
    const userKey = user?.email;
    if (userKey) {
      localStorage.setItem(`dashboard_templates_${userKey}`, JSON.stringify([...dashboardTemplates, template]));
    }
  };

  const createDashboardFromTemplate = (template) => {
    const templateReports = reports.filter(r => template.reportIds.includes(r.id));
    if (templateReports.length === 0) return;
    
    const newDashboard = {
      id: `d-${Date.now()}`,
      name: `${template.name} Dashboard`,
      insights: templateReports.map(report => ({
        id: `report-${report.id}-${Date.now()}`,
        kind: 'report',
        name: report.name,
        reportId: report.id,
        reportType: report.type,
        category: report.category,
        tags: report.tags,
        inflowRules: [...report.inflowRules],
        outflowRules: [...report.outflowRules],
        inflowGroupBy: report.inflowGroupBy,
        outflowGroupBy: report.outflowGroupBy,
        pageSize: 10,
        createdAt: new Date().toISOString(),
      }))
    };
    
    setCustomDashboards(prev => [...prev, newDashboard]);
    setActiveDashboard(newDashboard.id);
    
    // Save to localStorage
    const userKey = user?.email || 'anonymous';
    localStorage.setItem(`dashboards_${userKey}`, JSON.stringify([...customDashboards, newDashboard]));
  };
  const deleteSubDashboard = (id) => {
    if (id === 'default') return;
    const updated = customDashboards.filter(d => d.id !== id);
    setCustomDashboards(updated);
    if (activeDashboard === id) setActiveDashboard('default');
    const userKey = user?.email || 'anonymous';
    localStorage.setItem(`dashboards_${userKey}`, JSON.stringify(updated));
  };

  const startRenameSubDashboard = (d) => {
    if (d.id === 'default') return;
    setRenamingId(d.id);
    setRenamingName(d.name);
  };

  const applyRenameSubDashboard = () => {
    const name = (renamingName || '').trim();
    if (!renamingId || !name) { setRenamingId(null); setRenamingName(''); return; }
    const updated = customDashboards.map(d => d.id === renamingId ? { ...d, name } : d);
    setCustomDashboards(updated);
    const userKey = user?.email || 'anonymous';
    localStorage.setItem(`dashboards_${userKey}`, JSON.stringify(updated));
    setRenamingId(null); setRenamingName('');
  };

  const createCustomDashboard = () => {
    if (newDashboardName.trim()) {
      const newDashboard = {
        id: Date.now().toString(),
        name: newDashboardName.trim(),
        insights: []
      };
      const updated = [...customDashboards, newDashboard];
      setCustomDashboards(updated);
      setNewDashboardName('');
      setShowCreateDashboard(false);
      // Persist to localStorage per user
      const userKey = user?.email || 'anonymous';
      localStorage.setItem(`dashboards_${userKey}`, JSON.stringify(updated));
    }
  };

  // Get available fields for filtering based on filter type
  const getAvailableFields = () => {
    if (insightFilters.filterType === 'inflow') {
      return [
        { value: 'source', label: 'Source' },
        { value: 'bank', label: 'Bank' },
        { value: 'amount_rs', label: 'Amount (Rs)' },
        { value: 'request_inward', label: 'Inward Remittance' }
      ];
    } else {
      return [
        { value: 'expense', label: 'Expense' },
        { value: 'category', label: 'Category' },
        { value: 'sub_category', label: 'Sub-Category' },
        { value: 'vendor', label: 'Vendor' },
        { value: 'deducted_from', label: 'Deducted From' },
        { value: 'mode', label: 'Mode' },
        { value: 'nature', label: 'Nature' }
      ];
    }
  };

  // Load saved insights and dashboards from localStorage per user
  useEffect(() => {
    if (user?.email) {
      const userKey = user.email;
      const savedInsightsData = localStorage.getItem(`insights_${userKey}`);
      const savedDashboardsData = localStorage.getItem(`dashboards_${userKey}`);
      
      if (savedInsightsData) {
        try {
          setSavedInsights(JSON.parse(savedInsightsData));
        } catch (e) {
          // Error loading saved insights
        }
      }
      
      if (savedDashboardsData) {
        try {
          const dashboards = JSON.parse(savedDashboardsData);
          setCustomDashboards(dashboards);
        } catch (e) {
          // Error loading saved dashboards
        }
      }
    }
  }, [user?.email]);

  // Trigger dual insights generation when rules or paging/sort change
  useEffect(() => {
    if (!user?.email) return;
    const t = setTimeout(() => { loadInflowInsights(); }, 350);
    return () => clearTimeout(t);
  }, [user?.email, JSON.stringify(inflowRules), timePeriod, customStart, customEnd, inflowTable.page, inflowTable.pageSize, inflowTable.sortField, inflowTable.sortDir]);

  useEffect(() => {
    if (!user?.email) return;
    const t = setTimeout(() => { loadOutflowInsights(); }, 350);
    return () => clearTimeout(t);
  }, [user?.email, JSON.stringify(outflowRules), timePeriod, customStart, customEnd, outflowTable.page, outflowTable.pageSize, outflowTable.sortField, outflowTable.sortDir]);

  // Load chart data when rules or groupBy changes
  useEffect(() => {
    if (!user?.email) return;
    const t = setTimeout(() => { loadInflowChartData(); }, 350);
    return () => clearTimeout(t);
  }, [user?.email, JSON.stringify(inflowRules), inflowGroupBy]);

  useEffect(() => {
    if (!user?.email) return;
    const t = setTimeout(() => { loadOutflowChartData(); }, 350);
    return () => clearTimeout(t);
  }, [user?.email, JSON.stringify(outflowRules), outflowGroupBy]);

  // Load reports from backend
  useEffect(() => {
    const loadReportsFromBackend = async () => {
      if (!user?.email) return;
      try {
        const resp = await listReportsApi();
        const reportsData = (resp.data || []).map((r) => ({
          id: r.id,
          name: r.name,
          description: r.description,
          type: r.type,
          category: r.category,
          tags: r.tags || [],
          inflowRules: r.inflow_rules || [],
          outflowRules: r.outflow_rules || [],
          inflowGroupBy: r.inflow_group_by || 'month',
          outflowGroupBy: r.outflow_group_by || 'month',
          createdAt: r.created_at,
          updatedAt: r.updated_at,
        }));
        setReports(reportsData);
      } catch (e) {
        setReports([]); // Set empty array on error instead of localStorage fallback
      }
      // templates and active dashboard still from localStorage
      const savedTemplates = localStorage.getItem(`dashboard_templates_${user.email}`);
      if (savedTemplates) {
        try { setDashboardTemplates(JSON.parse(savedTemplates)); } catch {}
        }
      try {
        const savedActive = localStorage.getItem(`active_dashboard_${user.email}`);
        if (savedActive) setActiveDashboard(savedActive);
      } catch {}
    };
    loadReportsFromBackend();
  }, [user?.email]);
  // Auto-refresh removed per requirements
  // Load analytics series when groupBy changes or rules change
  useEffect(() => {
    if (!user?.email) return;
    const load = async () => {
      try {
        const payload = {
          root: buildGroupFromRules([...inflowRules, ...outflowRules], (r)=> (r.field && (r.field==='date' || r.field==='amount_rs' || r.field==='amount_usd' || r.field==='request_inward') ? (r.field in {date:1, amount_rs:1, amount_usd:1, request_inward:1} ? (r.field==='date' ? mapInflowUIRuleToBackend(r) : mapInflowUIRuleToBackend(r)) : null) : (mapInflowUIRuleToBackend(r) || mapOutflowUIRuleToBackend(r)))),
          viz: { type: 'bar', groupBy: inflowGroupBy },
        };
        const res = await insightsRun(payload);
        const rr = res.data || {};
        setInsightsSeries(rr.series || []);
        setInsightsTotals(rr.totals || {});
      } catch (e) { /* ignore for now */ }
    };
    // Debounce a bit
    const t = setTimeout(load, 400);
    return () => clearTimeout(t);
  }, [user?.email, inflowGroupBy, outflowGroupBy, JSON.stringify(inflowRules), JSON.stringify(outflowRules)]);

  // Import Data modal state
  const [showImportModal, setShowImportModal] = useState(false);
  const [importType, setImportType] = useState('inflow'); // 'inflow' or 'outflow'
  const [importFile, setImportFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState({ data: [], error: null });
  const [columnMapping, setColumnMapping] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [validatedRows, setValidatedRows] = useState([]);
  const [showImportSummary, setShowImportSummary] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState(null);

  // Helper function to clean and normalize header names for matching
  const cleanHeaderName = (header) => {
    if (!header) return '';
    return header
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '') // Remove all non-alphanumeric characters
      .trim();
  };

  // Calculate similarity between two strings using Levenshtein distance
  const calculateSimilarity = (str1, str2) => {
    const len1 = str1.length;
    const len2 = str2.length;
    
    if (len1 === 0) return len2 === 0 ? 1 : 0;
    if (len2 === 0) return 0;
    
    const matrix = [];
    for (let i = 0; i <= len1; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= len2; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= len1; i++) {
      for (let j = 1; j <= len2; j++) {
        const cost = str1[i - 1] === str2[j - 1] ? 0 : 1;
        matrix[i][j] = Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost
        );
      }
    }
    
    const maxLen = Math.max(len1, len2);
    return (maxLen - matrix[len1][len2]) / maxLen;
  };

  // Auto-map CSV headers to field keys with fuzzy matching
  const autoMapHeaders = (csvHeaders, fieldDefinitions) => {
    const mapping = {};
    const mappingConfidence = {};
    
    // Define common variations for each field
    const fieldVariations = {
      date: ['date', 'transactiondate', 'txndate', 'datetime', 'timestamp'],
      reflecting_month: ['reflectingmonth', 'month', 'financialmonth', 'fymonth'],
      source: ['source', 'incomesource', 'from', 'sender', 'origin'],
      amount_rs: ['amountrs', 'amountinrs', 'amount', 'value', 'sum', 'total', 'rupees'],
      amount_usd: ['amountusd', 'amountindollar', 'dollar', 'usd', 'amountdollar'],
      bank: ['bank', 'bankname', 'account', 'bankaccount'],
      request_inward: ['requestinward', 'inward', 'remittance', 'inwardremittance', 'requestforinward'],
      expense: ['expense', 'description', 'particulars', 'details', 'item'],
      sub_category: ['subcategory', 'subcat', 'category2', 'type'],
      category: ['category', 'cat', 'maincat', 'maincategory'],
      deducted_from: ['deductedfrom', 'paidfrom', 'account', 'paymentaccount'],
      mode: ['mode', 'paymentmode', 'method', 'paymentmethod', 'type'],
      nature: ['nature', 'kind', 'classification'],
      vendor: ['vendor', 'supplier', 'payee', 'merchant', 'company'],
      remark: ['remark', 'note', 'comment', 'remarks', 'notes', 'memo']
    };
    
    fieldDefinitions.forEach(field => {
      const fieldKey = field.key;
      const variations = fieldVariations[fieldKey] || [fieldKey];
      
      let bestMatch = null;
      let bestSimilarity = 0;
      
      csvHeaders.forEach(header => {
        const cleanHeader = cleanHeaderName(header);
        
        variations.forEach(variation => {
          const similarity = calculateSimilarity(cleanHeader, variation);
          if (similarity > bestSimilarity && similarity >= 0.75) {
            bestSimilarity = similarity;
            bestMatch = header;
          }
        });
      });
      
      if (bestMatch) {
        mapping[fieldKey] = bestMatch;
        mappingConfidence[fieldKey] = Math.round(bestSimilarity * 100);
      }
    });
    
    return { mapping, confidence: mappingConfidence };
  };

  // Handler for file input
  const handleImportFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImportFile(file);

      // Parse CSV with headers - now accepts any header format
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          try {
            const rows = Array.isArray(results.data) ? results.data : [];
            if (!rows.length) {
              setCsvPreview({ data: [], error: 'No rows found in CSV.' });
              return;
            }

            // Get CSV headers
            const csvHeaders = Object.keys(rows[0] || {});
            if (!csvHeaders.length) {
              setCsvPreview({ data: [], error: 'No headers found in CSV file.' });
              return;
            }

            // Helper: normalize cell (trim, keep as string, preserve date format)
            const norm = (v) => (v == null ? '' : String(v).trim());

            let dataObjects = [];

            // Process rows - more flexible filtering
            for (let i = 0; i < rows.length; i++) {
              const row = rows[i];
              const normalizedRow = {};
              
              // Normalize all values
              Object.keys(row).forEach(key => {
                normalizedRow[key] = norm(row[key]);
              });

              // Check if row has any meaningful data
              const hasAnyData = Object.values(normalizedRow).some(v => v && v.trim());
              
              // More flexible amount detection - look for any numeric field
              let hasAmount = false;
              const numericFields = Object.keys(normalizedRow).filter(key => {
                const value = normalizedRow[key];
                if (!value) return false;
                const numericValue = parseFloat(value.replace(/[,\s]/g, ''));
                return !isNaN(numericValue) && numericValue > 0;
              });
              
              hasAmount = numericFields.length > 0;
              
              if (hasAnyData && hasAmount) {
                dataObjects.push(normalizedRow);
              }
            }

            if (!dataObjects.length) {
              setCsvPreview({ 
                data: [], 
                error: `No valid data rows found. Please ensure your CSV file contains rows with numeric values.` 
              });
              return;
            }

            // Auto-map headers to field definitions
            const allFields = [...getCurrentRequiredFields(), ...getCurrentOptionalFields()];
            const autoMapping = autoMapHeaders(csvHeaders, allFields);
            
            // Set the auto-mapped column mapping
            setColumnMapping(autoMapping.mapping);
            
            // Store confidence scores for UI display
            setCsvPreview({ 
              data: dataObjects, 
              error: null, 
              headers: csvHeaders,
              autoMapping: autoMapping.mapping,
              confidence: autoMapping.confidence
            });
          } catch (err) {
            setCsvPreview({ data: [], error: err?.message || 'Failed to parse CSV.' });
          }
        },
        error: (err) => {
          setCsvPreview({ data: [], error: err.message });
        },
      });
    }
  };

  // Get current field definitions based on import type
  const getCurrentRequiredFields = () => importType === 'inflow' ? INFLOW_REQUIRED_FIELDS : OUTFLOW_REQUIRED_FIELDS;
  const getCurrentOptionalFields = () => importType === 'inflow' ? INFLOW_OPTIONAL_FIELDS : OUTFLOW_OPTIONAL_FIELDS;
  const getCurrentSchema = () => importType === 'inflow' ? inflowSchema : outflowSchema;

  // Handler for opening import modal
  const openImportModal = (type) => {
    setImportType(type);
    setShowImportModal(true);
    setImportFile(null);
    setCsvPreview({ data: [], error: null });
    setColumnMapping({});
    setValidationErrors([]);
    setValidatedRows([]);
    setShowImportSummary(false);
    setImporting(false);
    setImportResult(null);
    setImportError(null);
  };

  // Handler for mapping dropdowns
  const handleMappingChange = (fieldKey, csvHeader) => {
    setColumnMapping((prev) => ({ ...prev, [fieldKey]: csvHeader }));
  };

  // Check if all required fields are mapped
  const allRequiredMapped = getCurrentRequiredFields().every(f => columnMapping[f.key]);

  // Handler for Continue (validate data)
  const handleValidateAndContinue = async () => {
    const errors = [];
    const validRows = [];
    const schema = getCurrentSchema();
    for (let i = 0; i < csvPreview.data.length; i++) {
      const row = csvPreview.data[i];
      const mapped = {};
      const allFields = [...getCurrentRequiredFields(), ...getCurrentOptionalFields()];
      for (const field of allFields) {
        mapped[field.key] = row[columnMapping[field.key]] || '';
      }
      try {
        await schema.validate(mapped, { abortEarly: false });
        validRows.push(mapped);
      } catch (err) {
        errors.push({ row: i + 1, errors: err.errors });
      }
    }
    setValidationErrors(errors);
    setValidatedRows(validRows);
    setShowImportSummary(errors.length === 0);
  };

  // Handler for going back to mapping
  const handleBackToMapping = () => {
    setShowImportSummary(false);
    setValidationErrors([]);
  };
  const handleImport = async () => {
    setImporting(true);
    setImportResult(null);
    setImportError(null);
    try {
      // Starting import
      
      const res = importType === 'inflow' 
        ? await importInflowsApi(validatedRows)
        : await importOutflowsApi(validatedRows);
      
      setImportResult(res.data);
      
      // Refresh data after successful import
      if (res.data.imported > 0) {
        if (importType === 'inflow') {
          // Refresh inflows data
          const opts = ((timePeriod === 'custom' || timePeriod === 'custom_range' || timePeriod === 'custom_fy') && customStart && customEnd) ? { start: customStart, end: customEnd } : {};
          const apiPeriod = (timePeriod === 'custom_range' || timePeriod === 'custom_fy') ? 'custom' : timePeriod;
          getInflows(apiPeriod, opts);
        } else {
          // Refresh outflows data
          const opts = ((timePeriod === 'custom' || timePeriod === 'custom_range' || timePeriod === 'custom_fy') && customStart && customEnd) ? { start: customStart, end: customEnd } : {};
          const apiPeriod = (timePeriod === 'custom_range' || timePeriod === 'custom_fy') ? 'custom' : timePeriod;
          getOutflows(apiPeriod, opts);
        }
      }
    } catch (err) {
      setImportError(err.userMessage || err.message || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };
  if (!user) {
    // Premium Apple-style Login page with GYP logo and OTP support
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif' }}>
        <div className="bg-white p-12 rounded-3xl shadow-2xl w-96 border border-gray-100">
          <div className="text-center mb-10">
            <img src={gypLogo} alt="GYP Logo" className="w-48 h-auto mx-auto mb-6" />
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">
              {showOTPForm ? "Enter Verification Code" : "Welcome Back"}
            </h1>
            <p className="text-gray-500 text-sm">
              {showOTPForm
                ? `We sent a 6-digit code to ${loginEmail}`
                : "Sign in to your finance dashboard"
              }
            </p>
          </div>

          {!showOTPForm ? (
            // Login Form
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Email Address</label>
                <input
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 text-gray-900 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter your email"
                  required
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Password</label>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 text-gray-900 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter your password"
                  required
                />
              </div>
              {loginError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                  {loginError}
                </div>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-xl font-semibold transition-all transform hover:scale-[1.02] active:scale-[0.98]"
              >
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>
          ) : (
            // OTP Form
            <form onSubmit={handleOTPVerification} className="space-y-6">
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-2">Verification Code</label>
                <input
                  type="text"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full px-4 py-3 bg-gray-50 text-gray-900 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-center text-2xl tracking-widest"
                  placeholder="000000"
                  maxLength={6}
                  required
                />
              </div>
              {otpError && (
                <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm">
                  {otpError}
                </div>
              )}
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={loading || otpCode.length !== 6}
                  className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white rounded-xl font-semibold transition-all transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  {loading ? "Verifying..." : "Verify Code"}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowOTPForm(false);
                    setOtpCode("");
                    setOtpError("");
                    setTempToken("");
                  }}
                  className="w-full py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition-all"
                >
                  Back to Login
                </button>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500">
                  Didn't receive the code? Check your spam folder or contact IT support.
                </p>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="app-root-container">
      <div className="h-screen bg-white text-gray-900 flex flex-col overflow-hidden" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif' }}>
      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-20 bg-white shadow-sm border-b border-gray-100">
        <div className="px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img src={gypLogo} alt="GYP Logo" className="h-8 w-auto" />
              <span className="text-2xl font-semibold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-blue-700">Finance Dashboard</span>
            </div>
            <div className="flex items-center space-x-6">
              {/* IST Clock - modern style */}
              <div className="hidden sm:flex items-center gap-2 px-4 py-1.5 rounded-full text-sm bg-gradient-to-r from-indigo-50 to-blue-50 border border-blue-100 text-blue-700 shadow-sm">
                <span className="font-mono font-semibold tracking-wider">{istDisplay}</span>
                <span className="ml-1 text-[10px] text-blue-500">IST</span>
              </div>
              {/* Manual Refresh Button */}
              <button
                onClick={handleManualRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-700 hover:from-green-100 hover:to-emerald-100 hover:border-green-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                title="Refresh data"
              >
                <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">{refreshing ? 'Syncing...' : 'Sync'}</span>
              </button>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <UserIcon className="h-5 w-5 text-gray-500" />
                  <span className="text-gray-700 text-sm font-medium">{user.name || user.email}</span>
                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">{user.role}</span>
                </div>
                <button
                  onClick={handleLogout}
                  className="bg-red-50 hover:bg-red-100 px-4 py-2 rounded-lg text-red-600 text-sm font-medium transition-all"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>
  <div className="flex h-full">
    {/* Global Settings Modals (rendered at top-level so they work on any tab) */}
    {/* Modals: Source */}
    {showSourceModal && (
      <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold tracking-tight text-gray-900">{editingSourceId ? 'Edit Source' : 'Add Source'}</h3>
            <button onClick={() => setShowSourceModal(false)} className="text-gray-500 hover:text-gray-700"><XMarkIcon className="h-5 w-5" /></button>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Name</label>
            <input type="text" value={sourceForm.name} onChange={(e)=>setSourceForm({ name: e.target.value })} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setShowSourceModal(false)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg">Cancel</button>
            <button onClick={saveSource} disabled={!adminCanWrite} className={`px-4 py-2 rounded-lg text-white ${adminCanWrite ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'}`}>{editingSourceId ? 'Save Changes' : 'Add Source'}</button>
          </div>
        </div>
      </div>
    )}

    {/* Modals: Bank */}
    {showBankModal && (
      <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold tracking-tight text-gray-900">{editingBankId ? 'Edit Bank Account' : 'Add Bank Account'}</h3>
            <button onClick={() => setShowBankModal(false)} className="text-gray-500 hover:text-gray-700"><XMarkIcon className="h-5 w-5" /></button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Name</label>
              <input type="text" value={bankForm.name} onChange={(e)=>setBankForm((p)=>({...p, name: e.target.value}))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="flex items-center gap-2">
              <input id="is_default_cash" type="checkbox" checked={!!bankForm.is_default_cash} onChange={(e)=>setBankForm((p)=>({...p, is_default_cash: e.target.checked}))} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <label htmlFor="is_default_cash" className="text-sm text-gray-700">Default Cash Account</label>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setShowBankModal(false)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg">Cancel</button>
            <button onClick={saveBank} disabled={!adminCanWrite} className={`px-4 py-2 rounded-lg text-white ${adminCanWrite ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'}`}>{editingBankId ? 'Save Changes' : 'Add Bank'}</button>
          </div>
        </div>
      </div>
    )}

    {/* Modals: Category */}
    {showCategoryModal && (
      <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold tracking-tight text-gray-900">{editingCategoryId ? 'Edit Category' : 'Add Category'}</h3>
            <button onClick={() => setShowCategoryModal(false)} className="text-gray-500 hover:text-gray-700"><XMarkIcon className="h-5 w-5" /></button>
          </div>
          <div>
            <label className="block text-sm text-gray-700 mb-1">Name</label>
            <input type="text" value={categoryForm.name} onChange={(e)=>setCategoryForm({ name: e.target.value })} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setShowCategoryModal(false)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg">Cancel</button>
            <button onClick={saveCategory} disabled={!adminCanWrite} className={`px-4 py-2 rounded-lg text-white ${adminCanWrite ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'}`}>{editingCategoryId ? 'Save Changes' : 'Add Category'}</button>
          </div>
        </div>
      </div>
    )}

    {/* Modals: Subcategory */}
    {showSubcategoryModal && (
      <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold tracking-tight text-gray-900">{editingSubcategoryId ? 'Edit Subcategory' : 'Add Subcategory'}</h3>
            <button onClick={() => setShowSubcategoryModal(false)} className="text-gray-500 hover:text-gray-700"><XMarkIcon className="h-5 w-5" /></button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-700 mb-1">Name</label>
              <input type="text" value={subcategoryForm.name} onChange={(e)=>setSubcategoryForm((p)=>({...p, name: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Category</label>
              <select value={subcategoryForm.category_id} onChange={(e)=>setSubcategoryForm((p)=>({...p, category_id: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="" disabled>Select category</option>
                {outflowCategories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-700 mb-1">Accounting Treatment</label>
              <select value={subcategoryForm.accounting_treatment || OUTFLOW_TREATMENT_EXPENSE} onChange={(e)=>setSubcategoryForm((p)=>({...p, accounting_treatment: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500">
                {OUTFLOW_TREATMENT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setShowSubcategoryModal(false)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg">Cancel</button>
            <button onClick={saveSubcategory} disabled={!adminCanWrite} className={`px-4 py-2 rounded-lg text-white ${adminCanWrite ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'}`}>{editingSubcategoryId ? 'Save Changes' : 'Add Subcategory'}</button>
          </div>
        </div>
      </div>
    )}
        {/* Fixed Sidebar */}
        <aside className="w-16 bg-white fixed left-0 top-16 bottom-0 overflow-y-auto z-10 border-r border-gray-100">
          <nav className="py-4">
            <ul className="space-y-2 px-2">
              {NAV_ITEMS.map((item) => {
                const IconComponent = item.icon;
                const active = nav === item.key;
                return (
                  <li key={item.key}>
                    <button
                      onClick={() => setNav(item.key)}
                      title={item.label}
                      className={`w-full flex items-center justify-center px-2 py-3 rounded-lg transition-all ${active
                        ? "bg-blue-50"
                        : "hover:bg-gray-50"
                        }`}
                    >
                      <IconComponent className={`h-6 w-6 ${active ? "text-blue-600" : "text-blue-500"}`} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </aside>
        {/* Main Content */}
        <main className="flex-1 ml-16 h-full overflow-y-auto">
          <div className="p-8 pt-24">
            {nav !== "dashboard" && (
              <div className="mb-5 rounded-2xl border border-slate-200/80 bg-gradient-to-r from-white via-slate-50 to-blue-50 px-4 py-3 shadow-sm overflow-x-auto">
                <div className="flex items-center gap-6 text-sm md:text-base text-slate-700 whitespace-nowrap min-w-max">
                  <span className="inline-flex items-center gap-1"><span className="font-medium text-slate-500">Opening Balance</span><span className="font-semibold text-slate-900">{formatCurrency(Number(dashboardData?.opening_balance || 0))}</span></span>
                  <span className="inline-flex items-center gap-1"><span className="font-medium text-slate-500">Sales</span><span className="font-semibold text-blue-700">{formatCurrency(Number(dashboardData?.total_sales || 0))}</span></span>
                  <span className="inline-flex items-center gap-1"><span className="font-medium text-slate-500">Other revenue</span><span className="font-semibold text-sky-700">{formatCurrency(Number(dashboardOtherIncome || 0))}</span></span>
                  <span className="inline-flex items-center gap-1"><span className="font-medium text-slate-500">Total Inflow</span><span className="font-semibold text-emerald-700">{formatCurrency(Number(analytics?.inflow || dashboardData?.total_inflow || 0))}</span></span>
                  <span className="inline-flex items-center gap-1"><span className="font-medium text-slate-500">Total Outflow</span><span className="font-semibold text-rose-700">{formatCurrency(Number(analytics?.outflow || dashboardData?.total_outflow || 0))}</span></span>
                  <span className="inline-flex items-center gap-1"><span className="font-medium text-slate-500">Net profit</span><span className={`font-semibold ${Number(analytics?.profit || dashboardData?.net_profit || 0) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{formatCurrency(Number(analytics?.profit || dashboardData?.net_profit || 0))}</span></span>
                  <span className="inline-flex items-center gap-1"><span className="font-medium text-slate-500">Withdrawal</span><span className="font-semibold text-amber-700">{formatCurrency(Number(analytics?.withdrawals || dashboardData?.total_withdrawals || 0))}</span></span>
                  <span className="inline-flex items-center gap-1"><span className="font-medium text-slate-500">Closing balance</span><span className="font-semibold text-slate-900">{formatCurrency(Number(dashboardData?.closing_balance || 0))}</span></span>
                  <span className="ml-2 inline-flex items-center rounded-full bg-white/90 border border-slate-200 px-3 py-1 text-xs md:text-sm text-slate-600">
                    Period: {headerPeriodRange || 'N/A'}
                  </span>
                </div>
              </div>
            )}
            {nav === "dashboard" && (
              <div className="space-y-8">
                {/* Premium Header */}
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-800">Finance Overview</h2>
                    <p className="text-gray-500 mt-1 flex items-center">
                      <CalendarIcon className="h-4 w-4 mr-2" />
                      {displayPeriodLabel}
                    </p>
                  </div>
                  {/* Controls removed per requirements */}
                  <div className="flex items-center gap-4">
                    <label className="text-gray-700 text-sm font-medium">Time Period:</label>
                    {showAdvancedFilters && (
                      <div className="flex items-center gap-2 mr-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                        {timePeriod === 'custom_fy' && (
                          <>
                            <select
                              value={selectedFinancialYear}
                              onChange={(e) => {
                                const newFY = parseInt(e.target.value);
                                setSelectedFinancialYear(newFY);
                              }}
                              className="px-3 py-2 rounded-lg bg-white text-gray-900 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {FINANCIAL_YEARS.map((year) => (
                                <option key={year.value} value={year.value}>{year.label}</option>
                              ))}
                            </select>
                            <button
                              onClick={handleCustomFyApply}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-all transform hover:scale-105"
                            >
                              Apply
                            </button>
                          </>
                        )}
                        {timePeriod === 'custom' && (
                          <>
                            <select
                              value={selectedFinancialYear}
                              onChange={(e) => {
                                const newFY = parseInt(e.target.value);
                                setSelectedFinancialYear(newFY);
                                setSelectedMonth(""); // Reset month when FY changes
                              }}
                              className="px-3 py-2 rounded-lg bg-white text-gray-900 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {FINANCIAL_YEARS.map((year) => (
                                <option key={year.value} value={year.value}>{year.label}</option>
                              ))}
                            </select>
                            <select
                              value={selectedMonth}
                              onChange={(e) => setSelectedMonth(e.target.value)}
                              className="px-3 py-2 rounded-lg bg-white text-gray-900 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select Month</option>
                              {getFinancialYearMonthsFor(selectedFinancialYear).map((month) => (
                                <option key={month.value} value={month.value}>{month.label}</option>
                              ))}
                            </select>
                            <button
                              onClick={handleCustomPeriodApply}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-all transform hover:scale-105"
                            >
                              Apply
                            </button>
                          </>
                        )}
                        {timePeriod === 'custom_range' && (
                          <>
                            <input type="date" value={customStart || ''} onChange={(e) => setCustomStart(e.target.value)} className="px-3 py-2 rounded-lg bg-white text-gray-900 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            <span className="text-gray-600 text-sm">to</span>
                            <input type="date" value={customEnd || ''} onChange={(e) => setCustomEnd(e.target.value)} className="px-3 py-2 rounded-lg bg-white text-gray-900 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            <button
                              onClick={handleCustomRangeApply}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-all transform hover:scale-105"
                            >
                              Apply
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setShowAdvancedFilters(false)}
                          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-all"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                    <select
                      value={timePeriod}
                      onChange={(e) => handleTimePeriodChange(e.target.value)}
                      onClick={() => { if (timePeriod === 'custom' || timePeriod === 'custom_range' || timePeriod === 'custom_fy') setShowAdvancedFilters(true); }}
                      className="px-4 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {TIME_PERIODS.map((period) => (
                        <option key={period.value} value={period.value}>{period.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {/* Premium Metrics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                  {/* Opening Balance */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm font-medium">Opening Balance</p>
                        <p className="text-gray-900 text-2xl font-bold mt-1">{formatCurrency(dashboardData.opening_balance)}</p>
                        <p className="text-xs text-gray-500 mt-2">Balance carried forward from before this period</p>
                        <p className="text-xs text-slate-600 mt-1 font-medium">Carried forward: {formatCurrency(dashboardData.opening_balance)}</p>
                      </div>
                      <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                        <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  {/* Sales (Revenue) */}
                  <div onClick={() => handleMetricCardClick('sales_revenue')} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm font-medium">Sales</p>
                        <p className="text-gray-900 text-2xl font-bold mt-1">{formatCurrency(dashboardData.total_sales)}</p>
                        <p className="text-xs text-slate-600 mt-2 font-medium">Sales sources total = {formatCurrency(dashboardData.total_sales)}</p>
                        {dashboardData.total_sales_usd > 0 && (
                          <p className="text-gray-700 text-base font-semibold mt-1">${dashboardData.total_sales_usd.toLocaleString()}</p>
                        )}
                        {dashboardData.has_previous_comparison ? (
                          <p className={`text-xs mt-1 flex items-center ${dashboardData.sales_change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {dashboardData.sales_change_percent >= 0 ? (
                              <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                            ) : (
                              <ArrowTrendingDownIcon className="h-3 w-3 mr-1" />
                            )}
                            {Math.abs(dashboardData.sales_change_percent)}% vs previous
                          </p>
                        ) : (
                          <p className="text-gray-500 text-xs mt-1">No previous comparison</p>
                        )}
                      </div>
                      <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                        <ChartBarIcon className="h-6 w-6 text-emerald-600" />
                      </div>
                    </div>
                  </div>

                  {/* Other Revenue */}
                  <div onClick={() => handleMetricCardClick('other_income')} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm font-medium">Other Revenue</p>
                        <p className="text-gray-900 text-2xl font-bold mt-1">{formatCurrency(dashboardOtherIncome)}</p>
                        <p className="text-xs text-slate-600 mt-2 font-medium">
                          Total Inflow {formatCurrency(dashboardData.total_inflow)} - Sales {formatCurrency(dashboardData.total_sales)} = {formatCurrency(dashboardOtherIncome)}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-sky-50 rounded-xl flex items-center justify-center">
                        <FunnelIcon className="h-6 w-6 text-sky-600" />
                      </div>
                    </div>
                  </div>

                  {/* Total Inflow */}
                  <div onClick={() => handleMetricCardClick('total_inflow')} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm font-medium">Total Inflow</p>
                        <p className="text-gray-900 text-2xl font-bold mt-1">{formatCurrency(dashboardData.total_inflow)}</p>
                        <p className="text-xs text-slate-600 mt-2 font-medium">
                          Sales {formatCurrency(dashboardData.total_sales)} + Other Income {formatCurrency(dashboardOtherIncome)} = {formatCurrency(dashboardData.total_inflow)}
                        </p>
                        {dashboardData.has_previous_comparison ? (
                          <p className={`text-xs mt-1 flex items-center ${dashboardData.inflow_change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {dashboardData.inflow_change_percent >= 0 ? (
                          <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                            ) : (
                              <ArrowTrendingDownIcon className="h-3 w-3 mr-1" />
                            )}
                          {Math.abs(dashboardData.inflow_change_percent)}% vs previous
                        </p>
                        ) : (
                          <p className="text-gray-500 text-xs mt-1">
                            No previous comparison
                          </p>
                        )}
                      </div>
                      <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                        <ArrowTrendingUpIcon className="h-6 w-6 text-green-600" />
                      </div>
                    </div>
                  </div>

                  {/* Total Outflow */}
                  <div onClick={() => handleMetricCardClick('total_outflow')} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm font-medium">Total Outflow</p>
                        <p className="text-gray-900 text-2xl font-bold mt-1">{formatCurrency(dashboardData.total_outflow)}</p>
                        <p className="text-xs text-slate-600 mt-2 font-medium">Business expenses only = {formatCurrency(dashboardData.total_outflow)}</p>
                        {dashboardData.has_previous_comparison ? (
                          <p className={`text-xs mt-1 flex items-center ${dashboardData.outflow_change_percent >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {dashboardData.outflow_change_percent >= 0 ? (
                              <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                            ) : (
                          <ArrowTrendingDownIcon className="h-3 w-3 mr-1" />
                            )}
                          {Math.abs(dashboardData.outflow_change_percent)}% vs previous
                        </p>
                        ) : (
                          <p className="text-gray-500 text-xs mt-1">
                            No previous comparison
                          </p>
                        )}
                      </div>
                      <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center">
                        <ArrowTrendingDownIcon className="h-6 w-6 text-red-600" />
                      </div>
                    </div>
                  </div>

                  {/* Net Profit */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm font-medium">Net Profit</p>
                        <p className="text-gray-900 text-2xl font-bold mt-1">{formatCurrency(dashboardData.net_profit)}</p>
                        <p className="text-xs text-slate-600 mt-2 font-medium">
                          Sales {formatCurrency(dashboardData.total_sales)} - Outflow {formatCurrency(dashboardData.total_outflow)} = {formatCurrency(dashboardData.net_profit)}
                        </p>
                        <p className="text-gray-500 text-xs mt-1">{dashboardData.profit_margin}% margin</p>
                        {dashboardData.has_previous_comparison && (
                          <p className={`text-xs mt-1 flex items-center ${dashboardData.profit_change_percent >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {dashboardData.profit_change_percent >= 0 ? (
                              <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                            ) : (
                              <ArrowTrendingDownIcon className="h-3 w-3 mr-1" />
                            )}
                            {Math.abs(dashboardData.profit_change_percent)}% vs previous
                          </p>
                        )}
                      </div>
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${dashboardData.net_profit >= 0 ? 'bg-emerald-50' : 'bg-orange-50'}`}>
                        <CurrencyDollarIcon className={`h-6 w-6 ${dashboardData.net_profit >= 0 ? 'text-emerald-600' : 'text-orange-600'}`} />
                      </div>
                    </div>
                  </div>

                  {/* Withdrawals */}
                  <div onClick={() => handleMetricCardClick('total_withdrawals')} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all cursor-pointer">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm font-medium">Withdrawals</p>
                        <p className="text-gray-900 text-2xl font-bold mt-1">{formatCurrency(dashboardData.total_withdrawals || 0)}</p>
                        <p className="text-xs text-gray-500 mt-2">Not treated as expense and included in net profit</p>
                      </div>
                      <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center">
                        <ArrowTrendingDownIcon className="h-6 w-6 text-amber-600" />
                      </div>
                    </div>
                  </div>

                  {/* Closing Balance */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-all">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-500 text-sm font-medium">Closing Balance</p>
                        <p className="text-gray-900 text-2xl font-bold mt-1">{formatCurrency(dashboardData.closing_balance)}</p>
                        <p className="text-xs text-gray-500 mt-2">Opening + Total Inflow - Total Outflow - Withdrawals</p>
                        <p className="text-xs text-slate-600 mt-1 font-medium">
                          {formatCurrency(dashboardData.opening_balance)} + {formatCurrency(dashboardData.total_inflow)} - {formatCurrency(dashboardData.total_outflow)} - {formatCurrency(dashboardData.total_withdrawals || 0)} = {formatCurrency(dashboardData.closing_balance)}
                        </p>
                      </div>
                      <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                        <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-lg font-semibold tracking-tight text-slate-900">Inflow Contribution by Source</h3>
                      <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden text-xs">
                        <button onClick={() => setInflowChartType('pie')} className={`px-3 py-1.5 ${inflowChartType === 'pie' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>Pie</button>
                        <button onClick={() => setInflowChartType('bar')} className={`px-3 py-1.5 ${inflowChartType === 'bar' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>Bar</button>
                      </div>
                    </div>
                    {inflowContributionData.excludedCount > 0 && (
                      <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-700">
                        Data warning: {inflowContributionData.excludedCount} record(s), {formatCurrency(inflowContributionData.excludedAmount)} excluded.
                      </div>
                    )}
                    <div className="h-80">
                      {inflowContributionData.chartRows.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-slate-500">
                          <div className="text-sm font-medium">No inflow contribution data</div>
                          <div className="text-xs mt-1">Try changing time period or filters.</div>
                        </div>
                      ) : (
                        <div className="h-full grid grid-cols-1 lg:grid-cols-5 gap-3">
                          <div className="lg:col-span-3 h-full">
                            <ResponsiveContainer width="100%" height="100%">
                              {inflowChartType === 'pie' ? (
                                <PieChart>
                                  <Pie
                                    data={inflowContributionData.chartRows}
                                    dataKey="amount"
                                    nameKey="key"
                                    outerRadius={108}
                                    label={({ percent }) => ((percent * 100) >= 5 ? `${(percent * 100).toFixed(0)}%` : '')}
                                  >
                                    {inflowContributionData.chartRows.map((row, idx) => (
                                      <Cell key={`inflow-cell-${idx}`} fill={row.isOthers ? '#94a3b8' : DASHBOARD_CONTRIBUTION_COLORS[idx % DASHBOARD_CONTRIBUTION_COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(value, _, payload) => [`${formatCurrency(Number(value || 0))} (${payload?.payload?.percentage || 0}%)`, 'Amount']} />
                                </PieChart>
                              ) : (
                                <BarChart data={inflowContributionData.chartRows} layout="vertical" margin={{ top: 8, right: 16, left: 10, bottom: 8 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                  <XAxis type="number" tickFormatter={(value) => Number(value).toLocaleString('en-IN')} />
                                  <YAxis type="category" dataKey="key" width={170} tickFormatter={(v) => String(v).length > 18 ? `${String(v).slice(0, 18)}...` : v} />
                                  <Tooltip formatter={(value, _, payload) => [`${formatCurrency(Number(value || 0))} (${payload?.payload?.percentage || 0}%)`, 'Amount']} />
                                  <Bar dataKey="amount" name="Inflow">
                                    {inflowContributionData.chartRows.map((row, idx) => (
                                      <Cell key={`inflow-bar-${idx}`} fill={row.isOthers ? '#94a3b8' : DASHBOARD_CONTRIBUTION_COLORS[idx % DASHBOARD_CONTRIBUTION_COLORS.length]} />
                                    ))}
                                  </Bar>
                                </BarChart>
                              )}
                            </ResponsiveContainer>
                          </div>
                          <div className="lg:col-span-2 overflow-y-auto pr-1">
                            <div className="space-y-1">
                              {inflowContributionData.chartRows.map((item, idx) => (
                                <div key={`infl-leg-${item.key}-${idx}`} className="flex items-center justify-between gap-2 text-xs">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.isOthers ? '#94a3b8' : DASHBOARD_CONTRIBUTION_COLORS[idx % DASHBOARD_CONTRIBUTION_COLORS.length] }} />
                                    <span className="truncate text-slate-700" title={item.key}>{item.key}</span>
                                  </div>
                                  <span className="text-slate-500">{item.percentage}%</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200">
                    <div className="flex items-center justify-between mb-3 gap-2">
                      <h3 className="text-lg font-semibold tracking-tight text-slate-900">Outflow Contribution</h3>
                      <div className="flex items-center gap-2">
                        <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden text-xs">
                          <button onClick={() => setOutflowContributionGroupBy('sub_category')} className={`px-3 py-1.5 ${outflowContributionGroupBy === 'sub_category' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>Subcategory</button>
                          <button onClick={() => setOutflowContributionGroupBy('category')} className={`px-3 py-1.5 ${outflowContributionGroupBy === 'category' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>Category</button>
                        </div>
                        <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden text-xs">
                          <button onClick={() => setOutflowChartType('pie')} className={`px-3 py-1.5 ${outflowChartType === 'pie' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>Pie</button>
                          <button onClick={() => setOutflowChartType('bar')} className={`px-3 py-1.5 ${outflowChartType === 'bar' ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}>Bar</button>
                        </div>
                      </div>
                    </div>
                    {activeOutflowContributionData.excludedCount > 0 && (
                      <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs text-amber-700">
                        Data warning: {activeOutflowContributionData.excludedCount} record(s), {formatCurrency(activeOutflowContributionData.excludedAmount)} excluded.
                      </div>
                    )}
                    <div className="h-80">
                      {activeOutflowContributionData.chartRows.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-slate-500">
                          <div className="text-sm font-medium">No outflow contribution data</div>
                          <div className="text-xs mt-1">Try changing time period or filters.</div>
                        </div>
                      ) : (
                        <div className="h-full grid grid-cols-1 lg:grid-cols-5 gap-3">
                          <div className="lg:col-span-3 h-full">
                            <ResponsiveContainer width="100%" height="100%">
                              {outflowChartType === 'pie' ? (
                                <PieChart>
                                  <Pie
                                    data={activeOutflowContributionData.chartRows}
                                    dataKey="amount"
                                    nameKey="key"
                                    outerRadius={108}
                                    label={({ percent }) => ((percent * 100) >= 5 ? `${(percent * 100).toFixed(0)}%` : '')}
                                  >
                                    {activeOutflowContributionData.chartRows.map((row, idx) => (
                                      <Cell key={`out-cell-${idx}`} fill={row.isOthers ? '#94a3b8' : DASHBOARD_CONTRIBUTION_COLORS[idx % DASHBOARD_CONTRIBUTION_COLORS.length]} />
                                    ))}
                                  </Pie>
                                  <Tooltip formatter={(value, _, payload) => [`${formatCurrency(Number(value || 0))} (${payload?.payload?.percentage || 0}%)`, 'Amount']} />
                                </PieChart>
                              ) : (
                                <BarChart data={activeOutflowContributionData.chartRows} layout="vertical" margin={{ top: 8, right: 16, left: 10, bottom: 8 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                  <XAxis type="number" tickFormatter={(value) => Number(value).toLocaleString('en-IN')} />
                                  <YAxis type="category" dataKey="key" width={170} tickFormatter={(v) => String(v).length > 18 ? `${String(v).slice(0, 18)}...` : v} />
                                  <Tooltip formatter={(value, _, payload) => [`${formatCurrency(Number(value || 0))} (${payload?.payload?.percentage || 0}%)`, 'Amount']} />
                                  <Bar dataKey="amount" name="Outflow">
                                    {activeOutflowContributionData.chartRows.map((row, idx) => (
                                      <Cell key={`out-bar-${idx}`} fill={row.isOthers ? '#94a3b8' : DASHBOARD_CONTRIBUTION_COLORS[idx % DASHBOARD_CONTRIBUTION_COLORS.length]} />
                                    ))}
                                  </Bar>
                                </BarChart>
                              )}
                            </ResponsiveContainer>
                          </div>
                          <div className="lg:col-span-2 overflow-y-auto pr-1">
                            <div className="space-y-1">
                              {activeOutflowContributionData.chartRows.map((item, idx) => (
                                <div key={`out-leg-${item.key}-${idx}`} className="flex items-center justify-between gap-2 text-xs">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.isOthers ? '#94a3b8' : DASHBOARD_CONTRIBUTION_COLORS[idx % DASHBOARD_CONTRIBUTION_COLORS.length] }} />
                                    <span className="truncate text-slate-700" title={item.key}>{item.key}</span>
                                  </div>
                                  <span className="text-slate-500">{item.percentage}%</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sales and Recent Transactions */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Sales Summary */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-xl font-semibold tracking-tight text-gray-900 mb-4">Sales Summary</h3>
                    <div className="mb-4">
                      <p className="text-gray-500 text-sm">Total Sales</p>
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(dashboardData.total_sales)}</p>
                      <p className="text-gray-500 text-sm">{dashboardData.sales_count} transactions</p>
                    </div>
                    {Object.keys(dashboardData.sales_by_source).length > 0 ? (
                      <div className="space-y-2">
                        {Object.entries(dashboardData.sales_by_source).map(([source, amount]) => (
                          <div key={source} className="flex justify-between items-center">
                            <span className="text-gray-700 text-sm">{source}</span>
                            <span className="text-gray-900 font-semibold">{formatCurrency(amount)}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-gray-500 text-center py-4">No sales data available</div>
                    )}
                  </div>

                  {/* Recent Transactions */}
                  <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-xl font-semibold tracking-tight text-gray-900 mb-4">Recent Transactions</h3>
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {recentTransactions.length > 0 ? (
                        recentTransactions.map((transaction) => (
                          <div key={transaction.id} className="flex items-center justify-between p-3 bg-gray-50 border border-gray-100 rounded-lg">
                            <div className="flex items-center gap-3">
                              <div className={`w-2 h-2 rounded-full ${transaction.type === 'inflow' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                              <div>
                                <p className="text-gray-900 text-sm font-medium">{transaction.description}</p>
                                <p className="text-gray-500 text-xs">{transaction.date}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`text-sm font-semibold ${transaction.type === 'inflow' ? 'text-green-600' : 'text-red-600'}`}>
                                {transaction.type === 'inflow' ? '+' : '-'}{formatCurrency(transaction.amount)}
                              </p>
                              <p className="text-gray-500 text-xs">{transaction.category}</p>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-gray-500 text-center py-8">No recent transactions</div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {nav === 'dashboard' && customReports.filter(r => r.tags?.includes('dashboard_card')).length > 0 && (
              <div id="custom-reports-section" className="mt-8 mb-4">
                <div className="flex flex-wrap gap-6 items-start">
                  {customReports.filter(r => r.tags?.includes('dashboard_card')).map((report, idx) => (
                    <AdaptiveReportCard
                      key={report.id}
                      report={report}
                      timePeriod={timePeriod}
                      customStart={customStart}
                      customEnd={customEnd}
                      onDelete={handleDeleteReport}
                      onEdit={handleEditReportFilters}
                      onDuplicate={handleDuplicateReport}
                      onResize={handleResizeReport}
                      isPinned={true}
                      onTogglePin={handleTogglePinReport}
                      onDragStart={(e) => handleReportDragStart(e, idx)}
                      onDragOver={(e) => handleReportDragOver(e, idx)}
                      onDragEnd={handleReportDragEnd}
                    />
                  ))}
                </div>
              </div>
            )}
            {nav === "inflow" && (
              <div>
                {editingReport && reportCreationType === 'inflow' && (
                  <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm animate-pulse-subtle">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-blue-500 text-white shadow-md shadow-blue-200 shrink-0">
                        <PencilIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="text-sm font-black text-gray-900">Editing Report: <span className="text-blue-700">{editingReport.name}</span></h4>
                        <p className="text-xs text-gray-500">Adjust the filters and period below, then click save to update the report card on the dashboard.</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button
                        onClick={handleCancelEditReport}
                        className="px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-xl text-xs font-bold transition-all shadow-sm"
                      >
                        Cancel Edit
                      </button>
                      <button
                        onClick={() => {
                          setNewReportName(editingReport.name);
                          setShowReportCreateModal(true);
                        }}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-200"
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-800">Cash Inflow</h2>
                  <div className="flex items-center gap-3">
                    {adminCanWrite && (
                      <button
                        onClick={() => openImportModal('inflow')}
                        className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                      >
                        <CloudUploadIcon style={{ width: 16, height: 16 }} />
                        Import Inflows
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowInflowForm((v) => !v);
                        if (showInflowForm) handleCancelEdit();
                      }}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
                    >
                      {showInflowForm || editingInflow ? 'Close' : '+ Add Inflow'}
                    </button>
                    <label className="text-gray-700 text-sm font-medium">Time Period:</label>
                    {showAdvancedFilters && (
                      <div className="flex items-center gap-2 mr-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                        {timePeriod === 'custom_fy' && (
                          <>
                            <select
                              value={selectedFinancialYear}
                              onChange={(e) => {
                                const newFY = parseInt(e.target.value);
                                setSelectedFinancialYear(newFY);
                              }}
                              className="px-3 py-2 rounded-lg bg-white text-gray-900 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {FINANCIAL_YEARS.map((year) => (
                                <option key={year.value} value={year.value}>{year.label}</option>
                              ))}
                            </select>
                            <button
                              onClick={handleCustomFyApply}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-all transform hover:scale-105"
                            >
                              Apply
                            </button>
                          </>
                        )}
                        {timePeriod === 'custom' && (
                          <>
                            <select
                              value={selectedFinancialYear}
                              onChange={(e) => {
                                const newFY = parseInt(e.target.value);
                                setSelectedFinancialYear(newFY);
                                setSelectedMonth(""); // Reset month when FY changes
                              }}
                              className="px-3 py-2 rounded-lg bg-white text-gray-900 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {FINANCIAL_YEARS.map((year) => (
                                <option key={year.value} value={year.value}>{year.label}</option>
                              ))}
                            </select>
                            <select
                              value={selectedMonth}
                              onChange={(e) => setSelectedMonth(e.target.value)}
                              className="px-3 py-2 rounded-lg bg-white text-gray-900 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select Month</option>
                              {getFinancialYearMonthsFor(selectedFinancialYear).map((month) => (
                                <option key={month.value} value={month.value}>{month.label}</option>
                              ))}
                            </select>
                            <button
                              onClick={handleCustomPeriodApply}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-all transform hover:scale-105"
                            >
                              Apply
                            </button>
                          </>
                        )}
                        {timePeriod === 'custom_range' && (
                          <>
                            <input type="date" value={customStart || ''} onChange={(e) => setCustomStart(e.target.value)} className="px-3 py-2 rounded-lg bg-white text-gray-900 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            <span className="text-gray-600 text-sm">to</span>
                            <input type="date" value={customEnd || ''} onChange={(e) => setCustomEnd(e.target.value)} className="px-3 py-2 rounded-lg bg-white text-gray-900 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            <button
                              onClick={handleCustomRangeApply}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-all transform hover:scale-105"
                            >
                              Apply
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setShowAdvancedFilters(false)}
                          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-all"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                    <select
                      value={timePeriod}
                      onChange={(e) => handleTimePeriodChange(e.target.value)}
                      onClick={() => { if (timePeriod === 'custom' || timePeriod === 'custom_range' || timePeriod === 'custom_fy') setShowAdvancedFilters(true); }}
                      className="px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {TIME_PERIODS.map((period) => (
                        <option key={period.value} value={period.value}>{period.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mb-4 bg-white rounded-xl border border-gray-100 px-4 py-4 text-sm">
                  <div className="text-center">
                    <span className="text-gray-600">Total Inflow (Selected Period)</span>
                    <div className="flex justify-center items-center gap-6 mt-2">
                      <div className="text-center">
                        <span className="text-2xl font-bold text-green-600">{analytics.inflow.toLocaleString()}</span>
                        <p className="text-xs text-gray-500">INR (All)</p>
                      </div>
                      <div className="text-center">
                        <span className="text-2xl font-bold text-green-600">${analytics.inflow_usd ? analytics.inflow_usd.toLocaleString() : '0'}</span>
                        <p className="text-xs text-gray-500">USD (All)</p>
                      </div>
                    </div>
                    {(inflowQuery || inflowSourceFilter || inflowBankFilter || inflowReflectingBankFilter || inflowReflectingMonthFilter || inflowInwardFilter !== undefined || inflowMinAmount || inflowMaxAmount || Object.keys(inflowColumnFilters.filters).length > 0) && (
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <span className="text-gray-600 text-xs">Filtered Results ({inflowColumnFilters.filteredAndSortedData.length} records)</span>
                        <div className="flex justify-center items-center gap-6 mt-1">
                          <div className="text-center">
                            <span className="text-lg font-bold text-blue-600">{filteredInflowTotals.inr.toLocaleString()}</span>
                            <p className="text-xs text-gray-500">INR</p>
                          </div>
                          <div className="text-center">
                            <span className="text-lg font-bold text-blue-600">${filteredInflowTotals.usd.toLocaleString()}</span>
                            <p className="text-xs text-gray-500">USD</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {(showInflowForm || editingInflow) && (
                  <form onSubmit={handleInflowSubmit} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 space-y-4 mb-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">{editingInflow ? 'Edit Inflow' : 'Add New Inflow'}</h3>
                      {editingInflow && (
                        <button type="button" onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-700 text-sm">
                          Cancel Edit
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Date<span className="text-red-500">*</span></label>
                        <input type="date" name="date" value={inflowForm.date} onChange={handleInflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Reflecting Month<span className="text-red-500">*</span></label>
                        <select name="reflecting_month" value={inflowForm.reflecting_month} onChange={handleInflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required>
                          <option value="">Select Month</option>
                          {inflowFYMonths.map((month) => (
                            <option key={month.value} value={month.value}>{month.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Source<span className="text-red-500">*</span></label>
                        <select name="source" value={inflowForm.source} onChange={handleInflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required>
                          <option value="" disabled>Select Source</option>
                          {(
                            inflowSources.length ? inflowSources.map(s => s.name) : INFLOW_SOURCES
                          ).map((src) => (
                            <option key={src} value={src}>{src}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Amount in Rs<span className="text-red-500">*</span></label>
                        <input type="number" step="0.01" name="amount_rs" value={inflowForm.amount_rs} onChange={handleInflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Amount in $ <span className="text-gray-500">(optional)</span></label>
                        <input type="number" step="0.01" name="amount_usd" value={inflowForm.amount_usd} onChange={handleInflowChange} placeholder="Enter manually only" className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Bank<span className="text-red-500">*</span></label>
                        <select name="bank" value={inflowForm.bank} onChange={handleInflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required>
                          <option value="" disabled>Select Bank</option>
                          {(
                            bankAccounts.length ? bankAccounts.filter(b => !b.is_default_cash).map(b => b.name) : BANKS
                          ).map((b) => (
                            <option key={b} value={b}>{b}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Reflecting Bank<span className="text-red-500">*</span></label>
                        <select name="reflecting_bank" value={inflowForm.reflecting_bank} onChange={handleInflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required>
                          <option value="" disabled>Select Reflecting Bank</option>
                          {(
                            bankAccounts.length ? bankAccounts.filter(b => !b.is_default_cash).map(b => b.name) : BANKS
                          ).map((b) => (
                            <option key={b} value={b}>{b}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Request for Inwards Remittance<span className="text-red-500">*</span></label>
                        <div className="flex gap-4 mt-2">
                          <label className="inline-flex items-center text-gray-700">
                            <input type="radio" name="request_inward" value="yes" checked={inflowForm.request_inward === true} onChange={handleInflowChange} className="form-radio text-blue-600 mr-2" /> Yes
                          </label>
                          <label className="inline-flex items-center text-gray-700">
                            <input type="radio" name="request_inward" value="no" checked={inflowForm.request_inward === false} onChange={handleInflowChange} className="form-radio text-blue-600 mr-2" /> No
                          </label>
                        </div>
                      </div>
                    </div>
                    {/* Inline, non-blocking limit warning */}
                    {limitCheckLoading && (
                      <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-2 rounded-xl text-sm">
                        Checking bank limits...
                      </div>
                    )}
                    {limitCheck && (limitCheck.exceeds_monthly || limitCheck.exceeds_quarterly || limitCheck.exceeds_yearly) && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                        <div className="font-semibold mb-1">This inflow may exceed configured bank limits.</div>
                        <ul className="list-disc pl-5 space-y-1">
                          {limitCheck.exceeds_monthly && (
                            <li>
                              Monthly: used {(limitCheck.details.monthly.used ?? 0).toLocaleString()} of {limitCheck.details.monthly.limit == null ? 'Unlimited' : `${(limitCheck.details.monthly.limit).toLocaleString()}`}; remaining {limitCheck.details.monthly.limit == null ? '-' : `${(limitCheck.details.monthly.remaining ?? 0).toLocaleString()}`}
                            </li>
                          )}
                          {limitCheck.exceeds_quarterly && (
                            <li>
                              Quarterly: used {(limitCheck.details.quarterly.used ?? 0).toLocaleString()} of {limitCheck.details.quarterly.limit == null ? 'Unlimited' : `${(limitCheck.details.quarterly.limit).toLocaleString()}`}; remaining {limitCheck.details.quarterly.limit == null ? '-' : `${(limitCheck.details.quarterly.remaining ?? 0).toLocaleString()}`}
                            </li>
                          )}
                          {limitCheck.exceeds_yearly && (
                            <li>
                              Yearly: used {(limitCheck.details.yearly.used ?? 0).toLocaleString()} of {limitCheck.details.yearly.limit == null ? 'Unlimited' : `${(limitCheck.details.yearly.limit).toLocaleString()}`}; remaining {limitCheck.details.yearly.limit == null ? '-' : `${(limitCheck.details.yearly.remaining ?? 0).toLocaleString()}`}
                            </li>
                          )}
                        </ul>
                        <div className="mt-1 text-xs text-red-600">You can still proceed; this is a warning.</div>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Invoice File <span className="text-gray-500">(optional)</span></label>
                        <input type="file" name="invoice_file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleInflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Remark <span className="text-gray-500">(optional)</span></label>
                        <input type="text" name="remark" value={inflowForm.remark} onChange={handleInflowChange} placeholder="Add any remarks" className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      </div>
                    </div>
                    {inflowError && <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-xl text-sm">{inflowError}</div>}
                    <div className="flex gap-3">
                      <button type="submit" disabled={loading} className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 rounded-lg text-white font-semibold transition-colors">
                        {loading ? 'Processing...' : (editingInflow ? 'Update Inflow' : 'Add Inflow')}
                      </button>
                      {(showInflowForm || editingInflow) && (
                        <button type="button" onClick={handleCancelEdit} className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-semibold transition-colors">
                          Cancel
                        </button>
                      )}
                    </div>
                  </form>
                )}
                {/* Inflow Records Table */}
                <div className="overflow-x-auto">
                  <div className="mb-4 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                      <h3 className="text-lg font-semibold text-gray-900">Inflow Records</h3>
                      {!showInflowSelections ? (
                        <>
                          <button
                            onClick={() => { setShowInflowSelections(true); setSelectedInflows([]); }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                          >
                            Select
                          </button>
                          <button
                            onClick={() => handleTableExport("inflow")}
                            disabled={inflowColumnFilters.filteredAndSortedData.length === 0}
                            title="Exports selected rows, or filtered rows if none are selected"
                            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                              inflowColumnFilters.filteredAndSortedData.length === 0
                                ? 'bg-slate-300 text-white cursor-not-allowed'
                                : 'bg-slate-700 hover:bg-slate-800 text-white'
                            }`}
                          >
                            Export
                          </button>
                          {inflowColumnFilters.getActiveFiltersCount() > 0 && (
                            <button
                              onClick={() => {
                                inflowColumnFilters.clearAllFilters();
                                setInflowFilterRules([]);
                              }}
                              className="bg-red-50 hover:bg-red-100 text-red-600 px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2"
                            >
                              <XMarkIcon className="w-4 h-4" />
                              Clear Filters
                            </button>
                          )}
                          {/* Create Report button — shows when filters or period are active */}
                          {(() => {
                            if (editingReport) return null;
                            const hasFilters = inflowColumnFilters.getActiveFiltersCount() > 0 || timePeriod !== 'all';
                            if (!hasFilters) return null;
                            return (
                              <button
                                onClick={() => {
                                  setReportCreationType('inflow');
                                  setNewReportName('');
                                  setShowReportCreateModal(true);
                                }}
                                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 shadow-md shadow-blue-200"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                                Create Report
                              </button>
                            );
                          })()}
                          <button
                            onClick={() => {
                              setShowInflowFilterPopup(true);
                              loadInflowFilterDropdownData();
                            }}
                            className="hidden px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                            Filter ({inflowFilterRules.length})
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => {
                            setShowInflowSelections(false);
                            setSelectedInflows([]);
                          }}
                          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm transition-colors"
                        >
                          Cancel Selection
                        </button>
                      )}
                    </div>
                    {showInflowSelections && (
                      <div className="flex flex-wrap items-center justify-end gap-2 md:gap-3">
                        <span className="text-gray-700 text-sm whitespace-nowrap">
                          {selectedInflowSummary.count} item(s) selected
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 whitespace-nowrap">
                          Rs:
                          <span className="font-semibold">{selectedInflowSummary.amountRs.toLocaleString('en-IN')}</span>
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 whitespace-nowrap">
                          USD:
                          <span className="font-semibold">${selectedInflowSummary.amountUsd.toLocaleString('en-IN')}</span>
                        </span>
                        <button
                          onClick={() => handleTableExport("inflow")}
                          disabled={inflowColumnFilters.filteredAndSortedData.length === 0}
                          title="Exports selected rows, or filtered rows if none are selected"
                          className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                            inflowColumnFilters.filteredAndSortedData.length === 0
                              ? 'bg-slate-300 text-white cursor-not-allowed'
                              : 'bg-slate-700 hover:bg-slate-800 text-white'
                          }`}
                        >
                          Export
                        </button>
                        <button
                          onClick={handleBulkDeleteInflows}
                          disabled={selectedInflows.length === 0}
                          className={`px-4 py-2 rounded-lg text-sm transition-colors ${selectedInflows.length === 0 ? 'bg-red-300 text-white cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 text-white'}`}
                        >
                          Delete Selected ({selectedInflows.length})
                        </button>
                        <button
                          onClick={() => openBulkEditModal("inflow")}
                          disabled={selectedInflows.length === 0}
                          className={`px-4 py-2 rounded-lg text-sm transition-colors ${selectedInflows.length === 0 ? 'bg-amber-300 text-white cursor-not-allowed' : 'bg-amber-500 hover:bg-amber-600 text-white'}`}
                        >
                          Bulk Edit
                        </button>
                      </div>
                    )}
                  </div>
                  <div style={{ width: '100%' }}>
                    <TanTable
                      columns={[
                        { accessorKey: 'date', header: 'Date', cell: (info)=> info.getValue() },
                        { accessorKey: 'reflecting_month', header: 'Reflecting Month', cell: ({ row }) => {
                          const v = row.original?.reflecting_month ?? row.original?.reflectingMonth;
                          return v ? formatReflectingMonth(v) : '-';
                        } },
                        { accessorKey: 'source', header: 'Source' },
                        { accessorKey: 'amount_rs', header: 'Amount (Rs)', cell: ({ getValue }) => {
                          const v = getValue();
                          return v != null ? `${Number(v).toLocaleString()}` : '-';
                        } },
                        { accessorKey: 'amount_usd', header: 'Amount ($)', cell: ({ getValue }) => {
                          const v = getValue();
                          return v != null ? `$${Number(v).toLocaleString()}` : '-';
                        } },
                        { accessorKey: 'bank', header: 'Bank' },
                        { accessorKey: 'reflecting_bank', header: 'Reflecting Bank' },
                        { accessorKey: 'request_inward', header: 'Inward', cell: ({ getValue }) => (getValue() ? 'Yes' : 'No') },
                        { accessorKey: 'invoice_file', header: 'Invoice', cell: ({ row }) => row.original.invoice_file ? (
                          <button onClick={async ()=>{
                            try {
                              const response = await api.get(`/api/download/${row.original.invoice_file}`, { responseType: 'blob' });
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', row.original.invoice_file);
                              document.body.appendChild(link);
                              link.click();
                            } catch(e) { alert('Failed to download'); }
                          }} className="text-blue-600 underline">Download</button>
                        ) : '-' },
                        { accessorKey: 'remark', header: 'Remark' },
                      ]}
                      data={inflowColumnFilters.filteredAndSortedData.map(r=>({ ...r, id: r?.id }))}
                      page={inflowPage}
                      pageSize={inflowPageSize}
                      rowCount={inflowColumnFilters.filteredAndSortedData.length}
                      onPageChange={(p)=>{ setInflowPage(p); }}
                      onPageSizeChange={(ps)=>{ setInflowPageSize(ps); localStorage.setItem('inflowPageSize', String(ps)); setInflowPage(0); }}
                      sorting={[{ id: inflowSortBy || 'date', desc: (inflowSortDir || 'desc') === 'desc' }]}
                      onSortingChange={(updater)=>{
                        const next = Array.isArray(updater) ? updater : updater([ { id: inflowSortBy, desc: inflowSortDir === 'desc' } ]);
                        const m = next?.[0];
                        setInflowSortBy(m?.id || 'date');
                        setInflowSortDir(m?.desc ? 'desc' : 'asc');
                        setInflowPage(0);
                      }}
                      showSelection={!!showInflowSelections}
                      selectedRowIds={showInflowSelections ? selectedInflows : []}
                      onSelectedRowIdsChange={showInflowSelections ? setSelectedInflows : undefined}
                      getRowId={(row)=> row.id}
                      renderRowActions={(row)=> (
                        <div className="flex gap-2">
                          <button onClick={()=>handleEditInflow(row)} className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs">Edit</button>
                          <button onClick={()=>handleDuplicateInflow(row)} className="bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded text-xs">Duplicate</button>
                          <button onClick={()=>handleDeleteInflow(row.id)} className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs">Delete</button>
                        </div>
                      )}
                      loading={!!inflowLoading}
                      enableColumnFilters={true}
                      columnFilters={inflowColumnFilters.filters}
                      onColumnFilterChange={inflowColumnFilters.handleFilterChange}
                      columnFormatters={{
                        'reflecting_month': formatReflectingMonth
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
            {nav === "outflow" && (
              <div>
                {editingReport && reportCreationType === 'outflow' && (
                  <div className="mb-6 p-4 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm animate-pulse-subtle">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-xl bg-blue-500 text-white shadow-md shadow-blue-200 shrink-0">
                        <PencilIcon className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="text-sm font-black text-gray-900">Editing Report: <span className="text-blue-700">{editingReport.name}</span></h4>
                        <p className="text-xs text-gray-500">Adjust the filters and period below, then click save to update the report card on the dashboard.</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button
                        onClick={handleCancelEditReport}
                        className="px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-200 rounded-xl text-xs font-bold transition-all shadow-sm"
                      >
                        Cancel Edit
                      </button>
                      <button
                        onClick={() => {
                          setNewReportName(editingReport.name);
                          setShowReportCreateModal(true);
                        }}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-md shadow-blue-200"
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>
                )}
                <div className="flex justify-between items-center mb-3">
                  <h2 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-800">Cash Outflow</h2>
                  <div className="flex items-center gap-3">
                    {adminCanWrite && (
                      <button
                        onClick={() => openImportModal('outflow')}
                        className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                      >
                        <CloudUploadIcon style={{ width: 16, height: 16 }} />
                        Import Outflows
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowOutflowForm((v) => !v);
                        if (showOutflowForm) handleCancelEdit();
                      }}
                      className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
                    >
                      {showOutflowForm || editingOutflow ? 'Close' : '+ Add Outflow'}
                    </button>
                    <label className="text-gray-700 text-sm font-medium">Time Period:</label>
                    {showAdvancedFilters && (
                      <div className="flex items-center gap-2 mr-4 p-4 bg-blue-50 rounded-xl border border-blue-100">
                        {timePeriod === 'custom_fy' && (
                          <>
                            <select
                              value={selectedFinancialYear}
                              onChange={(e) => {
                                const newFY = parseInt(e.target.value);
                                setSelectedFinancialYear(newFY);
                              }}
                              className="px-3 py-2 rounded-lg bg-white text-gray-900 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {FINANCIAL_YEARS.map((year) => (
                                <option key={year.value} value={year.value}>{year.label}</option>
                              ))}
                            </select>
                            <button
                              onClick={handleCustomFyApply}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-all transform hover:scale-105"
                            >
                              Apply
                            </button>
                          </>
                        )}
                        {timePeriod === 'custom' && (
                          <>
                            <select
                              value={selectedFinancialYear}
                              onChange={(e) => {
                                const newFY = parseInt(e.target.value);
                                setSelectedFinancialYear(newFY);
                                setSelectedMonth(""); // Reset month when FY changes
                              }}
                              className="px-3 py-2 rounded-lg bg-white text-gray-900 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              {FINANCIAL_YEARS.map((year) => (
                                <option key={year.value} value={year.value}>{year.label}</option>
                              ))}
                            </select>
                            <select
                              value={selectedMonth}
                              onChange={(e) => setSelectedMonth(e.target.value)}
                              className="px-3 py-2 rounded-lg bg-white text-gray-900 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select Month</option>
                              {getFinancialYearMonthsFor(selectedFinancialYear).map((month) => (
                                <option key={month.value} value={month.value}>{month.label}</option>
                              ))}
                            </select>
                            <button
                              onClick={handleCustomPeriodApply}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-all transform hover:scale-105"
                            >
                              Apply
                            </button>
                          </>
                        )}
                        {timePeriod === 'custom_range' && (
                          <>
                            <input type="date" value={customStart || ''} onChange={(e) => setCustomStart(e.target.value)} className="px-3 py-2 rounded-lg bg-white text-gray-900 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            <span className="text-gray-600 text-sm">to</span>
                            <input type="date" value={customEnd || ''} onChange={(e) => setCustomEnd(e.target.value)} className="px-3 py-2 rounded-lg bg-white text-gray-900 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            <button
                              onClick={handleCustomRangeApply}
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-all transform hover:scale-105"
                            >
                              Apply
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setShowAdvancedFilters(false)}
                          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium transition-all"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                    <select
                      value={timePeriod}
                      onChange={(e) => handleTimePeriodChange(e.target.value)}
                      onClick={() => { if (timePeriod === 'custom' || timePeriod === 'custom_range' || timePeriod === 'custom_fy') setShowAdvancedFilters(true); }}
                      className="px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {TIME_PERIODS.map((period) => (
                        <option key={period.value} value={period.value}>{period.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="mb-4 bg-white rounded-xl border border-gray-100 px-4 py-4 text-sm">
                  <div className="text-center">
                    <span className="text-gray-600">Total Outflow (Selected Period)</span>
                    <div className="mt-2">
                      <span className="text-3xl font-bold text-red-600">{analytics.outflow.toLocaleString()}</span>
                      <p className="text-xs text-gray-500 mt-1">INR (All)</p>
                    </div>
                    <div className="mt-3">
                      <span className="text-gray-600">Withdrawals (Selected Period)</span>
                      <div className="mt-1">
                        <span className="text-2xl font-bold text-amber-600">{Number(analytics.withdrawals || 0).toLocaleString()}</span>
                        <p className="text-xs text-gray-500 mt-1">INR (Excluded from Outflow/Profit)</p>
                      </div>
                    </div>
                    {(outflowQuery || outflowReflectingMonthFilter || outflowExpenseFilter || outflowCategoryFilter || outflowSubcategoryFilter || outflowModeFilter || outflowVendorFilter || outflowRemarksFilter || outflowMinAmount || outflowMaxAmount || Object.keys(outflowColumnFilters.filters).length > 0) && (
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <span className="text-gray-600 text-xs">Filtered Results ({outflowColumnFilters.filteredAndSortedData.length} records)</span>
                        <div className="mt-1">
                          <span className="text-2xl font-bold text-blue-600">{filteredOutflowTotals.inr.toLocaleString()}</span>
                          <p className="text-xs text-gray-500">Expense INR</p>
                        </div>
                        <div className="mt-2">
                          <span className="text-xl font-bold text-amber-600">{Number(filteredOutflowTotals.withdrawals || 0).toLocaleString()}</span>
                          <p className="text-xs text-gray-500">Withdrawal INR</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {(showOutflowForm || editingOutflow) && (
                  <form onSubmit={handleOutflowSubmit} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 space-y-4 mb-6">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">{editingOutflow ? 'Edit Outflow' : 'Add New Outflow'}</h3>
                      {editingOutflow && (
                        <button type="button" onClick={handleCancelEdit} className="text-gray-500 hover:text-gray-700 text-sm">
                          Cancel Edit
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Date<span className="text-red-500">*</span></label>
                        <input type="date" name="date" value={outflowForm.date} onChange={handleOutflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Reflecting Month<span className="text-red-500">*</span></label>
                        <select name="reflecting_month" value={outflowForm.reflecting_month} onChange={handleOutflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required>
                          <option value="">Select Month</option>
                          {outflowFYMonths.map((month) => (
                            <option key={month.value} value={month.value}>{month.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Expense<span className="text-red-500">*</span></label>
                        <input type="text" name="expense" value={outflowForm.expense} onChange={handleOutflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Amount in Rs<span className="text-red-500">*</span></label>
                        <input type="number" step="0.01" name="amount_rs" value={outflowForm.amount_rs} onChange={handleOutflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Subcategory<span className="text-red-500">*</span></label>
                        <select name="sub_category" value={outflowForm.sub_category} onChange={handleOutflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required>
                          <option value="">Select Subcategory</option>
                          {outflowSubcategories.filter(sub => sub.is_active !== false).map((sub) => (
                            <option key={sub.id} value={sub.name}>{sub.name}</option>
                          ))}
                        </select>
                        {getOutflowAccountingTreatmentForSubcategory(outflowForm.sub_category) === OUTFLOW_TREATMENT_PROFIT_WITHDRAWAL && (
                          <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                            This will reduce balance but will not count in Outflow or Profit.
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Category<span className="text-red-500">*</span></label>
                        <select name="category" value={outflowForm.category} onChange={handleOutflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required disabled>
                          <option value="">Auto-selected</option>
                          {outflowCategories.map((cat) => (
                            <option key={cat.id} value={cat.name}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Deducted From<span className="text-red-500">*</span></label>
                        <select name="deducted_from" value={outflowForm.deducted_from} onChange={handleOutflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required>
                          <option value="">Select Bank Account</option>
                          {bankAccounts.filter(b => b.is_active !== false).map((bank) => (
                            <option key={bank.id} value={bank.name}>{bank.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Mode<span className="text-red-500">*</span></label>
                        <select name="mode" value={outflowForm.mode} onChange={handleOutflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" required>
                          <option value="" disabled>Select Mode</option>
                          {MODES.map((m) => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Nature <span className="text-gray-500">(optional)</span></label>
                        <input type="text" name="nature" value={outflowForm.nature} onChange={handleOutflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Vendor <span className="text-gray-500">(optional)</span></label>
                        <input type="text" name="vendor" value={outflowForm.vendor} onChange={handleOutflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Invoice File <span className="text-gray-500">(optional)</span></label>
                        <input type="file" name="invoice_file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleOutflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      </div>
                      <div>
                        <label className="block text-gray-700 mb-1 text-sm font-medium">Remark <span className="text-gray-500">(optional)</span></label>
                        <input type="text" name="remark" value={outflowForm.remark} onChange={handleOutflowChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 text-gray-900 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                      </div>
                    </div>
                    {outflowError && <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-2 rounded-xl text-sm">{outflowError}</div>}
                    <div className="flex gap-3">
                      <button type="submit" disabled={loading} className="flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 rounded-lg text-white font-semibold transition-colors">
                        {loading ? 'Processing...' : (editingOutflow ? 'Update Outflow' : 'Add Outflow')}
                      </button>
                      {(showOutflowForm || editingOutflow) && (
                        <button type="button" onClick={handleCancelEdit} className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-semibold transition-colors">
                          Cancel
                        </button>
                      )}
                    </div>
                  </form>
                )}
                {/* Outflow Records Table */}
                <div className="overflow-x-auto">
                  <div className="mb-4 flex justify-between items-center">
                    <div className="flex items-center gap-4">
                      <h3 className="text-lg font-semibold text-gray-900">Outflow Records</h3>
                      {!showOutflowSelections ? (
                        <>
                          <button
                            onClick={() => { setShowOutflowSelections(true); setSelectedOutflows([]); }}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm transition-colors"
                          >
                            Select
                          </button>
                          <button
                            onClick={() => handleTableExport("outflow")}
                            disabled={outflowColumnFilters.filteredAndSortedData.length === 0}
                            title="Exports selected rows, or filtered rows if none are selected"
                            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                              outflowColumnFilters.filteredAndSortedData.length === 0
                                ? 'bg-slate-300 text-white cursor-not-allowed'
                                : 'bg-slate-700 hover:bg-slate-800 text-white'
                            }`}
                          >
                            Export
                          </button>
                          {outflowColumnFilters.getActiveFiltersCount() > 0 && (
                            <button
                              onClick={() => {
                                outflowColumnFilters.clearAllFilters();
                                setOutflowFilterRules([]);
                              }}
                              className="bg-red-50 hover:bg-red-100 text-red-600 px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2"
                            >
                              <XMarkIcon className="w-4 h-4" />
                              Clear Filters
                            </button>
                          )}
                          {/* Create Report button — shows when filters or period are active */}
                          {(() => {
                            if (editingReport) return null;
                            const hasFilters = outflowColumnFilters.getActiveFiltersCount() > 0 || timePeriod !== 'all';
                            if (!hasFilters) return null;
                            return (
                              <button
                                onClick={() => {
                                  setReportCreationType('outflow');
                                  setNewReportName('');
                                  setShowReportCreateModal(true);
                                }}
                                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-all flex items-center gap-2 shadow-md shadow-blue-200"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                                Create Report
                              </button>
                            );
                          })()}
                          <button
                            onClick={() => {
                              setShowOutflowFilterPopup(true);
                              loadOutflowFilterDropdownData();
                            }}
                            className="hidden px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium flex items-center gap-2"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                            Filter ({outflowFilterRules.length})
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => {
                            setShowOutflowSelections(false);
                            setSelectedOutflows([]);
                          }}
                          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm transition-colors"
                        >
                          Cancel Selection
                        </button>
                      )}
                    </div>
                    {showOutflowSelections && (
                      <div className="flex flex-wrap items-center justify-end gap-2 md:gap-3">
                        <span className="text-gray-700 text-sm whitespace-nowrap">
                          {selectedOutflowSummary.count} item(s) selected
                        </span>
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-700 whitespace-nowrap">
                          Sum:
                          <span className="font-semibold">{selectedOutflowSummary.amountRs.toLocaleString('en-IN')}</span>
                        </span>
                        <button
                          onClick={() => handleTableExport("outflow")}
                          disabled={outflowColumnFilters.filteredAndSortedData.length === 0}
                          title="Exports selected rows, or filtered rows if none are selected"
                          className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                            outflowColumnFilters.filteredAndSortedData.length === 0
                              ? 'bg-slate-300 text-white cursor-not-allowed'
                              : 'bg-slate-700 hover:bg-slate-800 text-white'
                          }`}
                        >
                          Export
                        </button>
                        <button
                          onClick={handleBulkDeleteOutflows}
                          disabled={selectedOutflows.length === 0}
                          className={`px-4 py-2 rounded-lg text-sm transition-colors ${selectedOutflows.length === 0 ? 'bg-red-300 text-white cursor-not-allowed' : 'bg-red-600 hover:bg-red-700 text-white'}`}
                        >
                          Delete Selected ({selectedOutflows.length})
                        </button>
                        <button
                          onClick={() => openBulkEditModal("outflow")}
                          disabled={selectedOutflows.length === 0}
                          className={`px-4 py-2 rounded-lg text-sm transition-colors ${selectedOutflows.length === 0 ? 'bg-amber-300 text-white cursor-not-allowed' : 'bg-amber-500 hover:bg-amber-600 text-white'}`}
                        >
                          Bulk Edit
                        </button>
                        {adminCanWrite && (
                          <button
                            onClick={handleCreateRecurringFromSelectedOutflow}
                            disabled={selectedOutflows.length !== 1}
                            title={selectedOutflows.length !== 1 ? 'Select exactly 1 outflow to create recurring' : 'Create recurring from selected outflow'}
                            className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                              selectedOutflows.length === 1
                                ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                : 'bg-indigo-300 text-white cursor-not-allowed'
                            }`}
                          >
                            Make Recurring
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <table className="min-w-full bg-white rounded-2xl border border-gray-100 hidden">
                    <thead>
                      <tr className="text-gray-600 text-sm">
                        {showOutflowSelections && (
                          <th className="px-4 py-2">
                            <input
                              type="checkbox"
                              checked={selectedOutflows.length === outflows.length && outflows.length > 0}
                              onChange={(e) => handleSelectAllOutflows(e.target.checked)}
                              className="rounded border-gray-300 bg-white text-blue-600 focus:ring-blue-500"
                            />
                          </th>
                        )}
                        <th className="px-4 py-2">Date</th>
                        <th className="px-4 py-2">Reflecting Month</th>
                        <th className="px-4 py-2">Expense</th>
                        <th className="px-4 py-2">Amount (Rs)</th>
                        <th className="px-4 py-2">Sub-Category</th>
                        <th className="px-4 py-2">Category</th>
                        <th className="px-4 py-2">Deducted From</th>
                        <th className="px-4 py-2">Mode</th>
                        <th className="px-4 py-2">Nature</th>
                        <th className="px-4 py-2">Remarks</th>
                        <th className="px-4 py-2">Vendor</th>
                        <th className="px-4 py-2">Invoice File</th>
                        <th className="px-4 py-2">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outflows.length === 0 ? (
                        <tr>
                          <td colSpan="14" className="text-center text-gray-500 py-4">No outflow records yet.</td>
                        </tr>
                      ) : (
                        outflows.map((row) => (
                          <tr key={row.id} className="text-gray-700 text-sm border-t border-gray-100">
                            {showOutflowSelections && (
                              <td className="px-4 py-2">
                                <input
                                  type="checkbox"
                                  checked={selectedOutflows.includes(row.id)}
                                  onChange={(e) => handleSelectOutflow(row.id, e.target.checked)}
                                  className="rounded border-gray-300 bg-white text-blue-600 focus:ring-blue-500"
                                />
                              </td>
                            )}
                            <td className="px-4 py-2">{row.date}</td>
                            <td className="px-4 py-2">{formatReflectingMonth(row.reflecting_month)}</td>
                            <td className="px-4 py-2">{row.expense || "-"}</td>
                            <td className="px-4 py-2">{row.amount_rs?.toLocaleString() || "0"}</td>
                            <td className="px-4 py-2">{row.sub_category || "-"}</td>
                            <td className="px-4 py-2">{row.category || "-"}</td>
                            <td className="px-4 py-2">{row.deducted_from || "-"}</td>
                            <td className="px-4 py-2">{row.mode || "-"}</td>
                            <td className="px-4 py-2">{row.nature || "-"}</td>
                            <td className="px-4 py-2">{row.remark || "-"}</td>
                            <td className="px-4 py-2">{row.vendor || "-"}</td>
                            <td className="px-4 py-2">
                              {row.invoice_file ? (
                                <button
                                  onClick={async () => {
                                    try {
                                      const response = await api.get(`/api/download/${row.invoice_file}`, {
                                        responseType: "blob", // get file as binary
                                      });

                                      // Create a temporary download link
                                      const url = window.URL.createObjectURL(new Blob([response.data]));
                                      const link = document.createElement("a");
                                      link.href = url;
                                      link.setAttribute("download", row.invoice_file); // keep filename
                                      document.body.appendChild(link);
                                      link.click();
                                    } catch (err) {
                                      alert("Failed to download file. Please try again.");
                                    }
                                  }}
                                  className="text-blue-600 underline"
                                >
                                  Download
                                </button>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td className="px-4 py-2">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditOutflow(row)}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs transition-colors"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => handleDeleteOutflow(row.id)}
                                  className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs transition-colors"
                                >
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  <div style={{ width: '100%' }}>
                    <TanTable
                      columns={[
                        { accessorKey: 'date', header: 'Date' },
                        { accessorKey: 'reflecting_month', header: 'Reflecting Month', cell: ({ row, getValue }) => {
                          const v = row.original?.reflecting_month ?? row.original?.reflectingMonth ?? getValue();
                          return v ? formatReflectingMonth(v) : '-';
                        } },
                        { accessorKey: 'expense', header: 'Expense' },
                        { accessorKey: 'amount_rs', header: 'Amount (Rs)', cell: ({ getValue }) => {
                          const v = getValue();
                          return v != null ? `${Number(v).toLocaleString()}` : '-';
                        } },
                        { accessorKey: 'sub_category', header: 'Subcategory' },
                        { accessorKey: 'category', header: 'Category' },
                        { accessorKey: 'accounting_treatment', header: 'Type', cell: ({ row }) => (
                          <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${isProfitWithdrawalRow(row.original) ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                            {isProfitWithdrawalRow(row.original) ? 'Withdrawal' : 'Expense'}
                          </span>
                        ) },
                        { accessorKey: 'deducted_from', header: 'Deducted From' },
                        { accessorKey: 'mode', header: 'Mode' },
                        { accessorKey: 'nature', header: 'Nature' },
                        { accessorKey: 'vendor', header: 'Vendor' },
                        { accessorKey: 'invoice_file', header: 'Invoice', cell: ({ row }) => row.original.invoice_file ? (
                          <button onClick={async ()=>{
                            try {
                              const response = await api.get(`/api/download/${row.original.invoice_file}`, { responseType: 'blob' });
                              const url = window.URL.createObjectURL(new Blob([response.data]));
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', row.original.invoice_file);
                              document.body.appendChild(link);
                              link.click();
                            } catch(e) { alert('Failed to download'); }
                          }} className="text-blue-600 underline">Download</button>
                        ) : '-' },
                        { accessorKey: 'remark', header: 'Remarks' },
                      ]}
                      data={outflowColumnFilters.filteredAndSortedData.map(r=>({ ...r, id: r?.id }))}
                      page={outflowPage}
                      pageSize={outflowPageSize}
                      rowCount={outflowColumnFilters.filteredAndSortedData.length}
                      onPageChange={(p)=>{ setOutflowPage(p); }}
                      onPageSizeChange={(ps)=>{ setOutflowPageSize(ps); localStorage.setItem('outflowPageSize', String(ps)); setOutflowPage(0); }}
                      sorting={[{ id: outflowSortBy || 'date', desc: (outflowSortDir || 'desc') === 'desc' }]}
                      onSortingChange={(updater)=>{
                        const next = Array.isArray(updater) ? updater : updater([ { id: outflowSortBy, desc: outflowSortDir === 'desc' } ]);
                        const m = next?.[0];
                        setOutflowSortBy(m?.id || 'date');
                        setOutflowSortDir(m?.desc ? 'desc' : 'asc');
                        setOutflowPage(0);
                      }}
                      showSelection={!!showOutflowSelections}
                      selectedRowIds={showOutflowSelections ? selectedOutflows : []}
                      onSelectedRowIdsChange={showOutflowSelections ? setSelectedOutflows : undefined}
                      getRowId={(row)=> row.id}
                      renderRowActions={(row)=> (
                        <div className="flex gap-2">
                          <button onClick={()=>handleEditOutflow(row)} className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs">Edit</button>
                          <button onClick={()=>handleDuplicateOutflow(row)} className="bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1 rounded text-xs">Duplicate</button>
                          <button onClick={()=>handleDeleteOutflow(row.id)} className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs">Delete</button>
                          {adminCanWrite && (
                            <button onClick={()=>openCreateRecurringFromOutflow(row)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-2 py-1 rounded text-xs">Make Recurring</button>
                          )}
                        </div>
                      )}
                      loading={!!outflowLoading}
                      enableColumnFilters={true}
                      columnFilters={outflowColumnFilters.filters}
                      onColumnFilterChange={outflowColumnFilters.handleFilterChange}
                      columnFormatters={{
                        'reflecting_month': formatReflectingMonth
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
            {/* Insights Section (dual tables, no viz) */}
            {nav === "insights" && (
              <div className="space-y-6">
                {!user?.email ? (
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading user authentication...</p>
                    </div>
                  </div>
                ) : (
                  <>
                {/* Insights Header (no dashboard actions) */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                      <InsightIcon className="h-6 w-6 text-purple-500" />
                    Insights
                    </h2>
                  </div>

                {/* Single-pane layout: filters, results, analytics only */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                  {/* Left rail hidden per requirement */}
                  <div className="hidden">
                    <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 sticky top-24">
                      <h3 className="text-sm font-semibold text-gray-900 mb-3">Insights</h3>
                      <div className="space-y-5">
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-600">Dashboards</span>
                            <div className="flex items-center gap-2">
                              <input value={newSubName} onChange={(e)=> setNewSubName(e.target.value)} placeholder="New dashboard" className="px-2 py-1 rounded border border-gray-200 text-xs" />
                              <button onClick={createSubDashboardFromSidebar} className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700">Add</button>
                            </div>
                          </div>
                          <div className="space-y-1 max-h-[28vh] overflow-auto pr-1">
                            {customDashboards.map((d)=> (
                              <div key={d.id} className="flex items-center justify-between group">
                                <button onClick={()=> setInsightsActiveDashId(d.id)} className={`min-w-0 flex-1 text-left px-2 py-1.5 rounded ${insightsActiveDashId===d.id ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-50 text-gray-800'}`}>
                                  <div className="truncate text-sm">{d.name}</div>
                                  <div className="text-xs text-gray-400">{(d.insights?.length||0)} widgets</div>
                        </button>
                                <div className="flex items-center gap-1 ml-2 opacity-0 group-hover:opacity-100">
                                  {d.id !== 'default' && (
                                    <button title="Delete" onClick={()=> deleteSubDashboard(d.id)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                                      <TrashIcon className="h-4 w-4" />
                                    </button>
                                  )}
                                  <button title="Add current to this" onClick={()=> addReportToDashboard('current', d.id)} className="p-1 text-indigo-600 hover:bg-indigo-50 rounded">
                                    <PlusIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                            ))}
                          </div>
                        </div>
                        {false && (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-medium text-gray-600">Reports</span>
                            <button onClick={()=> setShowReportBuilder(true)} className="text-xs px-2 py-1 rounded bg-gray-800 text-white hover:bg-black">New</button>
                          </div>
                          <input value={reportSearch} onChange={(e)=> setReportSearch(e.target.value)} placeholder="Search reports" className="w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm mb-2" />
                          <div className="space-y-1 max-h-[28vh] overflow-auto pr-1">
                            {filteredReports.length === 0 ? (
                              <div className="text-xs text-gray-500 py-6 text-center">No reports</div>
                            ) : filteredReports.map((r)=> (
                              <div key={r.id} className="group flex items-center justify-between p-2 rounded hover:bg-gray-50 cursor-pointer" onClick={()=> { loadReport(r); setInsightsMode(INSIGHTS_VIEW.CUSTOM); setNav('insights'); }}>
                                <div className="truncate text-sm">{r.name}</div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                                  <button onClick={(e)=> { e.stopPropagation(); setShowDashboardSelector({ reportId: r.id, reportName: r.name }); }} className="p-1 text-indigo-600 hover:text-indigo-700" title="Add to Dashboard"><PlusIcon className="h-4 w-4" /></button>
                                  <button onClick={(e)=> { e.stopPropagation(); editReport(r); }} className="p-1 text-gray-500 hover:text-gray-700" title="Edit"><PencilIcon className="h-4 w-4" /></button>
                                  <button onClick={(e)=> { e.stopPropagation(); deleteReport(r.id); }} className="p-1 text-red-600 hover:text-red-700" title="Delete"><TrashIcon className="h-4 w-4" /></button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        )}
                      </div>
                    </div>
                </div>

                  {/* Right canvas full width */}
                  <div className="lg:col-span-12 space-y-6">
                    <div className="flex items-center justify-between">
                      <div className="text-xl font-semibold text-gray-900">Insights</div>
                      <button onClick={()=> setShowSaveReportModal(true)} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm">Save as Report</button>
                    </div>
                {/* Dual Filter Builders */}
                  <div className={'grid grid-cols-1 lg:grid-cols-2 gap-6'}>
                    {/* Inflow Builder */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Inflow Filters</h3>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Field</label>
                        <select value={inflowBuilder.field} onChange={(e)=> setInflowBuilder(prev=>({ ...prev, field: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200">
                                <option value="date">Date</option>
                                <option value="reflecting_month">Reflecting Month</option>
                                <option value="source">Source</option>
                                <option value="amount_rs">Amount (Rs)</option>
                                <option value="amount_usd">Amount (USD)</option>
                                <option value="bank">Bank</option>
                                <option value="reflecting_bank">Reflecting Bank</option>
                                <option value="request_inward">Inward Remittance</option>
                              </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Operator</label>
                        <select value={inflowBuilder.op} onChange={(e)=> setInflowBuilder(prev=>({ ...prev, op: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200">
                          {(inflowBuilder.field === 'source' || inflowBuilder.field === 'bank' || inflowBuilder.field === 'reflecting_bank') && (<>
                            <option value="is">is</option>
                            <option value="is_not">is not</option>
                            <option value="is_any">is any of</option>
                          </>)}
                          {(inflowBuilder.field === 'date') && (<option value="is">between</option>)}
                          {(inflowBuilder.field === 'reflecting_month') && (<option value="is">is</option>)}
                          {(inflowBuilder.field === 'amount_rs' || inflowBuilder.field === 'amount_usd') && (<option value="is">range</option>)}
                          {(inflowBuilder.field === 'request_inward') && (<option value="is">is</option>)}
                        </select>
                      </div>
                      {(inflowBuilder.field === 'date') && (
                        <>
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">Start</label>
                            <input type="date" value={inflowBuilder.start} onChange={(e)=> setInflowBuilder(prev=>({ ...prev, start: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200" />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">End</label>
                            <input type="date" value={inflowBuilder.end} onChange={(e)=> setInflowBuilder(prev=>({ ...prev, end: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200" />
                          </div>
                                  </>
                                )}
                      {(inflowBuilder.field === 'reflecting_month') && (
                        <>
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">Financial Year</label>
                            <select 
                              value={inflowBuilder.fyYear} 
                              onChange={(e)=> {
                                const selectedYear = e.target.value;
                                setInflowBuilder(prev=>({ ...prev, fyYear: selectedYear, reflectingMonth: '' }));
                              }} 
                              className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200"
                            >
                              <option value="">Select FY Year</option>
                              {FINANCIAL_YEARS.map(fy => (
                                <option key={fy.value} value={fy.value}>{fy.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">Reflecting Month</label>
                            <select 
                              value={inflowBuilder.reflectingMonth} 
                              onChange={(e)=> setInflowBuilder(prev=>({ ...prev, reflectingMonth: e.target.value }))} 
                              className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200"
                              disabled={!inflowBuilder.fyYear}
                            >
                              <option value="">Select Month</option>
                              {inflowBuilder.fyYear && getFinancialYearMonthsFor(parseInt(inflowBuilder.fyYear)).map(month => (
                                <option key={month.value} value={month.value}>{month.label}</option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}
                      {(inflowBuilder.field === 'source') && (
                        <div className="col-span-2">
                          <label className="block text-sm text-gray-700 mb-1">Value</label>
                          {inflowBuilder.op === 'is_any' ? (
                            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border border-gray-200 rounded-xl bg-gray-50">
                              {inflowSources.map(s => (
                                <label key={s.id} className="flex items-center gap-2 text-sm text-gray-700">
                                  <input type="checkbox" checked={Array.isArray(inflowBuilder.values) && inflowBuilder.values.includes(s.name)} onChange={(e)=> {
                                    setInflowBuilder(prev => {
                                      const set = new Set(prev.values || []);
                                      if (e.target.checked) set.add(s.name); else set.delete(s.name);
                                      return { ...prev, values: Array.from(set) };
                                    });
                                  }} />
                                  <span>{s.name}</span>
                                </label>
                              ))}
                            </div>
                          ) : (
                            <select value={inflowBuilder.value} onChange={(e)=> setInflowBuilder(prev=>({ ...prev, value: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200">
                              <option value="">Select source</option>
                              {inflowSources.map(s => (<option key={s.id} value={s.name}>{s.name}</option>))}
                              </select>
                          )}
                                  </div>
                      )}
                      {(inflowBuilder.field === 'bank') && (
                        <div className="col-span-2">
                          <label className="block text-sm text-gray-700 mb-1">Value</label>
                          {inflowBuilder.op === 'is_any' ? (
                            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border border-gray-200 rounded-xl bg-gray-50">
                              {bankAccounts.map(b => (
                                <label key={b.id} className="flex items-center gap-2 text-sm text-gray-700">
                                  <input type="checkbox" checked={Array.isArray(inflowBuilder.values) && inflowBuilder.values.includes(b.name)} onChange={(e)=> {
                                    setInflowBuilder(prev => {
                                      const set = new Set(prev.values || []);
                                      if (e.target.checked) set.add(b.name); else set.delete(b.name);
                                      return { ...prev, values: Array.from(set) };
                                    });
                                  }} />
                                  <span>{b.name}</span>
                                </label>
                              ))}
                            </div>
                          ) : (
                            <select value={inflowBuilder.value} onChange={(e)=> setInflowBuilder(prev=>({ ...prev, value: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200">
                              <option value="">Select bank</option>
                              {bankAccounts.map(b => (<option key={b.id} value={b.name}>{b.name}</option>))}
                                      </select>
                                    )}
                        </div>
                      )}
                      {(inflowBuilder.field === 'reflecting_bank') && (
                        <div className="col-span-2">
                          <label className="block text-sm text-gray-700 mb-1">Value</label>
                          {inflowBuilder.op === 'is_any' ? (
                            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border border-gray-200 rounded-xl bg-gray-50">
                              {bankAccounts.map(b => (
                                <label key={b.id} className="flex items-center gap-2 text-sm text-gray-700">
                                  <input type="checkbox" checked={Array.isArray(inflowBuilder.values) && inflowBuilder.values.includes(b.name)} onChange={(e)=> {
                                    setInflowBuilder(prev => {
                                      const set = new Set(prev.values || []);
                                      if (e.target.checked) set.add(b.name); else set.delete(b.name);
                                      return { ...prev, values: Array.from(set) };
                                    });
                                  }} />
                                  <span>{b.name}</span>
                                </label>
                              ))}
                                  </div>
                          ) : (
                            <select value={inflowBuilder.value} onChange={(e)=> setInflowBuilder(prev=>({ ...prev, value: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200">
                              <option value="">Select reflecting bank</option>
                              {bankAccounts.map(b => (<option key={b.id} value={b.name}>{b.name}</option>))}
                                      </select>
                                    )}
                        </div>
                      )}
                      {(inflowBuilder.field === 'amount_rs' || inflowBuilder.field === 'amount_usd') && (
                        <>
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">Min</label>
                            <input type="number" value={inflowBuilder.min} onChange={(e)=> setInflowBuilder(prev=>({ ...prev, min: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200" />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">Max</label>
                            <input type="number" value={inflowBuilder.max} onChange={(e)=> setInflowBuilder(prev=>({ ...prev, max: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200" />
                          </div>
                                  </>
                                )}
                      {(inflowBuilder.field === 'request_inward') && (
                        <div className="col-span-2">
                          <label className="block text-sm text-gray-700 mb-1">Value</label>
                          <select value={inflowBuilder.yesno} onChange={(e)=> setInflowBuilder(prev=>({ ...prev, yesno: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200">
                            <option value="true">Yes</option>
                            <option value="false">No</option>
                              </select>
                                  </div>
                      )}
                                  </div>
                    <div className="flex justify-between items-center">
                      {editingInflowRule !== null ? (
                        <>
                          <button onClick={saveInflowRule} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg">Save Changes</button>
                          <button onClick={cancelEditInflowRule} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm">Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={addInflowRule} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Add Filter</button>
                          <button onClick={()=> { setInflowRules([]); setInflowBuilder({ field: 'date', op: 'is', start: '', end: '', value: '', values: [], min: '', max: '', yesno: 'true' }); }} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm">Clear All</button>
                                  </>
                                )}
                              </div>
                    <div className="mt-4 space-y-2">
                      {inflowRules.length === 0 ? (
                        <div className="text-sm text-gray-500">No inflow filters added.</div>
                      ) : inflowRules.map((r, idx)=> (
                        <div key={idx} className={`flex justify-between items-center text-sm rounded-lg px-3 py-2 ${editingInflowRule === idx ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50 border border-gray-200'}`}>
                          <span className={editingInflowRule === idx ? 'text-blue-800 font-medium' : ''}>{describeInflowRule(r)}</span>
                          <div className="flex gap-2">
                            {editingInflowRule === idx ? (
                              <span className="px-2 py-1 text-blue-600 font-medium">Editing...</span>
                            ) : (
                              <button onClick={()=> editInflowRule(idx)} className="px-2 py-1 text-blue-600 hover:underline">Edit</button>
                            )}
                            <button onClick={()=> removeInflowRule(idx)} className="px-2 py-1 text-red-600 hover:underline">Remove</button>
                          </div>
                            </div>
                          ))}
                        </div>
                    </div>

                    {/* Outflow Builder */}
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Outflow Filters</h3>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Field</label>
                        <select value={outflowBuilder.field} onChange={(e)=> setOutflowBuilder(prev=>({ ...prev, field: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200">
                                <option value="date">Date</option>
                                <option value="reflecting_month">Reflecting Month</option>
                                <option value="expense">Expense</option>
                                <option value="amount_rs">Amount (Rs)</option>
                                <option value="sub_category">Sub-Category</option>
                                <option value="deducted_from">Deducted From</option>
                              </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Operator</label>
                        <select value={outflowBuilder.op} onChange={(e)=> setOutflowBuilder(prev=>({ ...prev, op: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200">
                          {(outflowBuilder.field === 'sub_category' || outflowBuilder.field === 'deducted_from') && (<>
                            <option value="is">is</option>
                            <option value="is_not">is not</option>
                            <option value="is_any">is any of</option>
                          </>)}
                          {(outflowBuilder.field === 'date') && (<option value="is">between</option>)}
                          {(outflowBuilder.field === 'reflecting_month') && (<option value="is">is</option>)}
                          {(outflowBuilder.field === 'amount_rs') && (<option value="is">range</option>)}
                          {(outflowBuilder.field === 'expense') && (<option value="contains">contains</option>)}
                        </select>
                      </div>
                      {(outflowBuilder.field === 'date') && (
                        <>
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">Start</label>
                            <input type="date" value={outflowBuilder.start} onChange={(e)=> setOutflowBuilder(prev=>({ ...prev, start: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200" />
                          </div>
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">End</label>
                            <input type="date" value={outflowBuilder.end} onChange={(e)=> setOutflowBuilder(prev=>({ ...prev, end: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200" />
                          </div>
                                  </>
                                )}
                      {(outflowBuilder.field === 'reflecting_month') && (
                        <>
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">Financial Year</label>
                            <select 
                              value={outflowBuilder.fyYear} 
                              onChange={(e)=> {
                                const selectedYear = e.target.value;
                                setOutflowBuilder(prev=>({ ...prev, fyYear: selectedYear, reflectingMonth: '' }));
                              }} 
                              className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200"
                            >
                              <option value="">Select FY Year</option>
                              {FINANCIAL_YEARS.map(fy => (
                                <option key={fy.value} value={fy.value}>{fy.label}</option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">Reflecting Month</label>
                            <select 
                              value={outflowBuilder.reflectingMonth} 
                              onChange={(e)=> setOutflowBuilder(prev=>({ ...prev, reflectingMonth: e.target.value }))} 
                              className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200"
                              disabled={!outflowBuilder.fyYear}
                            >
                              <option value="">Select Month</option>
                              {outflowBuilder.fyYear && getFinancialYearMonthsFor(parseInt(outflowBuilder.fyYear)).map(month => (
                                <option key={month.value} value={month.value}>{month.label}</option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}
                      {(outflowBuilder.field === 'expense') && (
                        <div className="col-span-2">
                          <label className="block text-sm text-gray-700 mb-1">Text</label>
                          <input type="text" value={outflowBuilder.value} onChange={(e)=> setOutflowBuilder(prev=>({ ...prev, value: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200" />
                                  </div>
                      )}
                      {(outflowBuilder.field === 'sub_category') && (
                        <div className="col-span-2">
                          <label className="block text-sm text-gray-700 mb-1">Value</label>
                          {outflowBuilder.op === 'is_any' ? (
                            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border border-gray-200 rounded-xl bg-gray-50">
                              {outflowSubcategories.map(s => (
                                <label key={s.id} className="flex items-center gap-2 text-sm text-gray-700">
                                  <input type="checkbox" checked={Array.isArray(outflowBuilder.values) && outflowBuilder.values.includes(s.name)} onChange={(e)=> {
                                    setOutflowBuilder(prev => {
                                      const set = new Set(prev.values || []);
                                      if (e.target.checked) set.add(s.name); else set.delete(s.name);
                                      return { ...prev, values: Array.from(set) };
                                    });
                                  }} />
                                  <span>{s.name}</span>
                                </label>
                              ))}
                                  </div>
                          ) : (
                            <select value={outflowBuilder.value} onChange={(e)=> setOutflowBuilder(prev=>({ ...prev, value: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200">
                              <option value="">Select subcategory</option>
                              {outflowSubcategories.map(s => (<option key={s.id} value={s.name}>{s.name}</option>))}
                                      </select>
                          )}
                        </div>
                      )}
                      {(outflowBuilder.field === 'deducted_from') && (
                        <div className="col-span-2">
                          <label className="block text-sm text-gray-700 mb-1">Value</label>
                          {outflowBuilder.op === 'is_any' ? (
                            <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border border-gray-200 rounded-xl bg-gray-50">
                              {bankAccounts.map(b => (
                                <label key={b.id} className="flex items-center gap-2 text-sm text-gray-700">
                                  <input type="checkbox" checked={Array.isArray(outflowBuilder.values) && outflowBuilder.values.includes(b.name)} onChange={(e)=> {
                                    setOutflowBuilder(prev => {
                                      const set = new Set(prev.values || []);
                                      if (e.target.checked) set.add(b.name); else set.delete(b.name);
                                      return { ...prev, values: Array.from(set) };
                                    });
                                  }} />
                                  <span>{b.name}</span>
                                </label>
                              ))}
                            </div>
                                    ) : (
                            <select value={outflowBuilder.value} onChange={(e)=> setOutflowBuilder(prev=>({ ...prev, value: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200">
                              <option value="">Select account</option>
                              {bankAccounts.map(b => (<option key={b.id} value={b.name}>{b.name}</option>))}
                                      </select>
                                    )}
                        </div>
                                )}
                      {(outflowBuilder.field === 'amount_rs') && (
                        <>
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">Min</label>
                            <input type="number" value={outflowBuilder.min} onChange={(e)=> setOutflowBuilder(prev=>({ ...prev, min: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200" />
                              </div>
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">Max</label>
                            <input type="number" value={outflowBuilder.max} onChange={(e)=> setOutflowBuilder(prev=>({ ...prev, max: e.target.value }))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200" />
                            </div>
                                  </>
                                )}
                        </div>
                    <div className="flex justify-between items-center">
                      {editingOutflowRule !== null ? (
                        <>
                          <button onClick={saveOutflowRule} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg">Save Changes</button>
                          <button onClick={cancelEditOutflowRule} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm">Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={addOutflowRule} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg">Add Filter</button>
                          <button onClick={()=> { setOutflowRules([]); setOutflowBuilder({ field: 'date', op: 'is', start: '', end: '', value: '', values: [], min: '', max: '' }); }} className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm">Clear All</button>
                        </>
                      )}
                    </div>
                    <div className="mt-4 space-y-2">
                      {outflowRules.length === 0 ? (
                        <div className="text-sm text-gray-500">No outflow filters added.</div>
                      ) : outflowRules.map((r, idx)=> (
                        <div key={idx} className={`flex justify-between items-center text-sm rounded-lg px-3 py-2 ${editingOutflowRule === idx ? 'bg-blue-50 border-2 border-blue-200' : 'bg-gray-50 border border-gray-200'}`}>
                          <span className={editingOutflowRule === idx ? 'text-blue-800 font-medium' : ''}>{describeOutflowRule(r)}</span>
                          <div className="flex gap-2">
                            {editingOutflowRule === idx ? (
                              <span className="px-2 py-1 text-blue-600 font-medium">Editing...</span>
                            ) : (
                              <button onClick={()=> editOutflowRule(idx)} className="px-2 py-1 text-blue-600 hover:underline">Edit</button>
                            )}
                            <button onClick={()=> removeOutflowRule(idx)} className="px-2 py-1 text-red-600 hover:underline">Remove</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                    {/* Dual Result Tables */}
                <div className={'grid grid-cols-1 lg:grid-cols-2 gap-6'}>
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-semibold text-gray-900">Inflow Results</h3>
                      {true && (
                        <div className="text-right">
                          <div className="text-2xl font-bold text-green-600">{Number(inflowTotalRs || 0).toLocaleString()}</div>
                          <div className="text-lg font-semibold text-blue-600">${Number(inflowTotalUsd || 0).toLocaleString()}</div>
                          <div className="text-sm text-gray-500">
                            {inflowTable.total} records
                          </div>
                        </div>
                      )}
                    </div>
                    {insightsLoading ? (
                      <div className="h-64 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
                    ) : (
                    <>
                    <div className="h-96 overflow-auto">
                    <DataGrid
                      style={{ height: '100%', width: '100%' }}
                      autoHeight={false}
                        rows={inflowTable.items || []}
                      columns={[
                          { field: 'date', headerName: 'Date', width: 110 },
                          { field: 'reflecting_month', headerName: 'Reflecting Month', width: 140, renderCell: (p) => formatReflectingMonth(p.value) },
                          { field: 'source', headerName: 'Source', width: 140 },
                          { field: 'bank', headerName: 'Bank', width: 120 },
                          { field: 'amount_rs', headerName: 'Amount ()', width: 140, renderCell: (p) => `${(p.value || 0).toLocaleString()}` },
                          { field: 'amount_usd', headerName: 'Amount ($)', width: 120 },
                          { field: 'request_inward', headerName: 'Inward', width: 90, renderCell: (p) => p.value ? 'Yes' : 'No' },
                        ]}
                        rowCount={inflowTable.total}
                        page={inflowTable.page}
                        pageSize={inflowTable.pageSize}
                        onPageChange={(page)=> setInflowTable(prev => ({ ...prev, page }))}
                        onPageSizeChange={(pageSize)=> setInflowTable(prev => ({ ...prev, pageSize, page: 0 }))}
                        paginationMode="server"
                      sortingMode="server"
                        onSortModelChange={(model)=> {
                          if (model.length > 0) setInflowTable(prev => ({ ...prev, sortField: model[0].field, sortDir: model[0].sort || 'asc', page: 0 }));
                        }}
                      slots={{ toolbar: GridToolbar }}
                    />
                  </div>
                    </>
                    )}
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-semibold text-gray-900">Outflow Results</h3>
                      {true && (
                        <div className="text-right">
                          <div className="text-2xl font-bold text-red-600">{Number(outflowTotalRs || 0).toLocaleString()}</div>
                          <div className="text-sm font-semibold text-amber-600 mt-1">Withdrawals: {Number(outflowWithdrawalsRs || 0).toLocaleString()}</div>
                          <div className="text-sm text-gray-500">
                            {outflowTable.total} records
                          </div>
                        </div>
                      )}
                    </div>
                    {insightsLoading ? (
                      <div className="h-64 flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
                    ) : (
                    <>
                    <div className="h-96 overflow-auto">
                    <DataGrid
                      style={{ height: '100%', width: '100%' }}
                      autoHeight={false}
                        rows={outflowTable.items || []}
                      columns={[
                          { field: 'date', headerName: 'Date', width: 110 },
                          { field: 'reflecting_month', headerName: 'Reflecting Month', width: 140, renderCell: (p) => formatReflectingMonth(p.value) },
                          { field: 'expense', headerName: 'Expense', width: 160 },
                          { field: 'sub_category', headerName: 'Subcategory', width: 140 },
                          { field: 'accounting_treatment', headerName: 'Type', width: 140, renderCell: (p) => (p.value === OUTFLOW_TREATMENT_PROFIT_WITHDRAWAL ? 'Withdrawal' : 'Expense') },
                          { field: 'deducted_from', headerName: 'Deducted From', width: 140 },
                          { field: 'amount_rs', headerName: 'Amount ()', width: 140, renderCell: (p) => `${(p.value || 0).toLocaleString()}` },
                        ]}
                        rowCount={outflowTable.total}
                        page={outflowTable.page}
                        pageSize={outflowTable.pageSize}
                        onPageChange={(page)=> setOutflowTable(prev => ({ ...prev, page }))}
                        onPageSizeChange={(pageSize)=> setOutflowTable(prev => ({ ...prev, pageSize, page: 0 }))}
                        paginationMode="server"
                      sortingMode="server"
                        onSortModelChange={(model)=> {
                          if (model.length > 0) setOutflowTable(prev => ({ ...prev, sortField: model[0].field, sortDir: model[0].sort || 'asc', page: 0 }));
                        }}
                      slots={{ toolbar: GridToolbar }}
                    />
                    </div>
                    </>
                    )}
                  </div>
                    </div>
                  </div>
                </div>

                {/* Analytics (Inflow) */}
                <div className={'bg-white rounded-2xl p-6 shadow-sm border border-gray-100'}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">Inflow Analytics</h3>
                    <select value={inflowGroupBy} onChange={(e)=> setInflowGroupBy(e.target.value)} className="px-3 py-2 rounded-xl bg-gray-50 border border-gray-200">
                      <option value="month">Month</option>
                      <option value="source">Source</option>
                      <option value="bank">Bank</option>
                      <option value="request_inward">Request Inward</option>
                      <option value="invoice">Invoice</option>
                    </select>
                  </div>
                  <div className="h-96 overflow-hidden">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={inflowChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={(inflowChartData?.[0]?.month ? 'month' : 'key') || 'key'} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="inflow" fill="#0ea5e9" name="Inflow" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Analytics (Outflow) */}
                <div className={'bg-white rounded-2xl p-6 shadow-sm border border-gray-100'}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">Outflow Analytics</h3>
                    <select value={outflowGroupBy} onChange={(e)=> setOutflowGroupBy(e.target.value)} className="px-3 py-2 rounded-xl bg-gray-50 border border-gray-200">
                      <option value="month">Month</option>
                      <option value="sub_category">Subcategory</option>
                      <option value="category">Category</option>
                      <option value="deducted_from">Deducted From</option>
                      <option value="mode">Mode</option>
                      <option value="vendor">Vendor</option>
                      <option value="invoice">Invoice</option>
                    </select>
                  </div>
                  <div className="h-96 overflow-hidden">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={outflowChartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey={(outflowChartData?.[0]?.month ? 'month' : 'key') || 'key'} />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="outflow" fill="#ef4444" name="Outflow" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Dashboard preview removed per requirement */}

                {/* No saved insights or dashboard actions here per requirements */}
                {/* Saved Insights */}
                {savedInsights.length > 0 && (
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <InsightIcon className="h-5 w-5 text-yellow-500" />
                      Saved Insights
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {savedInsights.map((insight) => (
                        <div key={insight.id} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                          <h4 className="font-semibold text-gray-900 mb-2">{insight.name}</h4>
                          <p className="text-sm text-gray-500 mb-3">
                            Created: {new Date(insight.createdAt).toLocaleDateString()}
                          </p>
                          <div className="flex gap-2">
                            <select
                              onChange={(e) => addInsightToDashboard(insight.id, e.target.value)}
                              className="flex-1 px-2 py-1 rounded-lg bg-gray-50 text-gray-900 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                              defaultValue=""
                            >
                              <option value="" disabled>Add to Dashboard</option>
                              {customDashboards.map((dashboard) => (
                                <option key={dashboard.id} value={dashboard.id}>{dashboard.name}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                  </div>
                )}
                {/* Combined Dashboard Canvas: left rail (reports) + right (cards) */}
                {nav === 'dashboard' && (
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-3">
                      <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 sticky top-24">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-semibold text-gray-900">My reports</h3>
                          <button onClick={() => setShowReportTypeSelection(true)} className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700">New</button>
                        </div>
                        <div className="relative mb-3">
                          <input value={reportSearch} onChange={(e)=> setReportSearch(e.target.value)} placeholder="Search by name only" className="w-full px-3 py-2 rounded-lg bg-gray-50 border border-gray-200 text-sm" />
                        </div>
                        <div className="space-y-1 max-h-[50vh] overflow-auto pr-1">
                          {filteredReports.length === 0 ? (
                            <div className="text-xs text-gray-500 py-6 text-center">No reports yet</div>
                          ) : filteredReports.map((r) => (
                            <button key={r.id} onClick={() => loadReport(r)} className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-gray-50 border border-transparent hover:border-gray-200 flex items-center justify-between">
                              <span className="truncate text-gray-800">{r.name}</span>
                              <span className="text-xs text-gray-400 capitalize">{r.type}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="lg:col-span-9">
                      {customDashboards.find(d => d.id === 'default')?.insights.length > 0 && (
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <InsightIcon className="h-5 w-5 text-purple-500" />
                            My dashboard
                    </h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {customDashboards.find(d => d.id === 'default')?.insights.map((insight, index) => (
                        <div key={index} className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
                                <div className="flex items-start justify-between mb-3">
                                  <h4 className="font-semibold text-gray-900">{insight.name || 'Saved Card'}</h4>
                                  <button onClick={() => removeCardFromDashboard('default', insight.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" title="Remove">
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                                {insight.kind === 'dual_tables' ? (
                                  <div className="grid grid-cols-1 gap-4">
                                    <div>
                                      <h5 className="font-medium text-gray-800 mb-2">Inflow</h5>
                                      <SmallDualTable kind="inflow" card={insight} />
                                    </div>
                                    <div>
                                      <h5 className="font-medium text-gray-800 mb-2">Outflow</h5>
                                      <SmallDualTable kind="outflow" card={insight} />
                                    </div>
                                  </div>
                                ) : insight.kind === 'report' ? (
                                  <div className="space-y-4">
                                    {insight.reportType === 'combined' || insight.reportType === 'inflow' ? (
                                      <div>
                                        <h5 className="font-medium text-gray-800 mb-2">Inflow</h5>
                                        <SmallDualTable kind="inflow" card={insight} />
                                      </div>
                                    ) : null}
                                    {insight.reportType === 'combined' || insight.reportType === 'outflow' ? (
                                      <div>
                                        <h5 className="font-medium text-gray-800 mb-2">Outflow</h5>
                                        <SmallDualTable kind="outflow" card={insight} />
                                      </div>
                                    ) : null}
                                  </div>
                                ) : (
                                  <div className="text-gray-500 text-sm">Legacy chart card</div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {customDashboards.find(d => d.id === 'default')?.insights.length === 0 && (
                        <div className="bg-white rounded-2xl p-10 shadow-sm border border-dashed border-gray-200 text-center">
                          <h3 className="text-lg font-semibold text-gray-900">Your dashboard is empty</h3>
                          <p className="text-sm text-gray-500 mt-1">Use Insights to create a widget or save a report then add it here.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {false && nav === 'dashboard' && customDashboards.find(d => d.id === activeDashboard)?.insights.length === 0 && (
                  <div className="bg-white rounded-2xl p-10 shadow-sm border border-dashed border-gray-200 text-center">
                    <h3 className="text-lg font-semibold text-gray-900">This dashboard is empty</h3>
                    <p className="text-sm text-gray-500 mt-1">Create widgets from Insights or add a saved report.</p>
                    <div className="mt-4 flex items-center justify-center gap-3">
                      <button onClick={()=> setNav('insights')} className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">Go to Insights</button>
                    </div>
                  </div>
                )}
                  </>
                )}
              </div>
            )}
            
            {/* Reports Section */}
            {nav === "reports" && (
              <div className="space-y-6">
                {!user?.email ? (
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="text-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading user authentication...</p>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Reports Header */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                            <DocumentTextIcon className="h-6 w-6 text-blue-500" />
                            Reports
                          </h2>
                          <p className="text-gray-600 mt-2">Manage your saved reports and insights</p>
                        </div>
                        <button
                          onClick={() => setShowReportTypeSelection(true)}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                        >
                          Create Report
                        </button>
                      </div>
                      
                      {/* Search and Filter Controls */}
                      <div className="flex gap-4">
                        <div className="flex-1">
                          <input
                            type="text"
                            placeholder="Search by name only"
                            value={reportSearch}
                            onChange={(e) => setReportSearch(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Reports Workspace removed per requirements */}

                    {/* Reports List */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {filteredReports.length} of {reports.length} reports
                        </h3>
                        {reportSearch && (
                          <button
                            onClick={() => {
                              setReportSearch('');
                            }}
                            className="text-sm text-blue-600 hover:text-blue-700"
                          >
                            Clear filters
                          </button>
                        )}
                              </div>
                      
                      {filteredReports.length === 0 ? (
                        <div className="text-center py-12">
                          <DocumentTextIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                          {reports.length === 0 ? (
                            <>
                              <h3 className="text-lg font-medium text-gray-900 mb-2">No reports yet</h3>
                              <p className="text-gray-500 mb-6">Create your first report from the Insights section</p>
                              <button 
                                onClick={() => setNav('insights')}
                                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
                              >
                                Go to Insights
                              </button>
                            </>
                          ) : (
                            <>
                              <h3 className="text-lg font-medium text-gray-900 mb-2">No reports match your filters</h3>
                              <p className="text-gray-500 mb-6">Try adjusting your search</p>
                              <button
                                onClick={() => {
                                  setReportSearch('');
                                }}
                                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors"
                              >
                                Clear Filters
                              </button>
                            </>
                            )}
                          </div>
                      ) : (
                        <div className="flex flex-wrap gap-6 items-start">
                          {filteredReports.map((report) => (
                            <AdaptiveReportCard
                              key={report.id}
                              report={report}
                              timePeriod={timePeriod}
                              customStart={customStart}
                              customEnd={customEnd}
                              onDelete={handleDeleteReport}
                              onEdit={handleEditReportFilters}
                              onDuplicate={handleDuplicateReport}
                              onResize={handleResizeReport}
                              isPinned={report.tags?.includes('dashboard_card')}
                              onTogglePin={handleTogglePinReport}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}

              </div>
            )}
            {nav === "reminders" && (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                      <CalendarIcon className="h-6 w-6 text-blue-500" />
                      Reminders & Alerts
                    </h2>
                    <div className="flex items-center gap-2">
                      <button onClick={handleViewAllRecurringTransactions} className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium">View All Recurring Transactions</button>
                      <button onClick={() => { loadReminders(); loadLimitsUsage(); loadBankBalanceThisMonth(); }} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">Refresh</button>
                    </div>
                  </div>
                  {remindersLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-3 text-gray-600">Loading reminders...</span>
                    </div>
                  ) : (
                    <>
                      {/* KPI Summary: Bank Balance, Dues, Projected Balance */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        {/* Card 1: Bank Balance (This Month) */}
                        <div className="bg-white rounded-xl p-4 border border-gray-100">
                          <div className="text-sm text-gray-500 mb-1">Bank Balance (This Month)</div>
                          <div className="text-2xl font-bold text-gray-900" title="Closing balance for the current month">
                            {cbLoading ? (
                              <span className="text-gray-400">Loading...</span>
                            ) : (
                              <>{Number(bankBalanceThisMonth || 0).toLocaleString()}</>
                            )}
                          </div>
                        </div>

                        {/* Card 2: Dues (Due + Upcoming) */}
                        <div className="bg-white rounded-xl p-4 border border-gray-100">
                          <div className="text-sm text-gray-500 mb-1">Dues</div>
                          <div className="text-2xl font-bold text-gray-900" title="Sum of Due and Upcoming reminders">{Number(duesTotal || 0).toLocaleString()}</div>
                          <div className="flex gap-2 mt-2 text-xs">
                            <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-600">Due: {Number(dueRemindersTotal || 0).toLocaleString()}</span>
                            <span className="px-2 py-0.5 rounded-full bg-orange-50 text-orange-600">Upcoming: {Number(upcomingRemindersTotal || 0).toLocaleString()}</span>
                          </div>
                        </div>

                        {/* Card 3: Projected Balance */}
                        <div className="bg-white rounded-xl p-4 border border-gray-100">
                          <div className="text-sm text-gray-500 mb-1">Projected Balance</div>
                          <div className={`text-2xl font-bold ${projectedBalance >= 0 ? 'text-green-600' : 'text-red-600'}`} title="Bank Balance (This Month) minus Dues">
                            <div className="flex items-center gap-2">
                              <span>{Number(projectedBalance || 0).toLocaleString()}</span>
                              {projectedBalance < 0 && (
                                <ExclamationTriangleIcon className="h-5 w-5 text-red-500" title="Projected deficit after commitments" />
                              )}
                            </div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">Bank Balance - Dues</div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      <div className="bg-white rounded-xl p-4 border border-gray-100 flex flex-col max-h-96">
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="text-lg font-semibold text-gray-900">Due</h3>
                          <div className="text-right">
                            <span className="text-sm text-gray-500">Total Amount</span>
                            <div className="text-lg font-bold text-red-600">{dueRemindersTotal.toLocaleString()}</div>
                          </div>
                        </div>
                        <div className="flex-1 overflow-y-auto rounded-xl border border-gray-100">
                        <table className="min-w-full bg-white">
                          <thead>
                            <tr className="text-gray-600 text-sm">
                              <th className="px-4 py-2 text-left">Name</th>
                              <th className="px-4 py-2 text-left">Amount</th>
                              <th className="px-4 py-2 text-left">Due Date</th>
                              <th className="px-4 py-2 text-left">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {dueReminders.length === 0 ? (
                              <tr><td colSpan="4" className="text-center text-gray-500 py-6">No due reminders</td></tr>
                            ) : (
                              [...dueReminders].sort((a,b) => (a.next_due_date||'').localeCompare(b.next_due_date||'')).map((r) => (
                                <tr key={r.id} className="text-gray-700 text-sm border-t border-gray-100">
                                  <td className="px-4 py-2">{r.name}</td>
                                  <td className="px-4 py-2">{(r.amount_rs ?? 0).toLocaleString()}</td>
                                  <td className="px-4 py-2">{r.next_due_date}</td>
                                  <td className="px-4 py-2">
                                    <button onClick={() => markReminderPaid(r.id)} className="px-2 py-1 rounded text-xs text-white bg-green-600 hover:bg-green-700">Mark Paid</button>
                                    <button onClick={() => requestSkipReminder(r)} className="ml-2 px-2 py-1 rounded text-xs text-white bg-gray-500 hover:bg-gray-600">Skip</button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                        </div>
                      </div>
                      <div className="bg-white rounded-xl p-4 border border-gray-100 flex flex-col max-h-96">
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="text-lg font-semibold text-gray-900">Upcoming</h3>
                          <div className="text-right">
                            <span className="text-sm text-gray-500">Total Amount</span>
                            <div className="text-lg font-bold text-orange-600">{upcomingRemindersTotal.toLocaleString()}</div>
                          </div>
                        </div>
                        <div className="flex-1 overflow-y-auto rounded-xl border border-gray-100">
                        <table className="min-w-full bg-white">
                          <thead>
                            <tr className="text-gray-600 text-sm">
                              <th className="px-4 py-2 text-left">Name</th>
                              <th className="px-4 py-2 text-left">Amount</th>
                              <th className="px-4 py-2 text-left">Due Date</th>
                              <th className="px-4 py-2 text-left">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {upcomingReminders.length === 0 ? (
                              <tr><td colSpan="4" className="text-center text-gray-500 py-6">No upcoming reminders</td></tr>
                            ) : (
                              [...upcomingReminders].sort((a,b) => (a.next_due_date||'').localeCompare(b.next_due_date||'')).map((r) => (
                                <tr key={r.id} className="text-gray-700 text-sm border-t border-gray-100">
                                  <td className="px-4 py-2">{r.name}</td>
                                  <td className="px-4 py-2">{(r.amount_rs ?? 0).toLocaleString()}</td>
                                  <td className="px-4 py-2">{r.next_due_date}</td>
                                  <td className="px-4 py-2">
                                    <button onClick={() => markReminderPaid(r.id)} className="px-2 py-1 rounded text-xs text-white bg-green-600 hover:bg-green-700">Mark Paid</button>
                                    <button onClick={() => requestSkipReminder(r)} className="ml-2 px-2 py-1 rounded text-xs text-white bg-gray-500 hover:bg-gray-600">Skip</button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                        </div>
                      </div>
                    </div>

                    </>
                  )}
                </div>
                {/* Bank Limits Overview */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold text-gray-900">Bank Limits Overview</h2>
                    <div className="inline-flex rounded-xl border border-gray-200 overflow-hidden">
                      <button onClick={() => setLimitsUsageTab('yearly')} className={`px-3 py-1 text-sm ${limitsUsageTab === 'yearly' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Yearly</button>
                      <button onClick={() => setLimitsUsageTab('quarterly')} className={`px-3 py-1 text-sm ${limitsUsageTab === 'quarterly' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Quarterly</button>
                      <button onClick={() => setLimitsUsageTab('monthly')} className={`px-3 py-1 text-sm ${limitsUsageTab === 'monthly' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}>Monthly</button>
                    </div>
                  </div>
                  {limitsUsageLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-3 text-gray-600">Loading bank limits...</span>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full bg-white rounded-xl border border-gray-100">
                        <thead>
                          <tr className="text-gray-600 text-sm">
                            <th className="px-4 py-2 text-left">Bank</th>
                            <th className="px-4 py-2 text-left">Used</th>
                            <th className="px-4 py-2 text-left">Limit</th>
                            <th className="px-4 py-2 text-left">Remaining</th>
                            <th className="px-4 py-2 text-left">Progress</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(() => {
                            const rows = limitsUsageTab === 'monthly' ? limitsUsageMonthly : (limitsUsageTab === 'quarterly' ? limitsUsageQuarterly : limitsUsageYearly);
                            if (!rows || rows.length === 0) {
                              return (
                                <tr><td colSpan="5" className="text-center text-gray-500 py-6">No data</td></tr>
                              );
                            }
                            return rows.map((r, idx) => {
                              const limit = r.limit == null ? null : Number(r.limit);
                              const used = Number(r.used || 0);
                              const remaining = r.remaining == null ? null : Number(r.remaining);
                              const over = !!r.over_limit;
                              const percent = limit == null ? null : Math.min(100, Math.max(0, (used / (limit || 1)) * 100));
                              return (
                                <tr key={`${r.bank_name}-${idx}`} className="text-gray-700 text-sm border-t border-gray-100">
                                  <td className="px-4 py-2">{r.bank_name}</td>
                                  <td className="px-4 py-2">{used.toLocaleString()}</td>
                                  <td className="px-4 py-2">{limit == null ? 'Unlimited' : `${limit.toLocaleString()}`}</td>
                                  <td className={`px-4 py-2 ${over ? 'text-red-600 font-semibold' : ''}`}>{limit == null ? '-' : `${(remaining ?? 0).toLocaleString()}`}</td>
                                  <td className="px-4 py-2">
                                    {limit == null ? (
                                      <span className="text-gray-500">Unlimited</span>
                                    ) : (
                                      <div className="w-full bg-gray-100 rounded-full h-2.5">
                                        <div className={`h-2.5 rounded-full ${over ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${percent}%` }} />
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            });
                          })()}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
            {nav === "settings" && (
              <div className="space-y-6">
                {/* Settings Header and Tabs */}
                <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Cog6ToothIcon className="h-6 w-6 text-gray-600" />
                        Settings
                      </h2>
                      <p className="text-gray-500 text-sm">Manage users and finance settings</p>
                    </div>
                    {/* Header action removed per request; use Users & Access -> New User */}
                  </div>
                  <div className="mt-6">
                    <div className="inline-flex rounded-xl border border-gray-200 overflow-hidden">
                      <button
                        onClick={() => setSettingsTab('users')}
                        className={`px-4 py-2 text-sm font-medium ${settingsTab === 'users' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                      >
                        Users & Access
                      </button>
                      <button
                        onClick={() => setSettingsTab('financial')}
                        className={`px-4 py-2 text-sm font-medium ${settingsTab === 'financial' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                      >
                        Financial
                      </button>
                      <button
                        onClick={() => setSettingsTab('categories')}
                        className={`px-4 py-2 text-sm font-medium ${settingsTab === 'categories' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                      >
                        Categories
                      </button>
                      <button
                        onClick={() => setSettingsTab('automation')}
                        className={`px-4 py-2 text-sm font-medium ${settingsTab === 'automation' ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                      >
                        Automation
                      </button>
                    </div>
                  </div>
                </div>

                {/* Users & Access Content */}
                {settingsTab === 'users' && (
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-semibold text-gray-900">Users</h3>
                      <button
                        onClick={openAddUser}
                        className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2"
                      >
                        <PlusIcon className="h-4 w-4" /> New User
                      </button>
                    </div>
                    <table className="min-w-full bg-white rounded-xl border border-gray-100">
                      <thead>
                        <tr className="text-gray-600 text-sm">
                          <th className="px-4 py-2 text-left">Name</th>
                          <th className="px-4 py-2 text-left">Email</th>
                          <th className="px-4 py-2 text-left">Role</th>
                          <th className="px-4 py-2 text-left">OTP</th>
                          <th className="px-4 py-2 text-left">Login</th>
                          <th className="px-4 py-2 text-left">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usersList.length === 0 ? (
                          <tr>
                            <td colSpan="6" className="text-center text-gray-500 py-6">No users yet.</td>
                          </tr>
                        ) : (
                          usersList.map((u) => (
                            <tr key={u.id} className="text-gray-700 text-sm border-t border-gray-100">
                              <td className="px-4 py-2">{u.name}</td>
                              <td className="px-4 py-2">{u.email}</td>
                              <td className="px-4 py-2">
                                <span className="px-2 py-1 bg-gray-100 rounded text-xs">{u.role}</span>
                              </td>
                              <td className="px-4 py-2">
                                <button
                                  onClick={() => toggleUserOTP(u.id)}
                                  disabled={isITAdmin && u.role === 'Owner'}
                                  title={isITAdmin && u.role === 'Owner' ? 'IT Admin cannot modify Owner' : ''}
                                  className={`px-3 py-1 rounded-lg text-xs font-medium ${u.otp_enabled ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-700'} ${isITAdmin && u.role === 'Owner' ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  {u.otp_enabled ? 'Enabled' : 'Disabled'}
                                </button>
                              </td>
                              <td className="px-4 py-2">
                                <button
                                  onClick={() => toggleUserLogin(u.id)}
                                  disabled={!isOwner || u.role === 'Owner' || u.id === user?.id}
                                  title={
                                    !isOwner ? 'Only Owners can enable/disable logins' :
                                    u.role === 'Owner' ? 'Cannot disable login for Owners' :
                                    u.id === user?.id ? 'Cannot disable your own login' : ''
                                  }
                                  className={`px-3 py-1 rounded-lg text-xs font-medium ${
                                    u.login_enabled ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                                  } ${
                                    (!isOwner || u.role === 'Owner' || u.id === user?.id) ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-80'
                                  }`}
                                >
                                  {u.login_enabled ? 'Enabled' : 'Blocked'}
                                </button>
                              </td>
                              <td className="px-4 py-2">
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => openEditUser(u)}
                                    disabled={isITAdmin && u.role === 'Owner'}
                                    title={isITAdmin && u.role === 'Owner' ? 'IT Admin cannot edit Owner' : ''}
                                    className={`px-2 py-1 rounded text-xs text-white ${isITAdmin && u.role === 'Owner' ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => deleteUser(u.id)}
                                    disabled={isITAdmin && u.role === 'Owner'}
                                    title={isITAdmin && u.role === 'Owner' ? 'IT Admin cannot delete Owner' : ''}
                                    className={`px-2 py-1 rounded text-xs text-white ${isITAdmin && u.role === 'Owner' ? 'bg-red-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'}`}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                    
                    {/* Delete All Data Section */}
                    <div className="mt-8 bg-red-50 border border-red-200 rounded-2xl p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                        <div>
                          <h3 className="text-lg font-semibold text-red-800">Danger Zone</h3>
                          <p className="text-red-700 text-sm">
                            {user?.role === 'Owner' 
                              ? 'Delete all Owner data (shared data visible to all Owners)'
                              : 'Delete all your personal data'
                            }
                          </p>
                        </div>
                      </div>
                      
                      <div className="bg-white rounded-xl p-4 border border-red-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">Delete All Data</h4>
                            <p className="text-sm text-gray-600 mt-1">
                              This will permanently delete all {user?.role === 'Owner' ? 'Owner' : 'your'} inflows, outflows, recurring transactions, and reports.
                              <span className="text-red-600 font-medium"> This action cannot be undone.</span>
                            </p>
                          </div>
                          <button
                            onClick={handleDeleteAllData}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
                          >
                            Delete All Data
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Automation Settings: Backup & Export + Recurring Transactions */}
                {settingsTab === 'automation' && (
                  <div className="space-y-8">
                    {/* Owner-only access message for IT Admins */}
                    {user?.role === 'IT Admin' && (
                      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
                        <div className="flex items-center gap-3">
                          <ExclamationTriangleIcon className="h-6 w-6 text-amber-600" />
                          <div>
                            <h3 className="text-lg font-semibold text-amber-800">Owner Access Required</h3>
                            <p className="text-amber-700 mt-1">Backup and Export features are restricted to Owner accounts only.</p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Automated Backup - DISABLED FOR FUTURE UPDATES */}
                    {user?.role === 'Owner' && (
                      <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                        <div className="flex items-center justify-between mb-6">
                          <h3 className="text-xl font-semibold text-gray-900">Automated Backup</h3>
                          <span className="px-3 py-1 bg-amber-100 text-amber-800 text-sm font-medium rounded-full">
                            Future Updates
                          </span>
                        </div>
                        
                        <div className="text-center py-12">
                          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </div>
                          <h4 className="text-lg font-medium text-gray-900 mb-2">Backup Feature Temporarily Disabled</h4>
                          <p className="text-gray-600 max-w-md mx-auto">
                            The automated backup functionality is currently disabled and will be available in future updates. 
                            Your data remains secure and can be exported manually using the Export Data section below.
                          </p>
                        </div>

                        {/* ORIGINAL BACKUP UI - COMMENTED OUT FOR FUTURE UPDATES */}
                        {/* 
                        <form onSubmit={saveBackupSettings} className="space-y-6">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-gray-700">Enable Backup</span>
                              <button
                                type="button"
                                onClick={() => adminCanWrite && setBackupSettings(p => ({ ...p, enabled: !p.enabled }))}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${backupSettings.enabled ? 'bg-blue-600' : 'bg-gray-300'} ${!adminCanWrite ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                disabled={!adminCanWrite}
                                aria-pressed={!!backupSettings.enabled}
                              >
                                <span
                                  className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${backupSettings.enabled ? 'translate-x-5' : 'translate-x-1'}`}
                                />
                              </button>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Daily Backup Time (IST)</label>
                              <input type="time" value={backupSettings.time_ist || ''} onChange={(e)=>setBackupSettings((p)=>({...p, time_ist: e.target.value}))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" disabled={!adminCanWrite} />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">Retention (days)</label>
                              <input type="number" value={backupSettings.retention_days ?? 30} onChange={(e)=>setBackupSettings((p)=>({...p, retention_days: Number(e.target.value||0)}))} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" disabled={!adminCanWrite} />
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Email Recipients</label>
                            <div className="flex items-center gap-2 mb-3">
                              <input
                                type="email"
                                value={recipientInput}
                                onChange={(e)=>setRecipientInput(e.target.value)}
                                onKeyDown={(e)=>{ if(e.key==='Enter'){ e.preventDefault(); addRecipient(); } }}
                                placeholder="name@company.com"
                                className="flex-1 px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                disabled={!adminCanWrite}
                              />
                              <button type="button" onClick={addRecipient} disabled={!adminCanWrite || !recipientInput} className={`px-3 py-2 rounded-lg text-sm text-white ${(!adminCanWrite || !recipientInput) ? 'bg-blue-300' : 'bg-blue-600 hover:bg-blue-700'}`}>Add</button>
                            </div>
                            {recipientList.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {recipientList.map((email) => (
                                  <span key={email} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                                    {email}
                                    {adminCanWrite && (
                                      <button type="button" onClick={()=>removeRecipient(email)} className="ml-1 text-blue-500 hover:text-red-500">x</button>
                                    )}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                            <div className="text-sm text-gray-600">
                              {backupSettings.enabled ? (
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                                  Daily backup scheduled at {backupSettings.time_ist || '04:00'} IST
                                </span>
                              ) : (
                                <span className="flex items-center gap-2">
                                  <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                                  Automated backup disabled
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {adminCanWrite && (
                                <button type="button" onClick={runBackupNow} disabled={backupLoading} className={`px-4 py-2 rounded-lg text-white ${backupLoading ? 'bg-blue-300' : 'bg-blue-600 hover:bg-blue-700'}`}>Run Backup Now</button>
                              )}
                              <button type="submit" disabled={!adminCanWrite || backupLoading} className={`px-4 py-2 rounded-lg text-white ${(!adminCanWrite||backupLoading) ? 'bg-blue-300' : 'bg-blue-600 hover:bg-blue-700'}`}>Save Settings</button>
                            </div>
                          </div>
                        </form>

                        <div className="mt-6">
                          <h4 className="text-md font-semibold text-gray-900 mb-2">Backup History</h4>
                          <div className="overflow-x-auto border border-gray-100 rounded-xl">
                            <div className="max-h-64 overflow-y-auto">
                            <table className="min-w-full bg-white">
                              <thead>
                                <tr className="text-gray-600 text-sm">
                                  <th className="px-4 py-2 text-left">ID</th>
                                  <th className="px-4 py-2 text-left">Started (IST)</th>
                                  <th className="px-4 py-2 text-left">Finished (IST)</th>
                                  <th className="px-4 py-2 text-left">Status</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(backupHistory || []).length === 0 ? (
                                  <tr><td colSpan="4" className="text-center text-gray-500 py-6">No history</td></tr>
                                ) : (
                                  backupHistory.map((h) => (
                                    <tr key={h.id} className="text-gray-700 text-sm border-t border-gray-100">
                                      <td className="px-4 py-2">{h.id}</td>
                                      <td className="px-4 py-2">{formatISTTs(h.started_at)}</td>
                                      <td className="px-4 py-2">{formatISTTs(h.finished_at)}</td>
                                      <td className="px-4 py-2">
                                        {(() => {
                                          const status = h.status || (h.error ? 'Failed' : (h.finished_at ? 'Success' : 'Running'));
                                          const cls = status === 'Success' ? 'bg-green-50 text-green-700' : (status === 'Failed' ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700');
                                          return <span className={`px-2 py-1 rounded text-xs ${cls}`}>{status}</span>;
                                        })()}
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                            </div>
                          </div>
                        </div>
                        */}

                      </div>
                    )}

                    {/* Export Data - Only show for Owners */}
                    {user?.role === 'Owner' && (
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-6">
                        <h3 className="text-xl font-semibold text-gray-900">Export Data</h3>
                      </div>

                      <form onSubmit={downloadManualExport} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-700">Include Inflows</span>
                            <button type="button" onClick={() => toggleExport('includeInflows')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${exportForm.includeInflows ? 'bg-blue-600' : 'bg-gray-300'}`} aria-pressed={exportForm.includeInflows}>
                              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${exportForm.includeInflows ? 'translate-x-5' : 'translate-x-1'}`} />
                            </button>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-700">Include Outflows</span>
                            <button type="button" onClick={() => toggleExport('includeOutflows')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${exportForm.includeOutflows ? 'bg-blue-600' : 'bg-gray-300'}`} aria-pressed={exportForm.includeOutflows}>
                              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${exportForm.includeOutflows ? 'translate-x-5' : 'translate-x-1'}`} />
                            </button>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-700">Export All Data</span>
                            <button type="button" onClick={() => toggleExport('all')} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${exportForm.all ? 'bg-blue-600' : 'bg-gray-300'}`} aria-pressed={exportForm.all}>
                              <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${exportForm.all ? 'translate-x-5' : 'translate-x-1'}`} />
                            </button>
                          </div>
                        </div>
                        
                        {!exportForm.all && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                              <input type="date" name="fromDate" value={exportForm.fromDate} onChange={onExportField} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                              <input type="date" name="toDate" value={exportForm.toDate} onChange={onExportField} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            </div>
                          </div>
                        )}

                        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                          <div className="text-sm text-gray-600">
                            {exportForm.all ? (
                              <span>Exporting all historical data</span>
                            ) : (
                              <span>Exporting data for selected date range</span>
                            )}
                          </div>
                          <button type="submit" className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium">
                            Export
                          </button>
                        </div>
                      </form>
                    </div>
                    )}
                    {/* Recurring Transactions */}
                    <div ref={recurringTransactionsSectionRef} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-xl font-semibold text-gray-900">Recurring Transactions</h3>
                          <p className="text-sm text-gray-500 mt-1">Total Amount (Rs): <span className="font-semibold text-gray-900">{recurringItemsTotalRs.toLocaleString('en-IN')}</span></p>
                        </div>
                        {adminCanWrite && (
                          <button onClick={openAddRecurring} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                            <PlusIcon className="h-4 w-4" /> New Recurring
                          </button>
                        )}
                      </div>
                      {showRecurringForm && (
                        <form onSubmit={saveRecurring} className="bg-white p-6 rounded-xl border border-blue-100 mb-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            {/* Basic Info */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Name<span className="text-red-500">*</span></label>
                              <input type="text" name="name" value={recurringForm.name} onChange={handleRecurringChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Expense<span className="text-red-500">*</span></label>
                              <input type="text" name="expense" value={recurringForm.expense} onChange={handleRecurringChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Amount (Rs)<span className="text-red-500">*</span></label>
                              <input type="number" step="0.01" name="amount_rs" value={recurringForm.amount_rs} onChange={handleRecurringChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" required />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            {/* Categories - Sub-category first, then Category */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Sub-Category<span className="text-red-500">*</span></label>
                              <select name="sub_category" value={recurringForm.sub_category} onChange={handleRecurringChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" required>
                                <option value="">Select Sub-Category</option>
                                {outflowSubcategories.map(sub => (
                                  <option key={sub.id} value={sub.name}>{sub.name}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Category<span className="text-red-500">*</span></label>
                              <select name="category" value={recurringForm.category} onChange={handleRecurringChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" required disabled>
                                <option value="">Auto-selected</option>
                                {outflowCategories.map(cat => (
                                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Deducted From<span className="text-red-500">*</span></label>
                              <select name="deducted_from" value={recurringForm.deducted_from} onChange={handleRecurringChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" required>
                                <option value="">Select Bank Account</option>
                                {bankAccounts.filter(b => b.is_active).map(bank => (
                                  <option key={bank.id} value={bank.name}>{bank.name}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                            {/* Payment & Optional Info */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Mode<span className="text-red-500">*</span></label>
                              <select name="mode" value={recurringForm.mode} onChange={handleRecurringChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" required>
                                {MODES.map((m) => (
                                  <option key={m} value={m}>{m}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Nature</label>
                              <input type="text" name="nature" value={recurringForm.nature} onChange={handleRecurringChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Vendor</label>
                              <input type="text" name="vendor" value={recurringForm.vendor} onChange={handleRecurringChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Remark</label>
                              <input type="text" name="remark" value={recurringForm.remark} onChange={handleRecurringChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500" />
                            </div>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                            {/* Schedule Settings */}
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Schedule<span className="text-red-500">*</span></label>
                              <select name="schedule_type" value={recurringForm.schedule_type} onChange={handleRecurringChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500">
                                <option value="monthly">Monthly</option>
                                <option value="quarterly">Quarterly</option>
                                <option value="yearly_financial">Yearly (Apr 1)</option>
                              </select>
                            </div>
                            {recurringForm.schedule_type === 'monthly' && (
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Day of Month</label>
                                <select name="day_of_month" value={recurringForm.day_of_month} onChange={handleRecurringChange} className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500">
                                  {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                                    <option key={d} value={String(d)}>{d}</option>
                                  ))}
                                  <option value="-1">Last Day of Month</option>
                                </select>
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-6">
                              <input type="checkbox" name="is_active" checked={recurringForm.is_active} onChange={handleRecurringChange} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                              <span className="text-sm text-gray-700">Active</span>
                            </div>
                          </div>
                          
                          <div className="flex gap-3 pt-4 border-t border-gray-100">
                            <button type="submit" className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium">{editingRecurringId ? 'Save Changes' : 'Add Recurring'}</button>
                            <button type="button" onClick={() => setShowRecurringForm(false)} className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium">Cancel</button>
                          </div>
                        </form>
                      )}
                      <div className="overflow-x-auto">
                        <div className="max-h-80 overflow-y-auto rounded-xl border border-gray-100">
                        <table className="min-w-full bg-white">
                          <thead>
                            <tr className="text-gray-600 text-sm">
                              <th className="px-4 py-2 text-left">Name</th>
                              <th className="px-4 py-2 text-left">Amount (Rs)</th>
                              <th className="px-4 py-2 text-left">Schedule</th>
                              <th className="px-4 py-2 text-left">Next Due</th>
                              <th className="px-4 py-2 text-left">Active</th>
                              <th className="px-4 py-2 text-left">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {recurringItems.length === 0 ? (
                              <tr><td colSpan="6" className="text-center text-gray-500 py-6">No recurring items</td></tr>
                            ) : (
                              sortedRecurringItems.map((it, idx) => (
                                <tr ref={idx === sortedRecurringItems.length - 1 ? recurringLastRowRef : null} key={it.id} className="text-gray-700 text-sm border-t border-gray-100">
                                  <td className="px-4 py-2">{it.name}</td>
                                  <td className="px-4 py-2">{(it.amount_rs ?? 0).toLocaleString()}</td>
                                  <td className="px-4 py-2 capitalize">{it.schedule_type.replace('_', ' ') + (it.schedule_type === 'monthly' && it.day_of_month ? ` (day ${it.day_of_month})` : '')}</td>
                                  <td className="px-4 py-2">{it.next_due_date}</td>
                                  <td className="px-4 py-2">
                                    <span className={`px-2 py-1 rounded text-xs ${it.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{it.is_active ? 'Yes' : 'No'}</span>
                                  </td>
                                  <td className="px-4 py-2">
                                    <div className="flex gap-2">
                                      <button onClick={() => openEditRecurring(it)} className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs">Edit</button>
                                      <button onClick={() => archiveRecurring(it.id)} className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs">Archive</button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Financial Settings: Bank Accounts + Bank Limits */}
                {settingsTab === 'financial' && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                    {/* Bank Accounts */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col h-96">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-semibold text-gray-900">Bank Accounts</h3>
                        {adminCanWrite && (
                          <button onClick={openAddBank} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                            <PlusIcon className="h-4 w-4" /> New Bank
                          </button>
                        )}
                      </div>
                      <div className="flex-1 overflow-y-auto">
                      <table className="min-w-full bg-white rounded-xl border border-gray-100">
                        <thead>
                          <tr className="text-gray-600 text-sm">
                            <th className="px-4 py-2 text-left">Name</th>
                            <th className="px-4 py-2 text-left">Default Cash</th>
                            <th className="px-4 py-2 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bankAccounts.length === 0 ? (
                            <tr><td colSpan="3" className="text-center text-gray-500 py-6">No bank accounts</td></tr>
                          ) : bankAccounts.map((b, idx) => (
                            <tr
                              key={b.id}
                              className="text-gray-700 text-sm border-t border-gray-100"
                              draggable={adminCanWrite}
                              onDragStart={() => handleDragStart('bank', b.id)}
                              onDragOver={handleDragOver}
                              onDrop={() => handleDrop('bank', b.id)}
                            >
                              <td className="px-4 py-2">{b.name}</td>
                              <td className="px-4 py-2"><span className={`px-2 py-1 rounded text-xs ${b.is_default_cash ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-700'}`}>{b.is_default_cash ? 'Yes' : 'No'}</span></td>
                              <td className="px-4 py-2">
                                <div className="flex gap-2 items-center">
                                  <button title="Move up" onClick={() => moveBank(b.id, 'up')} disabled={!adminCanWrite || idx === 0} className={`px-2 py-1 rounded text-xs ${idx===0||!adminCanWrite ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>^</button>
                                  <button title="Move down" onClick={() => moveBank(b.id, 'down')} disabled={!adminCanWrite || idx === bankAccounts.length - 1} className={`px-2 py-1 rounded text-xs ${idx===bankAccounts.length-1||!adminCanWrite ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>v</button>
                                  <button onClick={() => openEditBank(b)} disabled={!adminCanWrite} className={`px-2 py-1 rounded text-xs text-white ${adminCanWrite ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'}`}>Edit</button>
                                  <button onClick={() => deleteBank(b)} disabled={!adminCanWrite || b.is_default_cash} title={b.is_default_cash ? 'Cannot archive default cash account' : ''} className={`px-2 py-1 rounded text-xs text-white ${(adminCanWrite && !b.is_default_cash) ? 'bg-red-600 hover:bg-red-700' : 'bg-red-300 cursor-not-allowed'}`}>Archive</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </div>

                    {/* Bank Limits */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col lg:col-span-2">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-semibold text-gray-900">Bank Limits</h3>
                        <span className="text-xs text-gray-500">Leave blank for unlimited</span>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white rounded-xl border border-gray-100">
                          <thead>
                            <tr className="text-gray-600 text-sm">
                              <th className="px-4 py-2 text-left">Bank</th>
                              <th className="px-4 py-2 text-left">Monthly ()</th>
                              <th className="px-4 py-2 text-left">Quarterly ()</th>
                              <th className="px-4 py-2 text-left">Yearly ()</th>
                              <th className="px-4 py-2 text-left">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(bankLimits || []).length === 0 ? (
                              <tr><td colSpan="5" className="text-center text-gray-500 py-6">No banks found.</td></tr>
                            ) : (
                              bankLimits.map((bl) => (
                                <tr key={bl.bank_account_id} className="text-gray-700 text-sm border-t border-gray-100">
                                  <td className="px-4 py-2">{bl.bank_name}</td>
                                  <td className="px-4 py-2">
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={bl.monthly_limit_rs ?? ''}
                                      onChange={(e) => changeBankLimitField(bl.bank_account_id, 'monthly_limit_rs', e.target.value)}
                                      className="w-40 px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      disabled={!adminCanWrite}
                                    />
                                  </td>
                                  <td className="px-4 py-2">
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={bl.quarterly_limit_rs ?? ''}
                                      onChange={(e) => changeBankLimitField(bl.bank_account_id, 'quarterly_limit_rs', e.target.value)}
                                      className="w-40 px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      disabled={!adminCanWrite}
                                    />
                                  </td>
                                  <td className="px-4 py-2">
                                    <input
                                      type="number"
                                      step="0.01"
                                      value={bl.yearly_limit_rs ?? ''}
                                      onChange={(e) => changeBankLimitField(bl.bank_account_id, 'yearly_limit_rs', e.target.value)}
                                      className="w-40 px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                      disabled={!adminCanWrite}
                                    />
                                  </td>
                                  <td className="px-4 py-2">
                                    <button
                                      onClick={() => saveBankLimit(bl.bank_account_id)}
                                      disabled={!adminCanWrite}
                                      className={`px-3 py-2 rounded-lg text-sm text-white ${adminCanWrite ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'}`}
                                    >
                                      Save
                                    </button>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
                {/* Categories Settings: Categories + Subcategories + Inflow Sources */}
                {settingsTab === 'categories' && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Categories */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col h-96">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-semibold text-gray-900">Outflow Categories</h3>
                        {adminCanWrite && (
                          <button onClick={openAddCategory} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                            <PlusIcon className="h-4 w-4" /> New Category
                          </button>
                        )}
                      </div>
                      <div className="flex-1 overflow-y-auto">
                      <table className="min-w-full bg-white rounded-xl border border-gray-100">
                        <thead>
                          <tr className="text-gray-600 text-sm">
                            <th className="px-4 py-2 text-left">Name</th>
                            <th className="px-4 py-2 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {outflowCategories.length === 0 ? (
                            <tr><td colSpan="2" className="text-center text-gray-500 py-6">No categories</td></tr>
                          ) : outflowCategories.map((c, idx) => (
                            <tr
                              key={c.id}
                              className="text-gray-700 text-sm border-t border-gray-100"
                              draggable={adminCanWrite}
                              onDragStart={() => handleDragStart('category', c.id)}
                              onDragOver={handleDragOver}
                              onDrop={() => handleDrop('category', c.id)}
                            >
                              <td className="px-4 py-2">{c.name}</td>
                              <td className="px-4 py-2">
                                <div className="flex gap-2 items-center">
                                  <button title="Move up" onClick={() => moveCategory(c.id, 'up')} disabled={!adminCanWrite || idx === 0} className={`px-2 py-1 rounded text-xs ${idx===0||!adminCanWrite ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>^</button>
                                  <button title="Move down" onClick={() => moveCategory(c.id, 'down')} disabled={!adminCanWrite || idx === outflowCategories.length - 1} className={`px-2 py-1 rounded text-xs ${idx===outflowCategories.length-1||!adminCanWrite ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>v</button>
                                  <button onClick={() => openEditCategory(c)} disabled={!adminCanWrite} className={`px-2 py-1 rounded text-xs text-white ${adminCanWrite ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'}`}>Edit</button>
                                  <button onClick={() => deleteCategory(c.id)} disabled={!adminCanWrite} className={`px-2 py-1 rounded text-xs text-white ${adminCanWrite ? 'bg-red-600 hover:bg-red-700' : 'bg-red-300 cursor-not-allowed'}`}>Archive</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </div>

                    {/* Subcategories */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col h-96">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-semibold text-gray-900">Outflow Subcategories</h3>
                        {adminCanWrite && (
                          <button onClick={openAddSubcategory} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                            <PlusIcon className="h-4 w-4" /> New Subcategory
                          </button>
                        )}
                      </div>
                      <div className="flex-1 overflow-y-auto">
                      <table className="min-w-full bg-white rounded-xl border border-gray-100">
                        <thead>
                          <tr className="text-gray-600 text-sm">
                            <th className="px-4 py-2 text-left">Name</th>
                            <th className="px-4 py-2 text-left">Category</th>
                            <th className="px-4 py-2 text-left">Treatment</th>
                            <th className="px-4 py-2 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {outflowSubcategories.length === 0 ? (
                            <tr><td colSpan="4" className="text-center text-gray-500 py-6">No subcategories</td></tr>
                          ) : outflowSubcategories.map((s, idx) => {
                            const cat = outflowCategories.find(c => c.id === s.category_id);
                            return (
                              <tr
                                key={s.id}
                                className="text-gray-700 text-sm border-t border-gray-100"
                                draggable={adminCanWrite}
                                onDragStart={() => handleDragStart('subcategory', s.id)}
                                onDragOver={handleDragOver}
                                onDrop={() => handleDrop('subcategory', s.id)}
                              >
                                <td className="px-4 py-2">{s.name}</td>
                                <td className="px-4 py-2">{cat?.name || '-'}</td>
                                <td className="px-4 py-2">
                                  <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${s.accounting_treatment === OUTFLOW_TREATMENT_PROFIT_WITHDRAWAL ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'}`}>
                                    {s.accounting_treatment === OUTFLOW_TREATMENT_PROFIT_WITHDRAWAL ? 'Profit Withdrawal' : 'Expense'}
                                  </span>
                                </td>
                                <td className="px-4 py-2">
                                  <div className="flex gap-2 items-center">
                                    <button title="Move up" onClick={() => moveSubcategory(s.id, 'up')} disabled={!adminCanWrite || idx === 0} className={`px-2 py-1 rounded text-xs ${idx===0||!adminCanWrite ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>^</button>
                                    <button title="Move down" onClick={() => moveSubcategory(s.id, 'down')} disabled={!adminCanWrite || idx === outflowSubcategories.length - 1} className={`px-2 py-1 rounded text-xs ${idx===outflowSubcategories.length-1||!adminCanWrite ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>v</button>
                                    <button onClick={() => openEditSubcategory(s)} disabled={!adminCanWrite} className={`px-2 py-1 rounded text-xs text-white ${adminCanWrite ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'}`}>Edit</button>
                                    <button onClick={() => syncSubcategoryTreatment(s)} disabled={!adminCanWrite} className={`px-2 py-1 rounded text-xs text-white ${adminCanWrite ? 'bg-amber-500 hover:bg-amber-600' : 'bg-amber-300 cursor-not-allowed'}`}>Sync History</button>
                                    <button onClick={() => deleteSubcategory(s.id)} disabled={!adminCanWrite} className={`px-2 py-1 rounded text-xs text-white ${adminCanWrite ? 'bg-red-600 hover:bg-red-700' : 'bg-red-300 cursor-not-allowed'}`}>Archive</button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      </div>
                    </div>

                    {/* Inflow Sources */}
                    <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col h-96">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-xl font-semibold text-gray-900">Inflow Sources</h3>
                        {adminCanWrite && (
                          <button onClick={openAddSource} className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium flex items-center gap-2">
                            <PlusIcon className="h-4 w-4" /> New Source
                          </button>
                        )}
                      </div>
                      <div className="flex-1 overflow-y-auto">
                      <table className="min-w-full bg-white rounded-xl border border-gray-100">
                        <thead>
                          <tr className="text-gray-600 text-sm">
                            <th className="px-4 py-2 text-left">Name</th>
                            <th className="px-4 py-2 text-left">Consider for Sales</th>
                            <th className="px-4 py-2 text-left">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inflowSources.length === 0 ? (
                            <tr><td colSpan="3" className="text-center text-gray-500 py-6">No sources</td></tr>
                          ) : inflowSources.map((s, idx) => (
                            <tr
                              key={s.id}
                              className="text-gray-700 text-sm border-t border-gray-100"
                              draggable={adminCanWrite}
                              onDragStart={() => handleDragStart('source', s.id)}
                              onDragOver={handleDragOver}
                              onDrop={() => handleDrop('source', s.id)}
                            >
                              <td className="px-4 py-2">{s.name}</td>
                              <td className="px-4 py-2">
                                <label className="inline-flex items-center gap-2 text-xs">
                                  <input
                                    type="checkbox"
                                    checked={!!s.consider_for_sales}
                                    onChange={() => toggleSourceSales(s)}
                                    disabled={!adminCanWrite}
                                  />
                                  <span className={`${s.consider_for_sales ? 'text-green-700' : 'text-gray-600'}`}>
                                    {s.consider_for_sales ? 'Included' : 'Excluded'}
                                  </span>
                                </label>
                              </td>
                              <td className="px-4 py-2">
                                <div className="flex gap-2 items-center">
                                  <button title="Move up" onClick={() => moveSource(s.id, 'up')} disabled={!adminCanWrite || idx === 0} className={`px-2 py-1 rounded text-xs ${idx===0||!adminCanWrite ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>^</button>
                                  <button title="Move down" onClick={() => moveSource(s.id, 'down')} disabled={!adminCanWrite || idx === inflowSources.length - 1} className={`px-2 py-1 rounded text-xs ${idx===inflowSources.length-1||!adminCanWrite ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}>v</button>
                                  <button onClick={() => openEditSource(s)} disabled={!adminCanWrite} className={`px-2 py-1 rounded text-xs text-white ${adminCanWrite ? 'bg-blue-600 hover:bg-blue-700' : 'bg-blue-300 cursor-not-allowed'}`}>Edit</button>
                                  <button onClick={() => deleteSource(s.id)} disabled={!adminCanWrite} className={`px-2 py-1 rounded text-xs text-white ${adminCanWrite ? 'bg-red-600 hover:bg-red-700' : 'bg-red-300 cursor-not-allowed'}`}>Archive</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </div>
                  </div>
                )}


                

                {/* Add/Edit User Modal */}
                {showUserModal && (
                  <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/30">
                    <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-900">{editingUserId ? 'Edit User' : 'Add User'}</h3>
                        <button onClick={() => setShowUserModal(false)} className="text-gray-500 hover:text-gray-700">
                          <XMarkIcon className="h-5 w-5" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 gap-4">
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">Name</label>
                          <input
                            type="text"
                            name="name"
                            value={userForm.name}
                            onChange={handleUserFormChange}
                            className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">Email</label>
                          <input
                            type="email"
                            name="email"
                            value={userForm.email}
                            onChange={handleUserFormChange}
                            className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm text-gray-700 mb-1">Role</label>
                            <select
                              name="role"
                              value={userForm.role}
                              onChange={handleUserFormChange}
                              className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="Owner" disabled={isITAdmin}>Owner</option>
                              <option value="IT Admin">IT Admin</option>
                            </select>
                          </div>
                          <div className="flex items-center gap-2 mt-6">
                            <input
                              id="otp_enabled"
                              type="checkbox"
                              name="otp_enabled"
                              checked={userForm.otp_enabled}
                              onChange={handleUserFormChange}
                              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                            <label htmlFor="otp_enabled" className="text-sm text-gray-700">OTP Enabled</label>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm text-gray-700 mb-1">Password</label>
                          <input
                            type="password"
                            name="password"
                            value={userForm.password}
                            onChange={handleUserFormChange}
                            placeholder={editingUserId ? 'Leave blank to keep unchanged' : 'Set initial password'}
                            className="w-full px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="mt-6 flex justify-end gap-3">
                        <button onClick={() => setShowUserModal(false)} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg">Cancel</button>
                        <button
                          onClick={saveUser}
                          disabled={isITAdmin && userForm.role === 'Owner'}
                          title={isITAdmin && userForm.role === 'Owner' ? 'IT Admin cannot create or update Owner' : ''}
                          className={`px-4 py-2 rounded-lg text-white ${isITAdmin && userForm.role === 'Owner' ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
                        >
                          {editingUserId ? 'Save Changes' : 'Add User'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {false && nav === "new_insights" && (<div />)}
          </div>

          {/* Footer */}
          <footer className="bg-white border-t border-gray-100 py-6 mt-8">
            <div className="text-center">
              <p className="text-sm text-gray-500">
                Developed by Team IT GYP - &copy; Get Your Prospect 2025 | All rights reserved
              </p>
            </div>
          </footer>
        </main>
      </div>
      
      {/* Import Data Modal */}
      <Modal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        aria-labelledby="import-data-modal-title"
        aria-describedby="import-data-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 700,
            bgcolor: 'white',
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            p: 4,
            maxHeight: '85vh',
            overflowY: 'auto',
            border: '1px solid #e5e7eb',
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, pb: 2, borderBottom: '1px solid #f3f4f6' }}>
            <Typography 
              id="import-data-modal-title" 
              variant="h5" 
              component="h2"
              sx={{ 
                fontWeight: 600, 
                color: '#111827',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <CloudUploadIcon sx={{ color: '#3b82f6', fontSize: 24 }} />
              Import {importType === 'inflow' ? 'Cash Inflows' : 'Cash Outflows'}
            </Typography>
            <IconButton 
              onClick={() => setShowImportModal(false)}
              sx={{ 
                color: '#6b7280',
                '&:hover': { 
                  backgroundColor: '#f3f4f6',
                  color: '#374151'
                }
              }}
            >
              <XMarkIcon width={20} height={20} />
            </IconButton>
          </Box>
          <Box sx={{ mt: 2 }}>
            {/* File Upload Section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
                Step 1: Upload CSV File
              </Typography>
              <Box sx={{ 
                mb: 3, 
                p: 3, 
                backgroundColor: '#f0f9ff', 
                border: '1px solid #bae6fd', 
                borderRadius: 2 
              }}>
                <Typography variant="body2" sx={{ color: '#0369a1', fontWeight: 500, mb: 1 }}>
                  Expected CSV Format for {importType === 'inflow' ? 'Cash Inflows' : 'Cash Outflows'}:
                </Typography>
                <Typography variant="caption" sx={{ color: '#0c4a6e', display: 'block', mb: 1 }}>
                  {importType === 'inflow' 
                    ? 'Headers: Date, Reflecting Month, Source, Amount in Rs, Bank, Reflecting Bank, Request for Inwards Remittance, Amount in $ (optional), Remark (optional)'
                    : 'Headers: Date, Reflecting Month, Expense, Amount in Rs, Subcategory, Category, Deducted From, Mode, Nature (optional), Vendor (optional), Remark (optional)'
                  }
                </Typography>
                <Typography variant="caption" sx={{ color: '#0c4a6e', display: 'block' }}>
                  Tip: Make sure your CSV has headers and dates are in DD/MM/YYYY or YYYY-MM-DD format
                </Typography>
              </Box>
              <Box
                sx={{
                  border: '2px dashed #d1d5db',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: '#f9fafb',
                  position: 'relative',
                  '&:hover': {
                    borderColor: '#3b82f6',
                    backgroundColor: '#f0f9ff'
                  }
                }}
              >
                <input
                  accept=".csv"
                  type="file"
                  onChange={handleImportFileChange}
                  style={{ 
                    display: 'block', 
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    opacity: 0,
                    cursor: 'pointer'
                  }}
                />
                <CloudUploadIcon sx={{ fontSize: 48, color: '#6b7280', mb: 2 }} />
                <Typography variant="body1" sx={{ color: '#374151', mb: 1 }}>
                  {importFile ? importFile.name : 'Click to select CSV file or drag and drop'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Supports .csv files only
                </Typography>
              </Box>
            </Box>

            {/* Error Display */}
            {csvPreview.error && (
              <Box sx={{ 
                mb: 3, 
                p: 2, 
                backgroundColor: '#fef2f2', 
                border: '1px solid #fecaca',
                borderRadius: 2,
                color: '#dc2626'
              }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Error: {csvPreview.error}
                </Typography>
              </Box>
            )}

                            {/* CSV Preview */}
                {csvPreview.data.length > 0 && !showImportSummary && (
                  <>
                    <Box sx={{ mb: 4 }}>
                      <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
                        Step 2: Preview Data ({csvPreview.data.length} rows found)
                      </Typography>
                      <Box sx={{ 
                        maxHeight: 400, 
                        overflow: 'auto', 
                        border: '1px solid #e5e7eb', 
                        borderRadius: 2,
                        backgroundColor: '#ffffff',
                        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                      }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f8fafc', position: 'sticky', top: 0 }}>
                              {Object.keys(csvPreview.data[0]).map((header) => (
                                <th key={header} style={{ 
                                  borderBottom: '2px solid #e2e8f0', 
                                  padding: '16px 12px', 
                                  textAlign: 'left',
                                  fontWeight: 700,
                                  color: '#1e293b',
                                  fontSize: '14px',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.05em'
                                }}>
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {csvPreview.data.slice(0, 8).map((row, idx) => (
                              <tr key={idx} style={{ 
                                backgroundColor: idx % 2 === 0 ? '#ffffff' : '#f8fafc',
                                '&:hover': { backgroundColor: '#f1f5f9' }
                              }}>
                                {Object.keys(csvPreview.data[0]).map((header) => (
                                  <td key={header} style={{ 
                                    borderBottom: '1px solid #f1f5f9', 
                                    padding: '14px 12px',
                                    fontSize: '14px',
                                    color: '#334155',
                                    fontFamily: 'monospace',
                                    maxWidth: '200px',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}>
                                    {row[header] || '-'}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {csvPreview.data.length > 8 && (
                          <Box sx={{ 
                            p: 3, 
                            textAlign: 'center', 
                            backgroundColor: '#f8fafc', 
                            borderTop: '1px solid #e2e8f0',
                            borderBottomRadius: 2
                          }}>
                            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                              Showing first 8 rows of {csvPreview.data.length} total rows
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', mt: 1 }}>
                              Blank rows and rows without amounts have been automatically filtered out
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>

                {/* Column Mapping */}
                <Box sx={{ mb: 4 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
                    Step 3: Map Columns
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: '#6b7280' }}>
                    Map your CSV columns to the required fields. Required fields are marked with <span style={{ color: '#dc2626', fontWeight: 600 }}>*</span>
                  </Typography>
                  
                  {/* Auto-mapping summary */}
                  {csvPreview.autoMapping && Object.keys(csvPreview.autoMapping).length > 0 && (
                    <Box sx={{ 
                      mb: 3, 
                      p: 2, 
                      backgroundColor: '#f0f9ff', 
                      border: '1px solid #bae6fd', 
                      borderRadius: 2 
                    }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0369a1', mb: 1 }}>
                        Auto-mapped {Object.keys(csvPreview.autoMapping).length} field(s) based on header similarity
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#0284c7' }}>
                        Review the mappings below and adjust if needed. Green indicators show high confidence matches.
                      </Typography>
                    </Box>
                  )}
                  
                  {/* Required Fields */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>
                      Required Fields
                    </Typography>
                    {getCurrentRequiredFields().map((field) => {
                      const confidence = csvPreview.confidence?.[field.key];
                      const isAutoMapped = csvPreview.autoMapping?.[field.key];
                      
                      return (
                        <Box key={field.key} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 180 }}>
                            <Typography sx={{ fontWeight: 600, color: '#1e293b' }}>
                              {field.label}<span style={{ color: '#dc2626', fontWeight: 700 }}>*</span>
                            </Typography>
                            {isAutoMapped && confidence && (
                              <Box sx={{ 
                                ml: 1, 
                                px: 1, 
                                py: 0.5, 
                                borderRadius: 1, 
                                backgroundColor: confidence >= 90 ? '#dcfce7' : confidence >= 80 ? '#fef3c7' : '#fecaca',
                                border: `1px solid ${confidence >= 90 ? '#16a34a' : confidence >= 80 ? '#f59e0b' : '#ef4444'}`
                              }}>
                                <Typography variant="caption" sx={{ 
                                  color: confidence >= 90 ? '#16a34a' : confidence >= 80 ? '#f59e0b' : '#ef4444',
                                  fontWeight: 600,
                                  fontSize: '10px'
                                }}>
                                  {confidence}% match
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <select
                            value={columnMapping[field.key] || ''}
                            onChange={e => handleMappingChange(field.key, e.target.value)}
                            style={{ 
                              padding: '10px 14px',
                              minWidth: 220,
                              border: `2px solid ${isAutoMapped && confidence >= 85 ? '#16a34a' : '#e2e8f0'}`,
                              borderRadius: '8px',
                              backgroundColor: '#ffffff',
                              color: '#1e293b',
                              fontSize: '14px',
                              fontWeight: 500,
                              outline: 'none',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <option value="">Select column</option>
                            {Object.keys(csvPreview.data[0] || {}).map((header) => (
                              <option key={header} value={header}>{header}</option>
                            ))}
                          </select>
                          {columnMapping[field.key] && (
                            <Typography variant="caption" sx={{ ml: 3, color: '#64748b', fontStyle: 'italic', fontWeight: 500 }}>
                              Sample: <span style={{ color: '#1e293b', fontFamily: 'monospace' }}>{csvPreview.data[0][columnMapping[field.key]]}</span>
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Optional Fields */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, color: '#64748b' }}>
                      Optional Fields
                    </Typography>
                    {getCurrentOptionalFields().map((field) => {
                      const confidence = csvPreview.confidence?.[field.key];
                      const isAutoMapped = csvPreview.autoMapping?.[field.key];
                      
                      return (
                        <Box key={field.key} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 180 }}>
                            <Typography sx={{ fontWeight: 500, color: '#64748b' }}>
                              {field.label}
                            </Typography>
                            {isAutoMapped && confidence && (
                              <Box sx={{ 
                                ml: 1, 
                                px: 1, 
                                py: 0.5, 
                                borderRadius: 1, 
                                backgroundColor: confidence >= 90 ? '#dcfce7' : confidence >= 80 ? '#fef3c7' : '#fecaca',
                                border: `1px solid ${confidence >= 90 ? '#16a34a' : confidence >= 80 ? '#f59e0b' : '#ef4444'}`
                              }}>
                                <Typography variant="caption" sx={{ 
                                  color: confidence >= 90 ? '#16a34a' : confidence >= 80 ? '#f59e0b' : '#ef4444',
                                  fontWeight: 600,
                                  fontSize: '10px'
                                }}>
                                  {confidence}% match
                                </Typography>
                              </Box>
                            )}
                          </Box>
                          <select
                            value={columnMapping[field.key] || ''}
                            onChange={e => handleMappingChange(field.key, e.target.value)}
                            style={{ 
                              padding: '10px 14px',
                              minWidth: 220,
                              border: `2px solid ${isAutoMapped && confidence >= 85 ? '#16a34a' : '#e2e8f0'}`,
                              borderRadius: '8px',
                              backgroundColor: '#ffffff',
                              color: '#64748b',
                              fontSize: '14px',
                              fontWeight: 500,
                              outline: 'none',
                              transition: 'all 0.2s ease'
                            }}
                          >
                            <option value="">Select column (optional)</option>
                            {Object.keys(csvPreview.data[0] || {}).map((header) => (
                              <option key={header} value={header}>{header}</option>
                            ))}
                          </select>
                          {columnMapping[field.key] && (
                            <Typography variant="caption" sx={{ ml: 3, color: '#64748b', fontStyle: 'italic', fontWeight: 500 }}>
                              Sample: <span style={{ color: '#1e293b', fontFamily: 'monospace' }}>{csvPreview.data[0][columnMapping[field.key]]}</span>
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </Box>

                  {/* Validation Errors */}
                  {validationErrors.length > 0 && (
                    <Box sx={{ 
                      mt: 4, 
                      p: 4, 
                      backgroundColor: '#fef2f2', 
                      border: '2px solid #fecaca',
                      borderRadius: 3,
                      boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                    }}>
                      <Typography variant="body1" sx={{ mb: 3, fontWeight: 700, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span style={{ fontSize: '18px' }}>WARNING</span>
                        Validation Errors Found ({validationErrors.length} total)
                      </Typography>
                      <Box component="ul" sx={{ m: 0, pl: 3, color: '#dc2626' }}>
                        {validationErrors.slice(0, 6).map((err, idx) => (
                          <li key={idx} style={{ 
                            marginBottom: '8px', 
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '1.5'
                          }}>
                            <strong>Row {err.row}:</strong> {err.errors.join(', ')}
                          </li>
                        ))}
                        {validationErrors.length > 6 && (
                          <li style={{ 
                            marginTop: '12px', 
                            fontStyle: 'italic',
                            fontWeight: 600,
                            color: '#b91c1c'
                          }}>
                            ...and {validationErrors.length - 6} more errors
                          </li>
                        )}
                      </Box>
                    </Box>
                  )}

                  {/* Continue Button */}
                  <Box sx={{ mt: 4, display: 'flex', gap: 3 }}>
                    <Button
                      variant="contained"
                      disabled={!allRequiredMapped}
                      onClick={handleValidateAndContinue}
                      sx={{
                        backgroundColor: allRequiredMapped ? '#3b82f6' : '#9ca3af',
                        color: 'white',
                        fontWeight: 700,
                        px: 4,
                        py: 2,
                        borderRadius: 3,
                        fontSize: '15px',
                        textTransform: 'none',
                        boxShadow: allRequiredMapped ? '0 4px 6px -1px rgba(59, 130, 246, 0.3)' : 'none',
                        '&:hover': {
                          backgroundColor: allRequiredMapped ? '#2563eb' : '#9ca3af',
                          boxShadow: allRequiredMapped ? '0 6px 8px -1px rgba(59, 130, 246, 0.4)' : 'none'
                        }
                      }}
                    >
                      Continue to Validation
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setShowImportModal(false)}
                      sx={{
                        borderColor: '#d1d5db',
                        color: '#374151',
                        fontWeight: 600,
                        px: 4,
                        py: 2,
                        borderRadius: 3,
                        fontSize: '15px',
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#9ca3af',
                          backgroundColor: '#f9fafb'
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              </>
            )}
            {/* Import Summary */}
            {showImportSummary && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: '#374151' }}>
                  Step 4: Ready to Import
                </Typography>
                <Box sx={{ 
                  p: 3, 
                  backgroundColor: '#f0f9ff', 
                  border: '1px solid #bae6fd',
                  borderRadius: 2,
                  mb: 3
                }}>
                  <Typography variant="body1" sx={{ mb: 1, color: '#0c4a6e', fontWeight: 500 }}>
                    SUCCESS: {validatedRows.length} rows validated and ready to import
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#0369a1' }}>
                    All required fields are mapped and data validation passed successfully.
                  </Typography>
                </Box>

                {importError && (
                  <Box sx={{ 
                    mb: 3, 
                    p: 3, 
                    backgroundColor: '#fef2f2', 
                    border: '1px solid #fecaca',
                    borderRadius: 2
                  }}>
                    <Typography variant="body1" sx={{ color: '#dc2626', fontWeight: 500 }}>
                      ERROR: Import Error: {importError}
                    </Typography>
                  </Box>
                )}

                {importResult ? (
                  <Box sx={{ mb: 3 }}>
                    <Box sx={{ 
                      p: 3, 
                      backgroundColor: importResult.failed === 0 ? '#f0fdf4' : '#fef3c7',
                      border: importResult.failed === 0 ? '1px solid #bbf7d0' : '1px solid #fde68a',
                      borderRadius: 2,
                      mb: 2
                    }}>
                      <Typography variant="body1" sx={{ mb: 1, fontWeight: 600, color: importResult.failed === 0 ? '#166534' : '#92400e' }}>
                        {importResult.failed === 0 ? 'Import Complete!' : 'Import Completed with Errors'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: importResult.failed === 0 ? '#166534' : '#92400e' }}>
                        {importResult.imported} rows imported successfully, {importResult.failed} rows failed.
                      </Typography>
                    </Box>

                    {importResult.errors && importResult.errors.length > 0 && (
                      <Box sx={{ 
                        p: 3, 
                        backgroundColor: '#fef2f2', 
                        border: '1px solid #fecaca',
                        borderRadius: 2,
                        mb: 2
                      }}>
                        <Typography variant="body2" sx={{ mb: 2, fontWeight: 600, color: '#dc2626' }}>
                          Failed Rows:
                        </Typography>
                        <Box component="ul" sx={{ m: 0, pl: 2, color: '#dc2626' }}>
                          {importResult.errors.slice(0, 5).map((err, idx) => (
                            <li key={idx} style={{ marginBottom: '4px' }}>
                              Row {err.row}: {err.error}
                            </li>
                          ))}
                          {importResult.errors.length > 5 && (
                            <li style={{ marginTop: '8px', fontStyle: 'italic' }}>
                              ...and {importResult.errors.length - 5} more errors
                            </li>
                          )}
                        </Box>
                      </Box>
                    )}

                    <Button 
                      variant="contained" 
                      onClick={() => setShowImportModal(false)}
                      sx={{
                        backgroundColor: '#10b981',
                        color: 'white',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: '#059669'
                        }
                      }}
                    >
                      Close
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      onClick={handleImport}
                      disabled={importing}
                      sx={{
                        backgroundColor: '#10b981',
                        color: 'white',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: '#059669'
                        },
                        '&:disabled': {
                          backgroundColor: '#9ca3af'
                        }
                      }}
                    >
                      {importing ? 'Importing...' : 'Import Data'}
                    </Button>
                    <Button 
                      variant="outlined" 
                      onClick={handleBackToMapping} 
                      disabled={importing}
                      sx={{
                        borderColor: '#d1d5db',
                        color: '#374151',
                        fontWeight: 500,
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                          borderColor: '#9ca3af',
                          backgroundColor: '#f9fafb'
                        }
                      }}
                    >
                      Back to Mapping
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Bulk Edit Modal */}
      <Modal open={showBulkEditModal} onClose={() => !bulkEditSaving && closeBulkEditModal()}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '92%',
            maxWidth: '820px',
            maxHeight: '88vh',
            bgcolor: 'white',
            borderRadius: 3,
            boxShadow: 24,
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
          }}
        >
          <div className="flex flex-col max-h-[88vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Bulk Edit {bulkEditType === 'outflow' ? 'Outflows' : 'Inflows'}
                </h3>
                <p className="text-sm text-gray-500 mt-1">
                  {bulkEditType === 'outflow' ? selectedOutflows.length : selectedInflows.length} item(s) selected. Tick only the fields you want to replace.
                </p>
              </div>
              <button
                type="button"
                onClick={closeBulkEditModal}
                disabled={bulkEditSaving}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleBulkEditSubmit} className="flex-1 overflow-y-auto">
              <div className="px-6 py-5 space-y-4">
                <div className="rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-700">
                  Checked fields replace existing values on all selected rows. Leave checked optional fields blank to clear them.
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {getBulkEditFields(bulkEditType).map((field) => renderBulkEditField(field))}
                </div>

                {bulkEditError && (
                  <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                    {bulkEditError}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-white">
                <button
                  type="button"
                  onClick={closeBulkEditModal}
                  disabled={bulkEditSaving}
                  className="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={bulkEditSaving}
                  className="px-5 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold disabled:bg-amber-300"
                >
                  {bulkEditSaving ? 'Updating...' : `Update Selected ${bulkEditType === 'outflow' ? 'Outflows' : 'Inflows'}`}
                </button>
              </div>
            </form>
          </div>
        </Box>
      </Modal>
      
      {/* Save Report Modal */}
      <Modal open={showSaveReportModal} onClose={()=> setShowSaveReportModal(false)}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 560, bgcolor: 'white', borderRadius: 3, boxShadow: 24, p: 4, border: '1px solid #e5e7eb' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827' }}>Save Report</Typography>
            <IconButton onClick={()=> setShowSaveReportModal(false)}><XMarkIcon width={20} height={20} /></IconButton>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input value={reportBuilder.name} onChange={(e)=> setReportBuilder(prev=> ({ ...prev, name: e.target.value }))} placeholder="Enter report name" className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400" />
              </div>
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select value={reportBuilder.type} onChange={(e)=> setReportBuilder(prev=> ({ ...prev, type: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900">
                  <option value="combined">Combined (Inflow + Outflow)</option>
                <option value="inflow">Inflow</option>
                <option value="outflow">Outflow</option>
                </select>
              </div>
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select value={reportBuilder.category || 'general'} onChange={(e)=> setReportBuilder(prev=> ({ ...prev, category: e.target.value }))} className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900">
                  <option value="general">General</option>
                  <option value="financial">Financial Analysis</option>
                  <option value="operational">Operational</option>
                  <option value="compliance">Compliance</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                  <div className="flex gap-2">
                <input value={newTag} onChange={(e)=> setNewTag(e.target.value)} placeholder="Add tag" className="flex-1 px-3 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 placeholder-gray-400" />
                <button onClick={addTag} type="button" className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm">Add</button>
                  </div>
              {reportBuilder.tags && reportBuilder.tags.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {reportBuilder.tags.map((t, i)=> (
                    <span key={`${t}-${i}`} className="inline-flex items-center px-2 py-1 rounded-full bg-blue-50 text-blue-700 text-xs">
                      {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button variant="outlined" onClick={()=> setShowSaveReportModal(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveReport} disabled={!reportBuilder.name.trim()} sx={{ textTransform: 'none' }}>Save</Button>
          </Box>
        </Box>
      </Modal>

      {/* Report Type Selection Modal */}
      <Modal open={showReportTypeSelection} onClose={() => setShowReportTypeSelection(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 520,
          bgcolor: 'white',
          borderRadius: 4,
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
          p: 4,
          border: '1px solid #f3f4f6',
          outline: 'none'
        }}>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-gray-900">Create New Report</h3>
            <button
              onClick={() => setShowReportTypeSelection(false)}
              className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-50 hover:text-gray-600 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          <p className="text-sm text-gray-500 mb-6">
            Select the transaction type for your new report. You will be redirected to the transaction management page to configure your rules and column filters.
          </p>

          <div className="grid grid-cols-2 gap-4">
            {/* Cash Inflow Card */}
            <button
              onClick={() => {
                setShowReportTypeSelection(false);
                setNav('inflow');
              }}
              className="group relative flex flex-col items-center justify-center p-6 bg-white border border-gray-100 hover:border-emerald-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 text-left hover:-translate-y-0.5 overflow-hidden"
            >
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-400 to-teal-500 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl mb-4 group-hover:scale-110 transition-transform">
                <ArrowTrendingUpIcon className="h-6 w-6" />
              </div>
              <span className="font-bold text-gray-900 text-base mb-1">Cash Inflow</span>
              <span className="text-xs text-gray-400 text-center">Track sources and bank account receipts</span>
            </button>

            {/* Cash Outflow Card */}
            <button
              onClick={() => {
                setShowReportTypeSelection(false);
                setNav('outflow');
              }}
              className="group relative flex flex-col items-center justify-center p-6 bg-white border border-gray-100 hover:border-blue-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 text-left hover:-translate-y-0.5 overflow-hidden"
            >
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              <div className="p-3 bg-blue-50 text-blue-600 rounded-xl mb-4 group-hover:scale-110 transition-transform">
                <ArrowTrendingDownIcon className="h-6 w-6" />
              </div>
              <span className="font-bold text-gray-900 text-base mb-1">Cash Outflow</span>
              <span className="text-xs text-gray-400 text-center">Monitor category, vendor and utility expenses</span>
            </button>
          </div>
        </Box>
      </Modal>

      {/* Mark as Paid Success Modal */}
      <Modal open={showMarkPaidModal} onClose={() => setShowMarkPaidModal(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 500,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Transaction Redirected</h3>
          </div>
          <p className="text-gray-600 mb-6">{markPaidMessage}</p>
          <div className="flex justify-end">
            <button
              onClick={() => setShowMarkPaidModal(false)}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
            >
              Got it
            </button>
          </div>
        </Box>
      </Modal>

      {/* Skip Confirmation Modal */}
      <Modal open={!!skipConfirm} onClose={() => { if (!skipBusy) setSkipConfirm(null); }}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 480,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
        }}>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Skip this due?</h3>
          </div>
          {skipConfirm && (
            <div className="text-gray-700 mb-6">
              <p className="mb-2">
                Skip <span className="font-semibold">{skipConfirm.name}</span> for <span className="font-semibold">{skipConfirm.next_due_date}</span>
                {skipConfirm.amount_rs ? <> (<span className="font-semibold">{Number(skipConfirm.amount_rs).toLocaleString()}</span>)</> : null}?
              </p>
              <p className="text-sm text-gray-500">No entry will be created and it will be hidden from Due / Upcoming.</p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              onClick={() => setSkipConfirm(null)}
              disabled={skipBusy}
              className="px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 rounded-lg font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={confirmSkipReminder}
              disabled={skipBusy}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium disabled:opacity-50"
            >
              {skipBusy ? 'Skipping…' : 'Skip'}
            </button>
          </div>
        </Box>
      </Modal>
      {/* Dashboard Metric Modal */}
      <Modal open={showMetricModal} onClose={() => setShowMetricModal(false)}>
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: '1200px',
          maxHeight: '80vh',
          bgcolor: 'background.paper',
          borderRadius: '16px',
          boxShadow: 24,
          p: 0,
          overflow: 'hidden'
        }}>
          <div className="bg-white rounded-2xl max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                  <ChartBarIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">{metricModalData.title}</h3>
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-gray-500">{metricModalData.data.length} transactions</p>
                    {metricModalData.type === 'total_outflow' || metricModalData.type === 'total_withdrawals' ? (
                      <p className="text-sm font-medium text-gray-700">
                        Total: {(metricModalData.totalInr || 0).toLocaleString()}
                      </p>
                    ) : (
                      <div className="flex items-center gap-3">
                        <p className="text-sm font-medium text-gray-700">
                          Total: {(metricModalData.totalInr || 0).toLocaleString()}
                        </p>
                        {metricModalData.totalUsd > 0 && (
                          <p className="text-sm font-medium text-blue-600">
                            ${(metricModalData.totalUsd || 0).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={() => setShowMetricModal(false)}
                className="p-2 hover:bg-gray-200 rounded-xl transition-colors"
              >
                <XMarkIcon className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
              {metricModalData.loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading transactions...</span>
                </div>
              ) : metricModalData.data.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ChartBarIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">No transactions found</h4>
                  <p className="text-gray-500">There are no transactions for this metric in the selected period.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Date</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Description</th>
                        <th className="text-right py-3 px-4 font-medium text-gray-700">Amount ()</th>
                        {(metricModalData.type === 'total_inflow' || metricModalData.type === 'sales_revenue' || metricModalData.type === 'other_income') && (
                          <>
                            <th className="text-right py-3 px-4 font-medium text-gray-700">Amount ($)</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Bank</th>
                            <th className="text-center py-3 px-4 font-medium text-gray-700">Inward</th>
                          </>
                        )}
                        {(metricModalData.type === 'total_outflow' || metricModalData.type === 'total_withdrawals') && (
                          <>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Category</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Sub-Category</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Mode</th>
                            <th className="text-left py-3 px-4 font-medium text-gray-700">Vendor</th>
                          </>
                        )}
                        <th className="text-left py-3 px-4 font-medium text-gray-700">Remark</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metricModalData.data.map((row, index) => (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm text-gray-900">{new Date(row.date).toLocaleDateString()}</td>
                          <td className="py-3 px-4 text-sm text-gray-900">{row.description}</td>
                          <td className="py-3 px-4 text-sm text-gray-900 text-right font-medium">{parseFloat(row.amount_rs || 0).toLocaleString()}</td>
                          {(metricModalData.type === 'total_inflow' || metricModalData.type === 'sales_revenue' || metricModalData.type === 'other_income') && (
                            <>
                              <td className="py-3 px-4 text-sm text-gray-900 text-right font-medium">${parseFloat(row.amount_usd || 0).toLocaleString()}</td>
                              <td className="py-3 px-4 text-sm text-gray-600">{row.bank || '-'}</td>
                              <td className="py-3 px-4 text-center">
                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                  row.request_inward === 'Yes' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {row.request_inward}
                                </span>
                              </td>
                            </>
                          )}
                          {(metricModalData.type === 'total_outflow' || metricModalData.type === 'total_withdrawals') && (
                            <>
                              <td className="py-3 px-4 text-sm text-gray-600">{row.category || '-'}</td>
                              <td className="py-3 px-4 text-sm text-gray-600">{row.sub_category || '-'}</td>
                              <td className="py-3 px-4 text-sm text-gray-600">{row.mode || '-'}</td>
                              <td className="py-3 px-4 text-sm text-gray-600">{row.vendor || '-'}</td>
                            </>
                          )}
                          <td className="py-3 px-4 text-sm text-gray-600 max-w-xs truncate">{row.remark || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  Total: {metricModalData.data.length} transactions
                </div>
                <button
                  onClick={() => setShowMetricModal(false)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </Box>
      </Modal>

      {/* Inflow Filter Popup */}
      <FilterPopup
        show={showInflowFilterPopup}
        onClose={() => setShowInflowFilterPopup(false)}
        title="Inflow Filters"
        filterBuilder={inflowFilterBuilder}
        setFilterBuilder={setInflowFilterBuilder}
        filterRules={inflowFilterRules}
        setFilterRules={setInflowFilterRules}
        onAddRule={addInflowFilterRule}
        onRemoveRule={removeInflowFilterRule}
        onApply={() => {
          // Apply filters by refreshing data with filter parameters
          refreshAllData();
          setShowInflowFilterPopup(false);
        }}
        onClearAll={clearInflowFilters}
        fieldOptions={[
          { value: 'reflecting_month', label: 'Month' },
          { value: 'source', label: 'Source' },
          { value: 'bank', label: 'Bank' },
          { value: 'reflecting_bank', label: 'Reflecting Bank' },
          { value: 'amount_rs', label: 'Amount ()' },
          { value: 'amount_usd', label: 'Amount ($)' },
          { value: 'request_inward', label: 'Request Inward' },
          { value: 'remark', label: 'Remarks' }
        ]}
        dropdownData={{
          financialYearMonths,
          inflowSources: inflowFilterDropdownData.sources.map(s => ({ name: s })),
          bankAccounts: inflowFilterDropdownData.banks.map(b => ({ name: b })),
          reflectingBanks: inflowFilterDropdownData.reflectingBanks.map(rb => ({ name: rb })),
          reflectingMonths: inflowFilterDropdownData.reflectingMonths
        }}
        currentData={[]}
      />

      {/* Outflow Filter Popup */}
      <FilterPopup
        show={showOutflowFilterPopup}
        onClose={() => setShowOutflowFilterPopup(false)}
        title="Outflow Filters"
        filterBuilder={outflowFilterBuilder}
        setFilterBuilder={setOutflowFilterBuilder}
        filterRules={outflowFilterRules}
        setFilterRules={setOutflowFilterRules}
        onAddRule={addOutflowFilterRule}
        onRemoveRule={removeOutflowFilterRule}
        onApply={() => {
          // Apply filters by refreshing data with filter parameters
          refreshAllData();
          setShowOutflowFilterPopup(false);
        }}
        onClearAll={clearOutflowFilters}
        fieldOptions={[
          { value: 'reflecting_month', label: 'Month' },
          { value: 'expense', label: 'Expense' },
          { value: 'category', label: 'Category' },
          { value: 'sub_category', label: 'Sub Category' },
          { value: 'mode', label: 'Mode' },
          { value: 'vendor', label: 'Vendor' },
          { value: 'amount_rs', label: 'Amount ()' },
          { value: 'remark', label: 'Remark' }
        ]}
        dropdownData={{
          financialYearMonths,
          outflowCategories: outflowFilterDropdownData.categories.map(c => ({ name: c })),
          outflowSubcategories: outflowFilterDropdownData.subCategories.map(sc => ({ name: sc })),
          expenses: outflowFilterDropdownData.expenses.map(e => ({ name: e })),
          modes: outflowFilterDropdownData.modes.map(m => ({ name: m })),
          vendors: outflowFilterDropdownData.vendors.map(v => ({ name: v })),
          reflectingMonths: outflowFilterDropdownData.reflectingMonths
        }}
        currentData={[]}
      />


      {/* ===== Create Report Modal ===== */}
      {showReportCreateModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowReportCreateModal(false)}
          />
          {/* Modal Panel */}
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            {/* Header */}
            <div className={`px-6 pt-6 pb-4 border-b border-gray-100 ${reportCreationType === 'inflow' ? 'bg-gradient-to-r from-emerald-50 to-blue-50' : 'bg-gradient-to-r from-blue-50 to-indigo-50'}`}>
              <div className="flex items-center gap-3 mb-1">
                <div className={`p-2 rounded-xl ${reportCreationType === 'inflow' ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'}`}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">{editingReport ? 'Edit Dashboard Card' : 'Create Dashboard Card'}</h3>
                  <p className="text-xs text-gray-500">{reportCreationType === 'inflow' ? 'Inflow' : 'Outflow'} report • Active filters will be saved</p>
                </div>
              </div>
            </div>

            {/* Active Filters Preview */}
            <div className="px-6 py-4 border-b border-gray-50">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Filters being saved</p>
              <div className="flex flex-wrap gap-1.5">
                {(() => {
                  const filters = reportCreationType === 'inflow'
                    ? (inflowColumnFilters.filters || {})
                    : (outflowColumnFilters.filters || {});
                  const keys = Object.keys(filters).filter(
                    k => filters[k] !== undefined && filters[k] !== '' && !(Array.isArray(filters[k]) && filters[k].length === 0)
                  );
                  if (keys.length === 0) return (
                    <span className="text-xs text-gray-400 italic">No column filters active</span>
                  );
                  return keys.map(k => (
                    <span key={k} className="inline-flex items-center gap-1 bg-blue-50 border border-blue-100 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full">
                      <span className="font-bold capitalize">{k.replace(/_/g, ' ')}</span>
                    </span>
                  ));
                })()}
              </div>
              <p className="text-[10px] text-gray-400 mt-2 italic">Active column filters and the selected period ({timePeriod}) will be baked into the card.</p>
            </div>

            {/* Name Input */}
            <div className="px-6 py-5">
              <label className="block text-sm font-bold text-gray-700 mb-2">Report Card Name</label>
              <input
                type="text"
                value={newReportName}
                onChange={e => setNewReportName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreateQuickReport()}
                placeholder={reportCreationType === 'inflow' ? 'e.g. Bouncely Inflows' : 'e.g. Food & Water Expenses'}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm font-medium text-gray-900 placeholder-gray-400 bg-gray-50"
                autoFocus
              />
            </div>

            {/* Actions */}
            <div className="px-6 pb-6 flex gap-3">
              <button
                onClick={() => setShowReportCreateModal(false)}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateQuickReport}
                disabled={isCreatingReport || !newReportName.trim()}
                className={`flex-1 py-2.5 rounded-xl text-white text-sm font-bold transition-all ${
                  isCreatingReport || !newReportName.trim()
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-md shadow-blue-200 hover:shadow-lg'
                }`}
              >
                {isCreatingReport ? (
                  <span className="flex items-center justify-center gap-2">
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    Saving...
                  </span>
                ) : editingReport ? 'Save Changes' : '✦ Create Card'}
              </button>
            </div>
          </div>
        </div>
      )}



    </div>
  </div>
  );
}

export default App;

